import Phaser, { CANVAS, Scene } from 'phaser'
import { storeToRefs } from 'pinia'
import { watch, type WatchStopHandle } from 'vue'
import {
  ActionType,
  CardPosition,
  EnginePhase,
  EventType,
  getCardKind,
  getEquipConfig,
  getFieldSpellConfig,
  CardKind,
  asCardFoil,
  parseResponseMode,
  type CardData,
  type CardFoilId,
  type CardInstance,
  type ChainLinkAddedEvent,
  type ChainLinkResolvingEvent,
  type GameEvent,
  type GameEventsPayload,
  type GameOverPayload,
  isOpeningTurn,
  type MatchState,
  type PendingTargets,
  type PlayerAction,
  type Seat,
  type GameStatePayload,
  STARTING_HAND_SIZE,
  lingeringTurnsFor,
  engineEffects,
  attackLockCode,
  cardAttacksDirectly,
  magnetismTargets,
  coinIgnitionOf,
  tributeIgnitionOf,
  tributeCandidateUids,
  CHEERFUL_COFFIN,
  cannotNormalSummon,
  COCOON_OF_EVOLUTION,
  ELEGANT_EGOTIST,
  evolveTribute,
  faceUpPetitMoths,
  GATE_GUARDIAN,
  gateTributeSet,
  handIgnitionOf,
  HARPIE_LADY,
  HARPIE_LADY_SISTERS,
  isGatePiece,
  isGateTributeSet,
  LARVAE_MOTH,
  mothEvolveTurn,
  PETIT_MOTH,
  IMMORTAL_OF_THUNDER,
  MASKED_SORCERER,
  ROBBIN_GOBLIN,
  SWORD_OF_DEEP_SEATED,
  THE_BISTRO_BUTCHER,
  WHITE_MAGICAL_HAT,
  EngineEffectTrigger,
  registerEngineEffects,
  effectiveStatBonus,
  soulExchangeTributeUids,
} from '@shared/index'
import { track, trackNow } from '../../analytics/track'
import { deckSearchPrompt } from '../deckSearchPrompt'
import lobData from '../../assets/lob-cards.json'
import {
  CARD_BACK_URL,
  getLocalCardImageUrl,
  setDuelSleeveUrlOverride,
} from '../../utils/cardImages'
import {
  forgetCardImage,
  loadCardImage,
  peekCardImage,
} from '../../utils/cardImageCache'
import { addPhaserCardTexture } from '../phaserCardTexture'
import { getAvailableFusions } from '../effects/FusionRegistry'
import type { GameClient } from '../net/GameClient'
import { DuelSfx } from '../audio/DuelSfx'
import {
  getDuelVfx,
  type DuelVfxFlyCard,
  type DuelVfxKind,
} from '../vfx/DuelVfx'
import type { ChainStackView } from '../vfx/ChainStack'
import {
  addActionGlow,
  addAttackSword,
  addEnemyTargetCue,
  addEquipLinkIcon,
  addTargetGlow,
  addTributeSpiral,
  ensureMarkerTextures,
  type TargetGlowKind,
} from '../vfx/BoardMarkers'
import {
  cancelDuelCoinToss,
  coinPauseBudgetMs,
  playDuelCoinToss,
  warmDuelCoin,
  type CoinDialogClose,
} from '../vfx/CoinToss'
import { duelCoinArt } from '../vfx/duelCoin'
import { AnimationSpeed } from '../AnimationSpeed'
import { englishName, localizedName } from '../../i18n/localizeCard'
import {
  eraseNameUrl,
  foilNameKind,
  foilNameParts,
  GHOST_ART_FILTER,
  type FoilNameKind,
} from '../../utils/foilName'
import { t } from '../../i18n/t'
import { localizeDuelMessage, localizeEngineError } from '../../i18n/engineError'
import { isWaitingOnOpponent } from '../duelStall'
import { useDuelPrefsStore } from '../../stores/duelPrefs'
import {
  gamematById,
  isGamematId,
  matTextureKey,
  pickRandomMatId,
  resolveDuelMatId,
} from '../gamemats'
import {
  isSleeveId,
  pickRandomSleeveId,
  resolveDuelSleeveId,
  sleeveTextureKey,
  sleeveUrl,
} from '../sleeves'
import {
  applyCardRarityFx,
  ensureRarityFoilTextures,
  FOIL_GLOW,
} from '../cardRarityFx'
import { DUEL_COMPACT_MQ, isDuelCompactNow, needsOwnSetPeekNow } from '../../ui/compactLayout'
import type { DuelToastTone } from '../../components/DuelPromptOverlay.vue'

const TOAST_TONE_COLOR: Record<DuelToastTone, string> = {
  guide: '#fde68a',
  warn: '#f87171',
  info: '#e2e8f0',
}

const BASE_CARD_W = 64
const BASE_CARD_H = 92
const BASE_ZONE_GAP = 12
const BASE_SLOT_GAP = 28
const BASE_HAND_SLOT_GAP = 8
const BASE_MID_GAP = 88

let CARD_W = BASE_CARD_W
let CARD_H = BASE_CARD_H
let ZONE_GAP = BASE_ZONE_GAP
let SLOT_GAP = BASE_SLOT_GAP
let HAND_SLOT_GAP = BASE_HAND_SLOT_GAP
let MID_GAP = BASE_MID_GAP
/** Hand sprites only; field cards stay at 1. */
let HAND_SCALE = 1

function resetBoardMetrics(): void {
  CARD_W = BASE_CARD_W
  CARD_H = BASE_CARD_H
  ZONE_GAP = BASE_ZONE_GAP
  SLOT_GAP = BASE_SLOT_GAP
  HAND_SLOT_GAP = BASE_HAND_SLOT_GAP
  MID_GAP = BASE_MID_GAP
  HAND_SCALE = 1
}

/**
 * Phone landscape: grow cards up to the vertical budget. Keep SLOT_GAP so the
 * five-zone grid stays a tight field instead of stretching across the canvas.
 */
function syncBoardMetrics(_worldW: number, worldH: number): void {
  if (!isDuelCompactNow()) {
    resetBoardMetrics()
    return
  }
  MID_GAP = 40
  ZONE_GAP = 4
  HAND_SLOT_GAP = 4
  HAND_SCALE = 1.12
  const maxH = (worldH - MID_GAP - 5 * ZONE_GAP - 16) / 6
  CARD_H = Math.round(Math.max(BASE_CARD_H, Math.min(maxH, 124)))
  CARD_W = Math.round((CARD_H * BASE_CARD_W) / BASE_CARD_H)
  SLOT_GAP = BASE_SLOT_GAP
}
// Deck shuffle: lift, split, riffle, bridge, drop (anime / table riffle).
const DECK_SHUFFLE_LIFT_MS = 280
const DECK_SHUFFLE_SPLIT_MS = 340
const DECK_SHUFFLE_RIFFLE_MS = 800
const DECK_SHUFFLE_BRIDGE_MS = 400
const DECK_SHUFFLE_DROP_MS = 380
const DECK_SHUFFLE_LAYERS = 4
const DECK_SHUFFLE_RIFFLE_CARDS = 10
const DECK_SHUFFLE_SCALE = 1.22
// Outward stack layers for Deck / Extra / GY / Banished (top card stays centered).
const PILE_LAYER_STEP = 2
const PILE_MAX_LAYERS = 12
/** Outer corners of each playmat half. Inner seam stays square. */
const MAT_CORNER_RADIUS = 22
/**
 * Pause after each AI action so the resolved board stays on screen.
 * Base duration at Anim: Slow; `thinkDelayMs` divides by the player's speed.
 */
const AI_ACTION_GAP_MS = 1000
/** How long the phase strip holds M1/BP/EP during an AI turn (speed 1). */
const AI_PHASE_DWELL_MS = 450
/** Extra beat after an AI attack before the next phase tick (speed 1). */
const AI_POST_COMBAT_MS = 250
/** Deck → hand flight. Multi-draw effects still play this once per card. */
const DRAW_FLY_MS = 520
const DRAW_SETTLE_MS = 90
/** Sit on the pile so the mesh is seen on the Deck before it lifts. */
const DRAW_HOLD_MS = 90
/** Gap after a draw lands before the next card may leave the deck. */
const DRAW_BEAT_MS = 80
/**
 * Minimal motion: one short beat so a multi-draw still lands one card at a
 * time, without waiting on a 3D mesh.
 */
const MINIMAL_BEAT_MS = 120
/**
 * Compact only: slide the 3D draw mesh vs the Deck sprite (world pixels on
 * the 1024×768 board). Negative X = left (toward the field). Negative Y = up.
 * Tune these; desktop is unchanged (nudge is skipped).
 */
const DRAW_START_NUDGE_X = -90
const DRAW_START_NUDGE_Y = -75
/** Compact swipe from an ATK monster must pass this to count as an attack. */
const SWIPE_ATTACK_PX = 28
/** HTML activation overlay in OnlineDuel.vue — keep Phaser in lockstep. */
const SPELL_OVERLAY_MS = 1200
/** White destello on the activating card (matches DuelVfx starFlash life). */
const EFFECT_FLASH_MS = 480
/** Tornado hold over a tribute before its soul leaves for the GY. */
const TRIBUTE_TORNADO_MS = 900
/** Shatter hold + shard scatter (unscaled during AI playback). */
const DESTROY_SHATTER_MS = 500
/** Soul wisps field → GY after a shatter. */
const DESTROY_SOUL_MS = 540
/** Start the soul while the last shards are still fading. */
const DESTROY_SOUL_OVERLAP_MS = 140
/** "Chain" banner when a second link joins. Cards gather as it leaves. */
const CHAIN_BANNER_MS = 950
/** Field slot → pile, and pile → field slot when a link resolves. */
const CHAIN_FLY_MS = 420
/** Gap between cards that join the pile in the same beat. */
const CHAIN_STAGGER_MS = 160
/** The resolving link steps forward and pulses before its effect plays. */
const CHAIN_FOCUS_MS = 560
/** Pile card size vs a board card, and the resolving link's step forward. */
const CHAIN_STACK_SCALE = 1.15
const CHAIN_FOCUS_SCALE = 1.55

/** Screen position of a slot (scale is always 1 on the flat board). */
type SlotPose = { x: number; y: number; scale: number }

/** Field slot a resolving card occupies while its overlay / effect plays. */
type SlotLand = SlotPose & { cardId?: string }

/** Pre-commit location a summoned card flies from (captured when the event is queued). */
type SummonOrigin = SlotPose & {
  from: 'hand' | 'graveyard' | 'extraDeck' | 'banished' | 'field' | 'deck'
}

// Build a quick id -> CardData lookup so the renderer can show names/stats.
const CARD_INDEX: Map<string, CardData> = (() => {
  const map = new Map<string, CardData>()
  const data = (lobData as any).data as any[]
  for (const c of data) map.set(String(c.id), c as CardData)
  return map
})()

const TOUCH_HIT_PAD = 14
/** Compact landscape camera band: hand, your field, or the opponent. */
export type FocusBand = 'hand' | 'mine' | 'opp'

/** Which half of the board a click-target prompt is asking about. */
export type DuelPickSide = 'opp' | 'mine' | 'both'

/** Vue LP HUD: ticker, hit/heal tint, optional floating delta. */
export type CompactLpTick = {
  mine: boolean
  value: number
  lit: 'hit' | 'heal' | false
  delta?: number
}
/** Phaser camera zoom when focusing a compact band (~2 rows fill the FIT view). */
const FOCUS_CAM_MS = 240
/** Beat looking at a play before the camera returns to the home band. */
const FOCUS_HOME_HOLD_MS = 480
/** Extra beat on the destination hand after a bounce (Wall of Illusion). */
const RETURN_HAND_HOLD_MS = 720
const FOCUS_ZOOM_MIN = 1.35
const FOCUS_ZOOM_MAX = 2.5
/** Visual top → bottom of the board (arrow up walks toward the opponent). */
const FOCUS_BAND_ORDER: FocusBand[] = ['opp', 'mine', 'hand']

function cardHitRect(): Phaser.Geom.Rectangle {
  const pad = isDuelCompactNow() ? TOUCH_HIT_PAD : 0
  return new Phaser.Geom.Rectangle(
    -CARD_W / 2 - pad,
    -CARD_H / 2 - pad,
    CARD_W + pad * 2,
    CARD_H + pad * 2,
  )
}

/**
 * Spell/Trap cards stand upright, so the touch pad reaches the next slot.
 * A filled neighbor stops the box at the midpoint. The later sprite used to
 * keep that whole edge, and a tap on Robbin' Goblin could land on Waboku.
 */
function spellTrapHitRect(openLeft = true, openRight = true): Phaser.Geom.Rectangle {
  const pad = isDuelCompactNow() ? TOUCH_HIT_PAD : 0
  const limit = (CARD_W + SLOT_GAP) / 2
  const want = CARD_W / 2 + pad
  const left = openLeft ? want : Math.min(want, limit)
  const right = openRight ? want : Math.min(want, limit)
  return new Phaser.Geom.Rectangle(
    -left,
    -CARD_H / 2 - pad,
    left + right,
    CARD_H + pad * 2,
  )
}

/**
 * Defense cards lie sideways and are wider than the slot, so each one
 * covers the inner edge of its neighbor. The container stays upright so
 * the hit box matches the art. A filled neighbor stops the box at the
 * midpoint: the later sprite used to keep that whole overlap, and the
 * covered monster could not be chosen (Horn of Heaven's Tribute).
 */
function defenseHitRect(openLeft = true, openRight = true): Phaser.Geom.Rectangle {
  const pad = isDuelCompactNow() ? TOUCH_HIT_PAD : 0
  const limit = (CARD_W + SLOT_GAP) / 2
  const want = CARD_H / 2 + pad
  const left = openLeft ? want : Math.min(want, limit)
  const right = openRight ? want : Math.min(want, limit)
  return new Phaser.Geom.Rectangle(
    -left,
    -CARD_W / 2 - pad,
    left + right,
    CARD_W + pad * 2,
  )
}

/**
 * The blue halo is drawn past the card. A click there used to fall through
 * to the board. Sideways slop only extends into an empty neighbor slot, so
 * two Defense monsters side by side do not steal each other's click.
 */
function targetHaloRect(
  defense: boolean,
  openLeft: boolean,
  openRight: boolean,
): Phaser.Geom.Rectangle {
  const base = defense ? defenseHitRect(openLeft, openRight) : cardHitRect()
  const wantX = defense ? 18 : 12
  const wantY = defense ? 16 : 10
  const haveX = base.width / 2 - (defense ? CARD_H : CARD_W) / 2
  const haveY = base.height / 2 - (defense ? CARD_W : CARD_H) / 2
  const extraX = Math.max(0, wantX - haveX)
  const padY = Math.max(0, wantY - haveY)
  const padLeft = openLeft ? extraX : 0
  const padRight = openRight ? extraX : 0
  return new Phaser.Geom.Rectangle(
    base.x - padLeft,
    base.y - padY,
    base.width + padLeft + padRight,
    base.height + padY * 2,
  )
}

/**
 * Hand cards sit a few pixels apart. A full horizontal pad overlaps the next
 * card, and `setTopOnly` then opens that neighbor. Vertical pad stays.
 */
function handHitRect(): Phaser.Geom.Rectangle {
  const padY = isDuelCompactNow() ? TOUCH_HIT_PAD : 0
  const padX = isDuelCompactNow() ? HAND_SLOT_GAP / (2 * HAND_SCALE) : 0
  return new Phaser.Geom.Rectangle(
    -CARD_W / 2 - padX,
    -CARD_H / 2 - padY,
    CARD_W + padX * 2,
    CARD_H + padY * 2,
  )
}

function textureKey(cardId: string): string {
  return `net_card_${useDuelPrefsStore().cardArtRegion}_${cardId}`
}

type ClickMode = 'idle' | 'selectAttackTarget' | 'selectSpellTarget' | 'selectTribute' | 'selectCocoonHost'

/** Compact drag: own ATK monster toward an opponent monster or empty half. */
type SwipeAttack = {
  attacker: CardInstance
  startX: number
  startY: number
  dragging: boolean
  line: Phaser.GameObjects.Graphics | null
}

interface PendingTribute {
  cardUid: string
  zoneIndex: number
  position: CardPosition
  faceDown: boolean
  count: number
  /** Gate Guardian: the three clicks must be one Sanga, one Kazejin and one Suijin. */
  gate?: boolean
}

/**
 * One stage of a multi-target selection: pick `count` uids out of `uids`. Cards that
 * need targets from both sides of the field (Two-Pronged Attack) use one step per
 * side, so the player is always told whose monsters they are choosing.
 */
interface TargetStep {
  prompt: string
  uids: string[]
  count: number
}

/** Screen-space anchor for the Vue field/hand action menu. */
export interface FieldMenuPayload {
  labels: string[]
  x: number
  y: number
  /** True when the menu should open below the card (opponent half). */
  below: boolean
  /** Place the menu to the side so it never covers the other zone in the column. */
  side?: 'left' | 'right'
}

/** One row in the Vue list picker. `owner` splits either-graveyard lists. */
export interface CardPickerListItem {
  uid: string
  cardId: string
  note?: string
  foil?: string
  /** Graveyard this card comes from, relative to the local player. */
  owner?: 'me' | 'opp'
}

/** Vue list picker (Monster Reborn, Polymerization, Gravedigger, …). */
export interface CardPickerRequest {
  title: string
  items: CardPickerListItem[]
  min: number
  max: number
  /** Review overlay (Stern Mystic / peek): no pick, dismiss with Close. */
  viewOnly?: boolean
  /** Card whose effect opened this picker (Last Will, Reborn, …). */
  source?: { cardId: string; foil?: string }
  /** Extra line under the title (Monster Reborn: bounce returns to the owner). */
  hint?: string
}

/** A field card the snapshot has and the drawn board does not. */
export interface BoardGap {
  card: string
  zone: string
  face: 'up' | 'down'
  side: 'me' | 'opp'
}

interface SharpCardNodes {
  art: HTMLImageElement
  stats: HTMLDivElement
  name: HTMLDivElement
  namePlate: HTMLSpanElement
  nameLine: HTMLSpanElement
}

/**
 * Event-driven PvP/vs-AI renderer. Holds NO rules: it renders the authoritative
 * MatchState snapshot, plays light animations for incoming GameEvents, and sends
 * PlayerAction intents through the injected GameClient.
 */
export class NetworkDuelScene extends Scene {
  private client!: GameClient
  private mySeat: Seat = 0
  private state: MatchState | null = null

  private boardLayer!: Phaser.GameObjects.Container
  private matLayer!: Phaser.GameObjects.Container
  private matMaskGfx: Phaser.GameObjects.Graphics | null = null
  private matImage: Phaser.GameObjects.Image | null = null
  private oppMatImage: Phaser.GameObjects.Image | null = null
  private resolvedMatId = ''
  private resolvedOppMatId = ''
  private matLoadGen = 0
  private oppMatLoadGen = 0
  private resolvedSleeveId = ''
  private resolvedOppSleeveId = ''
  /** Sleeve actually on the board. Stays until the next texture has loaded. */
  private shownSleeveId = ''
  private sleeveLoadGen = 0
  private uiLayer!: Phaser.GameObjects.Container
  private menuLayer!: Phaser.GameObjects.Container
  private displayedMyLp = 8000
  private displayedOppLp = 8000
  private lpTweenMine: Phaser.Tweens.Tween | null = null
  private lpTweenOpp: Phaser.Tweens.Tween | null = null
  private toastText!: Phaser.GameObjects.Text
  private matStop: WatchStopHandle | null = null
  private sleeveStop: WatchStopHandle | null = null
  private prefsStop: WatchStopHandle | null = null
  private responseModeStop: WatchStopHandle | null = null
  private foilSweepStop: WatchStopHandle | null = null
  private compactMql: MediaQueryList | null = null
  private onCompactMql: (() => void) | null = null
  private boardTapZone: Phaser.GameObjects.Zone | null = null
  private swipeAttack: SwipeAttack | null = null
  private currentFocusBand: FocusBand = 'hand'
  private lastAutoFocusKey = ''
  /** Paid Ultimate Offering window already panned to the hand. */
  private offeringHandFocusKey = ''
  private homeReturnEvent: Phaser.Time.TimerEvent | null = null
  /** `cam.preRender` once per Phaser frame while a 3D flight is tracking. */
  private cameraViewFrame = -1
  private cameraViewCached = { x: 0, y: 0, width: 1, height: 1 }
  private focusBandCb: ((band: FocusBand) => void) | null = null
  /** 0–1 down the canvas: top of our hand, so Vue can sit the wait line on it. */
  private handWaitCb: ((top: number | null) => void) | null = null
  private lpPunchCb: ((mine: boolean, durationMs?: number) => void) | null = null
  private lpHudCb: ((tick: CompactLpTick) => void) | null = null
  private screenShakeCb: ((durationMs: number) => void) | null = null

  private clickMode: ClickMode = 'idle'
  private pendingAttacker: string | null = null
  /** Set when the engine rejects the action just sent, before `send` returns. */
  private actionRejected = false
  // Legal field uids while picking a spell/trap target (glow + click).
  private pendingTargets: string[] = []
  /** Stern Mystic / Telescope / Spy: show after the board commits. */
  private pendingPeek: {
    cards: { uid: string; cardId: string }[]
    reveal?: boolean
    search?: boolean
  } | null = null
  /** Engine REQUEST_TARGETS window currently shown; null when none. */
  private engineTargetRequestId: string | null = null
  private chainOrderRequestId: string | null = null
  /** True while the chain-order list is the picker on screen. */
  private chainOrderPickerLive = false
  private flipListRequestId: string | null = null
  /** Flip target already sent for this request. A second tap must not resend. */
  private answeredFlipId: string | null = null
  /** Flip-effect field pick already panned. A new request id pans again. */
  private flipFieldFocusId: string | null = null
  /** How that window is on screen. Null when the click mode was lost. */
  private engineTargetUi: 'field' | 'list' | null = null
  /**
   * Spell/trap whose activation overlay already played. Cleared when that
   * activation's EFFECT_RESOLVED arrives, so a later ignition (Ultimate
   * Offering) or a delayed trigger (Last Will) can still animate.
   */
  private lastActivationUid: string | null = null
  // Multi-target selection in progress (Two-Pronged Attack: two of yours, one of theirs).
  private targetSteps: TargetStep[] = []
  private targetStepIndex = 0
  private targetPicked: string[] = []
  private targetFinish: ((uids: string[]) => void) | null = null
  // Manual tribute selection (level 5+ summons with more monsters than needed).
  private pendingTribute: PendingTribute | null = null
  private tributeSelected: string[] = []
  /** Main Phase this manual tribute or Cocoon pick opened in. */
  private tributePickAt: { phase: EnginePhase; turn: number } | null = null
  /** Cocoon of Evolution waiting for a Petit Moth click. */
  private pendingCocoonUid: string | null = null
  // Cards the player has picked to discard for the end-of-turn hand-size limit.
  private discardSelected: string[] = []
  // Screen position of the last clicked card, used to anchor the action menu.
  private menuAnchor: SlotPose | null = null
  // Monotonic counter so a deferred (post-flip) render is skipped if a newer
  // snapshot arrives first.
  private renderSeq = 0
  /** Coalesced redraw after a card JPG lands. Must not close the action menu. */
  private artRenderQueued = false
  private artRenderWaitStart = 0
  private artRenderTimer: ReturnType<typeof setTimeout> | null = null
  /** `performance.now()` when Phaser input was last muted. */
  private inputMutedAt = 0
  // True while the drawn board is an animation-frozen snapshot that the server has
  // already moved past. Input is ignored until the real snapshot lands, otherwise a
  // click would send the uid of a card that is no longer where it is drawn.
  private staleBoard = false
  /**
   * `this.time.now` when the playback watchdog should have unlocked the board.
   * A later hand tap past this asks for a snapshot instead of staying deaf.
   */
  private playbackUnlockAt = 0
  /**
   * `performance.now()` deadline for the same watchdog. Phaser's clock does
   * not move while the scene is paused, so a tap cannot use `time.now` to
   * notice the board has been deaf for the whole turn.
   */
  private playbackDeafUntil = 0
  /**
   * Wall-clock twin of the Phaser watchdog. The scene clock pauses for the
   * coin dialog, and a hidden tab stops frames entirely, so a lethal hit
   * used to sit on screen with game over queued behind it. While frames are
   * still landing, this timer waits: Phaser clamps a slow frame to one
   * slice, and cutting on real time was skipping summons that were still
   * in flight.
   */
  private playbackWallTimer: ReturnType<typeof setTimeout> | null = null
  private playbackWallDeadline = 0
  private playbackWallHoldStart = 0
  private playbackWallSeq = 0
  private playbackWallUnlock: (() => void) | null = null
  /** Game-time budget for the playback the wall is guarding. */
  private playbackSafetyTimer: { elapsed: number; delay: number } | null = null
  /** `game.loop.frame` last time we saw the scene take a step. */
  private playbackFrameSeen = -1
  /** `performance.now()` when that step was noticed, or when the stall grace started. */
  private playbackFrameAt = 0
  /** The first look after a stall gets a beat for the frame that lost the race. */
  private playbackFrameGrace = true
  /** Scene-clock elapsed at the previous pace sample. */
  private playbackElapsedSeen = 0
  /** `performance.now()` of that sample. The first one is not judged. */
  private playbackSampleAt = 0
  private playbackClockSampled = false
  /** Throttle and a hidden tab snap the board without an ops row. */
  private playbackSkipReport = false
  /** Phaser clock holds while the coin dialog is open. X releases one. */
  private coinClockHolds = 0
  /** `performance.now()` when the current coin hold started. */
  private coinHeldSince = 0
  /** Restarts the open coin's deadline. A hidden tab spent it with no frames. */
  private coinDeadlineRearm: (() => void) | null = null
  /** One snapshot request per stuck playback. Cleared when a snapshot lands. */
  private playbackResyncAsked = false
  // AI (and any burst of events) play one payload at a time so summons/attacks
  // don't stack on the same frame.
  private eventQ: GameEventsPayload[] = []
  private eventsPlaying = false
  /** Replay dwell divisor. 1 keeps the normal AI pause. */
  private replaySpeed = 1
  private matchReady = false
  private matchStarted = false
  private pendingBegin = false
  /** Hold GAME_EVENTS until the opening-hand deal is queued in front. */
  private openingDealPending = false
    /** Vue VS + dice overlays; deal starts when Vue calls releaseOpeningDeal. */
  private openingDealHeld = false
  private pendingOpeningStart: (() => void) | null = null
  /** True while the empty-hand → one-by-one opening deal is playing. */
  private openingDealPlaying = false
  /**
   * Snapshot this payload will commit. Drawn cards are already in that hand, so
   * arriving stubs can copy foil before Phaser swaps to the live board.
   */
  private pendingCommit: MatchState | null = null
  private queuedOver: GameOverPayload | null = null
  /** Lengthens summon/set flights while an AI batch is playing. */
  private aiPlayback = false
  // Drawn cards whose 3D flight has not landed yet. renderHand skips them so the
  // Phaser sprite cannot pop in while the overlay is still leaving the deck, and
  // the rest of the fan layouts as if those slots were already occupied.
  private arrivingHandUids = new Set<string>()
  private arrivingHandSeat = new Map<string, Seat>()
  private arrivingHandCardId = new Map<string, string>()
  private landedHandUids = new Set<string>()
  /** Last laid-out X for a hand uid; used to tween the fan when a draw opens a slot. */
  private handDisplayX = new Map<string, number>()
  /**
   * Cards that have left the hand visually (spell flying to the field) but are
   * still in the pre-commit snapshot. renderHand skips them so a field-slot
   * mask is not painted over the fan.
   */
  private departingHandUids = new Set<string>()
  /**
   * Hand discards that must stay in the fan until their own flight (Card
   * Destruction). pendingCommit already has the redrawn hand, so without this
   * the first renderAll empties both rows and the souls leave a blank fan.
   */
  private keptDiscardUids = new Set<string>()
  /**
   * Set spells/traps flipping on the field: skip the Phaser sprite so a slot
   * mask is not painted over the parked 3D card.
   */
  private departingFieldUids = new Set<string>()
  /** Uids in the chain pile, in activation order (2+ chain only). */
  private chainPile: string[] = []
  /**
   * Chain cards drawn in the 3D pile. Their field sprite stays hidden across
   * commits until the link resolves or the pile clears.
   */
  private chainHidden = new Set<string>()
  /**
   * Cards flying out of a pile (GY / Extra / Banished) while still in the
   * pre-commit snapshot. Pile render skips them so the top card does not twin.
   */
  private departingPileUids = new Set<string>()
  /**
   * GY → Banished uids already shown on BAN. departingPileUids still hides
   * them on the GY; this set lets banishedLive paint each sprite as that
   * soul leaves, instead of waiting for commitBoard.
   */
  private landedBanishedUids = new Set<string>()
  /**
   * Hand → GY uids already shown on the pile this batch. pendingCommit has
   * them; this.state does not until commitBoard.
   */
  private landedGraveUids = new Set<string>()
  /** Seats whose Deck pile is riding the shuffle overlay: frame only, no cards. */
  private deckLiftedSeats = new Set<Seat>()
  /** Field slot a resolving spell/trap occupies until the snapshot commits. */
  private activationLand = new Map<string, SlotLand>()
  /**
   * Monster zone a just-summoned card occupies in this batch. Trap Hole can
   * destroy it before the snapshot shows it on the field.
   */
  private fieldLand = new Map<string, SlotLand>()
  /** 3D park + Phaser cover for that spell/trap while effects play. */
  private activationHold = new Map<
    string,
    { gate: { released: boolean }; uncover: () => void }
  >()
  /**
   * Full-size Phaser card in a spell/trap slot after the activation flight
   * lands. The 3D mesh is smaller than the zone frame; this stand-in matches
   * the other field cards until commit or the trip to the graveyard.
   */
  private slotStandIns = new Map<
    string,
    {
      pose: SlotPose
      faceUp: boolean
      defense: boolean
      sprite: Phaser.GameObjects.Container | null
    }
  >()
  /** Park a summoned 3D mesh until it leaves (same-batch Trap Hole). */
  private meshHold = new Map<string, { released: boolean }>()
  /** Monsters revealed this batch (battle flip); GY flights stay face-up. */
  private revealedUids = new Set<string>()
  /** Face shown before commit when Minimal skips the 3D flip. */
  private previewFace = new Map<string, { faceUp: boolean; defense: boolean }>()
  /** Phaser texture keys currently being adopted from the shared image cache. */
  private pendingTextureKeys = new Map<string, Promise<void>>()
  /** URL + blit kind so we can rebuild after a WebGL context loss. */
  private textureSources = new Map<string, { url: string; kind: 'card' | 'image' }>()
  /** Earliest time a missing sleeve may be fetched again. */
  private sleeveRetryAt = new Map<string, number>()
  /** Failed sleeve fetches. Capped so a dead file does not poll all duel. */
  private sleeveMisses = new Map<string, number>()
  private restoringTextures = false
  private onVisRestore: (() => void) | null = null
  private onFocusRestore: (() => void) | null = null
  private onGlLost: ((ev: Event) => void) | null = null
  private onGlRestored: (() => void) | null = null
  /** WebGL stayed lost. Vue rebuilds the board on the Canvas renderer. */
  private rendererRetired = false
  private resumeBoard = false
  private glFallbackCb: (() => void) | null = null
  private glFallbackTimer = 0
  /** Capture-phase touchstart: free a pointer Safari never released. */
  private onStuckTouch: ((event: TouchEvent) => void) | null = null

  // Bridges to the Vue HUD (CardViewer, graveyard/banished overlays, game over).
  private hoverCb: ((card: any | null, faceDown: boolean, sleeveUrl?: string) => void) | null = null
  /** Field sprites by uid, rebuilt each `renderAll`. */
  private fieldSprites = new Map<string, Phaser.GameObjects.Container>()
  /** Field cards already reported as missing a sprite, per duel id. */
  private gapNoted = new Set<string>()
  private gapMatchId = ''
  /** Hand sprites by uid, rebuilt each `renderAll`. */
  private handSprites = new Map<string, Phaser.GameObjects.Container>()
  /**
   * Nítida draws the face and ATK/DEF in the page. The canvas stays 1024×768.
   * Chosen when the scene starts, so a settings change waits for the next duel.
   */
  private useSharpCards = false
  private sharpArtHost: HTMLElement | null = null
  private sharpStatHost: HTMLElement | null = null
  private sharpEls = new Map<string, SharpCardNodes>()
  private sharpSeq = 0
  private sharpMatrix = new Phaser.GameObjects.Components.TransformMatrix()
  private sharpParentMatrix = new Phaser.GameObjects.Components.TransformMatrix()
  /** Field card the pointer is over; used to keep the Equip link after redraws. */
  private hoveringFieldUid: string | null = null
  /** True while `renderAll` is destroying/rebuilding sprites. */
  private paintingBoard = false
  private equipLinkIcons: Phaser.GameObjects.Image[] = []
  private gyCb: ((cards: any[], owner: string) => void) | null = null
  private extraCb: ((cards: any[], owner: string) => void) | null = null
  private banishedCb: ((cards: any[], owner: string) => void) | null = null
  private gameOverCb: ((over: GameOverPayload) => void) | null = null
  // Shows the activated spell/trap as an HTML overlay (above the phase-manager HUD).
  private activationCb: ((
    cardId: string,
    kind?: 'spell' | 'trap' | 'summon',
    foil?: CardFoilId,
    turnMax?: number,
  ) => void) | null = null
  // Vue toast / targeting line (must sit above the phase-manager HUD).
  private toastCb: ((message: string, tone: DuelToastTone) => void) | null = null
  /** Paid Ultimate Offering, but a monster tap still could not summon. */
  private offeringHandFaultCb: (() => void) | null = null
  // Vue prompts / LP — only after the board snapshot is drawn.
  private boardStateCb: ((state: MatchState) => void) | null = null
  // Phase manager / turn label — as soon as a payload starts, so "your turn"
  // cannot linger while the opponent's attacks are already flying.
  private phaseHudCb: ((state: MatchState) => void) | null = null
  // True while an event payload is still animating / held; Vue locks phase clicks.
  private playbackCb: ((on: boolean) => void) | null = null
  // True while the player is picking field targets (hide the trap panel).
  private selectingCb: ((on: boolean) => void) | null = null
  // Which half the current click-pick lives on, so Vue can park the prompt.
  private pickSideCb: ((side: DuelPickSide | null) => void) | null = null
  // Field/hand action menu (Change Position, Summon, Activate, …) as HTML.
  private menuCb: ((menu: FieldMenuPayload | null) => void) | null = null
  private menuActions: (() => void)[] = []
  /** Coin and yes/no windows this seat already answered. A later commit of
   * the opening snapshot must not put those buttons back. */
  private answeredPromptIds = new Set<string>()
  // Swallow the leftover click after the HTML menu closes (it sits over the
  // other card in the same column; Phaser would otherwise treat it as a pick).
  private ignoreBoardPointerUntil = 0
  private boardInputLockGen = 0
  /** HTML dialogs sit above the canvas; Phaser still hears window touches. */
  private modalInputBlocked = false
  // Graveyard / Extra list picker (must sit above the phase-manager HUD).
  private pickerCb: ((req: CardPickerRequest | null) => void) | null = null
  private pickerFinish: ((uids: string[] | null) => void) | null = null
  // Face-up ATK / DEF picker for Special Summons (Monster Reborn, Polymerization).
  private positionCb:
    | ((
        card: { id: string; name: string; atk?: number; def?: number; foil?: string },
        done: (pos: CardPosition | null) => void,
      ) => void)
    | null = null

  constructor() {
    super({ key: 'NetworkDuelScene' })
  }

  onCardHover(cb: (card: any | null, faceDown: boolean, sleeveUrl?: string) => void): void {
    this.hoverCb = cb
  }

  onGraveyardClick(cb: (cards: any[], owner: string) => void): void {
    this.gyCb = cb
  }

  onExtraDeckClick(cb: (cards: any[], owner: string) => void): void {
    this.extraCb = cb
  }

  onBanishedClick(cb: (cards: any[], owner: string) => void): void {
    this.banishedCb = cb
  }

  onGameOver(cb: (over: GameOverPayload) => void): void {
    this.gameOverCb = cb
  }

  onFocusBand(cb: (band: FocusBand) => void): void {
    this.focusBandCb = cb
  }

  /** Current compact camera band (`hand` / `mine` / `opp`). */
  getFocusBand(): FocusBand {
    return this.currentFocusBand
  }

  /**
   * Zoom the Phaser camera onto a compact band. Desktop and the off pref
   * leave the full 1024×768 board in view.
   */
  focusBand(
    band: FocusBand,
    opts: { animate?: boolean; fromUser?: boolean } = {},
  ): void {
    if (opts.fromUser) this.cancelReturnToHome()
    this.currentFocusBand = band
    this.focusBandCb?.(band)
    this.applyFocusCamera(band, opts.animate !== false)
  }

  /**
   * Walk `opp` → `mine` → `hand` (dir +1) or the other way. Wraps: from the
   * hand the next tap looks at the opponent (back to the top of the field).
   */
  shiftFocusBand(dir: -1 | 1): void {
    if (!this.focusBandsActive()) return
    const index = FOCUS_BAND_ORDER.indexOf(this.currentFocusBand)
    const len = FOCUS_BAND_ORDER.length
    const next = FOCUS_BAND_ORDER[(index + dir + len) % len]
    if (!next) return
    this.focusBand(next, { fromUser: true })
  }

  /**
   * Recompute zoom after a resize, compact MQ change, or prefs toggle.
   */
  syncFocusCamera(animate = false): void {
    if (!this.sys?.isActive()) return
    if (!this.focusBandsActive()) {
      this.resetFocusCamera(animate)
      return
    }
    if (this.state && !this.lastAutoFocusKey) {
      this.applyAutoFocusFromState(this.state)
      return
    }
    this.applyFocusCamera(this.currentFocusBand, animate)
  }

  init(data: {
    client: GameClient
    hoverCb?: (card: any | null, faceDown: boolean) => void
    gyCb?: (cards: any[], owner: string) => void
    extraCb?: (cards: any[], owner: string) => void
    banishedCb?: (cards: any[], owner: string) => void
    gameOverCb?: (over: GameOverPayload) => void
    activationCb?: (
      cardId: string,
      kind?: 'spell' | 'trap' | 'summon',
      foil?: CardFoilId,
      turnMax?: number,
    ) => void
    toastCb?: (message: string, tone: DuelToastTone) => void
    offeringHandFaultCb?: () => void
    boardStateCb?: (state: MatchState) => void
    phaseHudCb?: (state: MatchState) => void
    playbackCb?: (on: boolean) => void
    selectingCb?: (on: boolean) => void
    pickSideCb?: (side: DuelPickSide | null) => void
    menuCb?: (menu: FieldMenuPayload | null) => void
    pickerCb?: (req: CardPickerRequest | null) => void
    positionCb?: (
      card: { id: string; name: string; atk?: number; def?: number; foil?: string },
      done: (pos: CardPosition | null) => void,
    ) => void
    focusBandCb?: (band: FocusBand) => void
    handWaitCb?: (top: number | null) => void
    lpPunchCb?: (mine: boolean, durationMs?: number) => void
    lpHudCb?: (tick: CompactLpTick) => void
    screenShakeCb?: (durationMs: number) => void
    /** Wait for Vue VS + dice before dealing opening hands. */
    holdOpeningDeal?: boolean
    /** Paint the current state. The match already started on the previous canvas. */
    resumeBoard?: boolean
    /** WebGL did not come back. Vue recreates this scene on Canvas. */
    glFallbackCb?: () => void
  }): void {
    this.client = data.client
    this.mySeat = data.client.seat
    warmDuelCoin()
    // Callbacks are injected here (not wired later) so hover/overlay bridges are
    // ready before the first render, independent of Phaser's boot timing.
    if (data.hoverCb) this.hoverCb = data.hoverCb
    if (data.gyCb) this.gyCb = data.gyCb
    if (data.extraCb) this.extraCb = data.extraCb
    if (data.banishedCb) this.banishedCb = data.banishedCb
    if (data.gameOverCb) this.gameOverCb = data.gameOverCb
    if (data.activationCb) this.activationCb = data.activationCb
    if (data.toastCb) this.toastCb = data.toastCb
    if (data.offeringHandFaultCb) this.offeringHandFaultCb = data.offeringHandFaultCb
    if (data.boardStateCb) this.boardStateCb = data.boardStateCb
    if (data.phaseHudCb) this.phaseHudCb = data.phaseHudCb
    if (data.playbackCb) this.playbackCb = data.playbackCb
    if (data.selectingCb) this.selectingCb = data.selectingCb
    if (data.pickSideCb) this.pickSideCb = data.pickSideCb
    if (data.menuCb) this.menuCb = data.menuCb
    if (data.pickerCb) this.pickerCb = data.pickerCb
    if (data.positionCb) this.positionCb = data.positionCb
    if (data.focusBandCb) this.focusBandCb = data.focusBandCb
    if (data.handWaitCb) this.handWaitCb = data.handWaitCb
    if (data.lpPunchCb) this.lpPunchCb = data.lpPunchCb
    if (data.lpHudCb) this.lpHudCb = data.lpHudCb
    if (data.screenShakeCb) this.screenShakeCb = data.screenShakeCb
    if (data.holdOpeningDeal) this.openingDealHeld = true
    if (data.resumeBoard) this.resumeBoard = true
    if (data.glFallbackCb) this.glFallbackCb = data.glFallbackCb
  }

  preload(): void {
    void this.adoptPhaserTexture('card-back', CARD_BACK_URL)
  }

  create(): void {
    this.sharpArtHost = document.getElementById('sharp-card-art')
    this.sharpStatHost = document.getElementById('sharp-card-stats')
    this.useSharpCards =
      useDuelPrefsStore().boardSharpness !== 'standard' &&
      !!this.sharpArtHost &&
      !!this.sharpStatHost
    if (this.useSharpCards) {
      this.sharpArtHost?.replaceChildren()
      this.sharpStatHost?.replaceChildren()
      this.events.on(Phaser.Scenes.Events.POST_UPDATE, this.syncSharpCards, this)
      this.events.once('shutdown', () => {
        this.events.off(Phaser.Scenes.Events.POST_UPDATE, this.syncSharpCards, this)
        this.clearSharpDom()
      })
    }
    this.events.once('shutdown', () => this.cancelPlaybackWall())
    this.events.once('destroy', () => this.cancelPlaybackWall())
    void this.adoptPhaserTexture('card-back', CARD_BACK_URL)
    ensureMarkerTextures(this)
    ensureRarityFoilTextures(this)
    this.matLayer = this.add.container(0, 0).setDepth(-10)
    this.matMaskGfx = this.add.graphics().setVisible(false)
    this.boardLayer = this.add.container(0, 0)
    this.uiLayer = this.add.container(0, 0).setDepth(100)
    this.menuLayer = this.add.container(0, 0).setDepth(1000)
    this.cameras.main.setRoundPixels(true)
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)')
    this.cameras.main.setBounds(0, 0, this.scale.width, this.scale.height)
    this.input.setTopOnly(true)
    this.boardTapZone = this.add
      .zone(
        this.scale.width / 2,
        this.scale.height / 2,
        this.scale.width,
        this.scale.height,
      )
      .setDepth(-5)
      .setInteractive()
    this.boardTapZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.swipeAttack) return
      // The zone sits behind every card. It only hears a tap the sprites
      // missed. Change of Heart used to toast here and leave the monster
      // unchosen, so the rest of the board stayed locked.
      if (this.blockingFieldPick()) {
        const hit = this.fieldCardAt(pointer.worldX, pointer.worldY)
        if (hit) {
          this.onFieldCardClick(hit.card, hit.seat, hit.zone, pointer)
          return
        }
        if (this.boardPointerBlocked()) return
        const uids = this.openFieldPickUids()
        if (uids.length > 0) this.focusTargets(uids)
        this.toast(t('duel.toast.highlighted'), 'warn')
        return
      }
      if (this.boardPointerBlocked()) return
      this.focusBandFromWorld(pointer.worldX, pointer.worldY, true)
    })
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      this.onSwipeAttackMove(pointer)
    })
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.onSwipeAttackUp(pointer)
    })
    this.input.on('pointerupoutside', (pointer: Phaser.Input.Pointer) => {
      this.onSwipeAttackUp(pointer)
    })

    // Direct attacks lunge at the defending hand. LP plates stay in the gutter.
    syncBoardMetrics(this.scale.width, this.scale.height)
    if (!this.isWatching()) this.installPlaymat()
    this.installSleeves()
    this.scale.on('resize', () => {
      syncBoardMetrics(this.scale.width, this.scale.height)
      this.layoutPlaymat()
      this.layoutBoardTapZone()
      this.cameras.main.setBounds(0, 0, this.scale.width, this.scale.height)
      this.syncFocusCamera(false)
      if (this.state) this.renderAll()
    })
    const prefs = useDuelPrefsStore()
    const { matId, matOpacity, focusBands, sleeveId, responseMode, foilSweep } =
      storeToRefs(prefs)
    this.matStop = watch([matId, matOpacity], ([id], prev) => {
      if (this.isWatching()) {
        if (prev && id === prev[0]) this.applyMatOpacity()
        return
      }
      if (!prev || id !== prev[0]) {
        this.switchPlaymat(id)
        this.sendSeatCosmetics()
      } else this.applyMatOpacity()
    })
    this.sleeveStop = watch(sleeveId, (id) => {
      if (this.isWatching()) return
      this.switchSleeve(id)
      this.sendSeatCosmetics()
    })
    this.prefsStop = watch(focusBands, (on) => {
      if (on && this.state) {
        this.lastAutoFocusKey = ''
        this.applyAutoFocusFromState(this.state)
        return
      }
      this.syncFocusCamera(true)
    })
    this.responseModeStop = watch(responseMode, () => {
      if (!this.matchStarted) return
      this.syncResponseMode()
    })
    this.foilSweepStop = watch(foilSweep, () => {
      if (this.state) this.renderAll({ keepMenu: true })
    })
    this.compactMql = window.matchMedia(DUEL_COMPACT_MQ)
    this.onCompactMql = () => this.syncFocusCamera(false)
    this.compactMql.addEventListener('change', this.onCompactMql)
    this.installTextureRestore()
    this.onStuckTouch = (event: TouchEvent) => {
      this.reclaimStuckTouches(event)
    }
    window.addEventListener('touchstart', this.onStuckTouch, { capture: true })
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.clearSwipeAttack()
      if (this.onStuckTouch) {
        window.removeEventListener('touchstart', this.onStuckTouch, { capture: true })
        this.onStuckTouch = null
      }
      this.matStop?.()
      this.sleeveStop?.()
      this.prefsStop?.()
      this.responseModeStop?.()
      this.foilSweepStop?.()
      setDuelSleeveUrlOverride(null)
      this.deckLiftedSeats.clear()
      this.uninstallTextureRestore()
      this.cancelPlaybackWall()
      if (this.artRenderTimer !== null) {
        window.clearTimeout(this.artRenderTimer)
        this.artRenderTimer = null
      }
      if (this.compactMql && this.onCompactMql) {
        this.compactMql.removeEventListener('change', this.onCompactMql)
      }
    })
    this.toastText = this.add
      .text(this.scale.width / 2, 64, '', {
        fontFamily: 'Roboto, system-ui, sans-serif',
        fontSize: '16px',
        color: TOAST_TONE_COLOR.guide,
        align: 'center',
        wordWrap: { width: Math.min(560, this.scale.width * 0.8) },
      })
      .setOrigin(0.5)
      .setStroke('#0c0e18', 4)
      .setDepth(200)
      .setVisible(!this.toastCb)

    this.client.setHandlers({
      onStart: (p) => {
        if (this.rendererRetired) return
        this.mySeat = p.viewerSeat
        this.syncResponseMode()
        this.playOpeningDeal(p)
      },
      onEvents: (p) => {
        if (this.rendererRetired) return
        this.enqueueEvents(p)
      },
      onState: (p) => {
        if (this.rendererRetired) return
        this.mySeat = p.viewerSeat
        if (this.isWatching()) {
          this.installWatchMats(p.state)
          this.installWatchSleeves(p.state)
        }
        this.applySnapshot(p.state)
      },
      onError: (msg, code, vars) => {
        if (this.rendererRetired) return
        this.actionRejected = true
        this.toast(msg, 'warn', code, vars)
        // The server already closed this answer. Drop it now; the state
        // push that follows replaces it with the real window, if any.
        if (code === 'noPendingResponse' || code === 'notYourResponse') {
          this.dropStaleAnswerWindow()
          return
        }
        // A rejected SELECT_TARGETS already cleared the click mode. PvP
        // reports that after send() returned, so put the highlights back.
        if (
          this.clickMode === 'idle' &&
          this.engineTargetUi !== 'list' &&
          this.engineTargetRequestId &&
          this.state?.pendingTargets?.chooser === this.mySeat
        ) {
          this.reopenEngineTargetPrompt()
        }
        if (this.state?.pendingChainOrder?.chooser === this.mySeat) {
          this.chainOrderRequestId = null
          this.chainOrderPickerLive = false
          this.syncChainOrderPrompt()
        }
        if (
          code !== 'noFlipPending' &&
          this.answeredFlipId &&
          this.state?.pendingFlipEffect?.requestId === this.answeredFlipId
        ) {
          this.answeredFlipId = null
        }
      },
      onOver: (over) => {
        if (this.rendererRetired) return
        this.queuedOver = over
        this.flushQueuedOver()
      },
      onCosmetics: (p) => {
        if (this.rendererRetired) return
        if (this.isWatching()) {
          const bottom = p.seat === 0
          if (bottom) {
            if (p.matId !== this.resolvedMatId) this.switchPlaymat(p.matId)
            if (p.sleeveId) this.applySeatSleeve(true, p.sleeveId)
          } else {
            this.applyOppMatId(p.matId)
            if (p.sleeveId) this.applyOppSleeveId(p.sleeveId)
          }
          return
        }
        if (p.seat === this.mySeat) return
        this.applyOppMatId(p.matId)
        if (p.sleeveId) this.applyOppSleeveId(p.sleeveId)
      },
    })

    // Esc cancels any in-progress selection (tribute / target / attack).
    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.clickMode !== 'idle') this.cancelSelection()
    })

    this.input.on('pointerdown', () => DuelSfx.prime())

    this.matchReady = true
    const parked = this.client.takeParkedOpening?.()
    if (parked?.start) {
      this.matchStarted = true
      this.mySeat = parked.start.viewerSeat
      this.syncResponseMode()
      this.openingDealHeld = true
      this.playOpeningDeal(parked.start)
      for (const batch of parked.events) this.enqueueEvents(batch)
      if (parked.over) this.queuedOver = parked.over
      this.releaseOpeningDeal()
      return
    }
    this.beginMatch()
  }

  /**
   * Starts the local engine / requests network state. Vue may call this after
   * binding the 3D overlay; `create` also calls it so the board cannot stay empty
   * if that rAF missed the scene.
   */
  /** 1, 2, or 4. Shortens the pause between replay actions. */
  setReplaySpeed(speed: number): void {
    this.replaySpeed = speed === 4 ? 4 : speed === 2 ? 2 : 1
  }

  beginMatch(): void {
    if (this.matchStarted) return
    if (!this.matchReady) {
      this.pendingBegin = true
      return
    }
    this.matchStarted = true
    if (this.resumeBoard) {
      this.openingDealHeld = false
      this.client.requestState()
      return
    }
    this.client.start?.()
    if (!this.client.start) this.client.requestState()
  }

  /** Courtesy reorder of the local hand; not a play. */
  requestHandShuffle(): void {
    this.send({ type: ActionType.SHUFFLE_HAND })
  }

  /** Push the local trap-prompt preference into the engine. */
  private syncResponseMode(): void {
    if (this.isWatching()) return
    const mode = parseResponseMode(useDuelPrefsStore().responseMode)
    this.send({ type: ActionType.SET_RESPONSE_MODE, mode })
  }

  /** Admin watch: the renderer draws the board and never sends a play. */
  private isWatching(): boolean {
    return this.client.watch === true
  }

  /** Push this seat's resolved mat and sleeve. No-op while watching. */
  private sendSeatCosmetics(): void {
    if (this.isWatching()) return
    const sleeveId = this.resolvedSleeveId
    this.client.sendCosmetics?.({
      matId: this.resolvedMatId,
      ...(sleeveId ? { sleeveId } : {}),
    })
  }

  /** Bottom half is seat 0's mat, top half is seat 1's. */
  private installWatchMats(state: MatchState): void {
    const mine = state.seats[0]?.matId
    if (mine && mine !== this.resolvedMatId) this.switchPlaymat(mine)
    const opp = state.seats[1]?.matId
    if (opp) this.applyOppMatId(opp)
  }

  /**
   * Applies an authoritative snapshot immediately and cancels any animation-deferred
   * snapshot still queued (bumping renderSeq turns it into a no-op). Without this a
   * late deferred apply could roll the board back to an older snapshot.
   */
  private applySnapshot(state: MatchState): void {
    this.eventQ.length = 0
    this.setEventsPlaying(false)
    this.renderSeq += 1
    this.staleBoard = false
    this.playbackUnlockAt = 0
    this.playbackDeafUntil = 0
    this.playbackResyncAsked = false
    this.releaseAllActivationHolds()
    this.clearArrivingHands()
    if (this.chainPile.length > 0 || this.chainHidden.size > 0) this.clearChainPile()
    this.pendingCommit = null
    this.offeringHandFocusKey = ''
    this.state = state
    this.maybeInstallOppPlaymat()
    this.maybeInstallOppSleeve()
    this.snapLifePoints()
    this.renderAll()
    this.publishBoardState()
    this.flushQueuedOver()
  }

  /**
   * First snapshot has the opening hands already dealt. Show empty hands, then
   * CARD_DRAWN each card one at a time (yours, then the opponent's, then the
   * first player's sixth). Vue releases this after dice (if any), VS, and the roster.
   */
  private playOpeningDeal(p: GameStatePayload): void {
    // VS + dice hold the deal for a few seconds — spend that on art GETs so
    // the first face-up cards are textures, not name placeholders.
    this.prefetchKnownArt(p.state)
    const empty = this.stripOpeningHands(p.state)
    const events = this.openingDrawEvents(p.state)
    this.openingDealPending = true
    this.applySnapshot(empty)
    const start = (): void => {
      this.openingDealPlaying = events.length > 0
      if (events.length > 0) {
        this.eventQ.unshift({
          events,
          state: p.state,
          viewerSeat: p.viewerSeat,
        })
      } else {
        this.applySnapshot(p.state)
      }
      this.openingDealPending = false
      this.pumpEventQueue()
    }
    if (this.openingDealHeld) this.pendingOpeningStart = start
    else start()
  }

  /**
   * Lets the opening-hand deal run after dice, the VS cinematic, and the roster.
   */
  releaseOpeningDeal(): void {
    this.openingDealHeld = false
    const start = this.pendingOpeningStart
    this.pendingOpeningStart = null
    start?.()
  }

  /**
   * The player can see a live board. False during dice / VS / roster, and
   * after WebGL was retired, so leaving that black screen is not a forfeit.
   */
  boardReady(): boolean {
    return this.state != null && !this.rendererRetired && !this.openingDealHeld
  }

  /** Hands emptied onto the deck so pile counts match the upcoming draws. */
  private stripOpeningHands(final: MatchState): MatchState {
    const s: MatchState = JSON.parse(JSON.stringify(final)) as MatchState
    for (const seat of [0, 1] as Seat[]) {
      const z = s.zones[seat]
      z.deck = [...z.hand, ...z.deck]
      z.hand = []
    }
    return s
  }

  /**
   * One CARD_DRAWN per opening card. Same sequential rule as Pot of Greed:
   * flashEvents chains them so they never leave the deck in parallel.
   */
  private openingDrawEvents(final: MatchState): GameEvent[] {
    const events: GameEvent[] = []
    const me = this.mySeat
    const opp = (me === 0 ? 1 : 0) as Seat
    const push = (seat: Seat, skip: number, count: number): void => {
      const cards = final.zones[seat].hand.slice(skip, skip + count)
      for (const card of cards) {
        events.push({
          type: EventType.CARD_DRAWN,
          seat,
          cardUid: card.uid,
          cardId: seat === me && card.cardId ? card.cardId : null,
        })
      }
    }
    push(me, 0, STARTING_HAND_SIZE)
    push(opp, 0, STARTING_HAND_SIZE)
    const first = final.turnPlayer
    if (final.zones[first].hand.length > STARTING_HAND_SIZE) {
      push(first, STARTING_HAND_SIZE, 1)
    }
    return events
  }

  /** Ignores a tap on the Set trap until this time. Matches the prompt arm. */
  private trapPromptArmUntil = 0
  private trapPromptArmId = ''

  /**
   * A trap question that just appeared must not take the finger already on
   * the summoned monster, nor a tap on the glowing trap in that same beat.
   */
  private armTrapPrompt(state: MatchState): void {
    const pending = state.pendingPriority
    if (!pending || pending.responder !== this.mySeat || pending.activatableUids.length === 0) {
      return
    }
    if (pending.requestId === this.trapPromptArmId) return
    this.trapPromptArmId = pending.requestId
    this.trapPromptArmUntil = performance.now() + 400
  }

  private trapPromptArmed(): boolean {
    return performance.now() < this.trapPromptArmUntil
  }

  /** Prompts follow the drawn board; phase HUD is also refreshed on commit. */
  private publishBoardState(): void {
    if (!this.state) return
    this.armTrapPrompt(this.state)
    this.phaseHudCb?.(this.state)
    this.boardStateCb?.(this.state)
    this.syncEngineTargetPrompt()
    this.syncChainOrderPrompt()
    this.syncFlipListPrompt()
    this.syncPeekPrompt()
    this.syncAttackPick()
    this.syncTributePick()
    this.applyAutoFocusFromState(this.state)
    this.maybeFocusOfferingHand(this.state, 'commit')
    this.syncFlipFieldFocus()
  }

  private setEventsPlaying(on: boolean): void {
    this.eventsPlaying = on
    if (!on) this.cancelPlaybackWall()
    this.playbackCb?.(on)
  }

  /**
   * Unlocks playback even if the Phaser clock is paused (blur, a frozen
   * frame). Time spent in the coin dialog is added back so the toss is not
   * snapped.
   */
  private armPlaybackWall(safetyMs: number, seq: number, unlock: () => void): void {
    this.cancelPlaybackWall()
    const wait = Number.isFinite(safetyMs) ? Math.max(0, safetyMs) : 2_500
    this.playbackWallDeadline = performance.now() + wait
    this.playbackWallSeq = seq
    this.playbackWallUnlock = unlock
    if (this.coinClockHolds > 0) this.playbackWallHoldStart = performance.now()
    this.schedulePlaybackWall(this.coinClockHolds > 0 ? 400 : wait)
  }

  private schedulePlaybackWall(ms: number): void {
    if (this.playbackWallTimer !== null) window.clearTimeout(this.playbackWallTimer)
    this.playbackWallTimer = window.setTimeout(() => this.onPlaybackWall(), ms)
  }

  private onPlaybackWall(): void {
    this.playbackWallTimer = null
    if (this.playbackWallDeadline === 0) return
    if (this.playbackWallSeq !== this.renderSeq) {
      this.cancelPlaybackWall()
      return
    }
    // A paused scene used to drop this timer. Chat still works; the board
    // stayed deaf for the rest of the clock. Keep the deadline and retry.
    if (!this.sys?.isActive()) {
      if (this.playbackOverdue()) {
        this.forceUnlockPlayback()
        return
      }
      this.schedulePlaybackWall(500)
      return
    }
    if (this.coinClockHolds > 0) {
      if (this.coinHoldStuck()) {
        this.forceUnlockPlayback()
        return
      }
      if (this.playbackWallHoldStart === 0) this.playbackWallHoldStart = performance.now()
      this.schedulePlaybackWall(400)
      return
    }
    if (this.playbackWallHoldStart > 0) {
      const held = performance.now() - this.playbackWallHoldStart
      this.playbackWallDeadline += held
      if (this.playbackDeafUntil > 0) this.playbackDeafUntil += held
      this.playbackWallHoldStart = 0
    }
    const left = this.playbackWallDeadline - performance.now()
    if (left > 40) {
      this.schedulePlaybackWall(left)
      return
    }
    // Real time is up. A hitch clamps Phaser's delta, so the summon can
    // still be on the scene clock. Wait for that clock while it is moving.
    const pace = this.playbackPace()
    if (pace === 'play') {
      this.playbackWallDeadline = performance.now() + 400
      this.schedulePlaybackWall(400)
      return
    }
    this.playbackSkipReport = pace === 'cut'
    const unlock = this.playbackWallUnlock
    this.playbackWallDeadline = 0
    this.playbackWallUnlock = null
    if (!this.eventsPlaying || !unlock) {
      this.playbackSkipReport = false
      return
    }
    unlock()
  }

  private cancelPlaybackWall(): void {
    if (this.playbackWallTimer !== null) {
      window.clearTimeout(this.playbackWallTimer)
      this.playbackWallTimer = null
    }
    this.playbackWallDeadline = 0
    this.playbackWallHoldStart = 0
    this.playbackWallUnlock = null
    this.playbackSafetyTimer = null
  }

  /** Coin dialog paused the Phaser clock. Hold the wall deadline with it. */
  private noteCoinClockHeld(): void {
    if (this.playbackWallDeadline === 0 || this.playbackWallHoldStart > 0) return
    this.playbackWallHoldStart = performance.now()
  }

  /** Dialog closed. The animation still has whatever the Phaser clock had left. */
  private noteCoinClockReleased(): void {
    if (this.playbackWallHoldStart === 0) return
    const held = performance.now() - this.playbackWallHoldStart
    this.playbackWallDeadline += held
    if (this.playbackDeafUntil > 0) this.playbackDeafUntil += held
    this.playbackWallHoldStart = 0
    if (this.playbackWallDeadline === 0 || this.playbackWallSeq !== this.renderSeq) return
    const left = Math.max(0, this.playbackWallDeadline - performance.now())
    this.schedulePlaybackWall(left)
  }

  /**
   * Plays event payloads in order. Each AI action arrives as its own payload with
   * the board state after that action, so a summon lands before the next attack.
   */
  private enqueueEvents(p: GameEventsPayload): void {
    this.mySeat = p.viewerSeat
    // The coin question has to be on screen before the toast. commitBoard
    // waits out the previous animation, and a hand tap in that gap only
    // says the card is Main Phase.
    this.revealOwnedPrompt(p.state)
    if (p.events.length === 0) {
      if (
        !this.openingDealPending &&
        !this.eventsPlaying &&
        this.eventQ.length === 0
      ) {
        this.applySnapshot(p.state)
      }
      return
    }
    this.eventQ.push(p)
    if (!this.openingDealPending) this.pumpEventQueue()
  }

  private pumpEventQueue(): void {
    if (this.eventsPlaying) return
    const p = this.eventQ.shift()
    if (!p) {
      this.staleBoard = false
      this.setEventsPlaying(false)
      this.flushQueuedOver()
      return
    }
    this.setEventsPlaying(true)
    this.staleBoard = true
    this.mySeat = p.viewerSeat
    this.pendingCommit = p.state
    // A pass that also resolves damage used to keep the dialog up until the
    // hit and the AI hold finished. Drop the window this seat already
    // answered now; the next question still waits for commitBoard.
    this.releaseClosedPrompt(p.state)
    // A commit of the previous payload can wipe the question revealed at
    // enqueue. Put it back before the toast, while attack-target mode is
    // still able to hide the buttons.
    this.revealOwnedPrompt(p.state)
    this.aiPlayback = this.openingDealPlaying
      ? false
      : p.replay === true || this.payloadHasAiActor(p)
    // Phase strip follows this payload immediately. The engine skips Draw in
    // the same tick (DRAW → STANDBY), so publishPhaseHud stops at DP while
    // the card flies; commitBoard then publishes Standby.
    // Prompts stay on commitBoard so Trap Hole cannot open before the summon.
    // Hand-limit discard + turn pass share one payload. Keep EP / this hand
    // on screen until the cards finish flying (publishBoardState then jumps).
    // Unhappy Maiden ends the Battle Phase in the same payload as the hit.
    // Keep BP on the strip until the attack and her activation finish.
    if (!this.isHandLimitPass(p.events) && !this.phaseFollowsBattle(p.events)) {
      this.publishPhaseHud(p)
    }
    // Show the resolved board as soon as the flight ends, then hold so a Set
    // is actually visible before the AI skips to the next phase. Skip the long
    // hold when this snapshot is waiting on the local player (Trap Hole, etc.).
    const thinkRaw = this.thinkDelayMs(p)
    const paced =
      p.replay === true && this.replaySpeed > 1
        ? Math.max(this.ms(40), Math.round(thinkRaw / this.replaySpeed))
        : thinkRaw
    const think = Number.isFinite(paced) ? Math.max(0, paced) : 0
    this.renderSeq += 1
    const seq = this.renderSeq

    const unlockPlayback = (reason: 'watchdog' | 'throw' = 'watchdog'): void => {
      if (seq !== this.renderSeq) {
        this.releaseAbandonedPlayback()
        return
      }
      if (!this.eventsPlaying) return
      if (this.coinClockHolds === 0 && this.time.paused) this.time.paused = false
      const skip = this.playbackSkipReport
      this.playbackSkipReport = false
      if (!skip) this.noteSummonLock(reason)
      if (this.pendingCommit) {
        this.state = this.pendingCommit
        this.pendingCommit = null
        this.clearDepartureTracking()
        this.dropStaleArrivals()
        this.renderAll()
        this.publishBoardState()
      }
      this.staleBoard = false
      this.aiPlayback = false
      this.playbackResyncAsked = false
      this.setEventsPlaying(false)
      this.pumpEventQueue()
    }

    // Armed before flashEvents. A throw used to skip this timer and leave
    // the ranked board deaf until the player left and came back.
    this.playbackUnlockAt = this.time.now + 2_500
    this.playbackDeafUntil = performance.now() + 2_500
    const safety = this.time.delayedCall(2_500, unlockPlayback)

    let delay = 0
    try {
      const delayRaw = this.flashEvents(p.events, p)
      delay = Number.isFinite(delayRaw) ? Math.max(0, delayRaw) : 0
    } catch (err) {
      console.error('flashEvents', err)
      safety.remove(false)
      unlockPlayback('throw')
      return
    }

    safety.remove(false)
    const safetyMs = delay + think + 2_500
    this.playbackUnlockAt = this.time.now + safetyMs
    this.playbackDeafUntil = performance.now() + safetyMs
    // A missed timer (NaN delay, paused clock, a burst from the other device)
    // used to leave eventsPlaying set and the phone unable to play.
    // The wall clock is the backstop when frames stop. It must not win
    // while the scene is still stepping: one clamped frame used to count
    // a normal summon as a lock.
    this.armPlaybackWall(safetyMs, seq, () => unlockPlayback())
    this.playbackFrameSeen = this.game.loop.frame
    this.playbackFrameAt = performance.now()
    this.playbackFrameGrace = true
    this.playbackElapsedSeen = 0
    this.playbackSampleAt = performance.now()
    this.playbackClockSampled = false
    this.playbackSkipReport = false
    this.playbackSafetyTimer = this.time.delayedCall(safetyMs, () => unlockPlayback())

    const commitBoard = (): boolean => {
      if (seq !== this.renderSeq) {
        this.releaseAbandonedPlayback()
        return false
      }
      // Draw the Phaser board first, then drop parked 3D meshes. Releasing
      // the overlay before renderAll left an empty slot (Monster Reborn:
      // GY fly ends, mesh dies, then the spell still has to hit the GY).
      this.state = this.withoutAnsweredPrompt(p.state)
      this.pendingCommit = null
      this.clearDepartureTracking()
      this.dropStaleArrivals()
      if (
        (p.state.chain?.length ?? 0) < 2 &&
        (this.chainPile.length > 0 || this.chainHidden.size > 0)
      ) {
        this.chainPile = []
        this.chainHidden.clear()
        getDuelVfx()?.clearChainStack(this.visualMs(240) / 1000)
      }
      try {
        this.renderAll()
      } catch (err) {
        console.error('renderAll', err)
        this.paintingBoard = false
      }
      try {
        this.publishBoardState()
      } catch (err) {
        console.error('publishBoardState', err)
      }
      // The snapshot we just drew can be older than a coin question already
      // queued. Put that question back; an answered one stays down.
      const queued = this.eventQ[0]
      if (queued) this.revealOwnedPrompt(queued.state)
      this.releaseParkedMeshes()
      for (const uid of [...this.arrivingHandUids]) this.tryRevealHandCard(uid)
      if (this.openingDealPlaying) this.openingDealPlaying = false
      return true
    }

    this.time.delayedCall(delay, () => {
      if (!commitBoard()) return
      this.time.delayedCall(think, () => {
        if (seq !== this.renderSeq) {
          this.releaseAbandonedPlayback()
          return
        }
        this.aiPlayback = false
        this.setEventsPlaying(false)
        this.pumpEventQueue()
      })
    })
  }

  /**
   * A newer snapshot owns the board. Clear the deaf flag and drain whatever
   * is still queued. Leave a live newer playback alone.
   */
  private releaseAbandonedPlayback(): void {
    if (this.eventsPlaying) return
    this.staleBoard = false
    this.aiPlayback = false
    this.pumpEventQueue()
  }

  /** True once the animation safety window has elapsed and the scene clock has stopped. */
  private playbackOverdue(): boolean {
    if (this.coinClockHolds > 0) return false
    if (!this.eventsPlaying && !this.staleBoard) return false
    if (this.playbackDeafUntil <= 0 || performance.now() < this.playbackDeafUntil) return false
    const pace = this.playbackPace()
    if (pace === 'cut') this.playbackSkipReport = true
    return pace !== 'play'
  }

  /**
   * What the wall clock should do once its real-time budget is gone.
   * `play` — the scene clock is still spending the animation.
   * `cut` — hidden tab, or frames so slow that Phaser froze the delta. Snap
   * without an ops row.
   * `stall` — the loop stopped while the player could see the board.
   */
  private playbackPace(): 'play' | 'cut' | 'stall' {
    // Hidden first: a frozen tab pauses the scene, and that used to look like
    // a stall instead of a cut. Phase buttons still worked; cards did not.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return 'cut'
    }
    const timer = this.playbackSafetyTimer
    if (!timer || this.time.paused || !this.sys?.isActive()) return 'stall'
    if (timer.elapsed + 40 >= timer.delay) return 'stall'
    const now = performance.now()
    const frame = this.game?.loop?.frame ?? this.playbackFrameSeen
    const stepped = frame > this.playbackFrameSeen
    if (stepped) {
      this.playbackFrameSeen = frame
      this.playbackFrameAt = now
      this.playbackFrameGrace = true
    } else if (this.playbackFrameGrace) {
      this.playbackFrameGrace = false
      this.playbackFrameAt = now
    } else if (now - this.playbackFrameAt >= 1_500) {
      return 'stall'
    }
    const wallGain = now - this.playbackSampleAt
    const elapsedGain = timer.elapsed - this.playbackElapsedSeen
    this.playbackSampleAt = now
    this.playbackElapsedSeen = timer.elapsed
    // The sample that includes the hitch is not a verdict. A 3s decode
    // leaves elapsed near zero, then the clock resumes on the next frames.
    if (!this.playbackClockSampled) {
      this.playbackClockSampled = true
      return 'play'
    }
    if (wallGain < 300) return 'play'
    // Unfocused window: rAF drops to about 1fps and each step counts as 16ms.
    if (stepped && elapsedGain < 40) return 'cut'
    return 'play'
  }

  /** Coin dialog never called its done callback. Don't pause the clock forever. */
  private coinHoldStuck(): boolean {
    return (
      this.coinClockHolds > 0 &&
      this.coinHeldSince > 0 &&
      performance.now() - this.coinHeldSince > 20_000
    )
  }

  /**
   * A tap past the safety window commits the snapshot instead of staying deaf.
   * A slow frame still owns the flight; this only runs once that clock has stopped.
   */
  private recoverStaleBoard(): void {
    this.ensureInputHears()
    if (!this.eventsPlaying && !this.staleBoard) return
    if (!this.playbackOverdue()) return
    this.forceUnlockPlayback()
  }

  /**
   * Commit the board the server already reached and listen for clicks again.
   * Used when the Phaser timer and the wall timer both failed to land.
   */
  private forceUnlockPlayback(): void {
    if (!this.eventsPlaying && !this.staleBoard) {
      this.playbackSkipReport = false
      return
    }
    while (this.coinClockHolds > 0) this.releaseCoinClock()
    const unlock = this.playbackWallUnlock
    const seq = this.playbackWallSeq
    this.playbackDeafUntil = 0
    this.cancelPlaybackWall()
    if (unlock && seq === this.renderSeq) {
      unlock()
      this.ensureInputHears()
      return
    }
    if (this.pendingCommit) {
      this.state = this.pendingCommit
      this.pendingCommit = null
    }
    this.clearDepartureTracking()
    this.dropStaleArrivals()
    this.staleBoard = false
    this.aiPlayback = false
    this.playbackResyncAsked = false
    this.eventQ.length = 0
    this.renderSeq += 1
    this.setEventsPlaying(false)
    if (this.sys?.isActive() && this.state) {
      try {
        this.renderAll()
      } catch (err) {
        console.error('renderAll', err)
        this.paintingBoard = false
      }
      try {
        this.publishBoardState()
      } catch (err) {
        console.error('publishBoardState', err)
      }
    }
    this.ensureInputHears()
    if (!this.isWatching()) this.client.requestState()
  }

  /**
   * Turn Phaser input back on when no dialog is holding it. The 300 ms mute
   * after a menu click stays; a mute whose timer never fired does not.
   */
  private ensureInputHears(): void {
    if (!this.canEnableBoardInput()) return
    if (this.input.enabled && this.time.now >= this.ignoreBoardPointerUntil) return
    if (this.inputMutedAt > 0 && performance.now() - this.inputMutedAt < 1_500) return
    this.boardInputLockGen += 1
    this.ignoreBoardPointerUntil = 0
    this.inputMutedAt = 0
    this.input.enabled = true
    this.reclaimStuckTouches()
  }

  /**
   * Idle board with a card the engine has and the canvas does not. An attack
   * used to be the next paint. Also finishes a face that arrived as a sleeve
   * because the JPG was still in flight.
   */
  repairIdleField(): void {
    if (!this.sys?.isActive()) return
    if (this.openingDealHeld || this.openingDealPending) return
    this.ensureInputHears()
    if (this.coinHoldStuck() || this.playbackOverdue()) this.forceUnlockPlayback()
    if (!this.state || this.state.status !== 'active') return
    if (!this.matchStarted || !this.sys?.isActive()) return
    if (this.eventsPlaying || this.eventQ.length > 0 || this.paintingBoard || this.staleBoard) {
      return
    }
    const chainLive = (this.state.chain?.length ?? 0) >= 2
    const stuckHide =
      this.departingFieldUids.size > 0 ||
      this.departingHandUids.size > 0 ||
      (!chainLive && this.chainHidden.size > 0)
    if (stuckHide) {
      this.clearDepartureTracking()
      if (!chainLive) {
        this.chainPile = []
        this.chainHidden.clear()
      }
    }
    const stillHidden = (uid: string): boolean =>
      this.chainHidden.has(uid) || this.departingFieldUids.has(uid)
    let missing = this.fieldArtWaiting()
    if (!missing) {
      for (const seat of [0, 1] as Seat[]) {
        const zones = this.state.zones[seat]
        for (const card of [...zones.monsters, ...zones.spellTraps]) {
          if (card && !stillHidden(card.uid) && !this.spriteOnBoard(card.uid)) {
            missing = true
            break
          }
        }
        if (missing) break
        const field = zones.fieldSpell
        if (field && !stillHidden(field.uid) && !this.spriteOnBoard(field.uid)) missing = true
      }
    }
    if (!missing) return
    try {
      this.renderAll()
    } catch (err) {
      console.error('renderAll', err)
      this.paintingBoard = false
    }
  }

  /** Face-up art that is still a sleeve even though the texture has landed. */
  private fieldArtWaiting(): boolean {
    for (const sprite of this.fieldSprites.values()) {
      const id = sprite.getData('awaitArt')
      if (typeof id === 'string' && id && this.textures.exists(textureKey(id))) return true
    }
    return false
  }

  /** Ops row for a board that stopped accepting summons until a snapshot. */
  private noteSummonLock(reason: 'throw' | 'watchdog' | 'resync'): void {
    const state = this.state ?? this.pendingCommit
    const cpu = !!state && (state.seats[0].isAI || state.seats[1].isAI)
    track('summon_lock', {
      reason,
      mode: this.client.start ? 'local' : 'network',
      phase: state?.phase ?? '',
      turn: state && state.turnPlayer === this.mySeat ? 'me' : 'opp',
      cpu,
      turnN: state?.absoluteTurnCount ?? 0,
    })
  }

  /**
   * Own Main Phase, but a summon tap cannot open the menu. Say why.
   */
  private toastSummonBlocked(): void {
    const state = this.state
    if (!state) return
    if (state.pendingTargets?.chooser === this.mySeat) {
      this.toast(t('duel.toast.mustChooseTarget'), 'warn')
      return
    }
    if (state.pendingChainOrder?.chooser === this.mySeat) {
      this.toast(t('duel.picker.chainOrder'), 'warn')
      return
    }
    if (state.pendingFlipEffect?.chooser === this.mySeat) {
      this.toast(t('duel.toast.flipFirst'), 'warn')
      return
    }
    if (state.pendingPriority || state.chain.length > 0) {
      const mine = state.pendingPriority?.responder === this.mySeat
      this.toast(
        mine ? t('duel.error.notNow') : t('duel.prompt.waitingRespond'),
        'warn',
      )
      return
    }
    this.toast('', 'warn', 'normalSummonUsed')
  }

  /**
   * The answer we just sent does not match any open window. Hide ours so a
   * phase tap is not turned into another dead pass while the clock runs.
   */
  private dropStaleAnswerWindow(): void {
    const shown = this.state
    if (!shown) return
    const patch: Partial<MatchState> = {}
    if (shown.pendingPriority?.responder === this.mySeat) patch.pendingPriority = null
    if (shown.pendingChainOrder?.chooser === this.mySeat) patch.pendingChainOrder = null
    if (Object.keys(patch).length === 0) return
    this.chainOrderRequestId = null
    this.state = { ...shown, ...patch }
    this.publishBoardState()
  }

  /**
   * Hides a window this seat already answered. The board snapshot stays
   * the pre-resolution one so the hit can still play; commitBoard publishes
   * the real prompt (or none) when that animation ends.
   * The coin call sits on top of the toss dialog on a phone, so it has to
   * drop as soon as Cara or Cruz is chosen, not when the dialog closes.
   */
  private releaseClosedPrompt(next: MatchState): void {
    const shown = this.state
    if (!shown) return
    const patch: Partial<MatchState> = {}
    const priority = shown.pendingPriority
    if (
      priority &&
      priority.responder === this.mySeat &&
      next.pendingPriority?.requestId !== priority.requestId
    ) {
      patch.pendingPriority = null
    }
    const coin = shown.pendingCoinCall
    if (
      coin &&
      coin.chooser === this.mySeat &&
      next.pendingCoinCall?.requestId !== coin.requestId
    ) {
      patch.pendingCoinCall = null
    }
    const choice = shown.pendingChoice
    if (
      choice &&
      choice.chooser === this.mySeat &&
      next.pendingChoice?.requestId !== choice.requestId
    ) {
      patch.pendingChoice = null
    }
    if (Object.keys(patch).length === 0) return
    this.state = { ...shown, ...patch }
    this.publishBoardState()
  }

  /**
   * Shows this seat's coin call or yes/no as soon as the payload arrives.
   * Those buttons used to wait for commitBoard, so the Battle Phase toast
   * ("call Heads or Tails") was the only control and every other click died.
   */
  private revealOwnedPrompt(next: MatchState): void {
    if (!this.state || this.isWatching()) return
    const coin = next.pendingCoinCall
    const choice = next.pendingChoice
    const showCoin =
      !!coin &&
      coin.chooser === this.mySeat &&
      !this.answeredPromptIds.has(coin.requestId) &&
      this.state.pendingCoinCall?.requestId !== coin.requestId
    const showChoice =
      !!choice &&
      choice.chooser === this.mySeat &&
      !this.answeredPromptIds.has(choice.requestId) &&
      this.state.pendingChoice?.requestId !== choice.requestId
    if (!showCoin && !showChoice) return
    this.clearAttackPick()
    this.clearMenu()
    this.state = {
      ...this.state,
      pendingCoinCall: showCoin ? coin : this.state.pendingCoinCall,
      pendingChoice: showChoice ? choice : this.state.pendingChoice,
      pendingAttack: next.pendingAttack,
      battleStep: next.battleStep,
    }
    this.publishBoardState()
  }

  /**
   * Pause after an AI action so the board can be read. Scales with the
   * animation-speed setting (slow ×1, normal ×½, fast ×⅓). Do not stall when
   * the committed snapshot already opened a window the local player must answer.
   * Phase-only ticks still dwell so M1/BP/EP stay visible on the strip.
   */
  private thinkDelayMs(p: GameEventsPayload): number {
    const waitingOnMe =
      p.state.pendingPriority?.responder === this.mySeat ||
      p.state.pendingDiscard?.seat === this.mySeat ||
      p.state.pendingFlipEffect?.chooser === this.mySeat ||
      p.state.pendingTargets?.chooser === this.mySeat ||
      p.state.pendingCoinCall?.chooser === this.mySeat ||
      p.state.pendingChoice?.chooser === this.mySeat ||
      p.state.pendingChainOrder?.chooser === this.mySeat ||
      p.state.pendingOfferingSummon === this.mySeat
    if (!p.replay && waitingOnMe) return this.ms(80)
    if (this.openingDealPlaying) return this.ms(80)
    if (p.events.every((e) => e.type === EventType.HAND_SHUFFLED)) return this.ms(80)
    if (!this.aiPlayback) return this.ms(80)
    const types = new Set(p.events.map((e) => e.type))
    const phaseOnly = p.events.every(
      (e) => e.type === EventType.PHASE_CHANGED || e.type === EventType.MESSAGE,
    )
    if (phaseOnly) return this.ms(AI_PHASE_DWELL_MS)
    if (
      types.has(EventType.BATTLE_RESOLVED) &&
      types.has(EventType.PHASE_CHANGED)
    ) {
      return this.ms(AI_ACTION_GAP_MS)
    }
    if (types.has(EventType.CARD_SUMMONED) || types.has(EventType.CARD_SET)) {
      return this.ms(AI_ACTION_GAP_MS)
    }
    if (
      types.has(EventType.ATTACK_DECLARED) ||
      types.has(EventType.ATTACK_REPLAY) ||
      types.has(EventType.CARD_FLIPPED) ||
      types.has(EventType.SPELL_ACTIVATED) ||
      types.has(EventType.TRAP_ACTIVATED)
    ) {
      return this.ms(AI_POST_COMBAT_MS)
    }
    return this.ms(AI_ACTION_GAP_MS)
  }

  /**
   * Drive the phase strip from this payload's events when present, falling
   * back to the post-action snapshot (which must be THAT action, not the
   * whole AI turn).
   *
   * Draw is auto-skipped in the engine, so the snapshot is already Standby.
   * Stop at DRAW so DP stays lit until the card lands.
   */
  /** Battle damage and a phase change in one payload. The strip waits for the hit. */
  private phaseFollowsBattle(events: GameEvent[]): boolean {
    return (
      events.some((e) => e.type === EventType.BATTLE_RESOLVED) &&
      events.some((e) => e.type === EventType.PHASE_CHANGED)
    )
  }

  private publishPhaseHud(p: GameEventsPayload): void {
    let { phase, turnPlayer, turnCount, absoluteTurnCount } = p.state
    let lockedOnDraw = false
    for (const e of p.events) {
      if (e.type === EventType.PHASE_CHANGED) {
        if (lockedOnDraw) break
        phase = e.phase
        turnPlayer = e.turnPlayer
        if (e.phase === EnginePhase.DRAW) lockedOnDraw = true
      } else if (e.type === EventType.TURN_CHANGED) {
        turnPlayer = e.turnPlayer
        turnCount = e.turnCount
        absoluteTurnCount = e.absoluteTurnCount
      }
    }
    const hudState = {
      ...p.state,
      phase,
      turnPlayer,
      turnCount,
      absoluteTurnCount,
    }
    this.phaseHudCb?.(hudState)
    this.syncTributePick(hudState, false)
    this.applyAutoFocusFromState(hudState)
    this.maybeFocusOfferingHand(hudState, 'start')
  }

  /** Unscaled while the AI is playing so speed 3 cannot skip past a lunge. */
  private visualMs(duration: number): number {
    const scaled = this.aiPlayback ? duration : this.ms(duration)
    if (!this.motionMinimal()) return scaled
    return Math.min(scaled, MINIMAL_BEAT_MS)
  }

  /** Flight length. Minimal keeps a short beat so events do not stack. */
  private flightMs(duration: number): number {
    const scaled = this.ms(duration)
    if (!this.motionMinimal()) return scaled
    return Math.min(scaled, MINIMAL_BEAT_MS)
  }

  private motionMinimal(): boolean {
    return useDuelPrefsStore().motionLevel === 'minimal'
  }

  /** Card shown on the board, with a Minimal flip preview applied. */
  private shownCard(card: CardInstance): CardInstance {
    const preview = this.previewFace.get(card.uid)
    if (!preview) return card
    return {
      ...card,
      faceUp: preview.faceUp,
      position: preview.defense ? CardPosition.DEFENSE : CardPosition.ATTACK,
    }
  }

  /** This device asked to drop particles, shake, and field atmosphere. */
  private animationsReduced(): boolean {
    return useDuelPrefsStore().motionLevel !== 'full'
  }

  /** Shatter pause before the graveyard flight. Zero when bursts are off. */
  private shatterHoldMs(): number {
    if (this.animationsReduced()) return 0
    return this.visualMs(
      Math.max(0, DESTROY_SHATTER_MS - DESTROY_SOUL_OVERLAP_MS),
    )
  }

  private flushQueuedOver(): void {
    if (!this.queuedOver) return
    if (this.openingDealPending || this.openingDealPlaying) return
    if (this.eventsPlaying || this.eventQ.length > 0) return
    const over = this.queuedOver
    this.queuedOver = null
    this.showGameOver(over)
  }

  /**
   * True when this batch was produced by an AI seat (its own turn, or a trap
   * it fired on the human's turn). CARD_DRAWN / SUMMONED / TRAP_ACTIVATED carry
   * `seat`; otherwise the post-action turn player is used.
   */
  private payloadHasAiActor(p: GameEventsPayload): boolean {
    const seats = p.state.seats
    if (seats[p.state.turnPlayer]?.isAI) return true
    return p.events.some((e) => {
      if (e.type === EventType.PHASE_CHANGED) {
        return seats[e.turnPlayer]?.isAI === true
      }
      if (!('seat' in e)) return false
      const seat = (e as { seat?: Seat }).seat
      return seat === 0 || seat === 1 ? seats[seat]?.isAI === true : false
    })
  }

  // ---------------------------------------------------------------------------
  // Layout helpers
  // ---------------------------------------------------------------------------

  private rowY(row: number): number {
    const totalH = 6 * (CARD_H + ZONE_GAP) + MID_GAP
    const top = (this.scale.height - totalH) / 2 + CARD_H / 2
    const midShift = row >= 3 ? MID_GAP : 0
    return top + row * (CARD_H + ZONE_GAP) + midShift
  }

  private focusBandsActive(): boolean {
    return isDuelCompactNow() && useDuelPrefsStore().focusBands
  }

  /**
   * World-space strip the camera should fill. Uniform zoom; no stretched slots.
   */
  private bandWorldRect(band: FocusBand): { cx: number; cy: number; h: number } {
    const pad = 12
    let top: number
    let bottom: number
    if (band === 'hand') {
      top = this.rowY(4) - CARD_H / 2 - pad
      bottom = this.rowY(5) + (CARD_H * HAND_SCALE) / 2 + pad
    } else if (band === 'mine') {
      top = this.rowY(2) - CARD_H / 2 - pad
      bottom = this.rowY(4) + CARD_H / 2 + pad
    } else {
      top = this.rowY(0) - (CARD_H * HAND_SCALE) / 2 - pad
      bottom = this.rowY(2) + CARD_H / 2 + pad
    }
    const h = Math.max(120, bottom - top)
    return { cx: this.scale.width / 2, cy: (top + bottom) / 2, h }
  }

  /**
   * Resting camera: your hand (where you play), your monsters in Battle, or
   * the opponent's field on their turn. A paid Ultimate Offering summon on
   * that Battle Phase rests on your hand until the summon or the pass.
   */
  private homeFocusBand(state: MatchState | null = this.state): FocusBand {
    if (!state) return 'hand'
    // A summon or a pass is already in flight. Rest on the field that
    // action leaves, not on the hand the payment was still showing.
    const newest = this.pendingCommit ?? state
    if (this.offeringSummonOwed(newest)) return 'hand'
    if (state.turnPlayer !== this.mySeat) return 'opp'
    if (state.phase === EnginePhase.BATTLE) return 'mine'
    return 'hand'
  }

  /** 500 LP already paid; this seat still has to Normal Summon, Set, or pass. */
  private offeringSummonOwed(state: MatchState): boolean {
    return (
      state.pendingOfferingSummon === this.mySeat &&
      state.zones[this.mySeat].extraNormalSummons > 0
    )
  }

  /**
   * Compact camera stays on the opponent during their Battle Phase, so the
   * hand that Ultimate Offering just paid for is off screen. Pan there as
   * soon as the 500 LP land. The return home waits until the summon
   * animation finishes, so a pass with no follow still leaves their field.
   */
  private maybeFocusOfferingHand(
    state: MatchState,
    when: 'start' | 'commit',
  ): void {
    if (!this.focusBandsActive()) return
    const owed = this.offeringSummonOwed(state)
    const zone = state.zones[this.mySeat]
    const key = owed ? `${state.absoluteTurnCount}:${zone.extraNormalSummons}` : ''
    if (owed) {
      if (key === this.offeringHandFocusKey) return
      this.offeringHandFocusKey = key
      this.cancelReturnToHome()
      this.focusBand('hand', { animate: true })
      return
    }
    if (when !== 'commit' || !this.offeringHandFocusKey) return
    this.offeringHandFocusKey = ''
    if (this.homeReturnEvent) return
    this.focusBand(this.homeFocusBand(state), { animate: true })
  }

  /**
   * Auto-focus on phase / turn change. A manual tap or arrow holds until then
   * unless a play follows the camera, which returns to the home band after.
   */
  private applyAutoFocusFromState(state: MatchState): void {
    if (!this.focusBandsActive()) return
    const key = `${state.turnPlayer}:${state.phase}`
    if (key === this.lastAutoFocusKey) return
    this.lastAutoFocusKey = key
    this.cancelReturnToHome()
    this.focusBand(this.homeFocusBand(state), { animate: true, fromUser: false })
  }

  private bandAtWorldY(y: number): FocusBand {
    const handCut = (this.rowY(4) + this.rowY(5)) / 2
    const mineCut = (this.rowY(2) + this.rowY(3)) / 2
    if (y > handCut) return 'hand'
    if (y > mineCut) return 'mine'
    return 'opp'
  }

  /**
   * HTML controls sit on top of the canvas. Phaser still hears those touches
   * on `window`, so a tap on Shuffle would pan to whatever band is under it.
   */
  private pointerStartedOnCanvas(): boolean {
    const el = this.input.activePointer?.downElement
    return !el || el === this.game.canvas
  }

  private focusBandFromWorld(_x: number, y: number, fromUser: boolean): void {
    if (!this.focusBandsActive()) return
    if (!this.pointerStartedOnCanvas()) return
    this.focusBand(this.bandAtWorldY(y), { fromUser, animate: true })
  }

  private layoutBoardTapZone(): void {
    this.boardTapZone
      ?.setPosition(this.scale.width / 2, this.scale.height / 2)
      .setSize(this.scale.width, this.scale.height)
  }

  private applyFocusCamera(band: FocusBand, animate: boolean): void {
    if (!this.focusBandsActive()) {
      this.resetFocusCamera(animate)
      return
    }
    const { cx, cy, h } = this.bandWorldRect(band)
    const zoom = this.bandZoom(band, h)
    const cam = this.cameras.main
    if (
      animate &&
      Math.abs(cam.zoom - zoom) < 0.04 &&
      Math.abs(cam.midPoint.x - cx) < 10 &&
      Math.abs(cam.midPoint.y - cy) < 10
    ) {
      return
    }
    this.moveFocusCamera(cx, cy, zoom, animate)
  }

  /**
   * Zoom so the band fills the height, without cropping Deck / Extra / GY.
   * Compact cards are larger, so the side piles sit wider than a 6-card fan.
   */
  private fieldWidthZoom(): number {
    const stack = (PILE_MAX_LAYERS - 1) * PILE_LAYER_STEP
    const left = this.sideColX('left') - CARD_W / 2 - 12 - stack
    const right = this.banishedPos(this.mySeat).x + CARD_W / 2 + 12
    return this.scale.width / Math.max(160, right - left)
  }

  /**
   * Hand zoom is capped so a 5–6 card fan stays on screen; field bands can go
   * closer. All bands stay wide enough that the side piles remain in view.
   */
  private bandZoom(band: FocusBand, h: number): number {
    const zoomH = this.scale.height / h
    let zoom = zoomH
    if (band === 'hand') {
      const pitch = CARD_W * HAND_SCALE + Math.max(4, HAND_SLOT_GAP)
      const fan = 6 * pitch + CARD_W * HAND_SCALE
      zoom = Math.min(zoomH, this.scale.width / fan)
    }
    const pileZoom = this.fieldWidthZoom()
    zoom = Math.min(zoom, pileZoom)
    const minZ = Math.min(FOCUS_ZOOM_MIN, pileZoom)
    return Phaser.Math.Clamp(zoom, Math.max(1, minZ), FOCUS_ZOOM_MAX)
  }

  /**
   * During your Battle, stay on the monster rows. Do not jump to the
   * opponent's hand band when a clash sits on their side.
   */
  private followBandFor(y: number): FocusBand {
    if (this.homeFocusBand() === 'mine') return 'mine'
    return this.bandAtWorldY(y)
  }

  /**
   * Field → hand bounce. Follow the flight, settle on that hand, then home.
   * Battle home is the monster rows; followFlight would snap back too soon.
   */
  private followReturnToHand(
    from: { x: number; y: number },
    to: { x: number; y: number },
    seat: Seat,
    durationMs: number,
  ): void {
    if (!this.focusBandsActive()) return
    this.cancelReturnToHome()
    const destBand: FocusBand = seat === this.mySeat ? 'hand' : 'opp'
    const span = this.spanFocus(from, to)
    this.currentFocusBand = destBand
    this.focusBandCb?.(destBand)
    this.moveFocusCamera(span.cx, span.cy, span.zoom, true)
    this.time.delayedCall(durationMs, () => {
      if (!this.focusBandsActive() || !this.sys?.isActive()) return
      this.focusBand(destBand, { animate: true })
    })
    this.scheduleReturnToHome(durationMs + RETURN_HAND_HOLD_MS)
  }

  /**
   * Pan/zoom the compact camera onto a world point (activations, attacks, piles).
   * Keeps the point in view with a margin; does not center on side piles so the
   * shot never runs off the left/right of the mat.
   */
  private followAction(
    x: number,
    y: number,
    opts: { delay?: number; restMs?: number } = {},
  ): void {
    const run = (): void => {
      if (!this.focusBandsActive() || !this.sys?.isActive()) return
      if (this.isFieldPickOpen()) return
      const band = this.followBandFor(y)
      this.currentFocusBand = band
      this.focusBandCb?.(band)
      const { h } = this.bandWorldRect(band)
      const zoom = this.bandZoom(band, h)
      const { cx, cy } = this.nudgeFocusToPoint(x, y, zoom)
      const onScreen =
        this.pointInFocusView(x, y, CARD_W * 0.65) &&
        this.overlayPointVisible(x, y)
      this.moveFocusCamera(cx, cy, zoom, onScreen)
    }
    const delay = opts.delay ?? 0
    if (delay > 0) this.time.delayedCall(delay, run)
    else run()
    const rest = opts.restMs ?? FOCUS_HOME_HOLD_MS
    this.scheduleReturnToHome(delay + FOCUS_CAM_MS + rest)
  }

  /** Targeting on the field: keep that shot until the pick closes. */
  private isFieldPickOpen(): boolean {
    return (
      this.clickMode !== 'idle' ||
      this.pendingTribute !== null ||
      this.engineTargetRequestId !== null ||
      this.myFlipFieldTargets().length > 0
    )
  }

  /**
   * Monsters and Spell/Traps the open Flip Effect can still click.
   * The window lives on the upcoming snapshot while the flip is animating,
   * so a Main Phase camera must not slide back to the hand before the pick.
   */
  private myFlipFieldTargets(): string[] {
    const current = this.state?.pendingFlipEffect
    const upcoming = this.pendingCommit?.pendingFlipEffect
    const flip =
      current?.chooser === this.mySeat
        ? current
        : upcoming?.chooser === this.mySeat
          ? upcoming
          : null
    if (!flip) return []
    return flip.targetUids.filter((uid) => this.uidIsBoardTarget(uid))
  }

  /** Spell-target uids, or the Flip Effect's field uids when that window is open. */
  private openFieldPickUids(): string[] {
    const engine = this.engineFieldTargetUids()
    if (engine.length > 0) return engine
    return this.myFlipFieldTargets()
  }

  /**
   * A field pick is open. Cards that are not legal targets must not take the
   * tap: the Equip just played sits in the spell row under the monster, and
   * its hit box covers the bottom of that monster.
   */
  private blockingFieldPick(): boolean {
    if (this.clickMode !== 'idle') return true
    if (this.state?.pendingFlipEffect?.chooser === this.mySeat) return true
    return this.state?.pendingTargets?.chooser === this.mySeat
  }

  private fieldClickable(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
  ): boolean {
    if (!this.blockingFieldPick()) return true
    return this.targetGlowKind(card, seat, zone) != null
  }

  private cancelReturnToHome(): void {
    this.homeReturnEvent?.remove(false)
    this.homeReturnEvent = null
  }

  /**
   * After the last follow, ease back to the home band (hand on your Main).
   */
  private scheduleReturnToHome(fromNowMs: number): void {
    if (!this.focusBandsActive()) return
    this.cancelReturnToHome()
    this.homeReturnEvent = this.time.delayedCall(fromNowMs, () => {
      this.homeReturnEvent = null
      if (!this.focusBandsActive() || !this.sys?.isActive()) return
      if (this.isFieldPickOpen()) {
        this.scheduleReturnToHome(FOCUS_HOME_HOLD_MS)
        return
      }
      this.focusBand(this.homeFocusBand(), { animate: true })
    })
  }

  /**
   * Hold still during a flight so the overlay dest does not drift. If the
   * slot is outside the current band, snap once so the mesh can finish.
   */
  private followFlight(
    from: { x: number; y: number },
    to: { x: number; y: number },
    durationMs: number,
    opts?: { camera?: boolean },
  ): void {
    if (opts?.camera === false) return
    if (!this.focusBandsActive()) return
    if (this.isFieldPickOpen()) {
      this.scheduleReturnToHome(durationMs + FOCUS_HOME_HOLD_MS)
      return
    }
    if (!this.pointInFocusView(to.x, to.y, CARD_H * 0.35)) {
      const span = this.spanFocus(from, to)
      this.currentFocusBand = this.followBandFor(to.y)
      this.focusBandCb?.(this.currentFocusBand)
      this.moveFocusCamera(span.cx, span.cy, span.zoom, false)
    }
    this.scheduleReturnToHome(durationMs + FOCUS_HOME_HOLD_MS)
  }

  /**
   * Draw flights (every turn change) share the band auto-focus just started.
   * A second snap-zoom here forced the phone to rescale the whole board
   * while the 3D card was already in the air. If that band is already the
   * destination, leave the tween alone — a later "return home" would read
   * the pre-commit turn and yank the camera back.
   */
  private followDrawCamera(
    to: { x: number; y: number },
    durationMs: number,
  ): void {
    if (!this.focusBandsActive()) return
    const band = this.followBandFor(to.y)
    if (band === this.currentFocusBand) return
    this.focusBand(band, { animate: true })
    this.scheduleReturnToHome(durationMs + FOCUS_HOME_HOLD_MS)
  }

  /** True when a world point projects onto the visible overlay (not off-mat). */
  private overlayPointVisible(x: number, y: number): boolean {
    const n = this.overlayNorm(x, y)
    const pad = 0.06
    return n.x >= pad && n.x <= 1 - pad && n.y >= pad && n.y <= 1 - pad
  }

  /** True when (x, y) sits inside the current camera view with `pad` inset. */
  private pointInFocusView(x: number, y: number, pad: number): boolean {
    const v = this.cameraViewRect()
    return (
      x >= v.x + pad &&
      x <= v.x + v.width - pad &&
      y >= v.y + pad &&
      y <= v.y + v.height - pad
    )
  }

  /**
   * Camera center that keeps `x,y` on screen without leaving the board.
   * Prefers the field midline so Field/GY piles sit at the edge, not the middle.
   */
  private nudgeFocusToPoint(
    x: number,
    y: number,
    zoom: number,
  ): { cx: number; cy: number } {
    const viewW = this.scale.width / Math.max(1, zoom)
    const margin = CARD_W * 0.65
    let cx = this.scale.width / 2
    const left = cx - viewW / 2
    const right = cx + viewW / 2
    if (x - margin < left) cx = x - margin + viewW / 2
    else if (x + margin > right) cx = x + margin - viewW / 2
    return this.clampFocusCenter(cx, y, zoom)
  }

  /** Fit both ends of a flight in the view so the overlay never leaves the mat. */
  private spanFocus(
    a: { x: number; y: number },
    b: { x: number; y: number },
  ): { cx: number; cy: number; zoom: number } {
    const pad = CARD_H * 0.75
    const left = Math.min(a.x, b.x) - pad
    const right = Math.max(a.x, b.x) + pad
    const top = Math.min(a.y, b.y) - pad
    const bottom = Math.max(a.y, b.y) + pad
    const spanW = Math.max(CARD_W * 2, right - left)
    const spanH = Math.max(CARD_H * 2, bottom - top)
    const zoom = Math.max(
      1,
      Math.min(
        FOCUS_ZOOM_MAX,
        this.fieldWidthZoom(),
        this.scale.width / spanW,
        this.scale.height / spanH,
      ),
    )
    const cx = (left + right) / 2
    const cy = (top + bottom) / 2
    return { ...this.clampFocusCenter(cx, cy, zoom), zoom }
  }

  /** Keep a camera center inside the board for this zoom (no empty off-mat pan). */
  private clampFocusCenter(
    cx: number,
    cy: number,
    zoom: number,
  ): { cx: number; cy: number } {
    const z = Math.max(1, zoom)
    const viewW = this.scale.width / z
    const viewH = this.scale.height / z
    const minX = viewW / 2
    const maxX = this.scale.width - viewW / 2
    const minY = viewH / 2
    const maxY = this.scale.height - viewH / 2
    return {
      cx: Phaser.Math.Clamp(cx, Math.min(minX, maxX), Math.max(minX, maxX)),
      cy: Phaser.Math.Clamp(cy, Math.min(minY, maxY), Math.max(minY, maxY)),
    }
  }

  private resetFocusCamera(animate: boolean): void {
    this.moveFocusCamera(this.scale.width / 2, this.scale.height / 2, 1, animate)
  }

  private moveFocusCamera(
    cx: number,
    cy: number,
    zoom: number,
    animate: boolean,
  ): void {
    const z = Math.max(1, zoom)
    const c = this.clampFocusCenter(cx, cy, z)
    const cam = this.cameras.main
    cam.panEffect.reset()
    cam.zoomEffect.reset()
    if (!animate) {
      cam.setZoom(z)
      cam.centerOn(c.cx, c.cy)
      cam.preRender()
      this.publishHandWaitAnchor()
      return
    }
    cam.pan(c.cx, c.cy, FOCUS_CAM_MS, 'Sine.easeInOut')
    cam.zoomTo(z, FOCUS_CAM_MS, 'Sine.easeInOut')
    this.time.delayedCall(FOCUS_CAM_MS, () => {
      if (!this.sys?.isActive()) return
      this.publishHandWaitAnchor()
    })
  }

  private slotX(index: number, count: number, gap: number = SLOT_GAP): number {
    const totalW = count * (CARD_W + gap)
    const left = (this.scale.width - totalW) / 2 + CARD_W / 2
    return left + index * (CARD_W + gap)
  }

  /** Center-x of a hand card at `index` in a fan of `length` cards. */
  private handSlotX(index: number, length: number): number {
    const w = CARD_W * HAND_SCALE
    const gap = HAND_SLOT_GAP
    const count = Math.max(5, length)
    const shifted = index - (length - 5) / 2
    const totalW = count * (w + gap)
    const left = (this.scale.width - totalW) / 2 + w / 2
    return left + shifted * (w + gap)
  }

  private pose(x: number, y: number): SlotPose {
    return { x, y, scale: 1 }
  }

  /** Pose of a 5-zone (or custom) slot. */
  private at(
    row: number,
    index: number,
    count: number = 5,
    gap: number = SLOT_GAP,
  ): SlotPose {
    return this.pose(this.slotX(index, count, gap), this.rowY(row))
  }

  /** Pose of a hand card in row 0 or 5. */
  private handAt(row: number, index: number, length: number): SlotPose {
    return { x: this.handSlotX(index, length), y: this.rowY(row), scale: HAND_SCALE }
  }

  private handKeepsUid(state: MatchState | null, uid: string): boolean {
    if (!state) return false
    return state.zones.some((zone) => zone.hand.some((card) => card.uid === uid))
  }

  /**
   * A flying draw or bounce only keeps its fan slot while the payload's
   * hand will still hold it. A discard in that same payload must not.
   */
  private arrivalStillDue(uid: string): boolean {
    if (this.pendingCommit) return this.handKeepsUid(this.pendingCommit, uid)
    return this.handKeepsUid(this.state, uid)
  }

  /**
   * The payload already moved this uid out of every hand. `this.state` still
   * has the pre-commit fan, so a chain or landing `renderAll` would put the
   * discarded card back until commit.
   */
  private pendingRemovedFromHand(uid: string): boolean {
    if (this.keptDiscardUids.has(uid)) return false
    return !!this.pendingCommit && !this.handKeepsUid(this.pendingCommit, uid)
  }

  private forgetArrivingHand(uid: string): void {
    this.arrivingHandUids.delete(uid)
    this.arrivingHandSeat.delete(uid)
    this.arrivingHandCardId.delete(uid)
    this.landedHandUids.delete(uid)
  }

  /** Drop arrival slots the committed hand no longer has. */
  private dropStaleArrivals(): void {
    for (const uid of [...this.arrivingHandUids]) {
      if (this.handKeepsUid(this.state, uid)) continue
      this.forgetArrivingHand(uid)
      this.destroyHandSprite(uid)
      this.handDisplayX.delete(uid)
    }
  }

  /** Hand size including draws that are still flying in. */
  private extraArrivingUids(seat: Seat, hand: CardInstance[]): string[] {
    const extras: string[] = []
    for (const [uid, owner] of this.arrivingHandSeat) {
      if (owner !== seat) continue
      if (hand.some((c) => c.uid === uid)) continue
      if (this.departingHandUids.has(uid)) continue
      if (!this.arrivalStillDue(uid)) continue
      extras.push(uid)
    }
    return extras
  }

  private handLayoutLength(seat: Seat, hand: CardInstance[]): number {
    return hand.length + this.extraArrivingUids(seat, hand).length
  }

  /**
   * Screen position of a card on the field (monster or spell/trap zone) using the
   * current snapshot, plus whether a monster sits in Defense (rotated). Returns
   * null when the card is not on either field.
   */
  private cardFieldPos(
    uid: string,
  ): (SlotPose & { defense: boolean; zone: 'monsters' | 'spellTraps' | 'fieldSpell' }) | null {
    if (!this.state) return null
    for (const seat of [0, 1] as Seat[]) {
      const z = this.state.zones[seat]
      const mi = z.monsters.findIndex((c) => c?.uid === uid)
      if (mi !== -1) {
        const pose = this.at(seat === this.mySeat ? 3 : 2, mi)
        return {
          ...pose,
          defense: z.monsters[mi]!.position === CardPosition.DEFENSE,
          zone: 'monsters',
        }
      }
      const si = z.spellTraps.findIndex((c) => c?.uid === uid)
      if (si !== -1) {
        const pose = this.at(seat === this.mySeat ? 4 : 1, si)
        return { ...pose, defense: false, zone: 'spellTraps' }
      }
      if (z.fieldSpell?.uid === uid) {
        const slot = this.fieldSlotPos(seat)
        return { ...slot, defense: false, zone: 'fieldSpell' }
      }
    }
    return null
  }

  /** Screen position of a card still sitting in a hand row, or null. */
  private handScreenPos(seat: Seat, uid: string): SlotPose | null {
    if (!this.state) return null
    const hand = this.state.zones[seat].hand
    const index = hand.findIndex((c) => c.uid === uid)
    if (index === -1) return null
    const row = seat === this.mySeat ? 5 : 0
    return this.handAt(row, index, this.handLayoutLength(seat, hand))
  }

  /** Center of a monster/spell-trap zone slot. */
  private zoneSlotPos(
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
    zoneIndex: number,
  ): SlotPose {
    const mine = seat === this.mySeat
    let row: number
    if (zone === 'monsters') row = mine ? 3 : 2
    else row = mine ? 4 : 1
    return this.at(row, zoneIndex)
  }

  /** Monster-zone row for a seat (closer to the mid-field HUD). */
  private monsterRow(seat: Seat): number {
    return seat === this.mySeat ? 3 : 2
  }

  /** Spell/trap-zone row for a seat (closer to that seat's hand). */
  private spellRow(seat: Seat): number {
    return seat === this.mySeat ? 4 : 1
  }

  /**
   * Playmat side columns: Field/Extra left of the 5-zone row, GY/Deck right of it.
   */
  private sideColX(side: 'left' | 'right'): number {
    if (side === 'left') {
      return this.slotX(0, 5) - CARD_W / 2 - ZONE_GAP - CARD_W / 2
    }
    return this.slotX(4, 5) + CARD_W / 2 + ZONE_GAP + CARD_W / 2
  }

  private sideColPose(row: number, side: 'left' | 'right', extraX = 0): SlotPose {
    return this.pose(this.sideColX(side) + extraX, this.rowY(row))
  }

  /**
   * One field half: Field/Extra through Deck/GY, monsters to hand.
   * `mine` is the local player (bottom); the other is the opponent (top).
   */
  private halfMatBounds(mine: boolean): {
    x: number
    y: number
    w: number
    h: number
  } {
    const padX = 18
    const padEdge = 22
    const left = this.sideColX('left') - CARD_W / 2 - padX
    const right = this.sideColX('right') + CARD_W / 2 + padX
    const mid = (this.rowY(2) + this.rowY(3)) / 2
    const top = mine ? mid : this.rowY(0) - CARD_H / 2 - padEdge
    const bottom = mine ? this.rowY(5) + CARD_H / 2 + padEdge : mid
    return {
      x: left,
      y: top,
      w: Math.max(80, right - left),
      h: Math.max(80, bottom - top),
    }
  }

  /**
   * Desktop LP plates sit just left of the playmat. Fractions of the FIT
   * canvas so Vue can pin them in the dark margin, off the cards.
   */
  desktopLpAnchors(): { left: number; oppTop: number; meBottom: number } {
    const opp = this.halfMatBounds(false)
    const mine = this.halfMatBounds(true)
    const w = Math.max(1, this.scale.width)
    const h = Math.max(1, this.scale.height)
    return {
      left: opp.x / w,
      oppTop: opp.y / h,
      meBottom: (mine.y + mine.h) / h,
    }
  }

  private layoutPlaymat(): void {
    if (this.matImage?.frame?.data) {
      const box = this.halfMatBounds(true)
      this.matImage.setPosition(box.x, box.y).setDisplaySize(box.w, box.h)
    }
    if (this.oppMatImage?.frame?.data) {
      const box = this.halfMatBounds(false)
      this.oppMatImage.setPosition(box.x, box.y).setDisplaySize(box.w, box.h)
    }
    this.clipPlaymat()
    this.applyMatOpacity()
  }

  /**
   * One rounded clip around both halves so the field reads as a single mat.
   */
  private clipPlaymat(): void {
    const gfx = this.matMaskGfx
    if (!gfx) return
    const mine = this.halfMatBounds(true)
    const opp = this.halfMatBounds(false)
    const x = Math.min(mine.x, opp.x)
    const y = opp.y
    const w = Math.max(mine.w, opp.w)
    const h = mine.y + mine.h - opp.y
    const r = Math.min(MAT_CORNER_RADIUS, w / 6, h / 8)
    gfx.clear()
    gfx.fillStyle(0xffffff, 1)
    gfx.fillRoundedRect(x, y, w, h, r)
    if (!this.matLayer.mask) {
      this.matLayer.setMask(gfx.createGeometryMask())
    }
  }

  private applyMatOpacity(): void {
    const alpha = useDuelPrefsStore().matOpacity / 100
    this.matImage?.setAlpha(alpha)
    this.oppMatImage?.setAlpha(alpha)
  }

  /**
   * Local vs-AI exposes `start()`; network PvP does not. Snapshot `isAI`
   * covers a server CPU seat that arrives after create().
   */
  private opponentIsAi(): boolean {
    const opp = (this.mySeat === 0 ? 1 : 0) as Seat
    if (this.state?.seats[opp]?.isAI === true) return true
    return typeof this.client.start === 'function'
  }

  /**
   * Loads the chosen (or rolled) playmat once per duel, behind the cards.
   */
  private installPlaymat(): void {
    this.switchPlaymat(this.client.duelMatId ?? useDuelPrefsStore().matId)
    this.maybeInstallOppPlaymat()
  }

  /**
   * CPU half: one random mat for this duel. Human PvP uses the id from
   * the snapshot (and cosmetics updates). Not re-rolled if you change yours.
   */
  private maybeInstallOppPlaymat(): void {
    if (this.opponentIsAi()) {
      if (this.resolvedOppMatId) return
      this.applyOppMatId(pickRandomMatId(this.resolvedMatId || undefined))
      return
    }
    const opp = (this.mySeat === 0 ? 1 : 0) as Seat
    const id = this.state?.seats[opp]?.matId
    if (id) this.applyOppMatId(id)
  }

  /**
   * Draws the opponent half. No-op when the id is unknown or already shown.
   */
  private applyOppMatId(id: string): void {
    if (!isGamematId(id) || id === this.resolvedOppMatId) return
    this.resolvedOppMatId = id
    this.loadHalfMat('opp', id)
  }

  private switchPlaymat(pref: string): void {
    this.resolvedMatId = isGamematId(pref) ? pref : resolveDuelMatId(pref)
    this.loadHalfMat('mine', this.resolvedMatId)
  }

  /**
   * Texture key for a seat's face-down cards. The opponent's sleeve comes
   * from their seat. A spectator uses both seats, never the local pref.
   */
  private sleeveKey(mine: boolean): string {
    const id = mine ? this.resolvedSleeveId : this.resolvedOppSleeveId
    if (!id) return 'card-back'
    const key = sleeveTextureKey(id)
    if (this.textures.exists(key)) return key
    // A mid-duel swap must not flash the house back while the new file loads.
    if (mine && this.shownSleeveId) {
      const prev = sleeveTextureKey(this.shownSleeveId)
      if (this.textures.exists(prev)) return prev
    }
    return 'card-back'
  }

  private installSleeves(): void {
    if (this.isWatching()) return
    this.switchSleeve(useDuelPrefsStore().sleeveId)
    this.maybeInstallOppSleeve()
  }

  /** Bottom half is seat 0's sleeve, top half is seat 1's. */
  private installWatchSleeves(state: MatchState | null): void {
    if (!state) return
    const bottom = state.seats[0]?.sleeveId
    const top = state.seats[1]?.sleeveId
    if (bottom) this.applySeatSleeve(true, bottom)
    if (top) this.applyOppSleeveId(top)
  }

  /**
   * CPU backs: one random sleeve for this duel. Human sleeves come from
   * the snapshot. Not re-rolled if you change yours.
   */
  private maybeInstallOppSleeve(): void {
    if (this.isWatching()) {
      this.installWatchSleeves(this.state)
      return
    }
    if (this.resolvedOppSleeveId) return
    if (this.opponentIsAi()) {
      const id = pickRandomSleeveId(this.resolvedSleeveId || undefined)
      this.resolvedOppSleeveId = id
      void this.loadSleeveTexture(id).then(() => {
        if (!this.sys.isActive()) return
        if (this.state) this.renderAll()
      })
      return
    }
    const opp = (this.mySeat === 0 ? 1 : 0) as Seat
    const id = this.state?.seats[opp]?.sleeveId
    if (id) this.applyOppSleeveId(id)
  }

  /** Face-down cards owned by the bottom seat. Watching does not use the local pref. */
  private applySeatSleeve(mine: boolean, id: string): void {
    if (!mine) {
      this.applyOppSleeveId(id)
      return
    }
    if (!isSleeveId(id)) return
    if (id === this.resolvedSleeveId && this.shownSleeveId === id) return
    this.resolvedSleeveId = id
    if (!this.isWatching()) setDuelSleeveUrlOverride(sleeveUrl(id))
    const gen = ++this.sleeveLoadGen
    void this.loadSleeveTexture(id).then(() => {
      if (!this.sys.isActive() || gen !== this.sleeveLoadGen) return
      if (!this.textures.exists(sleeveTextureKey(id))) return
      this.shownSleeveId = id
      if (this.state) this.renderAll()
    })
  }

  private applyOppSleeveId(id: string): void {
    if (!isSleeveId(id) || id === this.resolvedOppSleeveId) return
    this.resolvedOppSleeveId = id
    void this.loadSleeveTexture(id).then(() => {
      if (!this.sys.isActive()) return
      if (this.state) this.renderAll()
    })
  }

  private switchSleeve(pref: string): void {
    const next = resolveDuelSleeveId(pref)
    this.resolvedSleeveId = next
    setDuelSleeveUrlOverride(sleeveUrl(next))
    const gen = ++this.sleeveLoadGen
    void this.loadSleeveTexture(next).then(() => {
      if (!this.sys.isActive() || gen !== this.sleeveLoadGen) return
      if (!this.textures.exists(sleeveTextureKey(next))) return
      this.shownSleeveId = next
      if (this.state) this.renderAll()
    })
  }

  private loadSleeveTexture(id: string): Promise<void> {
    // Same downsample as card faces. Full-res addImage has no mips,
    // so a detailed sleeve aliases into blocks at CARD_W.
    return this.adoptPhaserTexture(sleeveTextureKey(id), sleeveUrl(id))
  }

  /**
   * Face-down VFX / 3D flights. Each seat uses its own sleeve.
   */
  private sleeveBackUrl(mine: boolean): string {
    if (mine) {
      return this.resolvedSleeveId
        ? sleeveUrl(this.resolvedSleeveId)
        : CARD_BACK_URL
    }
    if (this.resolvedOppSleeveId) return sleeveUrl(this.resolvedOppSleeveId)
    return CARD_BACK_URL
  }

  private cardIsMine(uid: string): boolean {
    const card = this.findCardByUid(uid)
    if (!card) return true
    return card.owner === this.mySeat
  }

  /**
   * Sleeve of this copy. Follows the deck it was dealt from, even after
   * Change of Heart moves it onto the other field.
   */
  private sleeveMine(card: { owner: Seat }): boolean {
    return card.owner === this.mySeat
  }

  private loadHalfMat(which: 'mine' | 'opp', id: string): void {
    const def = gamematById(id)
    if (!def) return
    const key = matTextureKey(def.id)
    const gen = which === 'mine' ? ++this.matLoadGen : ++this.oppMatLoadGen
    void this.loadMatTexture(key, def.src).then(() => {
      if (!this.sys.isActive()) return
      if (which === 'mine' && gen !== this.matLoadGen) return
      if (which === 'opp' && gen !== this.oppMatLoadGen) return
      const prev = which === 'mine' ? this.matImage : this.oppMatImage
      prev?.destroy()
      const img = this.add.image(0, 0, key).setOrigin(0, 0)
      this.matLayer.add(img)
      if (which === 'mine') this.matImage = img
      else this.oppMatImage = img
      this.layoutPlaymat()
    })
  }

  private loadMatTexture(key: string, url: string): Promise<void> {
    this.textureSources.set(key, { url, kind: 'image' })
    if (this.textures.exists(key)) return Promise.resolve()
    const pending = this.pendingTextureKeys.get(key)
    if (pending !== undefined) return pending
    const promise = loadCardImage(url)
      .then((img) => {
        if (this.sys.settings.status >= Phaser.Scenes.SHUTDOWN) return
        if (!this.textures.exists(key)) {
          this.textures.addImage(key, img)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        this.pendingTextureKeys.delete(key)
      })
    this.pendingTextureKeys.set(key, promise)
    return promise
  }

  /** Center of a seat's Field Zone, left of their monster row. */
  private fieldSlotPos(seat: Seat): SlotPose {
    return this.sideColPose(this.monsterRow(seat), 'left')
  }

  /** Center of a seat's Extra Deck pile, left of their spell/trap row. */
  private extraPos(seat: Seat): SlotPose {
    return this.sideColPose(this.spellRow(seat), 'left')
  }

  /** Center of a seat's graveyard pile, right of their monster row. */
  private graveyardPos(seat: Seat): SlotPose {
    return this.sideColPose(this.monsterRow(seat), 'right')
  }

  /** Center of a seat's Main Deck pile, right of their spell/trap row. */
  private deckPos(seat: Seat): SlotPose {
    return this.sideColPose(this.spellRow(seat), 'right')
  }

  /** Center of a seat's Banished pile, further right of their Graveyard. */
  private banishedPos(seat: Seat): SlotPose {
    const stackClearance = (PILE_MAX_LAYERS - 1) * PILE_LAYER_STEP
    return this.sideColPose(
      this.monsterRow(seat),
      'right',
      CARD_W + ZONE_GAP + stackClearance,
    )
  }

  /** Visible stack layers for a pile. Empty stays a bare frame. */
  private pileLayerCount(count: number): number {
    if (count <= 0) return 0
    return Math.min(PILE_MAX_LAYERS, Math.max(1, Math.round(count / 3)))
  }

  /**
   * Back-to-front card backs, then the top face (or another back). The top card
   * stays at (0, 0); extra layers shift outward by `dir`.
   */
  private addPileStack(
    pile: Phaser.GameObjects.Container,
    dir: number,
    layers: number,
    topCard: CardInstance | null,
    faceDown: boolean,
    mine: boolean,
  ): void {
    const backKey = this.sleeveKey(mine)
    const backUrl = this.sleeveBackUrl(mine)
    for (let i = layers - 1; i >= 1; i--) {
      const ox = dir * i * PILE_LAYER_STEP
      this.addSleevePlate(pile, backKey, backUrl, ox, 0, true)
    }
    if (topCard && !faceDown && topCard.cardId) {
      pile.add(this.makeCardSprite(topCard, mine, true))
      return
    }
    this.addSleevePlate(pile, backKey, backUrl, 0, 0, false)
  }

  /**
   * One sleeve on a pile. Nítida wraps it so the full picture can replace
   * the downsampled bake. Lower layers stay slightly darker.
   */
  private addSleevePlate(
    pile: Phaser.GameObjects.Container,
    textureKey: string,
    url: string,
    x: number,
    y: number,
    wash: boolean,
  ): void {
    const image = this.sleevePlateImage(textureKey, url)
    if (!image) {
      pile.add(this.sleeveFrame(wash).setPosition(x, y))
      return
    }
    if (wash) image.setTint(0xbbbbbb)
    if (!this.useSharpCards) {
      image.setPosition(x, y)
      pile.add(image)
      return
    }
    const wrap = this.add.container(x, y)
    wrap.add(image)
    this.markSharpPlate(wrap, image, url, wash)
    pile.add(wrap)
  }

  /**
   * Downsampled sleeve, or null when that key is missing. A missing Phaser
   * key paints a black rectangle on the phone, so the caller draws a frame.
   */
  private sleevePlateImage(
    textureKey: string,
    url: string,
  ): Phaser.GameObjects.Image | null {
    if (!this.textures.exists(textureKey)) {
      // `card-back` is the house file. The sleeve URL must not be baked
      // into that key while the real sleeve texture is still loading.
      const fetchUrl = textureKey === 'card-back' ? CARD_BACK_URL : url
      this.kickSleeveLoad(textureKey, fetchUrl)
      return null
    }
    return this.add.image(0, 0, textureKey).setDisplaySize(CARD_W, CARD_H)
  }

  /**
   * Countable face-down card while the sleeve file is still out. Fill stays
   * off the mat; the stroke is the edge the player counts.
   */
  private sleeveFrame(wash: boolean): Phaser.GameObjects.Rectangle {
    return this.add
      .rectangle(0, 0, CARD_W, CARD_H, wash ? 0x12141a : 0x1c2230)
      .setStrokeStyle(2, wash ? 0x8a8172 : 0xd6c7a1)
  }

  /**
   * Ask again for a sleeve that did not become a texture. Backs off so a
   * redraw storm does not refetch, and stops after a few misses until the
   * app returns and {@link restoreBoardTextures} clears the cap.
   */
  private kickSleeveLoad(key: string, url: string): void {
    if (!url || this.textures.exists(key)) return
    const now = performance.now()
    if ((this.sleeveRetryAt.get(key) ?? 0) > now) return
    const misses = this.sleeveMisses.get(key) ?? 0
    if (misses >= 4) return
    this.sleeveRetryAt.set(key, now + 1600)
    void this.adoptPhaserTexture(key, url).then(() => {
      if (!this.sys?.isActive()) return
      if (!this.textures.exists(key)) {
        this.sleeveMisses.set(key, misses + 1)
        return
      }
      this.sleeveMisses.delete(key)
      this.sleeveRetryAt.delete(key)
      this.queueArtRender()
    })
  }

  /** Face-down art in Nítida: cut this image out and show `url` behind it. */
  private markSharpPlate(
    host: Phaser.GameObjects.Container,
    image: Phaser.GameObjects.Image,
    url: string,
    wash = false,
  ): void {
    if (!this.useSharpCards || !url) return
    image.setData('sharpErase', true)
    host.setData('sharpFace', url)
    if (!host.getData('sharpKey')) host.setData('sharpKey', `c${++this.sharpSeq}`)
    if (wash) host.setData('sharpWash', 'brightness(0.73)')
  }

  private ms(duration: number): number {
    return AnimationSpeed.getDuration(duration)
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  /**
   * The cursor's hit target is about to be destroyed. Phaser hit-tests that
   * sprite on the same frame; a hand card under the mouse (Change of Heart,
   * Dragon Treasure) used to end the frame early and the duel stopped
   * taking clicks.
   */
  private dropHit(obj: Phaser.GameObjects.GameObject | undefined): void {
    if (!obj?.input || !obj.scene) return
    // disableInteractive leaves the sprite in the hit list. Destroying it
    // under the cursor (the hand card Change of Heart just left) then
    // throws on the next move and the board never takes another click.
    obj.removeInteractive()
  }

  /**
   * New card sprites wait in Phaser's insertion queue until the next input
   * preUpdate. A target window that opens on the same turn as Change of Heart
   * used to draw the opponent's monsters before they could take a click.
   */
  private flushingInput = false
  private flushInputQueue(): void {
    if (this.flushingInput || !this.input) return
    const plugin = this.input as Phaser.Input.InputPlugin & { preUpdate?: () => void }
    if (!plugin.preUpdate) return
    this.flushingInput = true
    try {
      plugin.preUpdate()
    } catch (err) {
      console.error('flushInputQueue', err)
    } finally {
      this.flushingInput = false
    }
  }

  /** Every interactive object on the board, including nested card sprites. */
  private dropBoardHits(): void {
    const walk = (node: Phaser.GameObjects.GameObject): void => {
      this.dropHit(node)
      const kids = (node as Phaser.GameObjects.Container).list
      if (!kids) return
      for (let i = 0; i < kids.length; i++) {
        const child = kids[i] as Phaser.GameObjects.GameObject | undefined
        if (child) walk(child)
      }
    }
    if (this.boardLayer) walk(this.boardLayer)
  }

  /**
   * `keepMenu` is for art-only redraws. A gameplay snapshot must drop the menu:
   * the card it points at may already have left that zone.
   */
  private renderAll(opts?: { keepMenu?: boolean }): void {
    syncBoardMetrics(this.scale.width, this.scale.height)
    if (!this.state) return
    this.paintingBoard = true
    this.dropBoardHits()
    this.flushInputQueue()
    this.hoveringFieldUid = null
    this.clearEquipLinkIcons(false)
    this.fieldSprites.clear()
    this.handSprites.clear()
    this.boardLayer.removeAll(true)
    if (!opts?.keepMenu) this.clearMenu()

    const opp = (this.mySeat === 0 ? 1 : 0) as Seat
    const oppZones = this.state.zones[opp]
    const myZones = this.state.zones[this.mySeat]

    // Piles and the Field Zone sit beside the rows. Paint them first so a
    // Defense monster in the left zone stays clickable: its art overlaps the
    // Field card, and the later sprite wins the tap.
    this.renderPiles(opp)
    this.renderPiles(this.mySeat)
    this.renderFieldSpell(opp)
    this.renderFieldSpell(this.mySeat)

    // Rows top->bottom: opp hand, opp spell/trap, opp monsters, my monsters, my spell/trap, my hand.
    this.renderHand(oppZones.hand, 0, false)
    this.renderLine(oppZones.spellTraps, 1, opp, 'spellTraps')
    this.renderLine(oppZones.monsters, 2, opp, 'monsters')
    this.renderLine(myZones.monsters, 3, this.mySeat, 'monsters')
    this.renderLine(myZones.spellTraps, 4, this.mySeat, 'spellTraps')
    this.renderHand(myZones.hand, 5, true)

    this.updateInfo()
    this.pruneHandDisplayX()
    this.paintSlotStandIns()
    this.liftPickTargets()
    this.paintingBoard = false
    this.refreshEquipLinkIcons()
    this.publishHandWaitAnchor()
    this.flushInputQueue()
  }

  /**
   * Face-up or set cards the engine has on the field and this board is not
   * drawing. Empty while an animation is still playing. Each card is
   * returned once per duel.
   */
  boardGaps(): BoardGap[] {
    const state = this.state
    if (!state || state.status !== 'active') return []
    if (!this.matchStarted || this.openingDealPending || this.openingDealHeld) return []
    if (this.eventsPlaying || this.eventQ.length > 0 || this.paintingBoard || this.staleBoard) {
      return []
    }
    if (state.id !== this.gapMatchId) {
      this.gapMatchId = state.id
      this.gapNoted.clear()
    }
    const found: BoardGap[] = []
    const take = (zone: string, card: CardInstance, seat: Seat) => {
      if (found.length >= 4) return
      if (this.departingFieldUids.has(card.uid) || this.chainHidden.has(card.uid)) return
      if (this.gapNoted.has(card.uid)) return
      if (this.spriteOnBoard(card.uid)) return
      this.gapNoted.add(card.uid)
      const cardId = card.cardId && /^\d{1,10}$/.test(card.cardId) ? card.cardId : 'hidden'
      found.push({
        card: cardId,
        zone,
        face: card.faceUp ? 'up' : 'down',
        side: seat === this.mySeat ? 'me' : 'opp',
      })
    }
    for (const seat of [0, 1] as Seat[]) {
      const zones = state.zones[seat]
      zones.monsters.forEach((card, index) => {
        if (card) take(`m${index}`, card, seat)
      })
      zones.spellTraps.forEach((card, index) => {
        if (card) take(`st${index}`, card, seat)
      })
      if (zones.fieldSpell) take('field', zones.fieldSpell, seat)
    }
    return found
  }

  private spriteOnBoard(uid: string): boolean {
    const sprite = this.fieldSprites.get(uid)
    if (!sprite || !sprite.active || !sprite.visible) return false
    return sprite.alpha > 0.05
  }

  private pruneHandDisplayX(): void {
    if (!this.state) return
    const live = new Set<string>()
    for (const seat of [0, 1] as Seat[]) {
      for (const c of this.state.zones[seat].hand) live.add(c.uid)
    }
    for (const uid of this.arrivingHandUids) live.add(uid)
    for (const uid of [...this.handDisplayX.keys()]) {
      if (!live.has(uid)) this.handDisplayX.delete(uid)
    }
  }

  /**
   * Answers the Vue trap-response overlay. `cardUid` null is Pass.
   * Returns false when the engine rejected the answer or the card never
   * left the client (Two-Pronged Attack with too few monsters). A true
   * result with no queued events refreshes the snapshot, so Vue cannot
   * keep a question the board already closed.
   */
  answerChoice(accept: boolean): boolean {
    const pending = this.state?.pendingChoice
    if (!pending || pending.chooser !== this.mySeat) return false
    const requestId = pending.requestId
    const ok = this.send({
      type: ActionType.ANSWER_CHOICE,
      requestId,
      accept,
    })
    if (ok) this.noteAnsweredPrompt(requestId)
    return ok
  }

  callCoin(call: 'heads' | 'tails'): boolean {
    const pending = this.state?.pendingCoinCall
    if (!pending || pending.chooser !== this.mySeat) return false
    const requestId = pending.requestId
    const ok = this.send({
      type: ActionType.CALL_COIN,
      requestId,
      call,
    })
    if (ok) this.noteAnsweredPrompt(requestId)
    return ok
  }

  /** Drops a question this seat already sent, including on a late commit. */
  private noteAnsweredPrompt(requestId: string): void {
    this.answeredPromptIds.add(requestId)
    if (this.answeredPromptIds.size > 24) {
      const first = this.answeredPromptIds.values().next().value
      if (first) this.answeredPromptIds.delete(first)
    }
    const shown = this.state
    if (!shown) return
    const coin = shown.pendingCoinCall?.requestId === requestId
    const choice = shown.pendingChoice?.requestId === requestId
    if (!coin && !choice) return
    this.state = {
      ...shown,
      pendingCoinCall: coin ? null : shown.pendingCoinCall,
      pendingChoice: choice ? null : shown.pendingChoice,
    }
    this.publishBoardState()
  }

  private withoutAnsweredPrompt(state: MatchState): MatchState {
    let next = state
    const coinId = next.pendingCoinCall?.requestId
    const choiceId = next.pendingChoice?.requestId
    if (coinId && this.answeredPromptIds.has(coinId)) {
      next = { ...next, pendingCoinCall: null }
    }
    if (choiceId && this.answeredPromptIds.has(choiceId)) {
      next = { ...next, pendingChoice: null }
    }
    return next
  }

  respondToPriority(cardUid: string | null, boost?: 'atk' | 'def'): boolean {
    const pending = this.state?.pendingPriority
    if (!pending || pending.responder !== this.mySeat) return false
    const ok =
      cardUid === null
        ? this.send({
            type: ActionType.RESPOND_PRIORITY,
            requestId: pending.requestId,
            cardUid: null,
          })
        : this.respondWithTrap(pending.requestId, cardUid, boost)
    if (!ok) return false
    // Local apply is synchronous: an accepted answer with no events would
    // leave Vue on the question it already closed. PvP events arrive after
    // send returns, so a snapshot here would wipe that queue.
    if (
      this.client.start &&
      !this.eventsPlaying &&
      this.eventQ.length === 0
    ) {
      this.client.requestState()
    }
    return true
  }

  /**
   * Answers an open response window with a trap. The engine flips it first;
   * Two-Pronged Attack then asks for 2 of yours + 1 of theirs.
   */
  private respondWithTrap(requestId: string, cardUid: string, boost?: 'atk' | 'def'): boolean {
    const trap = this.findCardByUid(cardUid)
    if (!trap) return false
    const steps = this.multiTargetSteps(trap.cardId)
    if (steps?.some((s) => s.uids.length < s.count)) {
      this.toast(t('duel.toast.notEnoughField'), 'warn')
      return false
    }
    return this.send({
      type: ActionType.RESPOND_PRIORITY,
      requestId,
      cardUid: trap.uid,
      ...(boost ? { boost } : {}),
    })
  }

  /** Finds a card instance anywhere on either field/hand by its uid. */
  private findCardByUid(uid: string): CardInstance | null {
    return this.findCardInState(this.state, uid)
  }

  /**
   * Looks up a uid in a snapshot (live board or the payload about to commit).
   */
  private findCardInState(
    state: MatchState | null,
    uid: string,
  ): CardInstance | null {
    if (!state) return null
    for (const z of state.zones) {
      for (const c of [
        ...z.monsters,
        ...z.spellTraps,
        z.fieldSpell,
        ...z.hand,
        ...z.graveyard,
        ...z.extraDeck,
        ...z.banished,
        ...z.deck,
      ]) {
        if (c && c.uid === uid) return c
      }
    }
    return null
  }

  /** Which seat currently owns this uid, or null. */
  private seatOfUid(uid: string): Seat | null {
    if (!this.state) return null
    for (const seat of [0, 1] as Seat[]) {
      const z = this.state.zones[seat]
      const pile = [
        ...z.monsters,
        ...z.spellTraps,
        z.fieldSpell,
        ...z.hand,
        ...z.graveyard,
        ...z.extraDeck,
        ...z.banished,
      ]
      if (pile.some((c) => c && c.uid === uid)) return seat
    }
    return null
  }

  /**
   * Field (or hand) half a uid sits on, by zone occupancy — not `card.owner`,
   * which lies after Change of Heart / Snatch Steal.
   */
  private fieldSideOfUid(uid: string): DuelPickSide | null {
    if (!this.state) return null
    for (const seat of [0, 1] as Seat[]) {
      const z = this.state.zones[seat]
      const onField =
        z.monsters.some((c) => c?.uid === uid) ||
        z.spellTraps.some((c) => c?.uid === uid) ||
        z.fieldSpell?.uid === uid ||
        z.hand.some((c) => c.uid === uid)
      if (onField) return seat === this.mySeat ? 'mine' : 'opp'
    }
    return null
  }

  /** Combine candidate uids into one half, or `both` when they straddle. */
  private pickSideOfUids(uids: string[]): DuelPickSide | null {
    let mine = false
    let opp = false
    for (const uid of uids) {
      const side = this.fieldSideOfUid(uid)
      if (side === 'mine') mine = true
      else if (side === 'opp') opp = true
    }
    if (mine && opp) return 'both'
    if (mine) return 'mine'
    if (opp) return 'opp'
    return null
  }

  private setPickSide(side: DuelPickSide | null): void {
    this.pickSideCb?.(side)
  }

  /**
   * Push an opponent's revealed card into the HTML viewer when the pref is on.
   */
  private followOpponentReveal(seat: Seat, cardId: string, delay = 0): void {
    const run = (): void => {
      if (!useDuelPrefsStore().followOpponentViewer) return
      if (seat === this.mySeat || !cardId) return
      this.hoverCb?.(this.toDisplayCard(cardId), false)
    }
    if (delay > 0) this.time.delayedCall(delay, run)
    else run()
  }

  /** Show the attacking monster when the opponent declares an attack. */
  private followOpponentAttack(attackerUid: string): void {
    if (!useDuelPrefsStore().followOpponentViewer) return
    const seat = this.seatOfUid(attackerUid)
    if (seat == null || seat === this.mySeat) return
    const inst = this.findCardByUid(attackerUid)
    if (!inst) return
    const h = this.hoverData(inst, false)
    if (h.faceDown || !h.card) return
    this.hoverCb?.(h.card, false)
  }

  // Field Zone, left of the monster row (CardViewer is a side rail, not on-canvas).
  private renderFieldSpell(seat: Seat): void {
    if (!this.state) return
    const z = this.state.zones[seat]
    const pose = this.fieldSlotPos(seat)
    const { x, y } = pose
    const frame = this.add
      .rectangle(x, y, CARD_W, CARD_H, 0x14331a, 0.3)
      .setStrokeStyle(1, 0x4caf50)
    this.boardLayer.add(frame)
      if (!isDuelCompactNow()) {
        this.boardLayer.add(
          this.add
            .text(x, y - CARD_H / 2 - 7, t('duel.field'), {
              fontSize: '8px',
              color: '#7fd18a',
            })
            .setOrigin(0.5),
        )
      }

    const card = z.fieldSpell
    if (!card) return
    if (this.departingFieldUids.has(card.uid)) return
    if (this.chainHidden.has(card.uid)) return
    const mine = seat === this.mySeat
    const glow = this.targetGlowKind(card, seat, 'spellTraps')
    if (glow) addTargetGlow(this, this.boardLayer, x, y, glow, false)
    const sprite = this.makeCardSprite(
      card,
      mine,
      true,
      this.shouldPeekOwnSet(card, mine),
    )
    sprite.setPosition(x, y)
    if (!glow && this.fieldHasAction(card, seat, 'spellTraps')) addActionGlow(this, sprite)
    this.boardLayer.add(sprite)
    this.fieldSprites.set(card.uid, sprite)
    if (this.fieldClickable(card, seat, 'spellTraps')) {
      sprite.setInteractive(
        cardHitRect(),
        Phaser.Geom.Rectangle.Contains,
      )
      sprite.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        const hit = this.resolveFieldPick(card, seat, 'spellTraps', pointer)
        if (this.pointerBlockedFor(hit.card.uid)) return
        this.menuAnchor = { ...pose, x: sprite.x, y: sprite.y }
        this.onFieldCardClick(hit.card, hit.seat, hit.zone, pointer)
      })
    }
    this.attachHover(sprite, card, mine)
  }

  /**
   * Deck / Extra / GY / Banished as full-size card piles on the official playmat
   * sides. Thickness grows outward so sibling rows do not overlap.
   */
  private renderPiles(seat: Seat): void {
    if (!this.state) return
    const z = this.state.zones[seat]
    const owner = seat === this.mySeat ? 'player' : 'opponent'
    const mine = seat === this.mySeat
    const openPile = (fn: () => void): ((pointer: Phaser.Input.Pointer) => void) => (pointer) => {
      if (this.blockingFieldPick()) {
        const hit = this.fieldCardAt(pointer.worldX, pointer.worldY)
        if (hit) {
          this.onFieldCardClick(hit.card, hit.seat, hit.zone, pointer)
          return
        }
      }
      if (this.boardPointerBlocked()) return
      fn()
    }

    const makePile = (opts: {
      pos: SlotPose
      side: 'left' | 'right'
      label: string
      count: number
      color: number
      topCard: CardInstance | null
      faceDown: boolean
      onClick?: () => void
      /** Keep the frame and the counter, drop the card art (shuffle overlay). */
      hideStack?: boolean
    }) => {
      const { pos, side, label, count, color, topCard, faceDown, onClick } = opts
      const { x, y } = pos
      const dir = side === 'left' ? -1 : 1
      const layers = this.pileLayerCount(count)
      const thickness = count > 0 ? (layers - 1) * PILE_LAYER_STEP : 0
      const wantHover = !!(topCard && !faceDown)
      const pile = this.add.container(x, y)

      pile.add(
        this.add
          .rectangle(0, 0, CARD_W, CARD_H, color, count > 0 ? 0.15 : 0.25)
          .setStrokeStyle(1, 0xaaaaaa),
      )

      if (count > 0 && !opts.hideStack) {
        this.addPileStack(pile, dir, layers, topCard, faceDown, mine)
      }

      const badgeY = CARD_H / 2 - 10
      pile.add(this.add.rectangle(0, badgeY, 28, 14, 0x000000, 0.72))
      pile.add(
        this.add
          .text(0, badgeY, String(count), {
            fontSize: '11px',
            color: '#ffffff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      )

      if (!isDuelCompactNow()) {
        this.boardLayer.add(
          this.add
            .text(x, y - CARD_H / 2 - 7, label, {
              fontSize: '8px',
              color: '#cbd5e1',
            })
            .setOrigin(0.5),
        )
      }
      this.boardLayer.add(pile)

      const hitPad = isDuelCompactNow() ? TOUCH_HIT_PAD : 0
      const hitLeft = dir < 0 ? -CARD_W / 2 - thickness - hitPad : -CARD_W / 2 - hitPad
      if (count > 0 && (onClick || wantHover)) {
        pile.setInteractive({
          hitArea: new Phaser.Geom.Rectangle(
            hitLeft,
            -CARD_H / 2 - hitPad,
            CARD_W + thickness + hitPad * 2,
            CARD_H + hitPad * 2,
          ),
          hitAreaCallback: Phaser.Geom.Rectangle.Contains,
          useHandCursor: !!onClick,
        })
        if (onClick) pile.on('pointerdown', onClick)
        if (wantHover && topCard) this.attachHover(pile, topCard, mine)
      }
    }

    makePile({
      pos: this.deckPos(seat),
      side: 'right',
      label: 'DECK',
      count: z.deck.length,
      color: 0x1e3a8a,
      topCard: null,
      faceDown: true,
      hideStack: this.deckLiftedSeats.has(seat),
    })

    // Extra Deck is private: only your own pile may be opened.
    makePile({
      pos: this.extraPos(seat),
      side: 'left',
      label: 'EX',
      count: z.extraDeck.filter((c) => !this.departingPileUids.has(c.uid)).length,
      color: 0x1d4ed8,
      topCard: null,
      faceDown: true,
      onClick: mine
        ? openPile(() =>
            this.extraCb?.(
              z.extraDeck.map((c) => this.toDisplayCard(c)).filter(Boolean),
              owner,
            ),
          )
        : undefined,
    })

    const gyLive = this.graveLive(seat)
    const gyTop = gyLive[gyLive.length - 1] ?? null
    makePile({
      pos: this.graveyardPos(seat),
      side: 'right',
      label: 'GY',
      count: gyLive.length,
      color: 0x4b5563,
      topCard: gyTop,
      faceDown: false,
      onClick: openPile(() =>
        this.gyCb?.(gyLive.map((c) => this.toDisplayCard(c)).filter(Boolean), owner),
      ),
    })

    const banLive = this.banishedLive(seat)
    const banTop = banLive[banLive.length - 1] ?? null
    const arrivingTop = !!(banTop && this.landedBanishedUids.has(banTop.uid))
    makePile({
      pos: this.banishedPos(seat),
      side: 'right',
      label: 'BAN',
      count: banLive.length,
      color: 0x6b21a8,
      topCard: banTop,
      faceDown: arrivingTop ? false : !!(banTop && !banTop.faceUp),
      onClick: openPile(() =>
        this.banishedCb?.(
          banLive.map((c) => this.toDisplayCard(c)).filter(Boolean),
          owner,
        ),
      ),
    })
  }

  /**
   * Banished pile during a GY→Banished batch. `departingPileUids` hides the
   * card on the GY while it flies — it must NOT hide the same uid on BAN,
   * or the pile stays empty until commitBoard.
   */
  /**
   * GY pile during a discard/destroy batch. Cards still in the pre-commit hand
   * appear as they land so Card Destruction does not wait for commit.
   */
  private graveLive(seat: Seat): CardInstance[] {
    if (!this.state) return []
    const current = this.state.zones[seat].graveyard
    const pending = this.pendingCommit?.zones[seat].graveyard
    const source = pending ?? current
    return source.filter((card) => {
      const already = current.some((c) => c.uid === card.uid)
      if (already) return !this.departingPileUids.has(card.uid)
      return this.landedGraveUids.has(card.uid)
    })
  }

  private banishedLive(seat: Seat): CardInstance[] {
    if (!this.state) return []
    const current = this.state.zones[seat].banished
    const pending = this.pendingCommit?.zones[seat].banished
    const source = pending ?? current
    return source.filter((card) => {
      const already = current.some((c) => c.uid === card.uid)
      if (already) return !this.departingPileUids.has(card.uid)
      return this.landedBanishedUids.has(card.uid)
    })
  }

  private toDisplayCard(card: CardInstance | string): any {
    if (typeof card === 'string') {
      return CARD_INDEX.get(card) ?? { id: card, name: card, type: '', foil: 'common' }
    }
    const base = CARD_INDEX.get(card.cardId) ?? {
      id: card.cardId,
      name: card.cardId,
      type: '',
    }
    return { ...base, foil: asCardFoil(card.foil) }
  }

  private hoverData(card: CardInstance, mine: boolean): { card: any | null; faceDown: boolean } {
    const visible = card.faceUp || ((mine || this.isWatching()) && card.cardId !== '')
    if (!visible || !card.cardId) return { card: null, faceDown: true }
    const base = this.toDisplayCard(card)
    // Equips, fields, Reverse Trap, and Shield & Sword: the numbers battle uses.
    const bases = this.battleBases(card)
    const bonus = this.statBonus(card)
    const atk = Math.max(0, bases.atk + bonus.atk)
    const def = Math.max(0, bases.def + bonus.def)
    if (base.atk != null && (atk !== (base.atk ?? 0) || def !== (base.def ?? 0))) {
      return {
        card: {
          ...base,
          baseAtk: base.atk,
          baseDef: base.def ?? 0,
          atk,
          def,
        },
        faceDown: false,
      }
    }
    return { card: base, faceDown: false }
  }

  private renderLine(
    slots: (CardInstance | null)[],
    row: number,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
  ): void {
    slots.forEach((card, index) => {
      const pose = this.at(row, index)
      const { x, y } = pose
      const frame = this.add
        .rectangle(x, y, CARD_W, CARD_H, 0x000000, 0.15)
        .setStrokeStyle(1, 0x335577)
      this.boardLayer.add(frame)
      if (!card) return
      if (this.departingFieldUids.has(card.uid)) return
      if (this.chainHidden.has(card.uid)) return
      const shown = this.shownCard(card)
      const owned = this.sleeveMine(card)
      const controlled = seat === this.mySeat
      const defense =
        zone === 'monsters' && shown.position === CardPosition.DEFENSE
      const tributePicked =
        this.clickMode === 'selectTribute' && this.tributeSelected.includes(card.uid)
      const glow = this.targetGlowKind(card, seat, zone)
      const halo = glow
        ? addTargetGlow(this, this.boardLayer, x, y, glow, defense)
        : null
      const sprite = this.makeCardSprite(
        shown,
        owned,
        true,
        this.shouldPeekOwnSet(shown, controlled),
      )
      sprite.setPosition(x, y)
      if (!glow && this.fieldHasAction(card, seat, zone)) addActionGlow(this, sprite)
      this.boardLayer.add(sprite)
      this.fieldSprites.set(card.uid, sprite)
      if (zone === 'monsters' && shown.faceUp) {
        if (sprite.getData('sharpFace')) sprite.setData('sharpStats', 'field')
        else this.addStatLabel(shown, x, y)
      }
      if (tributePicked) {
        const ring = this.add
          .rectangle(x, y, CARD_W + 6, CARD_H + 6, 0xffd54f, 0)
          .setStrokeStyle(2, 0xffd54f)
        if (defense) ring.setAngle(90)
        this.boardLayer.add(ring)
        addTributeSpiral(this, this.boardLayer, x, y)
      }
      if (this.canShowAttackSword(card, seat, zone)) {
        addAttackSword(this, sprite)
      }
      if (defense) {
        for (const child of sprite.list) {
          if ('setAngle' in child && typeof child.setAngle === 'function') {
            child.setAngle(90)
          }
        }
      }
      if (this.fieldClickable(card, seat, zone)) {
        const openLeft = !slots[index - 1]
        const openRight = !slots[index + 1]
        const onDown = (pointer: Phaser.Input.Pointer) => {
          const hit = this.resolveFieldPick(card, seat, zone, pointer)
          if (this.pointerBlockedFor(hit.card.uid)) return
          this.menuAnchor = { ...pose, x: sprite.x, y: sprite.y }
          if (this.beginSwipeAttack(hit.card, hit.seat, hit.zone, pointer)) return
          this.onFieldCardClick(hit.card, hit.seat, hit.zone, pointer)
        }
        sprite.setInteractive(
          defense
            ? defenseHitRect(openLeft, openRight)
            : zone === 'spellTraps'
              ? spellTrapHitRect(openLeft, openRight)
              : cardHitRect(),
          Phaser.Geom.Rectangle.Contains,
        )
        sprite.on('pointerdown', onDown)
        if (halo) {
          halo.setInteractive(
            targetHaloRect(defense, openLeft, openRight),
            Phaser.Geom.Rectangle.Contains,
          )
          halo.on('pointerdown', onDown)
        }
      }
      this.attachHover(sprite, card, controlled)
    })
  }

  /**
   * ATK/DEF on a monster. Field uses a wide "2300 / 2100" strip under the card.
   * Hand uses a stacked pair inside the card width so tight spacing cannot overlap.
   * Boosts stay green; penalties go coral-red.
   */
  private addStatLabel(
    card: CardInstance,
    x: number,
    y: number,
    compact = false,
    parent?: Phaser.GameObjects.Container,
    scale = 1,
  ): void {
    const data = CARD_INDEX.get(card.cardId)
    if (!data || data.atk == null) return // monsters only
    const bonus = this.statBonus(card)
    const bases = this.battleBases(card)
    const atk = Math.max(0, bases.atk + bonus.atk)
    const def = Math.max(0, bases.def + bonus.def)
    const atkDelta = atk - (data.atk ?? 0)
    const defDelta = def - (data.def ?? 0)
    if (compact) {
      const ox = parent ? 0 : x
      const oy = parent ? 0 : y
      const lineH = 13
      const boxH = lineH * 2 + 4
      const boxW = CARD_W - 6
      const boxY = oy + CARD_H / 2 - boxH / 2 - 3
      const bg = this.add.rectangle(ox, boxY, boxW, boxH, 0x000000, 0.78).setOrigin(0.5)
      const atkRow = this.compactStatRow(ox, boxY - lineH / 2, 'ATK', '#f59e0b', atk, atkDelta)
      const defRow = this.compactStatRow(ox, boxY + lineH / 2, 'DEF', '#60a5fa', def, defDelta)
      const bits = [bg, ...atkRow, ...defRow]
      if (parent) parent.add(bits)
      else this.boardLayer.add(bits)
      return
    }
    const yPos = y + (CARD_H / 2 - 8) * scale
    const wrap = this.add.container(x, yPos).setScale(scale)
    const style = { fontSize: '14px', fontStyle: 'bold' }
    const atkText = this.add
      .text(0, 0, String(atk), { ...style, color: this.statColor(atkDelta) })
      .setOrigin(0, 0.5)
    const slash = this.add
      .text(0, 0, '/', { ...style, color: '#ffffff' })
      .setOrigin(0, 0.5)
    const defText = this.add
      .text(0, 0, String(def), { ...style, color: this.statColor(defDelta) })
      .setOrigin(0, 0.5)
    const gap = 4
    const totalW = atkText.width + slash.width + defText.width + gap * 2
    let cursor = -totalW / 2
    atkText.setPosition(cursor, 0)
    cursor += atkText.width + gap
    slash.setPosition(cursor, 0)
    cursor += slash.width + gap
    defText.setPosition(cursor, 0)
    const bg = this.add
      .rectangle(0, 0, totalW + 8, 16, 0x000000, 0.8)
      .setOrigin(0.5)
    wrap.add([bg, atkText, slash, defText])
    this.boardLayer.add(wrap)
  }

  /**
   * ATK/DEF strip for a monster whose stats just changed. Covers the previous
   * numbers until renderAll draws the committed label. A Set monster uses it
   * once the reveal is on screen.
   */
  private previewStatLabel(
    card: CardInstance,
    x: number,
    y: number,
    scale: number,
    atk: number,
    def: number,
  ): void {
    const data = CARD_INDEX.get(card.cardId)
    if (!data || data.atk == null) return
    if (this.useSharpCards) {
      const sprite = this.fieldSprites.get(card.uid)
      if (sprite?.getData('sharpFace')) {
        sprite.setData('sharpStatOverride', { atk, def })
        sprite.setData('sharpStats', 'field')
        return
      }
    }
    const atkDelta = atk - (data.atk ?? 0)
    const defDelta = def - (data.def ?? 0)
    const yPos = y + (CARD_H / 2 - 8) * scale
    const wrap = this.add.container(x, yPos).setScale(scale).setDepth(40)
    const style = { fontSize: '14px', fontStyle: 'bold' }
    const atkText = this.add
      .text(0, 0, String(atk), { ...style, color: this.statColor(atkDelta) })
      .setOrigin(0, 0.5)
    const slash = this.add
      .text(0, 0, '/', { ...style, color: '#ffffff' })
      .setOrigin(0, 0.5)
    const defText = this.add
      .text(0, 0, String(def), { ...style, color: this.statColor(defDelta) })
      .setOrigin(0, 0.5)
    const gap = 4
    const totalW = atkText.width + slash.width + defText.width + gap * 2
    let cursor = -totalW / 2
    atkText.setPosition(cursor, 0)
    cursor += atkText.width + gap
    slash.setPosition(cursor, 0)
    cursor += slash.width + gap
    defText.setPosition(cursor, 0)
    const bg = this.add
      .rectangle(0, 0, totalW + 8, 16, 0x000000, 0.8)
      .setOrigin(0.5)
    wrap.add([bg, atkText, slash, defText])
    this.boardLayer.add(wrap)
  }

  /** One hand-stat line: amber ATK / blue DEF tag plus the number. */
  private compactStatRow(
    x: number,
    y: number,
    tag: string,
    tagColor: string,
    value: number,
    delta: number,
  ): Phaser.GameObjects.Text[] {
    const tagText = this.add
      .text(0, 0, tag, { fontSize: '8px', fontStyle: 'bold', color: tagColor })
      .setOrigin(0, 0.5)
    const numText = this.add
      .text(0, 0, String(value), { fontSize: '11px', fontStyle: 'bold', color: this.statColor(delta) })
      .setOrigin(0, 0.5)
    const gap = 3
    const cursor = x - (tagText.width + gap + numText.width) / 2
    tagText.setPosition(cursor, y)
    numText.setPosition(cursor + tagText.width + gap, y)
    return [tagText, numText]
  }

  /** White if unchanged, green if raised, coral-red if lowered. */
  private statColor(delta: number): string {
    if (delta > 0) return '#7CFC00'
    if (delta < 0) return '#f87171'
    return '#ffffff'
  }

  private attachHover(sprite: Phaser.GameObjects.Container, card: CardInstance, mine: boolean): void {
    const publish = () => {
      const h = this.hoverData(card, mine)
      this.hoverCb?.(h.card, h.faceDown, this.sleeveBackUrl(this.sleeveMine(card)))
      this.hoveringFieldUid = card.uid
      this.refreshEquipLinkIcons()
    }
    sprite.on('pointerover', publish)
    sprite.on('pointerdown', () => {
      publish()
      this.focusBandFromWorld(sprite.x, sprite.y, true)
    })
    sprite.on('pointerout', () => {
      if (this.paintingBoard) return
      if (this.hoveringFieldUid !== card.uid) return
      this.hoveringFieldUid = null
      this.refreshEquipLinkIcons()
    })
  }

  /**
   * Equip Spell plus its host (or a host plus every attached Equip). Empty when
   * the hovered card is not in an Equip link.
   */
  private equipLinkUids(card: CardInstance): string[] {
    const uids = new Set<string>()
    if (card.equippedToUid) {
      uids.add(card.uid)
      uids.add(card.equippedToUid)
    }
    for (const uid of card.equippedUids) {
      uids.add(card.uid)
      uids.add(uid)
    }
    return [...uids]
  }

  private clearEquipLinkIcons(destroy: boolean): void {
    for (const icon of this.equipLinkIcons) {
      this.tweens.killTweensOf(icon)
      if (destroy && icon.scene) icon.destroy()
    }
    this.equipLinkIcons = []
  }

  /**
   * Centered Equip glyph on both ends of the hovered Equip link.
   */
  private refreshEquipLinkIcons(): void {
    this.clearEquipLinkIcons(true)
    if (!this.hoveringFieldUid) return
    const card = this.findCardByUid(this.hoveringFieldUid)
    if (!card?.faceUp) return
    for (const uid of this.equipLinkUids(card)) {
      const partner = this.findCardByUid(uid)
      if (!partner?.faceUp) continue
      const target = this.fieldSprites.get(uid)
      if (!target) continue
      this.equipLinkIcons.push(addEquipLinkIcon(this, target))
    }
  }

  /**
   * Open a gap for a draw (or reveal the card that just landed) without
   * destroying field sprites, foils, and piles. A full `renderAll` on every
   * turn draw stalled phones.
   */
  private relayoutHand(seat: Seat): void {
    if (!this.state || !this.sys?.isActive()) return
    const mine = seat === this.mySeat
    const hand = this.state.zones[seat].hand
    this.paintingBoard = true
    try {
      const dropping = new Set<string>()
      for (const card of hand) dropping.add(card.uid)
      for (const [uid, owner] of this.arrivingHandSeat) {
        if (owner === seat) dropping.add(uid)
      }
      for (const uid of dropping) this.dropHit(this.handSprites.get(uid))
      this.flushInputQueue()
      if (this.hoveringFieldUid && dropping.has(this.hoveringFieldUid)) {
        this.hoveringFieldUid = null
      }
      for (const card of hand) this.destroyHandSprite(card.uid)
      for (const [uid, owner] of this.arrivingHandSeat) {
        if (owner === seat) this.destroyHandSprite(uid)
      }
      this.renderHand(hand, mine ? 5 : 0, mine)
    } finally {
      this.paintingBoard = false
      this.flushInputQueue()
    }
  }

  private destroyHandSprite(uid: string): void {
    const sprite = this.handSprites.get(uid)
    if (!sprite) return
    this.tweens.killTweensOf(sprite)
    sprite.destroy()
    this.handSprites.delete(uid)
  }

  private renderHand(hand: CardInstance[], row: number, mine: boolean): void {
    const seat = (mine ? this.mySeat : this.mySeat === 0 ? 1 : 0) as Seat
    // Card Destruction keeps the pre-commit slots. Packing the fan as each
    // card leaves would slide the rest off the soul that was aimed at them.
    if (hand.some((c) => this.keptDiscardUids.has(c.uid))) {
      const layoutLen = this.handLayoutLength(seat, hand)
      hand.forEach((card, index) => {
        if (this.departingHandUids.has(card.uid)) return
        if (this.pendingRemovedFromHand(card.uid)) return
        this.placeHandCard(card, this.handAt(row, index, layoutLen), mine, true)
      })
      return
    }
    const visible = hand.filter(
      (c) =>
        !this.departingHandUids.has(c.uid) && !this.pendingRemovedFromHand(c.uid),
    )
    const extras = this.extraArrivingUids(seat, visible)
    const layoutLen = visible.length + extras.length
    visible.forEach((card, index) => {
      this.placeHandCard(card, this.handAt(row, index, layoutLen), mine, true)
    })
    extras.forEach((uid, i) => {
      const dest = this.handAt(row, visible.length + i, layoutLen)
      if (!this.landedHandUids.has(uid)) {
        this.handDisplayX.set(uid, dest.x)
        return
      }
      this.placeHandCard(this.arrivingStub(uid, seat), dest, mine, false)
    })
  }

  /**
   * Tweens a hand card into `destX`. Arriving cards that have not landed yet
   * leave a gap for the 3D overlay.
   */
  private placeHandCard(
    card: CardInstance,
    dest: SlotPose,
    mine: boolean,
    interactive: boolean,
  ): void {
    const destX = dest.x
    const y = dest.y
    const scale = dest.scale
    if (this.arrivingHandUids.has(card.uid) && !this.landedHandUids.has(card.uid)) {
      this.handDisplayX.set(card.uid, destX)
      return
    }
    const prevX = this.handDisplayX.get(card.uid)
    const startX =
      prevX !== undefined && Math.abs(prevX - destX) > 2 ? prevX : destX
    const sprite = this.makeCardSprite(card, mine)
    sprite.setPosition(startX, y).setScale(scale)
    this.boardLayer.add(sprite)
    if (mine || this.isWatching()) {
      if (sprite.getData('sharpFace') && CARD_INDEX.get(card.cardId)?.atk != null) {
        sprite.setData('sharpStats', 'hand')
      } else if (!sprite.getData('sharpFace')) {
        this.addStatLabel(card, 0, 0, true, sprite)
      }
    }
    const handPaused =
      mine && !!this.state && isWaitingOnOpponent(this.state, this.mySeat)
    if (handPaused) sprite.setAlpha(0.5)
    if (mine && interactive && this.handHasAction(card)) addActionGlow(this, sprite)
    if (mine && this.discardSelected.includes(card.uid)) {
      sprite.add(
        this.add.rectangle(0, 0, CARD_W + 6, CARD_H + 6, 0xf87171, 0).setStrokeStyle(3, 0xf87171),
      )
    }
    if (startX !== destX) {
      this.tweens.add({
        targets: sprite,
        x: destX,
        duration: this.aiPlayback ? 320 : this.ms(280),
        ease: 'Cubic.easeOut',
      })
    }
    this.handDisplayX.set(card.uid, destX)
    this.handSprites.set(card.uid, sprite)
    const canPlay = mine && interactive && !this.isWatching()
    const canHover = (mine || this.isWatching()) && card.cardId !== ''
    if (canPlay || canHover) {
      sprite.setInteractive(
        handHitRect(),
        Phaser.Geom.Rectangle.Contains,
      )
    }
    if (canPlay) {
      sprite.on('pointerdown', () => {
        if (this.pointerBlockedFor(card.uid)) return
        this.menuAnchor = { ...dest, x: sprite.x, y: sprite.y }
        this.onHandCardClick(card)
      })
    }
    if (canHover) this.attachHover(sprite, card, true)
  }

  private arrivingStub(uid: string, seat: Seat): CardInstance {
    const pending = this.findCardInState(this.pendingCommit, uid)
    if (pending && this.handKeepsUid(this.pendingCommit, uid)) return pending
    return {
      uid,
      cardId: this.arrivingHandCardId.get(uid) ?? '',
      owner: seat,
      faceUp: false,
      position: CardPosition.ATTACK,
      summonedThisTurn: false,
      attackedThisTurn: false,
      declaredAttackThisTurn: false,
      positionChangedThisTurn: false,
      setOnTurn: 0,
      statModifiers: [],
      equippedUids: [],
      equippedToUid: null,
      foil: 'common',
    }
  }

  /**
   * Face-up art and ATK/DEF for Nítida. The face image is drawn with
   * destination-out so the playmat opens a hole the size of the card.
   * The full JPG sits behind the canvas, in that hole. Foil, swords and
   * badges stay on the canvas, painted after the hole. ATK/DEF is HTML
   * in front, so the 1024×768 buffer never has to grow.
   */
  private syncSharpCards(): void {
    if (!this.useSharpCards || !this.boardLayer || !this.sys?.isActive()) return
    const artHost = this.sharpArtHost
    const statHost = this.sharpStatHost
    const canvas = this.game?.canvas
    if (!artHost || !statHost || !canvas) return
    const cr = canvas.getBoundingClientRect()
    if (cr.width < 2 || cr.height < 2) return
    const ar = artHost.getBoundingClientRect()
    const sr = statHost.getBoundingClientRect()
    const view = this.cameraViewRect()
    const seen = new Set<string>()
    const visit = (obj: Phaser.GameObjects.GameObject): void => {
      if (!(obj instanceof Phaser.GameObjects.Container)) return
      if (obj.getData('sharpFace')) this.placeSharpCard(obj, cr, ar, sr, view, seen)
      for (const child of obj.list) visit(child)
    }
    visit(this.boardLayer)
    for (const [key, els] of this.sharpEls) {
      if (seen.has(key)) continue
      els.art.remove()
      els.stats.remove()
      els.name.remove()
      this.sharpEls.delete(key)
    }
  }

  private placeSharpCard(
    sprite: Phaser.GameObjects.Container,
    cr: DOMRect,
    ar: DOMRect,
    sr: DOMRect,
    view: { x: number; y: number; width: number; height: number },
    seen: Set<string>,
  ): void {
    if (!sprite.active) return
    const key = sprite.getData('sharpKey') as string | undefined
    if (!key) return
    seen.add(key)
    const els = this.sharpNodes(key)
    const face = this.sharpFaceImage(sprite)
    if (!sprite.visible || sprite.alpha <= 0.02) {
      this.hideSharpCard(els, face)
      return
    }
    const m = sprite.getWorldTransformMatrix(this.sharpMatrix, this.sharpParentMatrix)
    const sx = Math.hypot(m.a, m.b) || 1
    const sy = Math.hypot(m.c, m.d) || 1
    const worldW = CARD_W * sx
    const worldH = CARD_H * sy
    const px = ((m.tx - view.x) / view.width) * cr.width
    const py = ((m.ty - view.y) / view.height) * cr.height
    const pw = (worldW / view.width) * cr.width
    const ph = (worldH / view.height) * cr.height
    const defense = this.sharpDrawnDefense(sprite)
    const src = String(sprite.getData('sharpFace') ?? '')
    const art = els.art
    if (art.dataset.src !== src) {
      art.dataset.src = src
      art.src = src
      art.dataset.plate = ''
      art.dataset.erased = ''
      face?.setBlendMode(Phaser.BlendModes.NORMAL)
      face?.setAlpha(1)
    }
    const inst = sprite.getData('sharpInst') as CardInstance | undefined
    const printed = inst ? englishName(inst.cardId) : ''
    const foilId = inst ? asCardFoil(inst.foil) : null
    const nameKind = foilNameKind(foilId)
    this.queueSharpNamePlate(art, src, nameKind, printed)
    const pad = 1
    const dim = sprite.alpha
    art.style.left = `${cr.left - ar.left + px - pw / 2 - pad}px`
    art.style.top = `${cr.top - ar.top + py - ph / 2 - pad}px`
    art.style.width = `${pw + pad * 2}px`
    art.style.height = `${ph + pad * 2}px`
    art.style.zIndex = String(Math.round(py))
    art.style.transform = defense ? 'rotate(90deg)' : ''
    const ghost = foilId === 'ghost'
    // The CSS class is only a preview. `style.filter` is set every frame and
    // would wipe it; once the pale JPEG lands, the pixels already carry Ghost.
    const bakedGhost = ghost && art.dataset.erased === '1'
    art.classList.toggle('is-ghost', ghost && !bakedGhost)
    const wash = String(sprite.getData('sharpWash') ?? '')
    const ghostFilter = ghost && !bakedGhost ? GHOST_ART_FILTER : ''
    art.style.filter = [ghostFilter, wash].filter((part) => part.length > 0).join(' ')
    const ready = art.complete && art.naturalWidth > 0
    const css = cr.height / Math.max(1, view.height)
    if (sprite.getData('sharpCover') === true) {
      const veil = this.sharpVeil(sprite)
      const host = this.sharpStatHost
      if (host && art.parentElement !== host) host.appendChild(art)
      const veilAlpha = veil?.alpha ?? 1
      // This image leaves the art layer (behind the canvas) so the sleeve can
      // sit in front of the face. The art-layer rule no longer positions it;
      // without absolute it paints at the corner and the Set shows face-up.
      // Keep the Phaser sleeve until the page image has pixels. Hiding it
      // early, or marking the page image visibility:hidden, leaves the face
      // bare — a phone often will not decode a hidden image.
      art.style.position = 'absolute'
      art.style.objectFit = 'fill'
      art.style.left = `${cr.left - sr.left + px - pw / 2 - pad}px`
      art.style.top = `${cr.top - sr.top + py - ph / 2 - pad}px`
      if (veil) veil.setVisible(!ready)
      art.style.visibility = 'visible'
      art.style.opacity = ready ? String(veilAlpha * dim) : '0'
      els.name.style.visibility = 'hidden'
      this.placeSharpStats(sprite, els.stats, cr, sr, px, py, pw, ph, dim, css)
      return
    }
    if (face && ready) {
      face.setAlpha(dim < 0.99 ? 1 / dim : 1)
      face.setBlendMode(Phaser.BlendModes.ERASE)
      art.style.visibility = 'visible'
      art.style.opacity = String(dim)
    } else {
      face?.setAlpha(1)
      face?.setBlendMode(Phaser.BlendModes.NORMAL)
      art.style.visibility = 'hidden'
    }
    const boxLeft = cr.left - sr.left + px - pw / 2 - pad
    const boxTop = cr.top - sr.top + py - ph / 2 - pad
    this.layoutSharpFoilName(
      els,
      nameKind,
      printed,
      boxLeft,
      boxTop,
      pw + pad * 2,
      ph + pad * 2,
      defense,
      dim,
      Math.round(py) + 2,
      ready && art.dataset.erased === '1',
    )
    this.placeSharpStats(sprite, els.stats, cr, sr, px, py, pw, ph, dim, css)
  }

  private sharpVeil(
    sprite: Phaser.GameObjects.Container,
  ): Phaser.GameObjects.Image | null {
    for (const child of sprite.list) {
      if (child instanceof Phaser.GameObjects.Image && child.getData('sharpVeil')) {
        return child
      }
    }
    return null
  }

  private placeSharpStats(
    sprite: Phaser.GameObjects.Container,
    stats: HTMLDivElement,
    cr: DOMRect,
    sr: DOMRect,
    px: number,
    py: number,
    pw: number,
    ph: number,
    dim: number,
    css: number,
  ): void {
    const mode = sprite.getData('sharpStats') as 'hand' | 'field' | undefined
    const inst = sprite.getData('sharpInst') as CardInstance | undefined
    const data = inst ? CARD_INDEX.get(inst.cardId) : undefined
    if (!mode || !inst || !data || data.atk == null) {
      stats.style.visibility = 'hidden'
      return
    }
    const override = sprite.getData('sharpStatOverride') as
      | { atk: number; def: number }
      | undefined
    const bonus = this.statBonus(inst)
    const bases = this.battleBases(inst)
    const atk = override?.atk ?? Math.max(0, bases.atk + bonus.atk)
    const def = override?.def ?? Math.max(0, bases.def + bonus.def)
    const atkDelta = atk - (data.atk ?? 0)
    const defDelta = def - (data.def ?? 0)
    const numPx = Math.round(
      Math.max(11, Math.min(22, (mode === 'hand' ? 11 : 14) * css)),
    )
    const sig = `${mode}|${atk}|${def}|${atkDelta}|${defDelta}|${numPx}`
    if (stats.dataset.sig !== sig) {
      stats.dataset.sig = sig
      stats.className = `sharp-stat-box ${mode}`
      stats.style.fontSize = `${numPx}px`
      const atkC = this.statColor(atkDelta)
      const defC = this.statColor(defDelta)
      stats.replaceChildren()
      if (mode === 'hand') {
        stats.append(this.sharpStatLine('ATK', '#f59e0b', atk, atkC))
        stats.append(this.sharpStatLine('DEF', '#60a5fa', def, defC))
      } else {
        stats.append(this.sharpStatNum(atk, atkC))
        const slash = document.createElement('span')
        slash.className = 'slash'
        slash.textContent = '/'
        stats.append(slash)
        stats.append(this.sharpStatNum(def, defC))
      }
    }
    const ox = cr.left - sr.left
    const oy = cr.top - sr.top
    stats.style.visibility = 'visible'
    stats.style.opacity = String(dim)
    stats.style.zIndex = String(Math.round(py) + 1)
    if (mode === 'hand') {
      const boxW = Math.max(((CARD_W - 6) / CARD_W) * pw, numPx * 4.2)
      const boxH = Math.max((30 / CARD_H) * ph, numPx * 2.3)
      const boxCy = py + ((CARD_H / 2 - 15 - 3) / CARD_H) * ph
      stats.style.left = `${ox + px - boxW / 2}px`
      stats.style.top = `${oy + boxCy - boxH / 2}px`
      stats.style.width = `${boxW}px`
      stats.style.height = `${boxH}px`
      stats.style.transform = ''
      return
    }
    stats.style.left = `${ox + px}px`
    stats.style.top = `${oy + py + ((CARD_H / 2 - 8) / CARD_H) * ph}px`
    stats.style.width = 'auto'
    stats.style.height = 'auto'
    stats.style.transform = 'translate(-50%, -50%)'
  }

  private sharpStatLine(
    tag: string,
    tagColor: string,
    value: number,
    valueColor: string,
  ): HTMLDivElement {
    const line = document.createElement('div')
    const label = document.createElement('span')
    label.className = 'tag'
    label.style.color = tagColor
    label.textContent = tag
    line.append(label, this.sharpStatNum(value, valueColor))
    return line
  }

  private sharpStatNum(value: number, color: string): HTMLSpanElement {
    const num = document.createElement('span')
    num.style.color = color
    num.textContent = String(value)
    return num
  }

  private sharpNodes(key: string): SharpCardNodes {
    const existing = this.sharpEls.get(key)
    if (existing) return existing
    const art = document.createElement('img')
    art.alt = ''
    art.draggable = false
    art.className = 'sharp-card-img'
    const stats = document.createElement('div')
    stats.className = 'sharp-stat-box'
    stats.style.visibility = 'hidden'
    const name = document.createElement('div')
    name.className = 'sharp-foil-host'
    name.style.position = 'absolute'
    name.style.pointerEvents = 'none'
    name.style.visibility = 'hidden'
    name.style.transformOrigin = 'center center'
    const namePlate = document.createElement('span')
    namePlate.className = 'foil-name'
    const nameLine = document.createElement('span')
    nameLine.className = 'foil-name-line'
    namePlate.append(nameLine)
    name.append(namePlate)
    this.sharpArtHost?.appendChild(art)
    this.sharpStatHost?.appendChild(stats)
    this.sharpStatHost?.appendChild(name)
    const els = { art, stats, name, namePlate, nameLine }
    this.sharpEls.set(key, els)
    return els
  }

  private sharpFaceImage(
    sprite: Phaser.GameObjects.Container,
  ): Phaser.GameObjects.Image | null {
    for (const child of sprite.list) {
      if (child instanceof Phaser.GameObjects.Image && child.getData('sharpErase')) {
        return child
      }
    }
    return null
  }

  private sharpDrawnDefense(sprite: Phaser.GameObjects.Container): boolean {
    for (const child of sprite.list) {
      if ('angle' in child && (child as { angle: number }).angle === 90) return true
    }
    return false
  }

  /**
   * Nítida shows the full JPG through a hole. Rare, Ultra, and Ghost
   * reprint the name in the page, the same way the open card does.
   * Ghost also bakes the pale-silver wash into that JPEG so the hole
   * cannot strip a CSS filter. A canvas stamp is only a few pixels tall
   * and leaves the printed ink.
   */
  private queueSharpNamePlate(
    art: HTMLImageElement,
    src: string,
    kind: FoilNameKind | null,
    printed: string,
  ): void {
    if (!kind || !printed || !src) {
      if (art.dataset.erased === '1' && art.dataset.src) art.src = art.dataset.src
      art.dataset.plate = ''
      art.dataset.erased = ''
      return
    }
    const plateKey = `${src}|${kind}`
    if (art.dataset.plate === plateKey) return
    art.dataset.plate = plateKey
    art.dataset.erased = ''
    void eraseNameUrl(src, kind === 'ghost').then((url) => {
      if (art.dataset.plate !== plateKey) return
      if (!url.startsWith('data:')) {
        art.dataset.erased = 'fail'
        return
      }
      const decoded = new Image()
      decoded.onload = () => {
        if (art.dataset.plate !== plateKey) return
        art.src = url
        art.dataset.erased = '1'
      }
      decoded.onerror = () => {
        if (art.dataset.plate !== plateKey) return
        art.dataset.erased = 'fail'
      }
      decoded.src = url
    })
  }

  private layoutSharpFoilName(
    els: SharpCardNodes,
    kind: FoilNameKind | null,
    printed: string,
    left: number,
    top: number,
    width: number,
    height: number,
    defense: boolean,
    dim: number,
    z: number,
    show: boolean,
  ): void {
    const host = els.name
    host.style.left = `${left}px`
    host.style.top = `${top}px`
    host.style.width = `${width}px`
    host.style.height = `${height}px`
    host.style.transform = defense ? 'rotate(90deg)' : ''
    host.style.opacity = String(dim)
    host.style.zIndex = String(z)
    const visible = show && !!kind && printed !== ''
    host.style.visibility = visible ? 'visible' : 'hidden'
    if (!visible || !kind) return
    if (!host.dataset.fontWait) {
      host.dataset.fontWait = '1'
      void document.fonts?.ready.then(() => {
        host.dataset.fitW = ''
      })
    }
    const fontPx = Math.max(1, height * 0.0526)
    const nameSig = `${kind}|${printed}`
    if (host.dataset.nameSig !== nameSig) {
      host.dataset.nameSig = nameSig
      host.dataset.fitW = ''
      els.namePlate.className = `foil-name foil-name-${kind}`
      els.nameLine.replaceChildren()
      for (const part of foilNameParts(printed)) {
        const span = document.createElement('span')
        if (part.small) span.className = 'sc'
        span.textContent = part.text
        els.nameLine.append(span)
      }
    }
    const fitW = String(Math.round(width))
    const fitH = fontPx.toFixed(1)
    if (host.dataset.fitW === fitW && host.dataset.fitH === fitH) return
    host.dataset.fitW = fitW
    host.dataset.fitH = fitH
    const line = els.nameLine
    line.style.fontSize = `${fontPx}px`
    line.style.transform = 'none'
    const available = width * 0.74
    const measured = line.scrollWidth
    if (available > 0 && measured > available + 0.5) {
      line.style.transform = `scaleX(${available / measured})`
    }
  }

  private hideSharpCard(
    els: SharpCardNodes,
    face: Phaser.GameObjects.Image | null,
  ): void {
    els.art.style.visibility = 'hidden'
    els.stats.style.visibility = 'hidden'
    els.name.style.visibility = 'hidden'
    face?.setAlpha(1)
    face?.setBlendMode(Phaser.BlendModes.NORMAL)
  }

  private clearSharpDom(): void {
    for (const els of this.sharpEls.values()) {
      els.art.remove()
      els.stats.remove()
      els.name.remove()
    }
    this.sharpEls.clear()
    this.sharpArtHost?.replaceChildren()
    this.sharpStatHost?.replaceChildren()
  }

  private makeCardSprite(
    card: CardInstance,
    mine: boolean,
    faceDownAsBack = false,
    peekSet = false,
  ): Phaser.GameObjects.Container {
    const container = this.add.container(0, 0)
    // On the field a face-down card is always drawn as a card back (even our own)
    // so the board state is readable at a glance. Hover/viewer still reveals it.
    // The back is the original owner's sleeve, not whoever currently controls it.
    const ownerMine = this.sleeveMine(card)
    const back = this.sleeveKey(ownerMine)
    const backUrl = this.sleeveBackUrl(ownerMine)
    const visible = faceDownAsBack
      ? card.faceUp
      : card.faceUp || ((mine || this.isWatching()) && card.cardId !== '')
    const peek = peekSet && !card.faceUp && !!card.cardId
    if ((visible || peek) && card.cardId) {
      const key = textureKey(card.cardId)
      if (this.textures.exists(key)) {
        const face = this.add.image(0, 0, key).setDisplaySize(CARD_W, CARD_H)
        container.add(face)
        if (this.useSharpCards && !peek) {
          face.setData('sharpErase', true)
          container.setData('sharpFace', getLocalCardImageUrl(card.cardId))
          container.setData('sharpInst', card)
          container.setData('sharpKey', `c${++this.sharpSeq}`)
        }
        applyCardRarityFx(
          this,
          container,
          asCardFoil(card.foil),
          CARD_W,
          CARD_H,
          face,
          card.cardId,
        )
      } else {
        container.setData('awaitArt', card.cardId)
        const waiting = this.sleevePlateImage(back, backUrl)
        if (waiting) {
          container.add(waiting)
          if (!peek) this.markSharpPlate(container, waiting, backUrl)
          else if (this.useSharpCards) waiting.setVisible(false)
        } else if (!peek) {
          container.add(this.sleeveFrame(false))
        }
        this.loadTexture(card.cardId)
      }
      if (peek) {
        const veilImage = this.sleevePlateImage(back, backUrl)
        const veil = veilImage ?? this.sleeveFrame(false)
        container.add(veil)
        if (veilImage && this.useSharpCards) {
          veilImage.setData('sharpVeil', true)
          container.setData('sharpCover', true)
          container.setData('sharpFace', backUrl)
          if (!container.getData('sharpKey')) {
            container.setData('sharpKey', `c${++this.sharpSeq}`)
          }
        }
        this.tweens.add({
          targets: veil,
          alpha: 0.35,
          duration: 1000,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        })
      }
    } else {
      const backImage = this.sleevePlateImage(back, backUrl)
      if (backImage) {
        container.add(backImage)
        this.markSharpPlate(container, backImage, backUrl)
      } else {
        container.add(this.sleeveFrame(false))
      }
    }
    this.addTurnCounterBadge(container, card)
    return container
  }

  /**
   * Remaining-turn badge in the center of a face-up lingering card (Swords).
   */
  private addTurnCounterBadge(
    container: Phaser.GameObjects.Container,
    card: CardInstance,
  ): void {
    const n = card.doomEndsLeft ?? card.turnCounter
    if (!card.faceUp || !n || n < 1) return
    const disc = this.add.circle(0, 0, 15, 0x000000, 0.78)
    disc.setStrokeStyle(2, 0xfacc15, 0.95)
    const label = this.add
      .text(0, 0, String(n), {
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#fde047',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
    container.add(disc)
    container.add(label)
  }

  private loadTexture(cardId: string): void {
    const key = textureKey(cardId)
    if (this.textures.exists(key)) return
    void this.adoptPhaserTexture(key, getLocalCardImageUrl(cardId)).then(() => {
      if (this.textures.exists(key) && this.sys.isActive()) this.queueArtRender()
    })
  }

  /**
   * Card art in PvP arrives one JPG at a time (the opponent's deck is not
   * cached). A full redraw on each file used to land in the same frame as a
   * tap: the action menu was cleared before it painted, and the new sprites
   * missed the finger. Phones felt that as "summon never pops up".
   * Wait out an open menu or a finger still down, then redraw without
   * dismissing that menu.
   */
  private queueArtRender(): void {
    if (this.artRenderQueued) return
    this.artRenderQueued = true
    if (this.artRenderWaitStart === 0) this.artRenderWaitStart = performance.now()
    if (this.artRenderTimer !== null) window.clearTimeout(this.artRenderTimer)
    // Wall clock, not the Phaser clock. A paused scene used to keep the
    // sleeve on the field until the next attack redraw.
    this.artRenderTimer = window.setTimeout(() => {
      this.artRenderTimer = null
      this.artRenderQueued = false
      if (!this.sys?.isActive() || !this.state) {
        this.artRenderWaitStart = 0
        return
      }
      const fingerDown = this.input.activePointer?.isDown === true
      const menuOpen = this.menuActions.length > 0
      const waited = performance.now() - this.artRenderWaitStart
      if ((fingerDown || menuOpen) && waited < 600) {
        this.queueArtRender()
        return
      }
      this.artRenderWaitStart = 0
      this.renderAll({ keepMenu: true })
    }, 80)
  }

  /**
   * Face-up / own-hand passcodes already on this snapshot. Opponent hands stay
   * masked so this does not leak hidden cardIds.
   */
  private prefetchKnownArt(state: MatchState): void {
    const ids = new Set<string>()
    for (const seat of [0, 1] as Seat[]) {
      const z = state.zones[seat]
      const mine = seat === this.mySeat
      for (const card of z.hand) {
        if (card.cardId && (mine || card.faceUp || this.isWatching())) ids.add(card.cardId)
      }
      for (const row of [z.monsters, z.spellTraps]) {
        for (const card of row) {
          if (card?.cardId && (card.faceUp || mine || this.isWatching())) ids.add(card.cardId)
        }
      }
      if (z.fieldSpell?.cardId && (z.fieldSpell.faceUp || mine || this.isWatching())) {
        ids.add(z.fieldSpell.cardId)
      }
    }
    const jobs: Promise<void>[] = []
    for (const id of ids) {
      const key = textureKey(id)
      if (this.textures.exists(key)) continue
      jobs.push(this.adoptPhaserTexture(key, getLocalCardImageUrl(id)))
    }
    void Promise.all(jobs).then(() => {
      if (!this.sys.isActive() || !this.state || jobs.length === 0) return
      this.queueArtRender()
    })
    this.prefetchOwnLibrary(state)
  }

  /**
   * After the opening hand, decode the rest of your deck/Extra in idle time
   * so a later summon is not a card-back until the JPG lands.
   */
  private prefetchOwnLibrary(state: MatchState): void {
    const z = state.zones[this.mySeat]
    const ids: string[] = []
    for (const card of [...z.deck, ...z.extraDeck, ...z.graveyard]) {
      if (card.cardId) ids.push(card.cardId)
    }
    const kick = (): void => {
      if (!this.sys.isActive()) return
      for (const id of ids) {
        const key = textureKey(id)
        if (this.textures.exists(key)) continue
        void this.adoptPhaserTexture(key, getLocalCardImageUrl(id))
      }
    }
    const idle = (
      window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void
      }
    ).requestIdleCallback
    if (idle) idle(kick, { timeout: 2500 })
    else window.setTimeout(kick, 400)
  }

  /**
   * Put a shared-cache HTMLImageElement into Phaser so Vue/Three GETs are reused.
   */
  private adoptPhaserTexture(key: string, url: string): Promise<void> {
    this.textureSources.set(key, { url, kind: 'card' })
    if (this.textures.exists(key)) return Promise.resolve()
    const pending = this.pendingTextureKeys.get(key)
    if (pending !== undefined) return pending
    const promise = loadCardImage(url)
      .then((img) => {
        if (this.sys.settings.status >= Phaser.Scenes.SHUTDOWN) return
        // A restore may have dropped this element and started a new decode.
        if (peekCardImage(url) !== img) return
        if (!this.textures.exists(key)) {
          addPhaserCardTexture(this, key, img)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.pendingTextureKeys.get(key) === promise) {
          this.pendingTextureKeys.delete(key)
        }
      })
    this.pendingTextureKeys.set(key, promise)
    return promise
  }

  /**
   * iOS / Android drop bitmaps after the app is backgrounded. Phaser still
   * thinks the keys exist, so sleeves paint black. Installed fullscreen
   * often skips visibilitychange and only fires focus.
   *
   * Chrome also freezes a hidden tab and cancels its rAF. Phaser still
   * thinks the loop is running, so `wake()` is a no-op and the canvas
   * stays blank until the player leaves. Phase buttons are Vue; cards are
   * Phaser — that is why only BP / M2 / EP still worked.
   */
  private installTextureRestore(): void {
    this.onVisRestore = () => {
      if (document.hidden) return
      this.resumeForegroundBoard({ snapPlayback: true, rebuildTextures: this.phoneBitmapsMayHaveDropped() })
    }
    this.onFocusRestore = () => {
      if (document.hidden) return
      this.resumeForegroundBoard({ snapPlayback: false, rebuildTextures: false })
    }
    document.addEventListener('visibilitychange', this.onVisRestore)
    window.addEventListener('pageshow', this.onVisRestore)
    window.addEventListener('focus', this.onFocusRestore)
    const canvas = this.game.canvas
    if (!canvas || this.game.renderer.type === CANVAS) return
    this.onGlLost = (ev: Event) => {
      ev.preventDefault()
      if (this.rendererRetired || this.glFallbackTimer) return
      // A phone that backgrounds often restores within a second. If the
      // context never returns, the canvas stays white: hand the board to Canvas.
      this.glFallbackTimer = window.setTimeout(() => {
        this.glFallbackTimer = 0
        if (this.rendererRetired || !this.sys.isActive()) return
        this.rendererRetired = true
        const cb = this.glFallbackCb
        window.setTimeout(() => cb?.(), 0)
      }, 1500)
    }
    this.onGlRestored = () => {
      if (this.glFallbackTimer) {
        window.clearTimeout(this.glFallbackTimer)
        this.glFallbackTimer = 0
      }
      void this.restoreBoardTextures()
    }
    canvas.addEventListener('webglcontextlost', this.onGlLost)
    canvas.addEventListener('webglcontextrestored', this.onGlRestored)
  }

  private uninstallTextureRestore(): void {
    if (this.onVisRestore) {
      document.removeEventListener('visibilitychange', this.onVisRestore)
      window.removeEventListener('pageshow', this.onVisRestore)
      this.onVisRestore = null
    }
    if (this.onFocusRestore) {
      window.removeEventListener('focus', this.onFocusRestore)
      this.onFocusRestore = null
    }
    const canvas = this.game.canvas
    if (canvas && this.onGlLost) {
      canvas.removeEventListener('webglcontextlost', this.onGlLost)
    }
    if (canvas && this.onGlRestored) {
      canvas.removeEventListener('webglcontextrestored', this.onGlRestored)
    }
    this.onGlLost = null
    this.onGlRestored = null
    if (this.glFallbackTimer) {
      window.clearTimeout(this.glFallbackTimer)
      this.glFallbackTimer = 0
    }
  }

  private dropGpuTexture(key: string): void {
    if (!this.textures.exists(key)) return
    try {
      this.textures.remove(key)
    } catch {
      /* already gone after a context loss */
    }
  }

  /**
   * Chrome freezes a background tab and drops the rAF callback. Phaser still
   * has `loop.running === true`, so `wake()` alone does nothing. Stop, then
   * start, so the canvas paints again.
   */
  private kickPhaserLoop(force = true): void {
    const loop = this.game?.loop
    if (!loop) return
    if (!force && loop.running) {
      const frame = loop.frame
      window.setTimeout(() => {
        if (document.hidden || !this.sys?.isActive()) return
        if ((this.game?.loop?.frame ?? frame) !== frame) return
        this.kickPhaserLoop(true)
      }, 80)
      return
    }
    try {
      loop.sleep()
    } catch {
      /* already stopped */
    }
    try {
      loop.wake(true)
    } catch {
      /* destroy in flight */
    }
  }

  /** iOS / Android often wipe decoded bitmaps; desktop Chrome does not. */
  private phoneBitmapsMayHaveDropped(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  }

  /**
   * Tab or window is in front again. Restart the frame loop, listen for
   * clicks, and redraw. Do not drop Canvas textures on desktop: that wipe
   * is what left the field empty while the HUD still advanced phases.
   */
  private resumeForegroundBoard(opts: {
    snapPlayback: boolean
    rebuildTextures: boolean
  }): void {
    this.kickPhaserLoop(opts.snapPlayback)
    if (this.sys.isPaused()) this.scene.resume()
    if (this.coinClockHolds === 0 && this.time.paused) this.time.paused = false
    if (this.canEnableBoardInput()) {
      this.ignoreBoardPointerUntil = 0
      this.inputMutedAt = 0
      this.input.enabled = true
      this.reclaimStuckTouches()
    }
    // The opening deal is its own playback. Snapping it leaves both hands empty.
    if (this.openingDealHeld || this.openingDealPending || this.openingDealPlaying) {
      getDuelVfx()?.resumeForeground(false)
      if (opts.rebuildTextures) void this.restoreBoardTextures()
      return
    }
    // Snapping now would draw the battle behind a coin that has not been
    // seen yet. Let the toss play, then the payload resumes in order.
    const coinOpen = this.coinClockHolds > 0 && !this.coinHoldStuck()
    if (coinOpen) this.coinDeadlineRearm?.()
    const snap = (opts.snapPlayback && !coinOpen) || this.playbackOverdue()
    if (snap) this.forceUnlockPlayback()
    this.scale.refresh()
    if (snap && this.state && this.sys.isActive()) {
      try {
        this.renderAll()
      } catch (err) {
        console.error('renderAll', err)
        this.paintingBoard = false
      }
      if (this.useSharpCards) this.syncSharpCards()
    }
    getDuelVfx()?.resumeForeground(snap)
    if (opts.rebuildTextures) void this.restoreBoardTextures()
    if (!snap) return
    window.setTimeout(() => {
      if (document.hidden || !this.sys?.isActive()) return
      this.kickPhaserLoop(true)
      if (this.eventsPlaying && !this.playbackOverdue()) return
      if (!this.state) return
      try {
        this.renderAll()
      } catch (err) {
        console.error('renderAll', err)
        this.paintingBoard = false
      }
      if (this.useSharpCards) this.syncSharpCards()
    }, 50)
  }

  /**
   * Rebuild card / mat / foil textures from the HTML image cache and
   * redraw the board. Safe to call after multitasking back into the app.
   */
  private async restoreBoardTextures(): Promise<void> {
    if (!this.sys.isActive() || this.restoringTextures) return
    this.restoringTextures = true
    try {
      this.kickPhaserLoop()
      this.scale.refresh()
      this.pendingTextureKeys.clear()
      this.matImage?.destroy()
      this.matImage = null
      this.oppMatImage?.destroy()
      this.oppMatImage = null
      for (const key of [...this.textureSources.keys()]) {
        this.dropGpuTexture(key)
      }
      const gpuKeys =
        typeof this.textures.getTextureKeys === 'function'
          ? this.textures.getTextureKeys()
          : []
      for (const key of gpuKeys) {
        if (
          key.startsWith('house-foil') ||
          key === 'board-glow' ||
          key === 'atk-sword' ||
          key === 'equip-link'
        ) {
          this.dropGpuTexture(key)
        }
      }
      ensureMarkerTextures(this)
      ensureRarityFoilTextures(this)
      this.sleeveRetryAt.clear()
      this.sleeveMisses.clear()
      for (const src of this.textureSources.values()) {
        if (src.kind === 'card') forgetCardImage(src.url)
      }
      const jobs = [...this.textureSources.entries()].map(([key, src]) =>
        src.kind === 'card'
          ? this.adoptPhaserTexture(key, src.url)
          : this.loadMatTexture(key, src.url),
      )
      await Promise.all(jobs)
      if (!this.sys.isActive()) return
      if (this.resolvedMatId) this.loadHalfMat('mine', this.resolvedMatId)
      if (this.resolvedOppMatId) this.loadHalfMat('opp', this.resolvedOppMatId)
      if (this.state) this.renderAll()
      getDuelVfx()?.restoreGpu()
    } finally {
      this.restoringTextures = false
    }
  }

  /**
   * Center of a seat's hand. Direct attacks lunge here so the monster goes
   * toward that player, not sideways to the LP plate.
   */
  private directAttackPos(seat: Seat): { x: number; y: number } {
    const row = seat === this.mySeat ? 5 : 0
    return { x: this.scale.width / 2, y: this.rowY(row) }
  }

  /**
   * Board-space anchor for the LP burst, beside the Vue plate in the gutter.
   */
  private lifePos(seat: Seat): { x: number; y: number } {
    const mine = seat === this.mySeat
    const box = this.halfMatBounds(mine)
    const canvasW = this.game.canvas?.clientWidth || this.scale.width
    const cssPerWorld = canvasW / Math.max(1, this.scale.width)
    const k = Math.max(0.25, cssPerWorld)
    const x = box.x - (12 + 78) / k
    const y = mine ? box.y + box.h - 28 / k : box.y + 58 / k
    return {
      x: Math.max(16, Math.min(box.x - 8, x)),
      y: Math.max(16, Math.min(this.scale.height - 16, y)),
    }
  }

  /**
   * Instant LP (reconnect / first snapshot). Stops any roll in progress.
   */
  private snapLifePoints(): void {
    if (!this.state) return
    this.lpTweenMine?.stop()
    this.lpTweenOpp?.stop()
    this.lpTweenMine = null
    this.lpTweenOpp = null
    const opp = (this.mySeat === 0 ? 1 : 0) as Seat
    this.displayedMyLp = this.state.zones[this.mySeat].lifePoints
    this.displayedOppLp = this.state.zones[opp].lifePoints
    this.pushLpHud(true, this.displayedMyLp, false)
    this.pushLpHud(false, this.displayedOppLp, false)
  }

  private pushLpHud(
    mine: boolean,
    value: number,
    lit: 'hit' | 'heal' | false,
    delta?: number,
  ): void {
    this.lpHudCb?.({ mine, value, lit, delta })
  }

  /**
   * Anime ticker: digits race in steps of 10 (or 50/100 on huge swings).
   */
  private lpRollPlan(span: number): { step: number; duration: number } {
    const abs = Math.max(0, Math.round(Math.abs(span)))
    let step = 10
    if (abs > 2500) step = 50
    if (abs > 6000) step = 100
    const ticks = Math.max(1, Math.ceil(abs / step))
    const lo = abs <= 200 ? 480 : 720
    const hi = 1400
    return {
      step,
      duration: this.visualMs(Math.min(hi, Math.max(lo, ticks * 22))),
    }
  }

  /** Quantize a linear LP tween so the HUD counts by `step`. */
  private lpStepped(
    from: number,
    next: number,
    raw: number,
    step: number,
  ): number {
    if (step <= 1) return Math.round(raw)
    if (next >= from) {
      return Math.min(next, from + Math.floor((raw - from) / step) * step)
    }
    return Math.max(next, from - Math.floor((from - raw) / step) * step)
  }

  /**
   * Anime ticker: digits race linearly; Vue CSS handles punch / settle.
   */
  private tweenLp(
    mine: boolean,
    next: number,
    plan?: { step: number; duration: number },
  ): void {
    const from = mine ? this.displayedMyLp : this.displayedOppLp
    const running = mine ? this.lpTweenMine : this.lpTweenOpp
    if (from === next) {
      running?.stop()
      if (mine) this.lpTweenMine = null
      else this.lpTweenOpp = null
      this.pushLpHud(mine, next, false)
      return
    }
    running?.stop()
    const hit = next < from
    const lit: 'hit' | 'heal' = hit ? 'hit' : 'heal'
    const roll = plan ?? this.lpRollPlan(Math.abs(next - from))
    this.pushLpHud(mine, from, lit)
    const proxy = { n: from }
    const tw = this.tweens.add({
      targets: proxy,
      n: next,
      duration: roll.duration,
      ease: 'Linear',
      onUpdate: () => {
        const n = this.lpStepped(from, next, proxy.n, roll.step)
        if (mine) this.displayedMyLp = n
        else this.displayedOppLp = n
        this.pushLpHud(mine, n, lit)
      },
      onComplete: () => {
        if (mine) {
          this.displayedMyLp = next
          this.lpTweenMine = null
        } else {
          this.displayedOppLp = next
          this.lpTweenOpp = null
        }
        this.pushLpHud(mine, next, false)
      },
    })
    if (mine) this.lpTweenMine = tw
    else this.lpTweenOpp = tw
  }

  private updateInfo(): void {
    if (!this.state) return
    // During a payload the LIFE_POINTS_CHANGED handler rolls the badge.
    // renderAll mid-flight must not snap the digits to the committed total.
    if (this.eventsPlaying || this.staleBoard) return
    const opp = (this.mySeat === 0 ? 1 : 0) as Seat
    this.tweenLp(true, this.state.zones[this.mySeat].lifePoints)
    this.tweenLp(false, this.state.zones[opp].lifePoints)
  }

  // ---------------------------------------------------------------------------
  // Controls
  // ---------------------------------------------------------------------------

  private makeButton(x: number, y: number, label: string, onClick: () => void): Phaser.GameObjects.Container {
    const c = this.add.container(x, y)
    const bg = this.add.rectangle(0, 0, 120, 28, 0x3355aa).setStrokeStyle(1, 0x88aaff)
    const txt = this.add.text(0, 0, label, { fontSize: '12px', color: '#ffffff' }).setOrigin(0.5)
    c.add([bg, txt])
    bg.setInteractive({ useHandCursor: true })
    bg.on('pointerdown', onClick)
    return c
  }

  // ---------------------------------------------------------------------------
  // Interaction
  // ---------------------------------------------------------------------------

  private onHandCardClick(card: CardInstance): void {
    if (this.isWatching()) return
    // Own coin call: a hand tap used to fall through to "Main Phase only"
    // because this seat is not waiting on the opponent. The buttons are the
    // answer; put them back if the overlay dropped them.
    if (this.state?.pendingCoinCall?.chooser === this.mySeat) {
      this.remindOwnCoinCall()
      return
    }
    if (!this.targetFinish && this.legalEngineFieldTarget(card.uid)) {
      this.submitEngineTargets([card.uid])
      return
    }
    // A target window used to swallow the hand click while the board was
    // still catching up, with no toast. Change of Heart looked frozen.
    if (this.state?.pendingFlipEffect?.chooser === this.mySeat) {
      const uids = this.myFlipFieldTargets()
      if (uids.length > 0) this.focusTargets(uids)
      this.toast(t('duel.toast.flipFirst'), 'warn')
      return
    }
    if (this.state?.pendingChoice?.chooser === this.mySeat) {
      if (card.uid === this.state.pendingChoice.cardUid) this.answerChoice(true)
      return
    }
    if (this.state?.pendingTargets?.chooser === this.mySeat) {
      this.toast(t('duel.toast.mustChooseTarget'), 'warn')
      return
    }
    if (this.staleBoard || this.boardPointerBlocked()) {
      if (this.staleBoard) this.recoverStaleBoard()
      return
    }
    // End-of-turn discard takes priority over any normal hand interaction.
    if (this.state?.pendingDiscard?.seat === this.mySeat) {
      this.toggleDiscard(card)
      return
    }
    if (this.state && isWaitingOnOpponent(this.state, this.mySeat)) {
      this.toast('', 'warn', 'notNow')
      return
    }
    const data = CARD_INDEX.get(card.cardId)
    const kind = data ? getCardKind(data) : CardKind.MONSTER
    if (!this.isMyMain() && !(kind === CardKind.MONSTER && this.canSummonNow())) {
      if (this.state && this.state.turnPlayer !== this.mySeat) {
        this.toast('', 'warn', 'notYourTurn')
        return
      }
      if (
        kind === CardKind.MONSTER &&
        this.state?.pendingOfferingSummon === this.mySeat &&
        this.state.zones[this.mySeat].extraNormalSummons <= 0
      ) {
        this.toast(t('duel.toast.offeringPayFirst'), 'warn')
        return
      }
      this.noteOfferingHandFault(kind)
      this.toast(t('duel.toast.mainPhaseOnly'), 'warn')
      return
    }
    const options: { label: string; action: PlayerAction }[] = []

    if (kind === CardKind.MONSTER) {
      if (mothEvolveTurn(card.cardId) !== null) {
        if (!this.canEvolveMoth(Number(card.cardId))) {
          const key =
            Number(card.cardId) === LARVAE_MOTH
              ? 'duel.toast.larvaeNeeds'
              : 'duel.toast.greatNeeds'
          this.toast(this.isMyMain() ? t(key) : t('duel.toast.mainPhaseOnly'), 'warn')
          return
        }
        this.showMenu([
          {
            label: t('duel.menu.specialAtk'),
            onClick: () => this.beginMothSummon(card.uid, Number(card.cardId), CardPosition.ATTACK),
          },
          {
            label: t('duel.menu.specialDef'),
            onClick: () => this.beginMothSummon(card.uid, Number(card.cardId), CardPosition.DEFENSE),
          },
        ])
        return
      }
      if (Number(card.cardId) === GATE_GUARDIAN) {
        if (!this.canGateSummon()) {
          this.toast(
            this.isMyMain() ? t('duel.toast.gateNeeds') : t('duel.toast.mainPhaseOnly'),
            'warn',
          )
          return
        }
        this.showMenu([
          {
            label: t('duel.menu.specialAtk'),
            onClick: () => this.beginGateSummon(card.uid, CardPosition.ATTACK),
          },
          {
            label: t('duel.menu.specialDef'),
            onClick: () => this.beginGateSummon(card.uid, CardPosition.DEFENSE),
          },
        ])
        return
      }
      if (cannotNormalSummon(card.cardId)) {
        const key =
          Number(card.cardId) === HARPIE_LADY_SISTERS
            ? 'duel.toast.sistersEgotist'
            : 'duel.error.noNormalSummon'
        this.toast(t(key), 'warn')
        return
      }
      // Thunder Dragon discards from the hand. That does not spend the
      // Normal Summon, and a Level 5 can still Tribute Summon afterwards.
      const thunder = this.canIgniteThunder(card)
      const summonOk = this.canSummonNow()
      const cocoon = Number(card.cardId) === COCOON_OF_EVOLUTION && this.canEquipCocoon()
      if (!summonOk && !thunder && !cocoon) {
        if (handIgnitionOf(card.cardId) === 'thunderDragon' && this.isMyMain()) {
          this.toast(t('duel.error.conditionsNotMet'), 'warn')
          return
        }
        this.noteOfferingHandFault(CardKind.MONSTER)
        this.toastSummonBlocked()
        return
      }
      const menu: { label: string; onClick: () => void }[] = []
      if (thunder) {
        menu.push({
          label: t('duel.menu.activate'),
          onClick: () =>
            this.dispatchAction({
              type: ActionType.ACTIVATE_MONSTER_EFFECT,
              cardUid: card.uid,
            }),
        })
      }
      if (summonOk) {
        const level = data?.level ?? 0
        if (level >= 5) {
          const need = level >= 7 ? 2 : 1
          if (this.tributeCandidateUids().length >= need) {
            const freeIdx = this.firstFree('monsters', this.mySeat)
            const zoneIndex = freeIdx === -1 ? 0 : freeIdx
            menu.push({
              label: t('duel.menu.tributeAtk'),
              onClick: () =>
                this.beginTributeSummon(card.uid, zoneIndex, need, CardPosition.ATTACK, false),
            })
            menu.push({
              label: t('duel.menu.tributeSet'),
              onClick: () =>
                this.beginTributeSummon(card.uid, zoneIndex, need, CardPosition.DEFENSE, true),
            })
          } else if (!thunder) {
            this.toast(t('duel.toast.notEnoughTribute'), 'warn')
            return
          }
        } else {
          const freeIdx = this.firstFree('monsters', this.mySeat)
          if (freeIdx === -1) {
            if (!thunder && !cocoon) {
              this.toast(t('duel.toast.noMonsterZone'), 'warn')
              return
            }
          } else {
            menu.push({
              label: t('duel.menu.normalAtk'),
              onClick: () =>
                this.dispatchAction({
                  type: ActionType.NORMAL_SUMMON,
                  cardUid: card.uid,
                  zoneIndex: freeIdx,
                }),
            })
            menu.push({
              label: t('duel.menu.setMonster'),
              onClick: () =>
                this.dispatchAction({
                  type: ActionType.SET_MONSTER,
                  cardUid: card.uid,
                  zoneIndex: freeIdx,
                }),
            })
          }
        }
      }
      if (cocoon) {
        menu.push({
          label: t('duel.menu.equip'),
          onClick: () => this.beginCocoon(card.uid),
        })
      }
      if (menu.length === 0) return
      this.showMenu(menu)
      return
    } else {
      const field = this.isFieldSpell(card.cardId)
      const freeIdx = this.firstFree('spellTraps', this.mySeat)
      if (!field && freeIdx === -1) {
        this.toast(t('duel.toast.noSpellZone'), 'warn')
        return
      }
      // Only offer "Activate" when the engine says the conditions are met.
      if (kind === CardKind.SPELL && this.isActivatable(card.uid)) {
        options.push({ label: t('duel.menu.activate'), action: { type: ActionType.ACTIVATE_SPELL_FROM_HAND, cardUid: card.uid } })
      } else if (kind === CardKind.SPELL) {
        const why = this.equipBlockToast(card.cardId)
        if (why) this.toast(why, 'warn')
      }
      options.push({
        label: t('duel.menu.set'),
        action: { type: ActionType.SET_SPELL_TRAP, cardUid: card.uid, zoneIndex: field ? -1 : freeIdx },
      })
    }

    this.showMenu(options.map((o) => ({ label: o.label, onClick: () => this.dispatchAction(o.action) })))
  }

  private isFieldSpell(cardId: string): boolean {
    return getFieldSpellConfig(Number(cardId)) !== undefined
  }

  /**
   * Steps for a card that needs several targets at once, or null when it does not.
   * Two-Pronged Attack destroys 2 monsters you control and 1 the opponent controls,
   * and the player picks all three — destroying your own is part of the effect.
   */
  private multiTargetSteps(cardId: string): TargetStep[] | null {
    if (!this.state || Number(cardId) !== 83887306) return null
    const opp = (this.mySeat === 0 ? 1 : 0) as Seat
    return [
      { prompt: t('duel.toast.destroyYours'), uids: this.myMonsterUids(), count: 2 },
      { prompt: t('duel.toast.destroyOpp'), uids: this.monsterUids(opp), count: 1 },
    ]
  }

  /**
   * Targets that are chosen from a list overlay rather than by clicking the field
   * (e.g. a graveyard monster or an extra-deck fusion). Returns null when the card
   * uses field-click targeting (or none).
   */
  private spellListTargets(
    cardId: string,
  ): {
    title: string
    items: CardPickerListItem[]
    min?: number
    max?: number
    hint?: string
  } | null {
    if (!this.state) return null
    // Monster Reborn: pick a monster from either graveyard.
    if (Number(cardId) === 83764718) {
      const items: CardPickerListItem[] = []
      const opp = (this.mySeat === 0 ? 1 : 0) as Seat
      for (const s of [this.mySeat, opp]) {
        for (const c of this.state.zones[s].graveyard) {
          const data = CARD_INDEX.get(c.cardId)
          if (data && getCardKind(data) === CardKind.MONSTER) {
            items.push({
              uid: c.uid,
              cardId: c.cardId,
              note: `${data.atk ?? 0}/${data.def ?? 0}`,
              foil: c.foil,
              owner: s === this.mySeat ? 'me' : 'opp',
            })
          }
        }
      }
      return {
        title: t('duel.picker.special'),
        items,
        hint: t('duel.picker.ownerHand'),
      }
    }
    // Polymerization: pick a Fusion Monster you can currently summon.
    if (Number(cardId) === 24094653) {
      return { title: t('duel.picker.fusion'), items: this.availableFusionItems() }
    }
    if (Number(cardId) === CHEERFUL_COFFIN) {
      const items = this.state.zones[this.mySeat].hand
        .filter((c) => {
          const data = CARD_INDEX.get(c.cardId)
          return !!data && getCardKind(data) === CardKind.MONSTER
        })
        .map((c) => ({ uid: c.uid, cardId: c.cardId, foil: c.foil }))
      return { title: t('duel.picker.coffin'), items, min: 1, max: 3 }
    }
    // Magic Jammer: discard 1 card from your hand.
    if (Number(cardId) === 77414722) {
      const items = this.state.zones[this.mySeat].hand.map((c) => ({
        uid: c.uid,
        cardId: c.cardId,
        foil: c.foil,
      }))
      return { title: t('duel.picker.magicJammer'), items, min: 1, max: 1 }
    }
    // Soul Release: banish up to 5 cards from either Graveyard.
    if (Number(cardId) === 5758500) {
      const items: CardPickerListItem[] = []
      const opp = (this.mySeat === 0 ? 1 : 0) as Seat
      for (const s of [this.mySeat, opp]) {
        for (const c of this.state.zones[s].graveyard) {
          items.push({
            uid: c.uid,
            cardId: c.cardId,
            foil: c.foil,
            owner: s === this.mySeat ? 'me' : 'opp',
          })
        }
      }
      return { title: t('duel.picker.soulRelease'), items, min: 1, max: 5 }
    }
    // Gravedigger Ghoul: banish 1 or 2 monsters from the opponent's GY.
    if (Number(cardId) === 82542267) {
      const opp = (this.mySeat === 0 ? 1 : 0) as Seat
      const items: { uid: string; cardId: string; note?: string; foil?: string }[] = []
      for (const c of this.state.zones[opp].graveyard) {
        const data = CARD_INDEX.get(c.cardId)
        if (data && getCardKind(data) === CardKind.MONSTER) {
          items.push({
            uid: c.uid,
            cardId: c.cardId,
            note: `${data.atk ?? 0}/${data.def ?? 0}`,
            foil: c.foil,
          })
        }
      }
      return { title: t('duel.picker.banishGy'), items, min: 1, max: 2 }
    }
    // Last Will: Special Summon from the Deck (ATK 1500 or less).
    if (Number(cardId) === 85602018) {
      const items = this.state.zones[this.mySeat].deck
        .filter((c) => {
          const data = CARD_INDEX.get(c.cardId)
          return !!data && getCardKind(data) === CardKind.MONSTER && (data.atk ?? 0) <= 1500
        })
        .map((c) => {
          const data = CARD_INDEX.get(c.cardId)
          return {
            uid: c.uid,
            cardId: c.cardId,
            note: `${data?.atk ?? 0}/${data?.def ?? 0}`,
            foil: c.foil,
          }
        })
      return { title: t('duel.picker.lastWill'), items, min: 0, max: 1 }
    }
    // Flute of Summoning Dragon: up to 2 Dragons from the hand.
    if (Number(cardId) === 43973174) {
      const items = this.state.zones[this.mySeat].hand
        .filter((c) => CARD_INDEX.get(c.cardId)?.race === 'Dragon')
        .map((c) => {
          const data = CARD_INDEX.get(c.cardId)
          return {
            uid: c.uid,
            cardId: c.cardId,
            note: `${data?.atk ?? 0}/${data?.def ?? 0}`,
            foil: c.foil,
          }
        })
      return { title: t('duel.picker.flute'), items, min: 1, max: 2 }
    }
    // Elegant Egotist: Harpie Lady or Harpie Lady Sisters from hand or Deck.
    if (Number(cardId) === ELEGANT_EGOTIST) {
      const zone = this.state.zones[this.mySeat]
      const items = [...zone.hand, ...zone.deck]
        .filter((c) => {
          const id = Number(c.cardId)
          return id === HARPIE_LADY || id === HARPIE_LADY_SISTERS
        })
        .map((c) => {
          const data = CARD_INDEX.get(c.cardId)
          const where = zone.hand.some((hand) => hand.uid === c.uid)
            ? t('duel.picker.fromHand')
            : t('duel.picker.fromDeck')
          return {
            uid: c.uid,
            cardId: c.cardId,
            note: `${where} ${data?.atk ?? 0}/${data?.def ?? 0}`,
            foil: c.foil,
          }
        })
      return { title: t('duel.picker.egotist'), items, min: 0, max: 1 }
    }
    // Inexperienced Spy: look at 1 card in the opponent's hand.
    if (Number(cardId) === 81820689) {
      const opp = (this.mySeat === 0 ? 1 : 0) as Seat
      const items = this.state.zones[opp].hand.map((c) => ({
        uid: c.uid,
        cardId: c.cardId || '',
        foil: c.foil,
      }))
      return { title: t('duel.picker.spy'), items, min: 1, max: 1 }
    }
    return null
  }

  /** Extra-deck fusions the player can currently summon (materials in hand/field). */
  private availableFusionItems(): { uid: string; cardId: string; note?: string; foil?: string }[] {
    if (!this.state) return []
    const z = this.state.zones[this.mySeat]
    const handIds = z.hand.map((c) => Number(c.cardId))
    const fieldIds = z.monsters.filter((m): m is CardInstance => m !== null).map((m) => Number(m.cardId))
    const extra = z.extraDeck.filter((c): c is CardInstance => c !== null)
    const extraIds = extra.map((c) => Number(c.cardId))
    const seen = new Set<string>()
    const items: { uid: string; cardId: string; note?: string; foil?: string }[] = []
    for (const recipe of getAvailableFusions([...handIds, ...fieldIds], extraIds)) {
      const inst = extra.find((c) => Number(c.cardId) === recipe.fusionId && !seen.has(c.uid))
      if (inst) {
        seen.add(inst.uid)
        items.push({
          uid: inst.uid,
          cardId: inst.cardId,
          note: `${recipe.atk}/${recipe.def}`,
          foil: inst.foil,
        })
      }
    }
    return items
  }

  /**
   * Activations go to the engine immediately. Targeting spells land on the field
   * first; pendingTargets then asks for the target.
   */
  private dispatchAction(action: PlayerAction): void {
    this.send(action)
  }

  /**
   * After the activation animation commits, open the engine's target window.
   */
  /**
   * Magician of Faith and Mask of Darkness pick a Graveyard card. Those
   * targets are not on the field, so the flip highlight cannot show them.
   */
  private syncFlipListPrompt(): void {
    const flip = this.state?.pendingFlipEffect
    if (!flip || flip.chooser !== this.mySeat || this.isWatching()) {
      this.flipListRequestId = null
      return
    }
    if (this.flipListRequestId === flip.requestId) return
    if (flip.targetUids.some((uid) => this.uidIsBoardTarget(uid))) return
    const items = this.pickerItemsForUids(flip.targetUids)
    if (items.length === 0) return
    this.flipListRequestId = flip.requestId
    const title =
      Number(flip.cardId) === 28933734 ? t('duel.picker.gyTrap') : t('duel.picker.gySpell')
    this.showCardPicker(title, items, (uid) => {
      this.sendFlipTarget(flip.requestId, uid)
    })
  }

  /**
   * Man-Eater Bug and Hane-Hane pick on the field. On a phone the Main Phase
   * camera sits on the hand, so the opponent's monster is off screen and a
   * spell tap looks like the card refused to activate. Pan to those targets
   * once per window and stay there.
   */
  private syncFlipFieldFocus(): void {
    const flip = this.state?.pendingFlipEffect
    if (!flip || flip.chooser !== this.mySeat || this.isWatching()) {
      if (this.flipFieldFocusId) {
        this.flipFieldFocusId = null
        if (
          this.clickMode === 'idle' &&
          !this.state?.pendingTargets &&
          this.engineTargetRequestId == null
        ) {
          this.setPickSide(null)
        }
      }
      return
    }
    const board = this.myFlipFieldTargets()
    if (board.length === 0) return
    if (this.flipFieldFocusId === flip.requestId) return
    this.flipFieldFocusId = flip.requestId
    this.cancelReturnToHome()
    this.liftShortBoardMute()
    this.setPickSide(this.pickSideOfUids(board))
    this.focusTargets(board)
    this.toast(t('duel.toast.selectTarget'))
  }

  private cardInGraveyard(uid: string): boolean {
    const state = this.state
    if (!state || !uid) return false
    return state.zones.some((zone) => zone.graveyard.some((card) => card.uid === uid))
  }

  private chainOrderNote(cardId: string): string {
    const id = Number(cardId)
    if (id === SWORD_OF_DEEP_SEATED) return t('duel.picker.chainSword')
    if (id === IMMORTAL_OF_THUNDER) return t('duel.picker.chainLife')
    if (id === WHITE_MAGICAL_HAT || id === ROBBIN_GOBLIN) return t('duel.picker.chainDiscard')
    if (id === MASKED_SORCERER) return t('duel.picker.chainDraw')
    if (id === THE_BISTRO_BUTCHER) return t('duel.picker.chainDrawOpp')
    return t('duel.picker.chainSearch')
  }

  /** Mandatory Graveyard triggers: click them in chain order. First resolves last. */
  private syncChainOrderPrompt(): void {
    const pending = this.state?.pendingChainOrder
    if (!pending || pending.chooser !== this.mySeat || this.isWatching()) {
      this.chainOrderRequestId = null
      this.chainOrderPickerLive = false
      return
    }
    if (this.chainOrderRequestId === pending.requestId && this.chainOrderPickerLive) return
    this.chainOrderRequestId = pending.requestId
    this.chainOrderPickerLive = false
    const items = pending.cards.map((card) => ({
      uid: card.uid,
      cardId: card.cardId,
      note: this.chainOrderNote(card.cardId),
      foil: this.foilOf(card.uid),
    }))
    const count = items.length
    this.showCardPicker(
      t('duel.picker.chainOrder'),
      items,
      () => {},
      count,
      false,
      undefined,
      count,
      (uids) => {
        this.send({
          type: ActionType.ORDER_CHAIN,
          requestId: pending.requestId,
          cardUids: uids,
        })
      },
    )
    this.chainOrderPickerLive = items.length > 0
  }

  /** Listed order, when the card list did not open. First card resolves last. */
  confirmChainOrder(): void {
    const pending = this.state?.pendingChainOrder
    if (!pending || pending.chooser !== this.mySeat || this.isWatching()) return
    this.chainOrderRequestId = pending.requestId
    this.chainOrderPickerLive = false
    this.send({
      type: ActionType.ORDER_CHAIN,
      requestId: pending.requestId,
      cardUids: pending.cards.map((card) => card.uid),
    })
  }

  private syncEngineTargetPrompt(): void {
    if (this.isWatching()) {
      this.engineTargetRequestId = null
      return
    }
    const pending = this.state?.pendingTargets
    if (!pending || pending.chooser !== this.mySeat) {
      const wasOpen = this.engineTargetRequestId !== null || this.engineTargetUi !== null
      this.engineTargetRequestId = null
      this.engineTargetUi = null
      if (wasOpen && this.clickMode === 'selectSpellTarget') {
        this.clickMode = 'idle'
        this.pendingTargets = []
        this.selectingCb?.(false)
        this.setPickSide(null)
      }
      return
    }
    if (this.engineTargetRequestId === pending.requestId) {
      // The id was latched, then the click mode was cleared (rejected pick,
      // or the list bailed). Open it again instead of leaving the turn paused.
      if (this.engineTargetUi == null && this.clickMode !== 'selectSpellTarget') {
        this.beginEngineTargetPrompt(pending)
      }
      return
    }
    this.engineTargetRequestId = pending.requestId
    this.beginEngineTargetPrompt(pending)
  }

  /** Puts the open engine target window back on screen after a failed pick. */
  private reopenEngineTargetPrompt(): void {
    this.engineTargetRequestId = null
    this.engineTargetUi = null
    this.syncEngineTargetPrompt()
  }

  /**
   * The "added to the hand" peek replaces whatever list was just opened.
   * Two Sangans resolve in one batch: the peek covers the second search,
   * and closing it used to leave the prompt with no cards.
   */
  private reopenPromptAfterPeek(): void {
    if (this.state?.pendingTargets?.chooser === this.mySeat) {
      this.reopenEngineTargetPrompt()
      return
    }
    if (this.state?.pendingChainOrder?.chooser === this.mySeat) {
      this.chainOrderRequestId = null
      this.syncChainOrderPrompt()
      return
    }
    if (this.state?.pendingFlipEffect?.chooser === this.mySeat) {
      this.flipListRequestId = null
      this.syncFlipListPrompt()
    }
  }

  private syncPeekPrompt(): void {
    if (!this.pendingPeek?.cards.length) return
    const reveal = !!this.pendingPeek.reveal
    const search = !!this.pendingPeek.search
    const items = this.pendingPeek.cards.map((c) => ({
      uid: c.uid,
      cardId: c.cardId,
      note: reveal
        ? this.cardIsMine(c.uid)
          ? t('duel.picker.yours')
          : t('duel.picker.opp')
        : undefined,
      foil: this.foilOf(c.uid),
    }))
    this.pendingPeek = null
    this.showCardPicker(
      t(search ? 'duel.picker.peekSearch' : reveal ? 'duel.picker.peekReveal' : 'duel.picker.peek'),
      items,
      () => {
        /* view only */
      },
      0,
      true,
    )
  }

  private beginEngineTargetPrompt(pending: PendingTargets): void {
    // A finished Two-Pronged pick used to stay armed. The next field window
    // still glowed, and every tap said this monster was the wrong step.
    this.clearStagedTargets()
    if (pending.reorderDeckTop) {
      const items = this.pickerItemsForUids(pending.candidateUids)
      this.engineTargetUi = 'list'
      if (items.length === 0) {
        this.submitEngineTargets(pending.candidateUids)
        return
      }
      const source = pending.cardId
        ? { cardId: pending.cardId, foil: this.foilOf(pending.sourceUid) }
        : undefined
      this.showCardPicker(
        t('duel.picker.bigEye'),
        items,
        (uid) => this.submitEngineTargets([uid]),
        pending.min,
        false,
        source,
        pending.max,
        (uids) => this.submitEngineTargets(uids),
      )
      return
    }
    if (pending.toDeckBottom) {
      const items = this.pickerItemsForUids(pending.candidateUids)
      this.engineTargetUi = 'list'
      if (items.length === 0) {
        this.submitEngineTargets([])
        return
      }
      const source = pending.cardId
        ? { cardId: pending.cardId, foil: this.foilOf(pending.sourceUid) }
        : undefined
      this.showCardPicker(
        t('duel.picker.yadoBottom'),
        items,
        () => {
          /* multi-select uses onMany */
        },
        pending.min,
        false,
        source,
        pending.max,
        (uids) => this.submitEngineTargets(uids),
      )
      return
    }
    if (pending.discardCost || pending.blastDestroy) {
      const items = this.pickerItemsForUids(pending.candidateUids).map((item) => {
        const data = CARD_INDEX.get(item.cardId)
        return data && pending.blastDestroy
          ? { ...item, note: `${data.atk ?? 0}/${data.def ?? 0}` }
          : item
      })
      this.engineTargetUi = 'list'
      const source = pending.cardId
        ? { cardId: pending.cardId, foil: this.foilOf(pending.sourceUid) }
        : undefined
      const title = pending.blastDestroy
        ? t('duel.picker.blastJuggler')
        : t('duel.picker.doomedDiscard')
      this.showCardPicker(
        title,
        items,
        (uid) => this.submitEngineTargets([uid]),
        pending.min,
        false,
        source,
        pending.max,
        pending.max > 1 ? (uids) => this.submitEngineTargets(uids) : undefined,
      )
      return
    }
    const cardId = pending.cardId
    const allowed = new Set(pending.candidateUids)
    const source = pending.cardId
      ? { cardId: pending.cardId, foil: this.foilOf(pending.sourceUid) }
      : undefined
    const list = this.spellListTargets(cardId)
    if (list) {
      let items = list.items.filter((i) => allowed.has(i.uid))
      if (items.length === 0 && allowed.size > 0) {
        items = this.pickerItemsForUids(pending.candidateUids)
      }
      if (items.length === 0 && (list.min ?? 1) > 0) {
        this.abandonEngineTargets(pending)
        return
      }
      const pick = (uids: string[]) => this.submitEngineTargets(uids)
      this.engineTargetUi = 'list'
      if ((list.min ?? 1) === 0 && items.length === 0) {
        pick([])
        return
      }
      if (list.max && list.max > 1) {
        this.showMultiCardPicker(
          list.title,
          items,
          list.min ?? 1,
          list.max,
          pick,
          source,
        )
        return
      }
      this.showCardPicker(
        list.title,
        items,
        (uid) => pick([uid]),
        list.min ?? 1,
        false,
        source,
        1,
        undefined,
        list.hint,
      )
      return
    }
    const steps = this.multiTargetSteps(cardId)
    if (steps) {
      this.engineTargetUi = 'field'
      this.beginTargetSelection(steps, (uids) => this.submitEngineTargets(uids))
      this.focusTargets(steps.flatMap((step) => step.uids))
      return
    }
    // A Deck search is never a click on the field. One of those uids can
    // still sit in the hand after the previous Sangan resolved.
    const boardUids = pending.addToHandFromDeck
      ? []
      : pending.candidateUids.filter((uid) => this.uidIsBoardTarget(uid))
    if (boardUids.length === 0) {
      if (pending.candidateUids.length === 0) {
        if ((pending.min ?? 1) === 0) this.submitEngineTargets([])
        else this.abandonEngineTargets(pending)
        return
      }
      this.engineTargetUi = 'list'
      const title = pending.addToHandFromDeck
        ? deckSearchPrompt(pending.cardId)
        : t('duel.prompt.flipTarget', { name: this.targetCardName(pending.cardId) })
      const items = this.pickerItemsForUids(pending.candidateUids).map((item) => {
        const data = CARD_INDEX.get(item.cardId)
        return data ? { ...item, note: `${data.atk ?? 0}/${data.def ?? 0}` } : item
      })
      const max = pending.max ?? 1
      if (max > 1) {
        this.showCardPicker(
          title,
          items,
          () => {
            /* multi-select uses onMany */
          },
          pending.min,
          false,
          source,
          max,
          (uids) => this.submitEngineTargets(uids),
        )
        return
      }
      this.showCardPicker(
        title,
        items,
        (uid) => this.submitEngineTargets([uid]),
        pending.min,
        false,
        source,
      )
      return
    }
    this.engineTargetUi = 'field'
    this.pendingTargets = boardUids
    this.clickMode = 'selectSpellTarget'
    this.setPickSide(this.pickSideOfUids(boardUids))
    this.focusTargets(boardUids)
    this.toast(
      Number(pending.cardId) === 66788016
        ? t('duel.toast.fissureTie')
        : t('duel.toast.selectTarget'),
    )
    this.liftShortBoardMute()
    this.renderAll()
  }

  /**
   * The engine opened a target window and this client cannot show a card.
   * Only an empty candidate list is allowed to fizzle; a real target must
   * still be clicked.
   */
  private abandonEngineTargets(pending: PendingTargets): void {
    this.engineTargetUi = null
    this.clickMode = 'idle'
    this.pendingTargets = []
    this.selectingCb?.(false)
    this.setPickSide(null)
    this.toast(t('duel.toast.noValidTarget'), 'warn')
    this.send({
      type: ActionType.SELECT_TARGETS,
      requestId: pending.requestId,
      targetUids: [],
      abandon: true,
    })
  }

  private pickerItemsForUids(uids: string[]): CardPickerListItem[] {
    const owners = uids.map((uid) => this.graveOwnerOf(uid))
    const allInGrave = owners.length > 0 && owners.every((owner) => owner != null)
    return uids.map((uid, index) => {
      const card = this.findCardByUid(uid)
      const owner = owners[index]
      return {
        uid,
        cardId: card?.cardId || uid,
        ...(card ? { foil: this.foilOf(uid) } : {}),
        ...(allInGrave && owner ? { owner } : {}),
      }
    })
  }

  /** Which graveyard holds this card, from the local player's point of view. */
  private graveOwnerOf(uid: string): 'me' | 'opp' | undefined {
    if (!this.state) return undefined
    for (const seat of [0, 1] as Seat[]) {
      if (this.state.zones[seat].graveyard.some((card) => card.uid === uid)) {
        return seat === this.mySeat ? 'me' : 'opp'
      }
    }
    return undefined
  }

  /**
   * A click on the board can reach this uid. The opponent's hand is not
   * interactive; those targets use the list picker instead.
   */
  private uidIsBoardTarget(uid: string): boolean {
    if (!this.state) return false
    for (const seat of [0, 1] as Seat[]) {
      const z = this.state.zones[seat]
      if (seat === this.mySeat && z.hand.some((c) => c.uid === uid)) return true
      if (z.monsters.some((c) => c?.uid === uid)) return true
      if (z.spellTraps.some((c) => c?.uid === uid)) return true
      if (z.fieldSpell?.uid === uid) return true
    }
    return false
  }

  /**
   * Field uids the engine is waiting on. The local `pendingTargets` array is
   * filled a beat later; a redraw in that gap used to paint Change of Heart
   * with no glow, and then no opponent monster would take a click.
   */
  private engineFieldTargetUids(): string[] {
    if (this.targetFinish) return this.pendingTargets
    const pending = this.state?.pendingTargets
    if (
      pending?.chooser === this.mySeat &&
      (pending.max ?? 1) === 1 &&
      this.engineTargetUi !== 'list'
    ) {
      const board = pending.candidateUids.filter((uid) => this.uidIsBoardTarget(uid))
      if (board.length > 0) return board
    }
    return this.pendingTargets
  }

  private legalEngineFieldTarget(uid: string): boolean {
    return this.engineFieldTargetUids().includes(uid)
  }

  private targetCardName(cardId: string): string {
    return localizedName(cardId, CARD_INDEX.get(cardId)?.name || t('duel.toast.selectTarget'))
  }

  /**
   * Compact camera: a boost's only monster may sit off the hand band.
   * Opponent monsters are row 2, so a pick that is only on their field
   * uses the opponent band instead of clipping that row.
   */
  private focusTargets(uids: string[]): void {
    if (!this.focusBandsActive() || uids.length === 0) return
    this.focusBand(this.bandForTargetUids(uids), { animate: true })
  }

  private bandForTargetUids(uids: string[]): FocusBand {
    const rows = uids
      .map((uid) => this.rowOfUid(uid))
      .filter((row): row is number => row != null)
    if (rows.length === 0) return 'mine'
    const min = Math.min(...rows)
    const max = Math.max(...rows)
    if (max <= 2) return 'opp'
    if (min >= 5) return 'hand'
    return 'mine'
  }

  /** Board row of a uid, matching renderAll (0 opp hand … 5 your hand). */
  private rowOfUid(uid: string): number | null {
    if (!this.state) return null
    for (const seat of [0, 1] as Seat[]) {
      const z = this.state.zones[seat]
      const mine = seat === this.mySeat
      if (z.hand.some((c) => c.uid === uid)) return mine ? 5 : 0
      if (z.monsters.some((c) => c?.uid === uid)) return mine ? 3 : 2
      if (z.spellTraps.some((c) => c?.uid === uid)) return mine ? 4 : 1
      if (z.fieldSpell?.uid === uid) return mine ? 3 : 2
    }
    return null
  }

  /**
   * The prompt button for a window that has exactly one card on the field.
   * Same send as clicking that card. A face-down name stays hidden.
   */
  confirmSoleTarget(): void {
    const uid = this.soleFieldTargetUid()
    if (!uid || !this.state) return
    const pending = this.state.pendingTargets
    if (
      pending?.chooser === this.mySeat &&
      pending.min === 1 &&
      pending.max === 1 &&
      pending.candidateUids.length === 1 &&
      pending.candidateUids[0] === uid
    ) {
      this.submitEngineTargets([uid])
      return
    }
    const flip = this.state.pendingFlipEffect
    if (
      !pending &&
      flip?.chooser === this.mySeat &&
      flip.targetUids.length === 1 &&
      flip.targetUids[0] === uid
    ) {
      this.sendFlipTarget(flip.requestId, uid)
    }
  }

  /**
   * One answer per Flip window. The highlighted card and the confirm button
   * can both see the same tap; the second send is rejected and, against the
   * AI, used to cancel the turn that should have followed.
   */
  private sendFlipTarget(requestId: string, targetUid: string): void {
    if (this.answeredFlipId === requestId) return
    this.answeredFlipId = requestId
    this.send({
      type: ActionType.RESOLVE_FLIP_TARGET,
      requestId,
      targetUid,
    })
    this.toast('')
  }

  /** The one legal target, when it is a monster, Spell/Trap, or Field Spell. */
  private soleFieldTargetUid(): string | null {
    const state = this.state
    if (!state) return null
    const pending = state.pendingTargets
    const flip = state.pendingFlipEffect
    const uids =
      pending?.chooser === this.mySeat && pending.min === 1 && pending.max === 1
        ? pending.candidateUids
        : !pending && flip?.chooser === this.mySeat
          ? flip.targetUids
          : null
    if (!uids || uids.length !== 1) return null
    const uid = uids[0]
    if (!uid) return null
    for (const seat of [0, 1] as Seat[]) {
      const zones = state.zones[seat]
      if (zones.monsters.some((card) => card?.uid === uid)) return uid
      if (zones.spellTraps.some((card) => card?.uid === uid)) return uid
      if (zones.fieldSpell?.uid === uid) return uid
    }
    return null
  }

  /**
   * Sends SELECT_TARGETS for the open engine window. Reborn/Fusion still pick
   * ATK or DEF after the target is chosen. Flute asks once per Dragon.
   */
  private submitEngineTargets(uids: string[]): void {
    const pending = this.state?.pendingTargets
    if (!pending || pending.chooser !== this.mySeat) return
    const requestId = pending.requestId
    const cardId = pending.cardId
    this.engineTargetUi = null
    this.clickMode = 'idle'
    this.pendingTargets = []
    this.selectingCb?.(false)
    this.setPickSide(null)
    this.toast('')
    this.renderAll()
    const send = (position?: CardPosition, positions?: CardPosition[]): void => {
      this.send({
        type: ActionType.SELECT_TARGETS,
        requestId,
        targetUids: uids,
        ...(position !== undefined ? { position } : {}),
        ...(positions && positions.length > 0 ? { positions } : {}),
      })
    }
    if (uids.length === 0) {
      send()
      return
    }
    const cancel = (): void => {
      this.toast(t('duel.toast.mustChooseTarget'), 'warn')
      this.engineTargetRequestId = null
      this.syncEngineTargetPrompt()
    }
    if (!this.needsSummonPosition(cardId) || !this.positionCb || uids.length === 0) {
      send()
      return
    }
    const ask = (uid: string | undefined, done: (pos: CardPosition | null) => void): void => {
      const previewId = (uid ? this.findCardByUid(uid)?.cardId : undefined) ?? cardId
      const data = CARD_INDEX.get(previewId)
      const inst = uid ? this.findCardByUid(uid) : undefined
      this.positionCb!(
        {
          id: previewId,
          name: data?.name ?? previewId,
          atk: data?.atk,
          def: data?.def,
          foil: asCardFoil(inst?.foil),
        },
        done,
      )
    }
    if (uids.length <= 1) {
      ask(uids[0], (pos) => {
        if (!pos) {
          cancel()
          return
        }
        send(pos)
      })
      return
    }
    const positions: CardPosition[] = []
    const askAt = (index: number): void => {
      ask(uids[index], (pos) => {
        if (!pos) {
          cancel()
          return
        }
        positions.push(pos)
        if (index + 1 >= uids.length) {
          send(positions[0], positions)
          return
        }
        requestAnimationFrame(() => askAt(index + 1))
      })
    }
    askAt(0)
  }

  /** Monster Reborn and Polymerization let the player pick face-up ATK or DEF. */
  private needsSummonPosition(cardId: string): boolean {
    const n = Number(cardId)
    return (
      n === 83764718 ||
      n === 24094653 ||
      n === 85602018 ||
      n === 43973174 ||
      n === ELEGANT_EGOTIST
    )
  }

  /**
   * The full-screen tap zone heard this click, so no sprite took it.
   * Pick the legal card whose art contains the point, including a Defense
   * monster lying sideways over the Field card.
   */
  private fieldCardAt(
    x: number,
    y: number,
  ): { card: CardInstance; seat: Seat; zone: 'monsters' | 'spellTraps' } | null {
    if (!this.state || !this.blockingFieldPick()) return null
    const pad = isDuelCompactNow() ? TOUCH_HIT_PAD : 8
    let best: { card: CardInstance; seat: Seat; zone: 'monsters' | 'spellTraps' } | null =
      null
    let bestD = Infinity
    const consider = (
      card: CardInstance | null,
      seat: Seat,
      zone: 'monsters' | 'spellTraps',
      cx: number,
      cy: number,
      defense: boolean,
    ): void => {
      if (!card || !this.pickAccepts(card, seat, zone)) return
      const hw = (defense ? CARD_H : CARD_W) / 2 + pad
      const hh = (defense ? CARD_W : CARD_H) / 2 + pad
      if (Math.abs(x - cx) > hw || Math.abs(y - cy) > hh) return
      const d = (x - cx) ** 2 + (y - cy) ** 2
      if (d >= bestD) return
      bestD = d
      best = { card, seat, zone }
    }
    for (const seat of [0, 1] as Seat[]) {
      const zones = this.state.zones[seat]
      zones.monsters.forEach((card, i) => {
        if (!card) return
        const p = this.at(this.monsterRow(seat), i)
        consider(
          card,
          seat,
          'monsters',
          p.x,
          p.y,
          card.position === CardPosition.DEFENSE,
        )
      })
      zones.spellTraps.forEach((card, i) => {
        if (!card) return
        const p = this.at(this.spellRow(seat), i)
        consider(card, seat, 'spellTraps', p.x, p.y, false)
      })
      const field = zones.fieldSpell
      if (field) {
        const p = this.fieldSlotPos(seat)
        consider(field, seat, 'spellTraps', p.x, p.y, false)
      }
    }
    return best
  }

  /**
   * Two Defense monsters side by side cover each other's inner edge, and a
   * Spell/Trap or the Field card can sit on that same point. The legal card
   * closer to the finger is the pick, not whichever sprite was painted last.
   */
  private resolveFieldPick(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
    pointer?: Phaser.Input.Pointer,
  ): { card: CardInstance; seat: Seat; zone: 'monsters' | 'spellTraps' } {
    if (pointer && this.blockingFieldPick()) {
      const hit = this.fieldCardAt(pointer.worldX, pointer.worldY)
      if (hit) return hit
    }
    return this.coveredPick(card, seat, zone, pointer) ?? { card, seat, zone }
  }

  /**
   * Highlighted cards must sit above the Spell/Trap row and the side piles.
   * Those are painted later or stick out, and a tap on the covered monster
   * was landing on the card on top.
   */
  private liftPickTargets(): void {
    if (!this.blockingFieldPick()) return
    for (const uid of this.openFieldPickUids()) {
      const sprite = this.fieldSprites.get(uid)
      if (sprite) this.boardLayer.bringToTop(sprite)
    }
  }

  /**
   * A Defense monster in the left zone overlaps the Field card. The tap can
   * land on the Field sprite. If a highlighted monster also contains the
   * point, that monster is the pick.
   */
  private coveredPick(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
    pointer?: Phaser.Input.Pointer,
  ): { card: CardInstance; seat: Seat; zone: 'monsters' | 'spellTraps' } | null {
    if (!pointer || !this.state) return null
    if (this.pickAccepts(card, seat, zone)) return null
    const x = pointer.worldX
    const y = pointer.worldY
    let best: { card: CardInstance; seat: Seat; zone: 'monsters' | 'spellTraps' } | null = null
    let bestD = Infinity
    for (const s of [0, 1] as Seat[]) {
      const monsters = this.state.zones[s].monsters
      for (const m of monsters) {
        if (!m || !this.pickAccepts(m, s, 'monsters')) continue
        const sprite = this.fieldSprites.get(m.uid)
        if (!sprite) continue
        const b = sprite.getBounds()
        if (x < b.x || y < b.y || x > b.right || y > b.bottom) continue
        const d = (x - b.centerX) ** 2 + (y - b.centerY) ** 2
        if (d < bestD) {
          bestD = d
          best = { card: m, seat: s, zone: 'monsters' }
        }
      }
    }
    return best
  }

  /** True when this card is a legal choice for the open pick. */
  private pickAccepts(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
  ): boolean {
    const flip = this.state?.pendingFlipEffect
    if (flip?.chooser === this.mySeat) return flip.targetUids.includes(card.uid)
    if (this.clickMode === 'selectTribute') {
      const own = zone === 'monsters' && seat === this.mySeat
      return own || this.isSoulTribute(card.uid)
    }
    if (this.clickMode === 'selectAttackTarget') {
      return zone === 'monsters' && seat !== this.mySeat
    }
    if (this.clickMode === 'selectSpellTarget') {
      if (this.targetFinish) {
        return (
          zone === 'monsters' &&
          (this.targetPicked.includes(card.uid) || this.targetPickedStepOk(card))
        )
      }
      return this.legalEngineFieldTarget(card.uid)
    }
    if (this.legalEngineFieldTarget(card.uid)) return true
    return false
  }

  private targetPickedStepOk(card: CardInstance): boolean {
    const step = this.targetSteps[this.targetStepIndex]
    return !!step && step.uids.includes(card.uid)
  }

  /**
   * Cara / Cruz live on the Vue overlay. A field tap used to die here while
   * that card was missing, so the Battle Phase looked frozen. Publish the
   * same call again so the buttons come back.
   */
  private remindOwnCoinCall(): void {
    const coin = this.state?.pendingCoinCall
    if (!this.state || !coin || coin.chooser !== this.mySeat) return
    this.state = { ...this.state, pendingCoinCall: coin }
    this.publishBoardState()
  }

  private onFieldCardClick(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
    pointer?: Phaser.Input.Pointer,
  ): void {
    if (this.isWatching()) return
    if (this.state?.pendingChoice?.chooser === this.mySeat) {
      if (card.uid === this.state.pendingChoice.cardUid) this.answerChoice(true)
      return
    }
    if (this.state?.pendingCoinCall?.chooser === this.mySeat) {
      this.remindOwnCoinCall()
      return
    }
    const covered = this.resolveFieldPick(card, seat, zone, pointer)
    if (covered.card !== card || covered.seat !== seat || covered.zone !== zone) {
      card = covered.card
      seat = covered.seat
      zone = covered.zone
    }
    if (this.boardPointerBlocked() && !this.acceptsMutedTap(card.uid)) return
    // Targeting windows open at commit while eventsPlaying/staleBoard is still
    // true for a beat; those clicks must go through.
    const picking =
      this.clickMode === 'selectSpellTarget' ||
      this.clickMode === 'selectTribute' ||
      this.clickMode === 'selectCocoonHost' ||
      this.clickMode === 'selectAttackTarget' ||
      this.state?.pendingFlipEffect?.chooser === this.mySeat ||
      this.state?.pendingTargets?.chooser === this.mySeat
    if (this.staleBoard && !picking) {
      this.recoverStaleBoard()
      return
    }
    // A Flip Effect is waiting for its controller to pick a target. Set cards are
    // hidden information, so the player picks one of the highlighted cards blindly;
    // the engine reveals and resolves it (e.g. Reaper of the Cards).
    const flip = this.state?.pendingFlipEffect
    if (flip && flip.chooser === this.mySeat) {
      if (flip.targetUids.includes(card.uid)) {
        this.sendFlipTarget(flip.requestId, card.uid)
      } else {
        const uids = this.myFlipFieldTargets()
        if (uids.length > 0) this.focusTargets(uids)
        this.toast(t('duel.toast.highlighted'))
      }
      return
    }

    // Manual tribute selection. A monster that can still Flip Summon, change
    // position, or activate keeps those actions; otherwise the tap tributes it.
    if (this.clickMode === 'selectTribute') {
      if (
        seat === this.mySeat &&
        zone === 'monsters' &&
        this.offerFieldActionDuringTribute(card)
      ) {
        return
      }
      this.toggleTribute(card, seat, zone)
      return
    }

    if (this.clickMode === 'selectCocoonHost') {
      const cocoonUid = this.pendingCocoonUid
      if (
        cocoonUid &&
        zone === 'monsters' &&
        card.faceUp &&
        Number(card.cardId) === PETIT_MOTH
      ) {
        this.pendingCocoonUid = null
        this.clickMode = 'idle'
        this.toast('')
        this.send({
          type: ActionType.EQUIP_FROM_HAND,
          cardUid: cocoonUid,
          targetUid: card.uid,
        })
      }
      return
    }

    // Attack targeting.
    if (this.clickMode === 'selectAttackTarget' && seat !== this.mySeat && zone === 'monsters') {
      const uid = this.pendingAttacker
      const attacker = uid
        ? (this.state?.zones[this.mySeat].monsters.find((m) => m?.uid === uid) ?? null)
        : null
      if (!attacker) {
        this.clearAttackPick()
        return
      }
      const block = this.attackBlockCode(attacker)
      if (block) {
        this.clearAttackPick()
        this.toast('', 'warn', block)
        return
      }
      const forced = magnetismTargets(this.state, (this.mySeat === 0 ? 1 : 0) as Seat)
      if (forced.length > 0 && !forced.some((m) => m.uid === card.uid)) {
        this.toast('', 'warn', 'ringOfMagnetism')
        return
      }
      const ok = this.send({
        type: ActionType.DECLARE_ATTACK,
        attackerUid: attacker.uid,
        targetUid: card.uid,
      })
      this.finishAttackPick(ok)
      return
    }

    // Spell targeting: pick a highlighted card (monster or face-up Trap).
    if (this.clickMode === 'selectSpellTarget' && this.targetFinish) {
      if (zone !== 'monsters') return
      this.pickTargetStep(card)
      return
    }
    if (this.legalEngineFieldTarget(card.uid)) {
      this.submitEngineTargets([card.uid])
      return
    }
    if (this.clickMode === 'selectSpellTarget') {
      this.toast(t('duel.toast.invalidTarget'), 'warn')
      return
    }

    if (this.state && isWaitingOnOpponent(this.state, this.mySeat)) {
      this.toast('', 'warn', 'notNow')
      return
    }

    if (seat !== this.mySeat) return

    if (zone === 'monsters') {
      // Battle Phase: clicking an attack-ready monster launches the attack
      // automatically (direct attack if the opponent has no monsters, or the sole
      // target if they have exactly one). No extra button needed.
      if (
        this.state?.phase === EnginePhase.BATTLE &&
        card.faceUp &&
        card.position === CardPosition.ATTACK &&
        !card.attackedThisTurn
      ) {
        this.initiateAttack(card)
        return
      }
      // Main Phase: only offer a position action the engine would accept.
      const opts = this.mainPhaseMonsterOptions(card)
      if (opts.length > 0) this.showMenu(opts)
    } else if (zone === 'spellTraps') {
      // Battle Phase prompts list face-up Ultimate Offering here. Clicking the
      // card answers that window; ACTIVATE_TRAP is refused while it is open.
      // A Set trap Auto left off the question (Robbin' Goblin on the
      // opponent's Battle Phase) still answers with that card, not the
      // neighbor the prompt named.
      if (this.priorityActivatable(card.uid) || this.ownSetTrapDuringPriority(card)) {
        if (pointer?.wasTouch && this.trapPromptArmed()) return
        this.respondToPriority(card.uid)
        return
      }
      // Only offer "Activate" when the engine confirms the conditions are met
      // (this already accounts for phase, turn, set-timing and the card's own
      // activation conditions). Face-up Ultimate Offering can be ignited again.
      if (!this.isActivatable(card.uid)) {
        if (this.isMyMain()) {
          const why = this.equipBlockToast(card.cardId)
          if (why) this.toast(why, 'warn')
        }
        return
      }
      const data = CARD_INDEX.get(card.cardId)
      const kind = data ? getCardKind(data) : null
      if (!card.faceUp && kind === CardKind.SPELL) {
        this.showMenu([{ label: t('duel.menu.activate'), onClick: () => this.dispatchAction({ type: ActionType.ACTIVATE_SET_SPELL, cardUid: card.uid }) }])
      } else if (kind === CardKind.TRAP) {
        this.showMenu([{ label: t('duel.menu.activate'), onClick: () => this.dispatchAction({ type: ActionType.ACTIVATE_TRAP, cardUid: card.uid }) }])
      }
    }
  }

  /**
   * Starts a tribute summon. If the player has exactly the number of monsters the
   * summon needs, the tributes are chosen automatically; otherwise the player picks
   * which monsters to tribute by clicking them on the field.
   */
  /** Equip Cocoon of Evolution onto a face-up Petit Moth. */
  private beginCocoon(cardUid: string): void {
    if (!this.state || !this.canEquipCocoon()) {
      this.toast(t('duel.toast.cocoonNeeds'), 'warn')
      return
    }
    const hosts = faceUpPetitMoths(this.state)
    const only = hosts[0]
    if (hosts.length === 1 && only) {
      this.send({
        type: ActionType.EQUIP_FROM_HAND,
        cardUid,
        targetUid: only.uid,
      })
      return
    }
    this.pendingCocoonUid = cardUid
    this.clickMode = 'selectCocoonHost'
    this.noteTributeWindow()
    this.toast(t('duel.toast.cocoonPick'))
    this.renderAll()
  }

  private canEquipCocoon(): boolean {
    if (!this.state || !this.isMyMain()) return false
    if (this.clickMode !== 'idle') return false
    if (isWaitingOnOpponent(this.state, this.mySeat)) return false
    if (this.firstFree('spellTraps', this.mySeat) === -1) return false
    return faceUpPetitMoths(this.state).length > 0
  }

  /** Larvae Moth or Great Moth, on exactly that counted turn. */
  private beginMothSummon(cardUid: string, cardId: number, position: CardPosition): void {
    if (!this.state) return
    const turn = mothEvolveTurn(cardId)
    if (turn === null) return
    const tribute = evolveTribute(this.state, this.mySeat, turn)
    if (!tribute) {
      this.toast(
        t(cardId === LARVAE_MOTH ? 'duel.toast.larvaeNeeds' : 'duel.toast.greatNeeds'),
        'warn',
      )
      return
    }
    this.send({
      type: ActionType.SPECIAL_SUMMON_PROC,
      cardUid,
      position,
      tributeUids: [tribute.moth.uid],
    })
  }

  private canEvolveMoth(cardId: number): boolean {
    if (!this.state || !this.isMyMain()) return false
    if (this.clickMode !== 'idle') return false
    if (isWaitingOnOpponent(this.state, this.mySeat)) return false
    const turn = mothEvolveTurn(cardId)
    if (turn === null) return false
    return evolveTribute(this.state, this.mySeat, turn) !== null
  }

  /**
   * Gate Guardian tributes one Sanga, one Kazejin and one Suijin.
   * One of each is sent immediately. Extra copies are clicked on the field.
   */
  private beginGateSummon(cardUid: string, position: CardPosition): void {
    if (!this.state) return
    const monsters = this.state.zones[this.mySeat].monsters.filter(
      (monster): monster is CardInstance => monster != null,
    )
    const tributeUids = gateTributeSet(monsters)
    if (!tributeUids) {
      this.toast(t('duel.toast.gateNeeds'), 'warn')
      return
    }
    const pieces = monsters.filter((monster) => isGatePiece(monster.cardId))
    const oneEach = pieces.length === 3
    if (oneEach) {
      this.send({
        type: ActionType.SPECIAL_SUMMON_PROC,
        cardUid,
        position,
        tributeUids,
      })
      return
    }
    this.pendingTribute = {
      cardUid,
      zoneIndex: 0,
      position,
      faceDown: false,
      count: 3,
      gate: true,
    }
    this.tributeSelected = []
    this.clickMode = 'selectTribute'
    this.noteTributeWindow()
    this.selectingCb?.(true)
    this.setPickSide('mine')
    this.toast(t('duel.toast.gateTributes'))
    this.renderAll()
  }

  private canGateSummon(): boolean {
    if (!this.state || !this.isMyMain() || this.clickMode !== 'idle') return false
    if (isWaitingOnOpponent(this.state, this.mySeat)) return false
    if (
      this.state.pendingPriority ||
      this.state.pendingChainOrder ||
      this.state.pendingTargets ||
      this.state.chain.length > 0
    ) {
      return false
    }
    const monsters = this.state.zones[this.mySeat].monsters.filter(
      (monster): monster is CardInstance => monster != null,
    )
    return gateTributeSet(monsters) != null
  }

  private beginTributeSummon(
    cardUid: string,
    zoneIndex: number,
    need: number,
    position: CardPosition,
    faceDown: boolean,
  ): void {
    const myMonsters = this.tributeCandidateUids()
    if (myMonsters.length < need) {
      this.toast(t('duel.toast.notEnoughTribute'), 'warn')
      return
    }
    if (myMonsters.length === need) {
      if (!this.tributeOpensZone(myMonsters)) {
        this.toast(t('duel.toast.soulExchangeNoZone'), 'warn')
        return
      }
      this.send({ type: ActionType.TRIBUTE_SUMMON, cardUid, zoneIndex, tributeUids: myMonsters, position, faceDown })
      return
    }
    // More monsters than needed: let the player choose which ones to tribute.
    this.pendingTribute = { cardUid, zoneIndex, position, faceDown, count: need }
    this.tributeSelected = []
    this.clickMode = 'selectTribute'
    this.noteTributeWindow()
    this.selectingCb?.(true)
    this.setPickSide(this.pickSideOfUids(myMonsters))
    this.focusTargets(myMonsters)
    this.toast(this.tributePrompt(need, 0))
    this.renderAll()
  }

  /**
   * Flip Summon, a position change, or an ignition, while a Tribute Summon is
   * still only a pick. The tap used to tribute the monster and nothing else,
   * so a face-down Magician of Faith could not be Flip Summoned.
   */
  private offerFieldActionDuringTribute(card: CardInstance): boolean {
    const actions = this.mainPhaseMonsterOptions(card)
    if (actions.length === 0) return false
    const picked = this.tributeSelected.includes(card.uid)
    this.showMenu([
      {
        label: t(picked ? 'duel.menu.undoTribute' : 'duel.menu.asTribute'),
        onClick: () => this.toggleTribute(card, this.mySeat, 'monsters'),
      },
      ...actions,
    ])
    return true
  }

  /** Main Phase actions on one of your monsters. Empty outside that window. */
  private mainPhaseMonsterOptions(
    card: CardInstance,
  ): { label: string; onClick: () => void }[] {
    if (!this.isMyMain()) return []
    const opts: { label: string; onClick: () => void }[] = []
    const duringPick = this.clickMode === 'selectTribute'
    if (this.canIgniteCoinEffect(card, duringPick) || this.canIgniteTribute(card, duringPick)) {
      opts.push({
        label: t('duel.menu.activate'),
        onClick: () =>
          this.send({ type: ActionType.ACTIVATE_MONSTER_EFFECT, cardUid: card.uid }),
      })
    }
    if (this.canFlipSummon(card)) {
      opts.push({
        label: t('duel.menu.flipSummon'),
        onClick: () => this.send({ type: ActionType.FLIP_SUMMON, cardUid: card.uid }),
      })
    } else if (this.canChangePosition(card)) {
      opts.push({
        label: t('duel.menu.changePosition'),
        onClick: () => this.send({ type: ActionType.CHANGE_POSITION, cardUid: card.uid }),
      })
    }
    return opts
  }

  /** Registers/deregisters a clicked monster as a tribute and fires the summon when ready. */
  private toggleTribute(card: CardInstance, seat: Seat, zone: 'monsters' | 'spellTraps'): void {
    const soulOk = zone === 'monsters' && this.isSoulTribute(card.uid)
    if ((seat !== this.mySeat || zone !== 'monsters') && !soulOk) {
      this.toast(t('duel.toast.clickOwnTribute'), 'warn')
      return
    }
    const tribute = this.pendingTribute
    if (!tribute) return
    if (this.tributeWindowClosed()) {
      this.clearTributePick(true)
      return
    }
    if (tribute.gate && !isGatePiece(card.cardId)) {
      this.toast(t('duel.toast.gateTributes'), 'warn')
      return
    }
    if (this.tributeSelected.includes(card.uid)) {
      this.tributeSelected = this.tributeSelected.filter((u) => u !== card.uid)
    } else {
      this.tributeSelected.push(card.uid)
    }
    if (this.tributeSelected.length >= tribute.count) {
      const tributeUids = this.tributeSelected.slice(0, tribute.count)
      if (tribute.gate) {
        const monsters = (this.state?.zones[this.mySeat].monsters ?? []).filter(
          (monster): monster is CardInstance => monster != null,
        )
        if (!isGateTributeSet(monsters, tributeUids)) {
          this.tributeSelected = this.tributeSelected.filter((uid) => uid !== card.uid)
          this.toast(t('duel.toast.gateTributes'), 'warn')
          this.renderAll()
          return
        }
        this.send({
          type: ActionType.SPECIAL_SUMMON_PROC,
          cardUid: tribute.cardUid,
          position: tribute.position,
          tributeUids,
        })
        this.cancelSelection()
        return
      }
      if (!this.tributeOpensZone(tributeUids)) {
        this.tributeSelected = this.tributeSelected.filter((u) => u !== card.uid)
        this.toast(t('duel.toast.soulExchangeNoZone'), 'warn')
        this.renderAll()
        return
      }
      this.send({
        type: ActionType.TRIBUTE_SUMMON,
        cardUid: tribute.cardUid,
        zoneIndex: tribute.zoneIndex,
        tributeUids,
        position: tribute.position,
        faceDown: tribute.faceDown,
      })
      this.cancelSelection()
      return
    }
    this.toast(this.tributePrompt(tribute.count, this.tributeSelected.length))
    this.renderAll()
  }

  /** Picks/unpicks a hand card for the mandatory end-of-turn discard. */
  private toggleDiscard(card: CardInstance): void {
    const pending = this.state?.pendingDiscard
    if (!pending || pending.seat !== this.mySeat) return
    if (this.discardSelected.includes(card.uid)) {
      this.discardSelected = this.discardSelected.filter((u) => u !== card.uid)
    } else {
      this.discardSelected.push(card.uid)
    }
    if (this.discardSelected.length >= pending.count) {
      const cardUids = this.discardSelected.slice(0, pending.count)
      this.discardSelected = []
      this.send({ type: ActionType.DISCARD, cardUids })
      return
    }
    this.renderAll()
  }

  /** Vue cancel button — phones have no Esc key. */
  cancelBoardSelection(): void {
    this.cancelSelection()
  }

  /** Clears any in-progress selection (tribute / target / attack) and its UI hints. */
  private cancelSelection(): void {
    const pending = this.state?.pendingTargets
    if (pending?.chooser === this.mySeat) {
      this.focusTargets(pending.candidateUids)
      if (this.clickMode !== 'selectSpellTarget' && this.engineTargetUi !== 'list') {
        this.reopenEngineTargetPrompt()
      }
      this.toast(t('duel.toast.selectTarget'), 'warn')
      return
    }
    this.clearMenu()
    this.clickMode = 'idle'
    this.pendingTribute = null
    this.tributeSelected = []
    this.tributePickAt = null
    this.pendingCocoonUid = null
    this.pendingTargets = []
    this.clearStagedTargets()
    this.pendingAttacker = null
    this.selectingCb?.(false)
    this.setPickSide(null)
    this.toast('')
    this.renderAll()
  }

  /**
   * Starts a staged target selection. Each step collects its own monsters; once the
   * last one is filled, `finish` gets every uid picked. Esc cancels the whole thing.
   */
  private beginTargetSelection(steps: TargetStep[], finish: (uids: string[]) => void): void {
    this.targetSteps = steps
    this.targetStepIndex = 0
    this.targetPicked = []
    this.targetFinish = finish
    this.clickMode = 'selectSpellTarget'
    this.pendingTargets = steps[0]?.uids ?? []
    this.setPickSide(this.pickSideOfUids(this.pendingTargets))
    this.toast(this.targetStepPrompt())
    this.liftShortBoardMute()
    this.renderAll()
  }

  /** Tribute guide. Phones get a Cancel button, so the Esc hint stays on desktop. */
  private tributePrompt(need: number, selected: number): string {
    return this.withEscCancel(t('duel.toast.selectTribute', { need, selected }))
  }

  /** Appends the Esc hint only where a keyboard can press it. */
  private withEscCancel(message: string): string {
    if (isDuelCompactNow()) return message
    return `${message}${t('duel.toast.escCancel')}`
  }

  /** How many uids the current step still needs, rendered into its prompt. */
  private targetStepPrompt(): string {
    const step = this.targetSteps[this.targetStepIndex]
    if (!step) return ''
    const progress = step.count > 1 ? ` (${this.pickedInCurrentStep()}/${step.count})` : ''
    return this.withEscCancel(`${step.prompt}${progress}`)
  }

  private pickedInCurrentStep(): number {
    const before = this.targetSteps
      .slice(0, this.targetStepIndex)
      .reduce((n, s) => n + s.count, 0)
    return this.targetPicked.length - before
  }

  /** Step that still needs a pick, given how many monsters are already chosen. */
  private stepIndexForPickCount(picked: number): number {
    let seen = 0
    for (let i = 0; i < this.targetSteps.length; i++) {
      const count = this.targetSteps[i]?.count ?? 0
      if (picked < seen + count) return i
      seen += count
    }
    return Math.max(0, this.targetSteps.length - 1)
  }

  /** Refreshes the prompt and highlights for the step currently being filled. */
  private showTargetStep(focus: boolean): void {
    const step = this.targetSteps[this.targetStepIndex]
    this.pendingTargets = step?.uids ?? []
    this.setPickSide(this.pickSideOfUids(this.pendingTargets))
    if (focus && step) this.focusTargets(step.uids)
    this.toast(this.targetStepPrompt())
    this.renderAll()
  }

  /**
   * Handles one click during a staged target selection.
   * A monster already chosen can be clicked again to drop it, even after
   * that step filled and the prompt moved on (Two-Pronged Attack).
   */
  private pickTargetStep(card: CardInstance): void {
    const pickedAt = this.targetPicked.indexOf(card.uid)
    if (pickedAt !== -1) {
      const before = this.targetStepIndex
      this.targetPicked.splice(pickedAt, 1)
      this.targetStepIndex = this.stepIndexForPickCount(this.targetPicked.length)
      this.showTargetStep(this.targetStepIndex !== before)
      return
    }

    const step = this.targetSteps[this.targetStepIndex]
    if (!step || !step.uids.includes(card.uid)) {
      this.toast(t('duel.toast.invalidStep'), 'warn')
      return
    }

    this.targetPicked.push(card.uid)
    if (this.pickedInCurrentStep() < step.count) {
      this.showTargetStep(false)
      return
    }

    this.targetStepIndex += 1
    if (this.targetSteps[this.targetStepIndex]) {
      this.showTargetStep(false)
      return
    }

    const finish = this.targetFinish
    const picked = [...this.targetPicked]
    // The engine window is still open, so cancelSelection would keep this
    // staged pick and the next card's highlighted monster would be refused.
    this.clearStagedTargets()
    this.clickMode = 'idle'
    finish?.(picked)
  }

  /** Drops a staged field pick (Two-Pronged Attack). Leaves the engine window. */
  private clearStagedTargets(): void {
    this.targetSteps = []
    this.targetStepIndex = 0
    this.targetPicked = []
    this.targetFinish = null
  }

  /**
   * Face-down cards you control pulse the back so a finger can tell Sets
   * apart without hovering the card viewer. Skip while a blind pick is open.
   */
  private shouldPeekOwnSet(card: CardInstance, mine: boolean): boolean {
    if (!mine || card.faceUp || !card.cardId) return false
    if (!needsOwnSetPeekNow()) return false
    if (!this.isMyTurn()) return false
    if (this.clickMode !== 'idle') return false
    if (this.state?.pendingFlipEffect) return false
    if (this.state?.pendingTargets) return false
    return true
  }

  /**
   * Compact: start tracking a possible attack swipe from an ATK-ready monster.
   * Returns true so the caller skips the tap menu until pointer up.
   */
  private beginSwipeAttack(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
    pointer: Phaser.Input.Pointer,
  ): boolean {
    if (this.isWatching()) return false
    if (!this.canSwipeAttack(card, seat, zone)) return false
    this.clearSwipeAttack()
    this.swipeAttack = {
      attacker: card,
      startX: pointer.worldX,
      startY: pointer.worldY,
      dragging: false,
      line: null,
    }
    return true
  }

  private canSwipeAttack(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
  ): boolean {
    if (!isDuelCompactNow()) return false
    if (zone !== 'monsters' || seat !== this.mySeat) return false
    const state = this.state
    if (!state || state.turnPlayer !== this.mySeat) return false
    if (state.pendingCoinCall?.chooser === this.mySeat) return false
    if (
      state.phase !== EnginePhase.MAIN_1 &&
      state.phase !== EnginePhase.BATTLE
    ) {
      return false
    }
    if (isOpeningTurn(state)) return false
    if (state.zones[seat].skipBattlePhase) return false
    if (state.zones[seat].attackLockTurns > 0) return false
    if (attackLockCode(state, seat, card)) return false
    if (this.clickMode !== 'idle') return false
    if (this.staleBoard) return false
    return (
      card.faceUp &&
      card.position === CardPosition.ATTACK &&
      !card.attackedThisTurn
    )
  }

  private onSwipeAttackMove(pointer: Phaser.Input.Pointer): void {
    const swipe = this.swipeAttack
    if (!swipe) return
    const dist = Math.hypot(
      pointer.worldX - swipe.startX,
      pointer.worldY - swipe.startY,
    )
    if (!swipe.dragging) {
      if (dist < SWIPE_ATTACK_PX) return
      swipe.dragging = true
      this.spanSwipeCamera(swipe.attacker)
      swipe.line = this.add.graphics().setDepth(90)
    }
    this.drawSwipeAttackLine(pointer.worldX, pointer.worldY)
  }

  private spanSwipeCamera(attacker: CardInstance): void {
    if (!this.focusBandsActive()) return
    const from = this.cardFieldPos(attacker.uid)
    if (!from) return
    const to = { x: this.scale.width / 2, y: this.rowY(2) }
    const span = this.spanFocus(from, to)
    this.currentFocusBand = 'mine'
    this.focusBandCb?.('mine')
    this.moveFocusCamera(span.cx, span.cy, span.zoom, false)
    this.cancelReturnToHome()
  }

  private drawSwipeAttackLine(toX: number, toY: number): void {
    const swipe = this.swipeAttack
    if (!swipe?.line) return
    const from =
      this.cardFieldPos(swipe.attacker.uid) ??
      { x: swipe.startX, y: swipe.startY }
    swipe.line.clear()
    swipe.line.lineStyle(3, 0xffe066, 0.9)
    swipe.line.beginPath()
    swipe.line.moveTo(from.x, from.y)
    swipe.line.lineTo(toX, toY)
    swipe.line.strokePath()
    swipe.line.fillStyle(0xffe066, 0.95)
    swipe.line.fillCircle(toX, toY, 7)
  }

  private onSwipeAttackUp(pointer: Phaser.Input.Pointer): void {
    const swipe = this.swipeAttack
    if (!swipe) return
    const dragging = swipe.dragging
    const attacker = swipe.attacker
    this.clearSwipeAttack()
    if (!dragging) {
      this.onFieldCardClick(attacker, this.mySeat, 'monsters')
      return
    }
    this.resolveSwipeAttack(attacker, pointer.worldX, pointer.worldY)
  }

  private clearSwipeAttack(): void {
    const swipe = this.swipeAttack
    this.swipeAttack = null
    swipe?.line?.destroy()
  }

  private resolveSwipeAttack(
    attacker: CardInstance,
    worldX: number,
    worldY: number,
  ): void {
    if (!this.state) return
    const oppSeat = (this.mySeat === 0 ? 1 : 0) as Seat
    const hit = this.monsterAtWorld(worldX, worldY, oppSeat)
    if (hit) {
      const forced = magnetismTargets(this.state, oppSeat)
      if (forced.length > 0 && !forced.some((m) => m.uid === hit.uid)) {
        this.toast('', 'warn', 'ringOfMagnetism')
        return
      }
      this.enterBattleThenAttack(attacker, hit.uid)
      return
    }
    const oppMonsters = this.state.zones[oppSeat].monsters.filter(
      (m: CardInstance | null): m is CardInstance => m !== null,
    )
    const oppHalf = worldY < (this.rowY(2) + this.rowY(3)) / 2
    if (oppMonsters.length === 0 && oppHalf) {
      this.enterBattleThenAttack(attacker, null)
      return
    }
    this.scheduleReturnToHome(FOCUS_HOME_HOLD_MS)
  }

  /**
   * Enter Battle from Main 1 if needed, then declare the attack. `targetUid`
   * null is a direct attack.
   */
  private enterBattleThenAttack(
    attacker: CardInstance,
    targetUid: string | null,
  ): void {
    if (!this.state) return
    if (this.state.zones[this.mySeat].skipBattlePhase) {
      this.toast(t('duel.toast.skipBattle'), 'warn')
      return
    }
    if (this.state.phase === EnginePhase.MAIN_1) {
      if (!this.send({ type: ActionType.ADVANCE_PHASE })) return
    }
    const ok = targetUid
      ? this.send({
          type: ActionType.DECLARE_ATTACK,
          attackerUid: attacker.uid,
          targetUid,
        })
      : this.send({
          type: ActionType.DIRECT_ATTACK,
          attackerUid: attacker.uid,
        })
    this.finishAttackPick(ok)
  }

  /**
   * Why this monster cannot declare an attack right now, as a `duel.error` code.
   * Null means the engine would accept the declaration.
   */
  private attackBlockCode(card: CardInstance): string | null {
    const state = this.state
    if (!state || state.turnPlayer !== this.mySeat) return 'notYourTurn'
    if (isWaitingOnOpponent(state, this.mySeat)) return 'notNow'
    if (state.phase !== EnginePhase.BATTLE) return 'notBattlePhase'
    // A response window already returned above. These pauses stay unnamed:
    // naming the substep tells the attacker which kind of Set card was offered.
    if (
      state.pendingAttack ||
      state.chain.length > 0 ||
      (state.battleStep && state.battleStep !== 'BATTLE')
    ) {
      return 'notNow'
    }
    if (isOpeningTurn(state)) return 'noFirstTurnAttack'
    if (state.zones[this.mySeat].attackLockTurns > 0) return 'swordsOfRevealingLight'
    const lock = attackLockCode(state, this.mySeat, card)
    if (lock) return lock
    if (!card.faceUp) return 'attackerFaceDown'
    if (card.position !== CardPosition.ATTACK) return 'attackerNotAttackPos'
    if (card.attackedThisTurn) return 'alreadyAttacked'
    return null
  }

  /**
   * Tribute and Cocoon picks are client-only. End Phase (or any other phase
   * or turn) used to leave the prompt up, and the next Main Phase still
   * treated a monster click as the Tribute.
   */
  dropTributePick(): void {
    this.clearTributePick(true)
  }

  private noteTributeWindow(): void {
    const s = this.state
    if (!s) return
    this.tributePickAt = { phase: s.phase, turn: s.absoluteTurnCount }
  }

  private tributeWindowClosed(): boolean {
    const at = this.tributePickAt
    const s = this.state
    if (!at || !s) return true
    return s.phase !== at.phase || s.absoluteTurnCount !== at.turn
  }

  private tributePickStillValid(): boolean {
    const s = this.state
    if (!s) return false
    const hand = s.zones[this.mySeat].hand
    if (this.clickMode === 'selectCocoonHost') {
      const uid = this.pendingCocoonUid
      return !!uid && hand.some((card) => card.uid === uid)
    }
    const tribute = this.pendingTribute
    if (!tribute) return false
    if (!hand.some((card) => card.uid === tribute.cardUid)) return false
    if (tribute.gate) return true
    const zone = s.zones[this.mySeat]
    return !zone.hasNormalSummoned || zone.extraNormalSummons > 0
  }

  private syncTributePick(
    shown?: { phase: EnginePhase; absoluteTurnCount: number },
    redraw = true,
  ): void {
    if (this.clickMode !== 'selectTribute' && this.clickMode !== 'selectCocoonHost') return
    const s = this.state
    const at = this.tributePickAt
    const phase = shown?.phase ?? s?.phase
    const turn = shown?.absoluteTurnCount ?? s?.absoluteTurnCount
    if (!s || !at || phase !== at.phase || turn !== at.turn || !this.tributePickStillValid()) {
      this.clearTributePick(redraw)
    }
  }

  private clearTributePick(redraw: boolean): void {
    const picking =
      this.clickMode === 'selectTribute' || this.clickMode === 'selectCocoonHost'
    if (!picking && !this.pendingTribute && !this.pendingCocoonUid) return
    if (picking) this.clickMode = 'idle'
    this.pendingTribute = null
    this.tributeSelected = []
    this.tributePickAt = null
    this.pendingCocoonUid = null
    this.selectingCb?.(false)
    this.setPickSide(null)
    this.toast('')
    if (redraw && this.sys?.isActive()) this.renderAll()
  }

  /**
   * Attack-target mode is client-only. Drop it once the attacker can no longer
   * swing (phase, turn, Swords of Revealing Light) so the prompt cannot stick.
   */
  private syncAttackPick(): void {
    if (this.clickMode !== 'selectAttackTarget') return
    const uid = this.pendingAttacker
    const card = uid
      ? (this.state?.zones[this.mySeat].monsters.find((m) => m?.uid === uid) ?? null)
      : null
    if (card && !this.attackBlockCode(card)) return
    this.clearAttackPick()
  }

  /** Leaves attack-target mode and clears its glow and prompt. */
  private clearAttackPick(): void {
    if (this.clickMode !== 'selectAttackTarget' && !this.pendingAttacker) return
    this.pendingAttacker = null
    if (this.clickMode === 'selectAttackTarget') this.clickMode = 'idle'
    this.selectingCb?.(false)
    this.setPickSide(null)
    this.toast('')
    this.renderAll()
  }

  /**
   * Closes a declared attack. A rejected send keeps the engine toast; a
   * successful one clears the "pick a target" guide.
   */
  private finishAttackPick(accepted: boolean): void {
    const wasPicking = this.clickMode === 'selectAttackTarget'
    this.pendingAttacker = null
    if (this.clickMode === 'selectAttackTarget') this.clickMode = 'idle'
    this.selectingCb?.(false)
    this.setPickSide(null)
    if (accepted) this.toast('')
    else if (wasPicking) this.renderAll()
  }

  private monsterAtWorld(
    x: number,
    y: number,
    seat: Seat,
  ): CardInstance | null {
    if (!this.state) return null
    const row = this.monsterRow(seat)
    const pad = Math.max(CARD_W, CARD_H) / 2 + 10
    const slots = this.state.zones[seat].monsters
    for (let i = 0; i < slots.length; i++) {
      const m = slots[i]
      if (!m) continue
      const p = this.at(row, i)
      if (Math.abs(x - p.x) <= pad && Math.abs(y - p.y) <= pad) return m
    }
    return null
  }

  /**
   * Launches an attack with the given monster, resolving the target automatically:
   * - opponent has no monsters  -> direct attack
   * - this monster can attack Life Points and the opponent has monsters -> choose
   * - opponent has exactly one  -> attack that monster
   * - opponent has two or more  -> ask the player to click a target
   */
  private initiateAttack(attacker: CardInstance): void {
    if (!this.state) return
    const block = this.attackBlockCode(attacker)
    if (block) {
      // Do not open target mode, and do not redraw under the finger: a compact
      // swipe blocked by Swords falls through to this tap and used to stick
      // the "pick a monster" prompt with no way to dismiss it.
      if (this.clickMode === 'selectAttackTarget') this.clearAttackPick()
      this.toast('', 'warn', block)
      return
    }
    const oppSeat = (this.mySeat === 0 ? 1 : 0) as Seat
    const forced = magnetismTargets(this.state, oppSeat)
    const oppMonsters =
      forced.length > 0
        ? forced
        : this.state.zones[oppSeat].monsters.filter(
            (m: CardInstance | null): m is CardInstance => m !== null,
          )

    if (forced.length === 0 && oppMonsters.length === 0) {
      const ok = this.send({ type: ActionType.DIRECT_ATTACK, attackerUid: attacker.uid })
      this.finishAttackPick(ok)
      return
    }

    if (forced.length === 0 && cardAttacksDirectly(attacker.cardId)) {
      const opts: { label: string; onClick: () => void }[] = [
        {
          label: t('duel.menu.attackDirect'),
          onClick: () => {
            const ok = this.send({ type: ActionType.DIRECT_ATTACK, attackerUid: attacker.uid })
            this.finishAttackPick(ok)
          },
        },
      ]
      if (oppMonsters.length === 1) {
        const target = oppMonsters[0]
        opts.push({
          label: target.faceUp
            ? t('duel.menu.attackThis', { name: this.targetCardName(target.cardId) })
            : t('duel.menu.attackFaceDown'),
          onClick: () => {
            const ok = this.send({
              type: ActionType.DECLARE_ATTACK,
              attackerUid: attacker.uid,
              targetUid: target.uid,
            })
            this.finishAttackPick(ok)
          },
        })
      } else {
        opts.push({
          label: t('duel.menu.attackPick'),
          onClick: () => {
            this.pendingAttacker = attacker.uid
            this.clickMode = 'selectAttackTarget'
            this.selectingCb?.(true)
            this.setPickSide('opp')
            this.toast(t('duel.toast.selectAttack'))
            this.renderAll()
          },
        })
      }
      this.showMenu(opts)
      return
    }

    if (oppMonsters.length === 1) {
      const ok = this.send({
        type: ActionType.DECLARE_ATTACK,
        attackerUid: attacker.uid,
        targetUid: oppMonsters[0].uid,
      })
      this.finishAttackPick(ok)
      return
    }

    // Multiple targets: let the player pick one. Cancel is the phone Esc.
    this.pendingAttacker = attacker.uid
    this.clickMode = 'selectAttackTarget'
    this.selectingCb?.(true)
    this.setPickSide('opp')
    this.toast(t('duel.toast.selectAttack'))
    this.renderAll()
  }

  // ---------------------------------------------------------------------------
  // Menu / toast / overlay
  // ---------------------------------------------------------------------------

  private showMenu(options: { label: string; onClick: () => void }[]): void {
    this.clearMenu()
    if (options.length === 0) return
    // HTML menu sits above the phase-manager HUD (canvas menus cannot).
    if (this.menuCb) {
      this.menuActions = options.map((o) => o.onClick)
      this.muteBoardInput()
      this.menuCb({ labels: options.map((o) => o.label), ...this.menuScreenPos() })
      return
    }
    const menuW = 200
    const h = options.length * 34 + 16
    // Anchor the menu just above the clicked card, clamped to stay on-screen.
    const anchor = this.menuAnchor
    let x = anchor ? anchor.x : this.scale.width / 2
    let y = anchor ? anchor.y - CARD_H / 2 - h / 2 - 6 : this.scale.height / 2
    x = Phaser.Math.Clamp(x, menuW / 2 + 8, this.scale.width - menuW / 2 - 8)
    y = Phaser.Math.Clamp(y, h / 2 + 8, this.scale.height - h / 2 - 24)
    const bg = this.add.rectangle(x, y, menuW, h, 0x111827, 0.95).setStrokeStyle(2, 0x88aaff)
    this.menuLayer.add(bg)
    options.forEach((opt, i) => {
      const oy = y - h / 2 + 24 + i * 34
      const ob = this.add.rectangle(x, oy, 180, 28, 0x2563eb).setStrokeStyle(1, 0x93c5fd)
      const ot = this.add.text(x, oy, opt.label, { fontSize: '12px', color: '#ffffff' }).setOrigin(0.5)
      ob.setInteractive({ useHandCursor: true })
      ob.on('pointerdown', () => {
        this.clearMenu()
        opt.onClick()
      })
      this.menuLayer.add([ob, ot])
    })
    // Cancel area.
    const cancel = this.add.text(x, y + h / 2 + 12, t('duel.picker.cancel'), { fontSize: '11px', color: '#fca5a5' }).setOrigin(0.5)
    cancel.setInteractive({ useHandCursor: true })
    cancel.on('pointerdown', () => this.clearMenu())
    this.menuLayer.add(cancel)
  }

  /**
   * Picks an item from the Vue field/hand menu. `index` -1 cancels.
   */
  pickMenuAction(index: number): void {
    const fn = index >= 0 ? this.menuActions[index] : undefined
    this.menuActions = []
    this.menuLayer.removeAll(true)
    this.menuCb?.(null)
    this.releaseBoardInputSoon()
    fn?.()
  }

  /**
   * True while the HTML action menu (or its leftover click) should not reach
   * field/hand sprites. The menu often overlaps the other zone in the column.
   */
  private boardPointerBlocked(): boolean {
    return this.modalInputBlocked || this.time.now < this.ignoreBoardPointerUntil
  }

  /** A legal spell or Flip Effect target still accepts the tap that closed the menu. */
  private pointerBlockedFor(uid: string): boolean {
    return this.boardPointerBlocked() && !this.acceptsMutedTap(uid)
  }

  private acceptsMutedTap(uid: string): boolean {
    return this.legalEngineFieldTarget(uid) || this.myFlipFieldTargets().includes(uid)
  }

  /**
   * HTML dialogs (special summon picker, position, graveyard) cover the board,
   * but Phaser also listens for touches on window and would still open a pile.
   */
  setModalInputBlocked(blocked: boolean): void {
    if (this.modalInputBlocked === blocked) return
    this.modalInputBlocked = blocked
    if (blocked) this.muteBoardInput()
    else if (this.menuActions.length === 0) this.releaseBoardInputSoon()
  }

  private canEnableBoardInput(): boolean {
    return !this.modalInputBlocked && this.menuActions.length === 0
  }

  /**
   * Mute Phaser input while the Vue menu is open so a click on Activar cannot
   * also pick the monster sitting in the same column.
   */
  private muteBoardInput(): void {
    const gen = ++this.boardInputLockGen
    this.inputMutedAt = performance.now()
    this.ignoreBoardPointerUntil = this.time.now + 60_000
    this.input.enabled = false
    // If the HTML menu never closes, do not leave the board deaf forever.
    this.time.delayedCall(60_000, () => {
      if (!this.sys?.isActive()) return
      if (gen !== this.boardInputLockGen) return
      if (!this.canEnableBoardInput()) return
      this.ignoreBoardPointerUntil = 0
      this.input.enabled = true
      this.reclaimStuckTouches()
    })
  }

  /**
   * The 300 ms mute stops the Activar click from also hitting the card under
   * the menu. A target window opens only after that click is gone, so the
   * next tap on the highlighted card has to count.
   */
  private liftShortBoardMute(): void {
    if (this.modalInputBlocked || this.menuActions.length > 0) return
    this.boardInputLockGen += 1
    this.ignoreBoardPointerUntil = 0
    this.input.enabled = true
    this.reclaimStuckTouches()
  }

  /**
   * Keep Phaser deaf until the leftover click from closing the HTML menu dies.
   */
  private releaseBoardInputSoon(ms = 300): void {
    const gen = ++this.boardInputLockGen
    this.inputMutedAt = performance.now()
    this.ignoreBoardPointerUntil = this.time.now + ms
    this.input.enabled = false
    const tryEnable = (left: number): void => {
      if (!this.sys?.isActive()) return
      if (gen !== this.boardInputLockGen) return
      if (!this.canEnableBoardInput()) {
        if (left <= 0) return
        this.time.delayedCall(50, () => tryEnable(left - 1))
        return
      }
      this.ignoreBoardPointerUntil = 0
      this.input.enabled = true
      this.reclaimStuckTouches()
    }
    this.time.delayedCall(ms, () => tryEnable(40))
  }

  /**
   * Safari drops touchend when the canvas loses pointer-events mid-gesture.
   * Phaser keeps that one touch pointer `active` and then ignores every later
   * tap. Free any pointer whose finger is already gone.
   */
  private reclaimStuckTouches(event?: TouchEvent): void {
    const pointers = this.input?.manager?.pointers
    if (!pointers) return
    if (!event) {
      for (const pointer of pointers) {
        if (pointer.id === 0) {
          if (pointer.isDown && pointer.buttons === 0) pointer.reset()
          continue
        }
        if (!pointer.active) continue
        pointer.reset()
      }
      return
    }
    const live = new Set<number>()
    for (let i = 0; i < event.touches.length; i++) {
      live.add(event.touches[i]!.identifier)
    }
    let free = false
    for (const pointer of pointers) {
      if (pointer.id === 0) continue
      if (pointer.active && !live.has(pointer.identifier)) pointer.reset()
      if (!pointer.active) free = true
    }
    if (!free) {
      for (const pointer of pointers) {
        if (pointer.id === 0) continue
        pointer.reset()
      }
    }
  }

  /**
   * Maps the last clicked card from game space to viewport pixels so the Vue
   * menu can sit against that card (Phaser FIT letterboxes the canvas).
   * Opens to the side so the buttons never cover the other zone in the column.
   */
  private menuScreenPos(): {
    x: number
    y: number
    below: boolean
    side: 'left' | 'right'
  } {
    const canvas = this.game.canvas
    const rect = canvas.getBoundingClientRect()
    const sx = rect.width / this.scale.width
    const sy = rect.height / this.scale.height
    const ax = this.menuAnchor?.x ?? this.scale.width / 2
    const ay = this.menuAnchor?.y ?? this.scale.height / 2
    const view = this.viewFromWorld(ax, ay)
    const z = this.cameras.main.zoom
    const gap = 10
    const menuW = 220
    const pad = 12
    const half = (CARD_W / 2) * (this.menuAnchor?.scale ?? 1) * z
    const spaceRightPx = (this.scale.width - view.x - half) * sx
    const side: 'left' | 'right' = spaceRightPx >= menuW + gap ? 'right' : 'left'
    const xGame = side === 'right' ? view.x + half + gap : view.x - half - gap
    let x = rect.left + xGame * sx
    let y = rect.top + view.y * sy
    if (side === 'right') {
      x = Math.min(Math.max(x, pad), window.innerWidth - pad - 80)
    } else {
      x = Math.min(Math.max(x, pad + 80), window.innerWidth - pad)
    }
    y = Math.min(Math.max(y, pad + 40), window.innerHeight - pad - 40)
    return { x, y, below: false, side }
  }

  private clearMenu(): void {
    this.menuLayer.removeAll(true)
    if (this.menuActions.length === 0) return
    this.menuActions = []
    this.menuCb?.(null)
    this.releaseBoardInputSoon()
  }

  /**
   * Answers the Vue card-list picker. `uids` null is cancel.
   */
  resolvePicker(uids: string[] | null): void {
    const finish = this.pickerFinish
    this.pickerFinish = null
    this.chainOrderPickerLive = false
    this.pickerCb?.(null)
    finish?.(uids)
  }

  /** Colored band so a fallback list still says which graveyard a card is from. */
  private paintPickerOwner(
    cont: Phaser.GameObjects.Container,
    owner: 'me' | 'opp' | undefined,
    cw: number,
    ch: number,
  ): void {
    if (!owner) return
    const mine = owner === 'me'
    cont.add(this.add.rectangle(0, -ch / 2 + 11, cw, 20, mine ? 0x0369a1 : 0xb91c1c, 0.95))
    cont.add(
      this.add
        .text(0, -ch / 2 + 11, mine ? t('duel.picker.yours') : t('duel.picker.opp'), {
          fontSize: '11px',
          color: '#f8fafc',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    )
  }

  /**
   * Full-screen overlay that lets the player pick one card from a list (e.g. a
   * graveyard monster for Monster Reborn, or a fusion for Polymerization).
   * Rendered in Vue so it sits above the phase-manager HUD.
   */
  private showCardPicker(
    title: string,
    items: CardPickerListItem[],
    onPick: (uid: string) => void,
    min = 1,
    viewOnly = false,
    source?: { cardId: string; foil?: string },
    max = 1,
    onMany?: (uids: string[]) => void,
    hint?: string,
  ): void {
    this.clearMenu()
    if (items.length === 0) return
    if (this.pickerCb) {
      this.pickerFinish = (uids) => {
        if (viewOnly) {
          this.reopenPromptAfterPeek()
          return
        }
        if (max > 1 && onMany) {
          const chosen = uids ?? []
          if (chosen.length >= min && chosen.length <= max) {
            onMany(chosen)
            return
          }
          // The order is mandatory. Cancel keeps the order already on screen.
          if (uids == null && this.state?.pendingChainOrder?.chooser === this.mySeat) {
            onMany(items.map((item) => item.uid))
            return
          }
          if (this.state?.pendingChainOrder?.chooser === this.mySeat) {
            this.toast(t('duel.toast.mustChooseTarget'), 'warn')
            this.chainOrderRequestId = null
            this.syncChainOrderPrompt()
            return
          }
          if (min === 0 && this.state?.pendingTargets?.chooser === this.mySeat) {
            this.submitEngineTargets([])
            return
          }
          if (this.state?.pendingTargets?.chooser === this.mySeat) {
            this.toast(t('duel.toast.mustChooseTarget'), 'warn')
            this.engineTargetRequestId = null
            this.syncEngineTargetPrompt()
          }
          return
        }
        if (uids?.[0]) onPick(uids[0])
        else if (min === 0 && this.state?.pendingTargets?.chooser === this.mySeat) {
          this.submitEngineTargets([])
        }
        else if (this.state?.pendingTargets?.chooser === this.mySeat) {
          this.toast(t('duel.toast.mustChooseTarget'), 'warn')
          this.engineTargetRequestId = null
          this.syncEngineTargetPrompt()
        }
        else if (this.state?.pendingFlipEffect?.chooser === this.mySeat) {
          this.toast(t('duel.toast.mustChooseTarget'), 'warn')
          this.flipListRequestId = null
          this.syncFlipListPrompt()
        }
      }
      this.pickerCb({ title, items, min, max, viewOnly, source, hint })
      return
    }
    if (max > 1 && onMany) {
      this.showMultiCardPicker(title, items, min, max, onMany, source)
      return
    }
    const W = this.scale.width
    const H = this.scale.height
    const cw = CARD_W * 1.3
    const ch = CARD_H * 1.3
    const gap = 14

    const backdrop = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.72).setInteractive()
    this.menuLayer.add(backdrop)

    const perRow = Math.max(1, Math.min(items.length, Math.floor((W - 80) / (cw + gap))))
    const rows = Math.ceil(items.length / perRow)
    const gridH = rows * ch + (rows - 1) * gap
    const startY = H / 2 - gridH / 2 + ch / 2

    this.menuLayer.add(
      this.add
        .text(W / 2, startY - ch / 2 - 26, title, {
          fontSize: '18px',
          color: '#ffffff',
          backgroundColor: '#000000aa',
          padding: { x: 10, y: 6 },
        })
        .setOrigin(0.5),
    )

    items.forEach((item, i) => {
      const r = Math.floor(i / perRow)
      const c = i % perRow
      const rowCount = Math.min(perRow, items.length - r * perRow)
      const rowW = rowCount * cw + (rowCount - 1) * gap
      const x = W / 2 - rowW / 2 + cw / 2 + c * (cw + gap)
      const y = startY + r * (ch + gap)

      const cont = this.add.container(x, y)
      const key = textureKey(item.cardId)
      if (this.textures.exists(key)) {
        const face = this.add.image(0, 0, key).setDisplaySize(cw, ch)
        cont.add(face)
        applyCardRarityFx(this, cont, asCardFoil(item.foil), cw, ch, face, item.cardId)
      } else {
        const data = CARD_INDEX.get(item.cardId)
        cont.add(this.add.rectangle(0, 0, cw, ch, 0x223344).setStrokeStyle(1, 0x88aacc))
        cont.add(
          this.add
            .text(0, -6, data?.name ?? item.cardId, {
              fontSize: '10px',
              color: '#ffffff',
              wordWrap: { width: cw - 8 },
              align: 'center',
            })
            .setOrigin(0.5),
        )
      }
      if (item.note) {
        cont.add(
          this.add
            .text(0, ch / 2 - 9, item.note, { fontSize: '10px', color: '#ffd54f', backgroundColor: '#000000aa' })
            .setOrigin(0.5),
        )
      }
      this.paintPickerOwner(cont, item.owner, cw, ch)
      const hit = this.add.rectangle(0, 0, cw, ch, 0xffffff, 0).setStrokeStyle(2, 0x66ccff)
      cont.add(hit)
      hit.setInteractive({ useHandCursor: true })
      hit.on('pointerover', () => hit.setStrokeStyle(3, 0xffd54f))
      hit.on('pointerout', () => hit.setStrokeStyle(2, 0x66ccff))
      hit.on('pointerdown', () => {
        this.clearMenu()
        onPick(item.uid)
      })
      this.menuLayer.add(cont)
    })

    const cancel = this.add
      .text(W / 2, startY + gridH - ch / 2 + 22, t('duel.picker.cancel'), { fontSize: '13px', color: '#fca5a5' })
      .setOrigin(0.5)
    cancel.setInteractive({ useHandCursor: true })
    cancel.on('pointerdown', () => {
      this.clearMenu()
      if (viewOnly) this.reopenPromptAfterPeek()
    })
    this.menuLayer.add(cancel)
  }

  /**
   * Overlay to pick `min`–`max` cards (Gravedigger Ghoul: 1 or 2). Confirm stays
   * disabled until at least `min` are selected. Cancel leaves the spell unplayed.
   */
  private showMultiCardPicker(
    title: string,
    items: CardPickerListItem[],
    min: number,
    max: number,
    onConfirm: (uids: string[]) => void,
    source?: { cardId: string; foil?: string },
  ): void {
    this.clearMenu()
    if (items.length === 0) return
    if (this.pickerCb) {
      this.pickerFinish = (uids) => {
        if (uids && uids.length >= min) onConfirm(uids)
        else if (this.state?.pendingTargets?.chooser === this.mySeat) {
          this.toast(t('duel.toast.mustChooseTarget'), 'warn')
          this.engineTargetRequestId = null
          this.syncEngineTargetPrompt()
        }
      }
      this.pickerCb({ title, items, min, max, source })
      return
    }
    const W = this.scale.width
    const H = this.scale.height
    const cw = CARD_W * 1.3
    const ch = CARD_H * 1.3
    const gap = 14
    const selected: string[] = []

    const backdrop = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.72).setInteractive()
    this.menuLayer.add(backdrop)

    const perRow = Math.max(1, Math.min(items.length, Math.floor((W - 80) / (cw + gap))))
    const rows = Math.ceil(items.length / perRow)
    const gridH = rows * ch + (rows - 1) * gap
    const startY = H / 2 - gridH / 2 + ch / 2

    this.menuLayer.add(
      this.add
        .text(W / 2, startY - ch / 2 - 26, title, {
          fontSize: '18px',
          color: '#ffffff',
          backgroundColor: '#000000aa',
          padding: { x: 10, y: 6 },
        })
        .setOrigin(0.5),
    )

    const strokes = new Map<string, Phaser.GameObjects.Rectangle>()
    const wraps = new Map<string, Phaser.GameObjects.Container>()
    const badges = new Map<string, Phaser.GameObjects.Text>()
    const paint = (): void => {
      for (const item of items) {
        const on = selected.includes(item.uid)
        const wrap = wraps.get(item.uid)
        const stroke = strokes.get(item.uid)
        const badge = badges.get(item.uid)
        wrap?.setScale(on ? 1.1 : 1)
        wrap?.setAlpha(selected.length > 0 && !on ? 0.42 : 1)
        stroke?.setStrokeStyle(on ? 5 : 2, on ? 0xfacc15 : 0x66ccff)
        if (badge) {
          badge.setVisible(on)
          if (on) badge.setText(String(selected.indexOf(item.uid) + 1))
        }
      }
      const orderAll = min === max && items.length === max && min > 1
      confirm.setAlpha(
        selected.length >= min || (orderAll && selected.length === min - 1) ? 1 : 0.35,
      )
    }

    items.forEach((item, i) => {
      const r = Math.floor(i / perRow)
      const c = i % perRow
      const rowCount = Math.min(perRow, items.length - r * perRow)
      const rowW = rowCount * cw + (rowCount - 1) * gap
      const x = W / 2 - rowW / 2 + cw / 2 + c * (cw + gap)
      const y = startY + r * (ch + gap)

      const cont = this.add.container(x, y)
      const key = textureKey(item.cardId)
      if (this.textures.exists(key)) {
        const face = this.add.image(0, 0, key).setDisplaySize(cw, ch)
        cont.add(face)
        applyCardRarityFx(this, cont, asCardFoil(item.foil), cw, ch, face, item.cardId)
      } else {
        const data = CARD_INDEX.get(item.cardId)
        cont.add(this.add.rectangle(0, 0, cw, ch, 0x223344).setStrokeStyle(1, 0x88aacc))
        cont.add(
          this.add
            .text(0, -6, data?.name ?? item.cardId, {
              fontSize: '10px',
              color: '#ffffff',
              wordWrap: { width: cw - 8 },
              align: 'center',
            })
            .setOrigin(0.5),
        )
      }
      if (item.note) {
        cont.add(
          this.add
            .text(0, ch / 2 - 9, item.note, { fontSize: '10px', color: '#ffd54f', backgroundColor: '#000000aa' })
            .setOrigin(0.5),
        )
      }
      this.paintPickerOwner(cont, item.owner, cw, ch)
      const hit = this.add.rectangle(0, 0, cw, ch, 0xffffff, 0).setStrokeStyle(2, 0x66ccff)
      cont.add(hit)
      strokes.set(item.uid, hit)
      wraps.set(item.uid, cont)
      const badge = this.add
        .text(cw / 2 - 6, -ch / 2 + 6, '', {
          fontSize: '16px',
          color: '#facc15',
          backgroundColor: '#14532d',
          fontStyle: 'bold',
          padding: { x: 6, y: 2 },
        })
        .setOrigin(0.5)
        .setVisible(false)
      cont.add(badge)
      badges.set(item.uid, badge)
      hit.setInteractive({ useHandCursor: true })
      hit.on('pointerdown', () => {
        const at = selected.indexOf(item.uid)
        if (at !== -1) selected.splice(at, 1)
        else if (selected.length < max) {
          selected.push(item.uid)
          if (min === max && items.length === max && min > 1) {
            const left = items.filter((entry) => !selected.includes(entry.uid))
            if (left.length === 1 && left[0]) selected.push(left[0].uid)
          }
        }
        paint()
      })
      this.menuLayer.add(cont)
    })

    const confirm = this.add
      .text(W / 2 - 50, startY + gridH - ch / 2 + 28, t('duel.picker.confirm'), {
        fontSize: '15px',
        color: '#86efac',
        backgroundColor: '#14532d',
        padding: { x: 10, y: 5 },
      })
      .setOrigin(0.5)
    confirm.setInteractive({ useHandCursor: true })
    confirm.on('pointerdown', () => {
      const orderAll = min === max && items.length === max && min > 1
      if (orderAll) {
        for (const entry of items) {
          if (!selected.includes(entry.uid)) selected.push(entry.uid)
        }
      }
      if (selected.length < min) return
      this.clearMenu()
      onConfirm([...selected])
    })
    this.menuLayer.add(confirm)

    const cancel = this.add
      .text(W / 2 + 60, startY + gridH - ch / 2 + 28, t('duel.picker.cancel'), { fontSize: '13px', color: '#fca5a5' })
      .setOrigin(0.5)
    cancel.setInteractive({ useHandCursor: true })
    cancel.on('pointerdown', () => this.clearMenu())
    this.menuLayer.add(cancel)
    paint()
  }

  private toast(
    message: string,
    tone: DuelToastTone = 'guide',
    code?: string,
    vars?: Record<string, string | number>,
  ): void {
    const waiting = this.state ? isWaitingOnOpponent(this.state, this.mySeat) : false
    const text = localizeEngineError(message, code, vars, waiting)
    if (this.toastCb) {
      this.toastCb(text, tone)
      return
    }
    this.toastText.setText(text)
    this.toastText.setColor(TOAST_TONE_COLOR[tone])
    if (!text || tone === 'guide') return
    this.time.delayedCall(2500, () => {
      if (this.toastText.text === text) this.toastText.setText('')
    })
  }

  private foilOf(cardUid: string): CardFoilId {
    const inst =
      this.findCardByUid(cardUid) ??
      this.findCardInState(this.pendingCommit, cardUid)
    return asCardFoil(inst?.foil)
  }

  /** Printed attribute of a monster still on the pre-commit snapshot. */
  private attributeOf(cardUid: string): string | undefined {
    const inst = this.findCardByUid(cardUid)
    if (!inst?.cardId) return undefined
    return CARD_INDEX.get(inst.cardId)?.attribute
  }

  /**
   * Add/subtract ATK/DEF currently applying to this card. Reverse Trap flips
   * the sign so the HUD matches battle damage.
   */
  private statBonus(card: CardInstance): { atk: number; def: number } {
    return effectiveStatBonus(card, Boolean(this.state?.reverseStatMods))
  }

  /**
   * Printed ATK/DEF, or the other one while Shield & Sword is on this monster.
   */
  private battleBases(card: CardInstance): { atk: number; def: number } {
    const data = CARD_INDEX.get(card.cardId)
    const atk = data?.atk ?? 0
    const def = data?.def ?? 0
    return card.baseSwapped ? { atk: def, def: atk } : { atk, def }
  }

  /** Current ATK on the field (printed + modifiers), or 0 if unknown. */
  private fieldAtk(cardUid: string): number {
    const inst = this.findCardByUid(cardUid)
    if (!inst?.cardId) return 0
    return Math.max(0, this.battleBases(inst).atk + this.statBonus(inst).atk)
  }

  /**
   * Screen shake for a battle hit. Scales with the attacker's ATK so a 300
   * swing is a tap and Blue-Eyes (3000) rattles the board.
   * Compact zoom + the 3D overlay sit still during Phaser shake, so intensity
   * is higher there; Vue also rattles the board DOM (see screenShakeCb).
   */
  private attackShake(atk: number): { duration: number; intensity: number } {
    if (this.animationsReduced()) return { duration: 0, intensity: 0 }
    const t = Math.min(1, Math.max(0, atk) / 3000)
    const compact = isDuelCompactNow() ? 1.4 : 1
    return {
      duration: this.ms(220 + t * 260),
      intensity: (0.014 + t * 0.032) * compact,
    }
  }

  /**
   * Visible world rectangle Phaser is painting (after pan/zoom). Live source
   * for overlay 0–1 so compact card size and focus bands stay aligned.
   */
  private cameraViewRect(): {
    x: number
    y: number
    width: number
    height: number
  } {
    const frame = this.game?.loop?.frame ?? -1
    if (frame >= 0 && frame === this.cameraViewFrame) return this.cameraViewCached
    const cam = this.cameras?.main
    if (!cam || !this.sys?.isActive()) {
      return {
        x: 0,
        y: 0,
        width: this.scale.width,
        height: this.scale.height,
      }
    }
    cam.preRender()
    const v = cam.worldView
    this.cameraViewCached.x = v.x
    this.cameraViewCached.y = v.y
    this.cameraViewCached.width = Math.max(1, v.width)
    this.cameraViewCached.height = Math.max(1, v.height)
    this.cameraViewFrame = frame
    return this.cameraViewCached
  }

  /**
   * Top of our hand as a 0–1 fraction of the live camera view. Null when that
   * row is off-screen (compact focus on the other half).
   */
  private publishHandWaitAnchor(): void {
    if (!this.handWaitCb) return
    this.cameraViewFrame = -1
    const top = this.rowY(5) - (CARD_H * HAND_SCALE) / 2
    const view = this.cameraViewRect()
    const ratio = (top - view.y) / view.height
    if (!Number.isFinite(ratio) || ratio < 0.02 || ratio > 1.02) {
      this.handWaitCb(null)
      return
    }
    this.handWaitCb(ratio)
  }

  /**
   * World point → Phaser canvas pixels (game resolution, 1024×768).
   * Same space as `menuScreenPos` (zoom/pan only; CSS letterbox is in toPx).
   */
  private viewFromWorld(x: number, y: number): { x: number; y: number } {
    const n = this.overlayNorm(x, y)
    return { x: n.x * this.scale.width, y: n.y * this.scale.height }
  }

  /**
   * 0–1 of the current camera view. DuelVfx.toPx maps this onto the
   * FIT-letterboxed Phaser canvas inside the overlay.
   */
  private overlayNorm(x: number, y: number): { x: number; y: number } {
    const v = this.cameraViewRect()
    return { x: (x - v.x) / v.width, y: (y - v.y) / v.height }
  }

  /** Overlay-normalized size from world pixels vs the live camera view. */
  private overlaySize(w: number, h: number): { w: number; h: number } {
    const v = this.cameraViewRect()
    return { w: w / v.width, h: h / v.height }
  }

  /**
   * Three.js overlay. Foil stings on every house print.
   */
  private playVfx(
    kind: DuelVfxKind,
    x: number,
    y: number,
    extra?: {
      x2?: number
      y2?: number
      foil?: CardFoilId
      attribute?: string
      scale?: number
    },
  ): void {
    const rarity = extra?.foil
    const at = this.overlayNorm(x, y)
    const to =
      extra?.x2 === undefined || extra.y2 === undefined
        ? undefined
        : this.overlayNorm(extra.x2, extra.y2)
    getDuelVfx()?.play(kind, {
      x: at.x,
      y: at.y,
      x2: to?.x,
      y2: to?.y,
      rarity,
      attribute: extra?.attribute,
      scale: (extra?.scale ?? 1) * this.cameras.main.zoom,
    })
    const foil = kind === 'foilField' || kind === 'foilActivate'
    if (!foil || !rarity || rarity === 'common') return
    if (
      !this.animationsReduced() &&
      (        rarity === 'ultra' ||
        rarity === 'ghost' ||
        rarity === 'helios' ||
        rarity === 'nightglass' ||
        rarity === 'stormwake')
    ) {
      this.cameras.main.shake(
        this.ms(140),
        rarity === 'stormwake' ? 0.007 : 0.005,
      )
    }
    // Activation already plays spell/trap SFX. Summon holograms may sting.
    if (kind === 'foilActivate') return
    if (rarity === 'stormwake' || rarity === 'nightglass' || rarity === 'helios') {
      DuelSfx.play('packSecret')
    } else if (rarity === 'ultra' || rarity === 'ghost') DuelSfx.play('packUltra')
    else if (rarity === 'super') DuelSfx.play('packSuper')
    else DuelSfx.play('packRare')
  }

  /**
   * Sends a 3D card mesh across the Three.js overlay. Interpolates in world
   * space so the mesh stays on the mat when the compact camera pans/zooms.
   */
  private flyOverlay(
    from: { x: number; y: number; scale?: number },
    to: { x: number; y: number; scale?: number },
    opts: Omit<DuelVfxFlyCard, 'from' | 'to' | 'cardW' | 'cardH' | 'worldTrack'> & {
      mine: boolean
      cardUid?: string
    },
  ): void {
    if (this.motionMinimal()) return
    const { mine, cardUid, ...rest } = opts
    const foil = cardUid ? this.foilOf(cardUid) : rest.foil
    const toS = to.scale ?? 1
    const fromS = from.scale ?? toS
    const size = this.overlaySize(CARD_W * toS, CARD_H * toS)
    const arcWorld = rest.arc ? rest.arc * this.scale.width : 0
    getDuelVfx()?.flyCard({
      ...rest,
      foil,
      lift: this.animationsReduced() ? 0 : rest.lift,
      ghosts: this.animationsReduced() ? false : rest.ghosts,
      backUrl: this.sleeveBackUrl(mine),
      from: this.overlayNorm(from.x, from.y),
      to: this.overlayNorm(to.x, to.y),
      cardW: size.w,
      cardH: size.h,
      arc: rest.arc,
      startScale: (rest.startScale ?? 1) * (fromS / toS),
      worldTrack: {
        from: { x: from.x, y: from.y },
        to: { x: to.x, y: to.y },
        peak: this.worldArcPeak(from, to, arcWorld),
        project: (x, y) =>
          this.sys?.isActive() ? this.overlayNorm(x, y) : { x: 0.5, y: 0.5 },
        cardSize: () =>
          this.sys?.isActive()
            ? this.overlaySize(CARD_W * toS, CARD_H * toS)
            : { w: size.w, h: size.h },
      },
    })
  }

  /** Bezier peak toward the board center, in world pixels. */
  private worldArcPeak(
    from: { x: number; y: number },
    to: { x: number; y: number },
    arcWorld: number,
  ): { x: number; y: number } {
    const midX = (from.x + to.x) / 2
    const midY = (from.y + to.y) / 2
    if (arcWorld <= 0) return { x: midX, y: midY }
    const tw = this.scale.width / 2 - midX
    const th = this.scale.height / 2 - midY
    const dist = Math.hypot(tw, th) || 1
    return {
      x: midX + (tw / dist) * arcWorld,
      y: midY + (th / dist) * arcWorld,
    }
  }

  private overlayShatter(opts: {
    x: number
    y: number
    cardId?: string | null
    faceUp?: boolean
    angle?: number
    scale?: number
    mine?: boolean
    foil?: CardFoilId
  }): boolean {
    if (this.animationsReduced()) return false
    const vfx = getDuelVfx()
    if (!vfx) return false
    const s = opts.scale ?? 1
    const at = this.overlayNorm(opts.x, opts.y)
    const size = this.overlaySize(CARD_W * s, CARD_H * s)
    vfx.shatterCard({
      x: at.x,
      y: at.y,
      cardW: size.w,
      cardH: size.h,
      cardId: opts.cardId,
      faceUp: opts.faceUp,
      angle: opts.angle,
      foil: opts.foil,
      backUrl: this.sleeveBackUrl(opts.mine !== false),
    })
    return true
  }

  private overlaySoul(
    from: { x: number; y: number },
    to: { x: number; y: number },
    duration: number,
  ): boolean {
    if (this.animationsReduced()) return false
    const vfx = getDuelVfx()
    if (!vfx) return false
    vfx.soulTrail({
      from: this.overlayNorm(from.x, from.y),
      to: this.overlayNorm(to.x, to.y),
      duration,
      arc: (48 / this.scale.width) * this.cameras.main.zoom,
      worldTrack: {
        from: { x: from.x, y: from.y },
        to: { x: to.x, y: to.y },
        peak: this.worldArcPeak(from, to, 48),
        project: (x, y) =>
          this.sys?.isActive() ? this.overlayNorm(x, y) : { x: 0.5, y: 0.5 },
      },
    })
    return true
  }

  /**
   * Hide the Phaser sprite and play the shatter + covering flash. The soul
   * trail to the GY is scheduled separately so resolving spells still fly
   * as cards. This is the only destroy SFX: tributes, discards, and a card
   * going to the GY after its effect resolves must not call this.
   */
  private playDestroyShatter(
    cardUid: string,
    pos?: { x: number; y: number; defense?: boolean } | SlotLand | null,
  ): void {
    this.dropSlotStandIn(cardUid)
    this.leaveFieldForActivation(cardUid)
    this.releaseActivationMesh(cardUid)
    DuelSfx.play('destroy')
    if (!pos) return
    const card = this.findCardByUid(cardUid)
    const cardId = (pos as SlotLand).cardId || card?.cardId || ''
    const defense = 'defense' in pos ? !!pos.defense : false
    const faceUp =
      !!card?.faceUp || this.revealedUids.has(cardUid)
    const shattered = this.overlayShatter({
      x: pos.x,
      y: pos.y,
      cardId,
      faceUp,
      angle: defense ? 90 : 0,
      scale: 'scale' in pos ? pos.scale : 1,
      mine: card ? card.owner === this.mySeat : true,
      foil: this.foilOf(cardUid),
    })
    if (!shattered) this.flashWhite(pos.x, pos.y, 'scale' in pos ? pos.scale : 1)
    this.playVfx('destroy', pos.x, pos.y)
    if (!this.animationsReduced()) {
      this.cameras.main.shake(this.visualMs(160), 0.006)
    }
  }

  private coverSlot(
    x: number,
    y: number,
    angle: number,
    ms: number,
    scale = 1,
  ): void {
    if (this.motionMinimal()) return
    const mask = this.maskSlot(x, y, angle, scale)
    this.time.delayedCall(ms, () => mask.forEach((m) => m.destroy()))
  }

  private ensureActivationHold(
    uid: string,
    uncover: () => void,
  ): { released: boolean } {
    const existing = this.activationHold.get(uid)
    if (existing) {
      uncover()
      return existing.gate
    }
    const gate = { released: false }
    this.activationHold.set(uid, { gate, uncover })
    return gate
  }

  /** Drops the parked 3D mesh so a graveyard flight can start from that slot. */
  private releaseActivationMesh(uid: string): void {
    const held = this.activationHold.get(uid)
    if (held) held.gate.released = true
    const mesh = this.meshHold.get(uid)
    if (mesh) mesh.released = true
  }

  /**
   * Swap the parked activation mesh for a field sprite at the zone size.
   * `renderAll` wipes the board layer, so the record is what gets painted again.
   */
  private parkSlotStandIn(
    uid: string,
    pose: SlotPose,
    faceUp: boolean,
    defense: boolean,
  ): void {
    const prev = this.slotStandIns.get(uid)
    if (prev?.sprite?.active) prev.sprite.destroy()
    this.slotStandIns.set(uid, {
      pose,
      faceUp,
      defense,
      sprite: null,
    })
    this.paintSlotStandIn(uid)
  }

  private dropSlotStandIn(uid: string): void {
    const entry = this.slotStandIns.get(uid)
    if (!entry) return
    if (entry.sprite?.active) entry.sprite.destroy()
    this.slotStandIns.delete(uid)
  }

  private paintSlotStandIns(): void {
    for (const uid of this.slotStandIns.keys()) this.paintSlotStandIn(uid)
  }

  private paintSlotStandIn(uid: string): void {
    const spec = this.slotStandIns.get(uid)
    if (!spec || !this.sys?.isActive()) return
    if (spec.sprite?.active) spec.sprite.destroy()
    spec.sprite = null
    if (this.chainHidden.has(uid) || this.fieldSprites.has(uid)) return
    const card =
      this.findCardByUid(uid) ?? this.findCardInState(this.pendingCommit, uid)
    if (!card) return
    const shown = { ...card, faceUp: spec.faceUp }
    const sprite = this.makeCardSprite(shown, this.sleeveMine(shown), true)
    sprite.setPosition(spec.pose.x, spec.pose.y)
    if (spec.pose.scale !== 1) sprite.setScale(spec.pose.scale)
    if (spec.defense) {
      for (const child of sprite.list) {
        if ('setAngle' in child && typeof child.setAngle === 'function') {
          child.setAngle(90)
        }
      }
    }
    this.boardLayer.add(sprite)
    spec.sprite = sprite
  }

  private ensureMeshHold(uid: string): { released: boolean } {
    const existing = this.meshHold.get(uid)
    if (existing) return existing
    const gate = { released: false }
    this.meshHold.set(uid, gate)
    return gate
  }

  /**
   * Phaser hide-flags for in-flight cards. Cleared on commit *before* renderAll
   * so the resolved sprite can exist under a still-parked 3D mesh.
   */
  private clearDepartureTracking(): void {
    this.activationLand.clear()
    this.fieldLand.clear()
    this.departingHandUids.clear()
    this.keptDiscardUids.clear()
    this.departingFieldUids.clear()
    this.departingPileUids.clear()
    this.landedBanishedUids.clear()
    this.landedGraveUids.clear()
    this.revealedUids.clear()
    this.previewFace.clear()
    for (const entry of this.slotStandIns.values()) {
      if (entry.sprite?.active) entry.sprite.destroy()
    }
    this.slotStandIns.clear()
  }

  /** Drops parked 3D overlays. Call after the Phaser board already shows the slot. */
  private releaseParkedMeshes(): void {
    for (const held of this.activationHold.values()) {
      held.gate.released = true
      held.uncover()
    }
    this.activationHold.clear()
    for (const gate of this.meshHold.values()) gate.released = true
    this.meshHold.clear()
  }

  private releaseAllActivationHolds(): void {
    this.clearDepartureTracking()
    this.releaseParkedMeshes()
  }

  /**
   * Hand cards discarded because battle damage already happened (Robbin'
   * Goblin, White Magical Hat). They stay in the fan until the hit is on
   * screen. A cost discard before BATTLE_RESOLVED still leaves immediately.
   */
  private battleHandDiscardUids(events: GameEvent[]): Set<string> {
    const uids = new Set<string>()
    let afterBattle = false
    for (const ev of events) {
      if (ev.type === EventType.BATTLE_RESOLVED) {
        afterBattle = true
        continue
      }
      if (!afterBattle || ev.type !== EventType.CARD_TO_GRAVEYARD) continue
      const uid = ev.cardUid
      if (this.cardFieldPos(uid) || this.fieldSpellSeatOf(uid)) continue
      const shown =
        this.handSeatOf(uid) !== null ||
        this.handSprites.has(uid) ||
        this.arrivingHandSeat.has(uid)
      if (shown) uids.add(uid)
    }
    return uids
  }

  /**
   * A hand card in this slice is going to the Graveyard. Hide it before any
   * chain or destello `renderAll`, which still paints the pre-commit fan.
   * The delayed soul used to be the first hide, so the card sat in the hand
   * through the effect and a later paint put it back.
   * `skip` stays visible until its own flight (battle damage discards).
   * `holdUntilFlight` is Card Destruction: the rest of both fans stay in
   * their slots until each soul starts. The activating spell still leaves now.
   */
  private pinHandDiscards(
    events: GameEvent[],
    skip?: ReadonlySet<string>,
    holdUntilFlight = false,
  ): Set<string> {
    const activating = new Set<string>()
    if (holdUntilFlight) {
      for (const ev of events) {
        if (
          ev.type === EventType.SPELL_ACTIVATED ||
          ev.type === EventType.TRAP_ACTIVATED
        ) {
          activating.add(ev.cardUid)
        }
      }
    }
    const pinned = new Set<string>()
    let wiped = false
    for (const ev of events) {
      if (ev.type !== EventType.CARD_TO_GRAVEYARD) continue
      const uid = ev.cardUid
      if (skip?.has(uid)) continue
      if (this.cardFieldPos(uid) || this.fieldSpellSeatOf(uid)) continue
      const shown =
        this.handSeatOf(uid) !== null ||
        this.handSprites.has(uid) ||
        this.arrivingHandSeat.has(uid)
      if (!shown) continue
      pinned.add(uid)
      if (holdUntilFlight && !activating.has(uid)) {
        this.keptDiscardUids.add(uid)
        continue
      }
      this.departingHandUids.add(uid)
      this.forgetArrivingHand(uid)
      this.releaseActivationMesh(uid)
      this.destroyHandSprite(uid)
      this.handDisplayX.delete(uid)
      wiped = true
    }
    if (wiped && this.sys?.isActive()) this.renderAll()
    return pinned
  }

  /**
   * Hide the Phaser hand copy as soon as the 3D card leaves for the field.
   * A board-slot mask over the fan looked like a duplicate zone.
   */
  private leaveHandForActivation(uid: string): void {
    this.forgetArrivingHand(uid)
    if (this.departingHandUids.has(uid)) return
    this.departingHandUids.add(uid)
    this.renderAll()
  }

  /**
   * Hide the Phaser field copy while a 3D mesh occupies that slot (Set flip,
   * or a card already flying to the GY). A timed slot mask expired before
   * commit and the pre-snapshot sprite popped back.
   */
  private leaveFieldForActivation(uid: string): void {
    if (this.departingFieldUids.has(uid)) return
    this.departingFieldUids.add(uid)
    this.renderAll()
  }

  /**
   * Drop a card from the GY / Extra / Banished pile visual as soon as it flies
   * out (Monster Reborn, fusion, etc.).
   */
  private leavePile(uid: string): void {
    if (this.departingPileUids.has(uid)) return
    this.departingPileUids.add(uid)
    this.renderAll()
  }

  /**
   * Where a summoned card should fly from, based on the pre-commit snapshot.
   * Normal summons leave the hand; Last Will leaves the Deck; Monster Reborn
   * leaves a graveyard; fusions leave the Extra Deck.
   */
  private summonOrigin(summonSeat: Seat, uid: string): SummonOrigin {
    if (!this.state) {
      return { ...this.handOrigin(summonSeat, uid), from: 'hand' }
    }
    for (const seat of [0, 1] as Seat[]) {
      const z = this.state.zones[seat]
      if (z.hand.some((c) => c.uid === uid)) {
        return { ...this.handOrigin(seat, uid), from: 'hand' }
      }
    }
    for (const seat of [0, 1] as Seat[]) {
      const z = this.state.zones[seat]
      if (z.deck.some((c) => c.uid === uid)) {
        return { ...this.deckPos(seat), from: 'deck' }
      }
      if (z.graveyard.some((c) => c.uid === uid)) {
        return { ...this.graveyardPos(seat), from: 'graveyard' }
      }
      if (z.extraDeck.some((c) => c.uid === uid)) {
        return { ...this.extraPos(seat), from: 'extraDeck' }
      }
      if (z.banished.some((c) => c.uid === uid)) {
        return { ...this.banishedPos(seat), from: 'banished' }
      }
    }
    const field = this.cardFieldPos(uid)
    if (field) return { ...field, from: 'field' }
    return { ...this.handOrigin(summonSeat, uid), from: 'hand' }
  }

  /**
   * Spell/trap zone the card occupies while it resolves (pre-commit snapshot).
   */
  private activationDest(
    seat: Seat,
    uid: string,
    events: GameEvent[],
  ): SlotLand {
    const landed = this.activationLand.get(uid)
    if (landed) return landed
    const onField = this.cardFieldPos(uid)
    if (onField) return { ...onField }
    const setEv = events.find(
      (ev) => ev.type === EventType.CARD_SET && ev.cardUid === uid,
    )
    if (setEv?.zone === 'fieldSpell') return this.fieldSlotPos(seat)
    const card = this.findCardByUid(uid)
    if (card && getFieldSpellConfig(Number(card.cardId))) {
      return this.fieldSlotPos(seat)
    }
    const idx = this.firstFree('spellTraps', seat)
    if (idx === -1) {
      return { x: this.scale.width / 2, y: this.scale.height / 2, scale: 1 }
    }
    return this.zoneSlotPos(seat, 'spellTraps', idx)
  }

  /**
   * Land or flip the card, then play the activation overlay. Effect animations
   * in the same batch wait on the returned time. The mesh stays parked until
   * the card goes to the GY or the snapshot commits (Continuous / Field / Equip).
   * Traps always flip on the field — they cannot activate from the hand.
   */
  private playActivation(
    kind: 'spell' | 'trap',
    e: { seat: Seat; cardUid: string; cardId: string },
    events: GameEvent[],
    flown: Set<string>,
    currentBase: number,
  ): number {
    const overlayMs = SPELL_OVERLAY_MS
    const dest = this.activationDest(e.seat, e.cardUid, events)
    this.activationLand.set(e.cardUid, { ...dest, cardId: e.cardId })
    this.lastActivationUid = e.cardUid
    const onField = this.cardFieldPos(e.cardUid)
    let landAt = currentBase
    const card = this.findCardByUid(e.cardUid)
    const setOnField = kind === 'trap' || !!onField
    const shouldFlip = kind === 'trap' || !card || !card.faceUp

    if (setOnField) {
      if (shouldFlip) {
        if (!this.motionMinimal()) {
          if (currentBase > 0) {
            this.time.delayedCall(currentBase, () =>
              this.leaveFieldForActivation(e.cardUid),
            )
          } else {
            this.leaveFieldForActivation(e.cardUid)
          }
        }
        const gate = this.motionMinimal()
          ? undefined
          : this.ensureActivationHold(e.cardUid, () => undefined)
        landAt = this.showFlipAnimation(
          e.cardId,
          dest.x,
          dest.y,
          onField?.defense ?? false,
          this.ms(180),
          currentBase,
          {
            holdGate: gate,
            scale: dest.scale,
            mine: e.seat === this.mySeat,
            cardUid: e.cardUid,
          },
        )
      }
    } else if (!flown.has(e.cardUid)) {
      this.leaveHandForActivation(e.cardUid)
      const gate = this.ensureActivationHold(e.cardUid, () => undefined)
      landAt = this.animateSummon(
        e.seat,
        e.cardUid,
        e.cardId,
        dest,
        true,
        false,
        { holdGate: gate },
      )
      flown.add(e.cardUid)
    }

    this.time.delayedCall(landAt, () => {
      DuelSfx.play(kind)
      this.followOpponentReveal(e.seat, e.cardId)
      this.followAction(dest.x, dest.y)
      this.flashActivation(
        e.cardId,
        kind,
        this.foilOf(e.cardUid),
        lingeringTurnsFor(e.cardId) ?? 0,
      )
      const burst = kind === 'spell' ? 0x66ccff : 0xf87171
      if (!this.playFoil('foilActivate', dest.x, dest.y, e.cardUid)) {
        this.burstRing(dest.x, dest.y, burst, 40, 120, this.visualMs(360))
        this.playVfx(kind, dest.x, dest.y)
      }
      const target = this.effectTargetPos(e.cardUid, events)
      if (target) {
        this.followAction(target.x, target.y, { delay: this.visualMs(280) })
      }
      this.scheduleReturnToHome(overlayMs)
    })
    return landAt + overlayMs
  }

  /** Mid-field pile slot for link `index` (0-based) of a `count`-link chain. */
  private chainSlotPos(index: number, count: number): SlotPose {
    const cx = this.scale.width / 2
    const cy = (this.rowY(2) + this.rowY(3)) / 2
    const offset = index - (count - 1) / 2
    return {
      x: cx + offset * CARD_W * 0.42,
      y: cy - offset * CARD_H * 0.1,
      scale: CHAIN_STACK_SCALE,
    }
  }

  /** Where the resolving link steps forward to. */
  private chainFocusPos(): SlotPose {
    return {
      x: this.scale.width / 2,
      y: (this.rowY(2) + this.rowY(3)) / 2,
      scale: CHAIN_FOCUS_SCALE,
    }
  }

  /** Field slot a chain card leaves from and returns to. Grave triggers lift off the pile. */
  private chainCardSlot(uid: string): SlotPose {
    const field = this.cardFieldPos(uid)
    if (field) return { x: field.x, y: field.y, scale: field.scale }
    const landed = this.activationLand.get(uid) ?? this.fieldLand.get(uid)
    if (landed) return { x: landed.x, y: landed.y, scale: landed.scale }
    const gy = this.graveSeatOf(uid)
    if (gy !== null) return this.graveyardPos(gy)
    return this.chainFocusPos()
  }

  private graveSeatOf(uid: string): Seat | null {
    if (!uid) return null
    for (const board of [this.state, this.pendingCommit]) {
      if (!board) continue
      for (const seat of [0, 1] as const) {
        if (board.zones[seat].graveyard.some((card) => card.uid === uid)) return seat
      }
    }
    return null
  }

  private chainStackView(): ChainStackView {
    return {
      project: (x, y) =>
        this.sys?.isActive() ? this.overlayNorm(x, y) : { x: 0.5, y: 0.5 },
      cardSize: () =>
        this.sys?.isActive()
          ? this.overlaySize(CARD_W, CARD_H)
          : { w: CARD_W / this.scale.width, h: CARD_H / this.scale.height },
    }
  }

  /**
   * A link joined a chain of 2+. The first time, the "Chain" banner plays and
   * every link so far gathers into the pile; later, only the new card flies
   * in. Returns when the last card lands, so the next prompt waits for it.
   */
  private playChainLinkAdded(e: ChainLinkAddedEvent, startAt: number): number {
    if (this.motionMinimal() || !getDuelVfx()) return startAt
    const fresh = e.links.filter((l) => !this.chainPile.includes(l.cardUid))
    if (fresh.length === 0) return startAt
    const first = this.chainPile.length === 0
    const order = e.links.map((l) => l.cardUid)
    this.chainPile = order
    let at = startAt
    if (first) {
      const banner = this.visualMs(CHAIN_BANNER_MS)
      const label = t('duel.chainBanner')
      this.time.delayedCall(at, () => {
        DuelSfx.play('trap')
        getDuelVfx()?.chainBanner(label, banner / 1000)
      })
      at += Math.round(banner * 0.7)
    }
    const fly = this.visualMs(CHAIN_FLY_MS)
    const stagger = this.visualMs(CHAIN_STAGGER_MS)
    const count = order.length
    // chainPile is scheduling state: a chain that resolves in this same
    // payload has already emptied it by the time these callbacks fire.
    const seq = this.renderSeq
    this.time.delayedCall(at, () => {
      if (seq !== this.renderSeq) return
      const stack = getDuelVfx()?.chainStack(this.chainStackView())
      order.forEach((uid, i) => {
        if (fresh.some((l) => l.cardUid === uid)) return
        stack?.moveTo(uid, this.chainSlotPos(i, count), fly / 1000)
      })
    })
    fresh.forEach((l, i) => {
      const from = this.chainCardSlot(l.cardUid)
      const slot = this.chainSlotPos(order.indexOf(l.cardUid), count)
      const mine = l.controller === this.mySeat
      this.time.delayedCall(at + i * stagger, () => {
        if (seq !== this.renderSeq) return
        const stack = getDuelVfx()?.chainStack(this.chainStackView())
        if (!stack) return
        this.dropSlotStandIn(l.cardUid)
        this.releaseActivationMesh(l.cardUid)
        stack.add(
          {
            uid: l.cardUid,
            link: l.link,
            cardId: l.cardId,
            backUrl: this.sleeveBackUrl(mine),
            foil: this.foilOf(l.cardUid),
          },
          from,
          slot,
          fly / 1000,
        )
        this.chainHidden.add(l.cardUid)
        this.renderAll()
        DuelSfx.play('set')
      })
    })
    return at + (fresh.length - 1) * stagger + fly
  }

  /**
   * One link of a 2+ chain resolves: it steps forward and pulses, then flies
   * back to its zone. Its effects start when it lands; a one-shot then takes
   * the usual GY flight from that slot.
   */
  private playChainLinkResolve(e: ChainLinkResolvingEvent, startAt: number): number {
    if (!this.chainPile.includes(e.cardUid)) return startAt
    const uid = e.cardUid
    const remaining = this.chainPile.filter((u) => u !== uid)
    this.chainPile = remaining
    const focus = this.visualMs(CHAIN_FOCUS_MS)
    const fly = this.visualMs(CHAIN_FLY_MS)
    const slot = this.chainCardSlot(uid)
    const data = CARD_INDEX.get(e.cardId)
    const sfx = data && getCardKind(data) === CardKind.TRAP ? 'trap' : 'spell'
    const mine = e.controller === this.mySeat
    this.time.delayedCall(startAt, () => {
      DuelSfx.play(sfx)
      getDuelVfx()
        ?.chainStack(this.chainStackView())
        ?.focusOn(uid, this.chainFocusPos(), focus / 1000, e.fizzled)
    })
    this.time.delayedCall(startAt + focus, () => {
      const stack = getDuelVfx()?.chainStack(this.chainStackView())
      const pose = stack?.take(uid) ?? null
      this.chainHidden.delete(uid)
      this.activationLand.set(uid, { ...slot, cardId: e.cardId })
      this.leaveFieldForActivation(uid)
      if (pose) {
        const old = this.meshHold.get(uid)
        if (old) old.released = true
        const gate = { released: false }
        this.meshHold.set(uid, gate)
        this.flyOverlay(pose, slot, {
          cardUid: uid,
          cardId: e.cardId,
          mine,
          faceUp: true,
          duration: fly / 1000,
          startScale: 1,
          endScale: 1,
          arc: 24 / this.scale.width,
          ease: 'cubicOut',
          lift: 0.3,
          holdGate: gate,
        })
      }
      remaining.forEach((u, i) => {
        stack?.moveTo(u, this.chainSlotPos(i, remaining.length), fly / 1000)
      })
    })
    return startAt + focus + fly
  }

  /** Uid of a chain card this event moves off the field, if any. */
  private chainLeaverUid(e: GameEvent): string | null {
    if (
      e.type !== EventType.CARD_DESTROYED &&
      e.type !== EventType.CARD_TO_GRAVEYARD &&
      e.type !== EventType.CARD_TO_HAND &&
      e.type !== EventType.CARD_BANISHED
    ) {
      return null
    }
    return this.chainPile.includes(e.cardUid) ? e.cardUid : null
  }

  /** A card still waiting in the pile left the field. It fades out of the pile. */
  private dropFromChainPile(uid: string, at: number): void {
    this.chainPile = this.chainPile.filter((u) => u !== uid)
    this.time.delayedCall(at, () => {
      getDuelVfx()?.chainStack(this.chainStackView())?.drop(uid, this.visualMs(240) / 1000)
      this.chainHidden.delete(uid)
      this.leaveFieldForActivation(uid)
    })
  }

  /** Chain over (or the board resynced): fade the pile and show the sprites. */
  private clearChainPile(): void {
    const hadHidden = this.chainHidden.size > 0
    this.chainPile = []
    this.chainHidden.clear()
    getDuelVfx()?.clearChainStack(this.visualMs(240) / 1000)
    if (hadHidden) this.renderAll()
  }

  /**
   * Where the camera should look after an activation. A destroyed card wins.
   * Otherwise the monster whose ATK/DEF just changed, preferring one that is
   * still Set in the snapshot (it is about to flip).
   */
  private effectTargetPos(
    activatorUid: string,
    events: GameEvent[],
  ): { x: number; y: number } | null {
    const poseOf = (uid: string): { x: number; y: number } | null => {
      const pos =
        this.cardFieldPos(uid) ??
        this.fieldLand.get(uid) ??
        this.activationLand.get(uid)
      return pos ? { x: pos.x, y: pos.y } : null
    }
    for (const e of events) {
      if (e.type !== EventType.CARD_DESTROYED) continue
      if (e.cardUid === activatorUid) continue
      const pos = poseOf(e.cardUid)
      if (pos) return pos
    }
    let statPos: { x: number; y: number } | null = null
    for (const e of events) {
      if (e.type !== EventType.STAT_CHANGED) continue
      if (e.cardUid === activatorUid) continue
      const pos = poseOf(e.cardUid)
      if (!pos) continue
      const inst = this.findCardByUid(e.cardUid)
      if (inst && !inst.faceUp) return pos
      if (!statPos) statPos = pos
    }
    return statPos
  }

  /** Rare+ hologram on summon / activation. Returns false for commons. */
  private playFoil(
    kind: 'foilField' | 'foilActivate',
    x: number,
    y: number,
    cardUid: string,
  ): boolean {
    const foil = this.foilOf(cardUid)
    if (foil === 'common') return false
    this.playVfx(kind, x, y, { foil })
    return true
  }

  /**
   * Cards tributed in this batch (GY events before the Tribute Summon / Set).
   */
  private tributeBatchUids(events: GameEvent[]): Set<string> {
    const uids = new Set<string>()
    for (const e of events) {
      if (e.type !== EventType.CARD_SUMMONED || !e.tributeUids) continue
      for (const uid of e.tributeUids) uids.add(uid)
    }
    const isTribute = events.some(
      (e) =>
        (e.type === EventType.CARD_SUMMONED && e.tribute) ||
        (e.type === EventType.CARD_SET && e.tribute),
    )
    if (!isTribute) return uids
    for (const e of events) {
      if (e.type === EventType.CARD_SUMMONED && e.tribute) break
      if (e.type === EventType.CARD_SET && e.tribute) break
      if (e.type === EventType.CARD_TO_GRAVEYARD) uids.add(e.cardUid)
    }
    return uids
  }

  /**
   * Share the Pain tributes both monsters in one payload. They are not a
   * Tribute Summon, so the grave pass would fling them (and the spell) at
   * the same moment. Play each monster's tribute first, yours then theirs.
   */
  private sharePainTributeGraves(events: GameEvent[]): { uid: string; seat: Seat }[] {
    const noted = events.some(
      (e) =>
        e.type === EventType.MESSAGE &&
        (e.code === 'sharePain' || e.code === 'shareNoMonster'),
    )
    if (!noted) return []
    const out: { uid: string; seat: Seat }[] = []
    for (const e of events) {
      if (e.type !== EventType.CARD_TO_GRAVEYARD) continue
      const card = this.findCardByUid(e.cardUid)
      if (!card || card.cardId === '56830749') continue
      const pos = this.cardFieldPos(e.cardUid)
      if (!pos || pos.zone !== 'monsters') continue
      out.push({ uid: e.cardUid, seat: e.seat })
    }
    return out
  }

  /**
   * Tornado over each tribute, then soul wisps to the GY, one card at a time.
   */
  private playTributeReleases(
    graves: { uid: string; seat: Seat }[],
    startAt: number,
    showSummonCard = false,
  ): number {
    const tornadoMs = this.visualMs(TRIBUTE_TORNADO_MS)
    const soulMs = this.visualMs(DESTROY_SOUL_MS)
    const overlayMs = this.visualMs(SPELL_OVERLAY_MS)
    const gap = this.visualMs(50)
    let t = startAt
    for (const g of graves) {
      const pos = this.cardFieldPos(g.uid)
      const uid = g.uid
      const seat = g.seat
      const monster = pos?.zone === 'monsters'
      const beat = showSummonCard ? Math.max(tornadoMs, overlayMs) : tornadoMs
      if (monster && pos && !this.animationsReduced()) {
        const x = pos.x
        const y = pos.y
        const scale = pos.scale
        this.time.delayedCall(t, () => {
          DuelSfx.play(showSummonCard ? 'summon' : 'spell')
          this.followAction(x, y)
          if (showSummonCard) {
            const card = this.findCardByUid(uid)
            if (card) this.flashActivation(card.cardId, 'summon', this.foilOf(uid))
          }
          this.playTributeTornado(x, y, scale)
        })
        this.time.delayedCall(t + beat, () => {
          this.animateDestroyToGrave(uid, seat)
        })
        t += beat + soulMs + gap
      } else {
        this.time.delayedCall(t, () => {
          if (showSummonCard) {
            const card = this.findCardByUid(uid)
            if (card) this.flashActivation(card.cardId, 'summon', this.foilOf(uid))
          }
          this.animateDestroyToGrave(uid, seat)
        })
        t += (showSummonCard ? overlayMs : 0) + soulMs + gap
      }
    }
    return t
  }

  /** 3D funnel over the sprite; Phaser spiral if the overlay is missing. */
  private playTributeTornado(x: number, y: number, scale = 1): void {
    this.playVfx('tornado', x, y, { scale })
    if (getDuelVfx()) return
    const wrap = addTributeSpiral(this, this.boardLayer, x, y, scale)
    this.time.delayedCall(this.visualMs(TRIBUTE_TORNADO_MS), () => wrap.destroy())
  }

  /**
   * Plays a payload. A chain of 2+ links resolves inside one payload, so it is
   * cut at each CHAIN_LINK_RESOLVING: the pile steps that link forward, then
   * its effects play from the time the previous link's animations ended.
   */
  private flashEvents(events: GameEvent[], payload: GameEventsPayload): number {
    const start = events.findIndex((e) => e.type === EventType.CHAIN_RESOLVING)
    if (start === -1) return this.flashBatch(events, payload)
    let at = this.flashBatch(events.slice(0, start), payload, { whole: events })
    let link: ChainLinkResolvingEvent | null = null
    let chunk: GameEvent[] = []
    const flush = (): void => {
      let resolvingUid: string | undefined
      if (link) {
        if (this.chainPile.includes(link.cardUid)) resolvingUid = link.cardUid
        at = this.playChainLinkResolve(link, at)
      }
      if (!link && chunk.length === 0) return
      at = this.flashBatch(chunk, payload, { startAt: at, whole: events, resolvingUid })
    }
    for (const e of events.slice(start + 1)) {
      if (e.type === EventType.CHAIN_LINK_RESOLVING) {
        flush()
        link = e
        chunk = []
        continue
      }
      chunk.push(e)
    }
    flush()
    const seq = this.renderSeq
    this.time.delayedCall(at, () => {
      if (seq === this.renderSeq) this.clearChainPile()
    })
    return at
  }

  /**
   * Plays light animations for a batch of events. Returns how long (ms) the caller
   * should defer applying the resolved snapshot, so a monster flip is shown before
   * the damage step / flip-effect that follows it.
   *
   * `opts.startAt` delays the whole batch (one chain link after the previous).
   * `opts.whole` is the full payload when this batch is one slice of it, so a
   * trap activated earlier in the payload still holds the battle that follows.
   * `opts.resolvingUid` is the chain card resolving in this slice. The pile
   * already pulsed it, so it gets no destello, and its GY flight goes last
   * like a card activated in this batch.
   *
   * Spell/trap activations: the card lands (or a Set flips), the overlay plays,
   * then effect animations, then the graveyard — unless the card stays (Field,
   * Equip, or Continuous). A Battle Phase response (Waboku, Reinforcements,
   * Castle Walls, …) in the same batch as damage calc waits for that overlay
   * and the trap's GY flight before the lunge and impact. A Set monster
   * flipped in that batch is revealed before any Damage Step card and
   * before any ATK/DEF change, so the target is face-up.
   *
   * Tribute Summon: each tribute gets a tornado then a soul to the GY (one at a
   * time) before the big monster's overlay and landing. Replacing a Field Spell
   * souls the old card to the GY before the new one lands and activates. Hand
   * discards and Graveyard → Banished moves use the same soul trail. Spell, Trap,
   * and Effect Monster activations flash a destello on the card.
   */
  private flashBatch(
    events: GameEvent[],
    payload: GameEventsPayload,
    opts: { startAt?: number; whole?: GameEvent[]; resolvingUid?: string } = {},
  ): number {
    // Phase 1: reveals/lunges/summons play immediately over the pre-resolution board.
    // Phase 2: cards leaving the field fly to the graveyard, staggered *after* phase 1
    // so a battle reads as lunge -> reveal -> destruction rather than all at once.
    let baseDelay = opts.startAt ?? 0
    // How long the attacker's lunge takes; the defender's reveal waits for it.
    let lunge = 0
    // CARD_DRAWN chain: the next draw must not start until this time. Separate
    // from baseDelay so a later Math.max cannot launch two draws in parallel.
    let drawChainMs = 0
    const pendingDraws: Extract<GameEvent, { type: EventType.CARD_DRAWN }>[] = []
    const graveEvents: { uid: string; seat: Seat }[] = []
    const gyFlashes: { cardId: string; foil: CardFoilId }[] = []
    const gyToDeck: { cardUid: string; seat: Seat }[] = []
    const handToDeck: { cardUid: string; seat: Seat }[] = []
    const returnsToDeck = new Set(
      events.flatMap((ev) =>
        ev.type === EventType.CARD_MOVED &&
        ev.from.zone === 'graveyard' &&
        ev.to.zone === 'deck'
          ? [ev.cardUid]
          : [],
      ),
    )
    /** Field-Zone cards already souled to GY before the replacement lands. */
    const fieldSlotSouled = new Set<string>()
    const flown = new Set<string>()
    const activatingUids = new Set<string>(opts.resolvingUid ? [opts.resolvingUid] : [])
    const destroyedUids = new Set<string>()
    const tributeUids = this.tributeBatchUids(events)
    const whole = opts.whole ?? events
    const materialSummon = events.some(
      (e) => e.type === EventType.CARD_SUMMONED && (e.tributeUids?.length ?? 0) > 0,
    )
    const trapInBatch = whole.some((ev) => ev.type === EventType.TRAP_ACTIVATED)
    const spellInBatch = whole.some((ev) => ev.type === EventType.SPELL_ACTIVATED)
    const battleInBatch = events.some((ev) => ev.type === EventType.BATTLE_RESOLVED)
    let deferPhaseChime = false
    // Attack declare is often a *previous* payload. The response (Waboku, …)
    // then arrives with TRAP/SPELL_ACTIVATED + BATTLE_RESOLVED together.
    const holdBattle = (trapInBatch || spellInBatch) && battleInBatch
    let skippedAttack: { attackerUid: string; targetUid: string | null } | null =
      null
    let deferredBattle: Extract<
      GameEvent,
      { type: EventType.BATTLE_RESOLVED }
    > | null = null
    const deferredDestroys: Extract<
      GameEvent,
      { type: EventType.CARD_DESTROYED }
    >[] = []
    const deferredLp: Extract<
      GameEvent,
      { type: EventType.LIFE_POINTS_CHANGED }
    >[] = []
    const deferredBounces: Extract<
      GameEvent,
      { type: EventType.CARD_TO_HAND }
    >[] = []
    // Battle-damage triggers (Robbin' Goblin, White Magical Hat) flash and
    // discard after the hit. A trap in this batch holds the lunge, so these
    // must not play in the grave pass that runs before that lunge.
    const deferredEffectFlashes: { uid: string; sfx: 'spell' | 'trap' }[] = []
    const deferredBattleToasts: string[] = []
    // Just Desserts (etc.) resolves before damage calc — only the LP tick that
    // matches BATTLE_RESOLVED waits for the deferred clash.
    let battleLpEvent: Extract<
      GameEvent,
      { type: EventType.LIFE_POINTS_CHANGED }
    > | null = null
    /** Outbound lunge time; a Set defender flips when the attacker arrives. */
    let lungeContact = 0
    /** Floor so two flips in the same batch never start together. */
    let flipLaneEnd = 0
    const battleFlipUids = new Set<string>()
    for (const ev of events) {
      if (
        ev.type === EventType.SPELL_ACTIVATED ||
        ev.type === EventType.TRAP_ACTIVATED
      ) {
        activatingUids.add(ev.cardUid)
      }
      if (ev.type === EventType.CARD_DESTROYED) destroyedUids.add(ev.cardUid)
      if (ev.type === EventType.ATTACK_DECLARED && ev.targetUid) {
        battleFlipUids.add(ev.targetUid)
      }
      if (ev.type === EventType.BATTLE_RESOLVED && ev.targetUid) {
        battleFlipUids.add(ev.targetUid)
      }
    }
    if (holdBattle) {
      const battle = events.find(
        (ev): ev is Extract<GameEvent, { type: EventType.BATTLE_RESOLVED }> =>
          ev.type === EventType.BATTLE_RESOLVED,
      )
      if (battle && battle.damage > 0 && battle.damageSeat !== null) {
        const seat = battle.damageSeat
        const delta = -battle.damage
        for (const ev of events) {
          if (
            ev.type === EventType.LIFE_POINTS_CHANGED &&
            ev.seat === seat &&
            ev.delta === delta
          ) {
            battleLpEvent = ev
          }
        }
      }
    }
    const lpInBatch = events.some(
      (ev) => ev.type === EventType.LIFE_POINTS_CHANGED && ev.delta !== 0,
    )
    const phaseInBatch = events.some(
      (ev) =>
        ev.type === EventType.PHASE_CHANGED || ev.type === EventType.TURN_CHANGED,
    )
    let statSfxPlayed = false
    const deferDraws = this.handDiscardsBeforeDraws(events)
    const holdCamera = this.isHandLimitPass(events)
    if (holdCamera && this.focusBandsActive()) {
      this.cancelReturnToHome()
    }
    // Flip Summon emits CARD_FLIPPED + POSITION_CHANGED together. Play one
    // stand-up; do not reveal in DEF and then snap to ATK.
    const flipSummonUids = this.flipSummonUids(events)
    const battleDiscardUids = this.battleHandDiscardUids(events)
    const handDiscardUids = this.pinHandDiscards(
      events,
      battleDiscardUids,
      deferDraws,
    )
    if (tributeUids.size > 0) {
      const graves: { uid: string; seat: Seat }[] = []
      for (const e of events) {
        if (e.type === EventType.CARD_TO_GRAVEYARD && tributeUids.has(e.cardUid)) {
          graves.push({ uid: e.cardUid, seat: e.seat })
        }
      }
      baseDelay = this.playTributeReleases(graves, baseDelay, materialSummon)
    }
    for (const e of events) {
      const leaving = this.chainLeaverUid(e)
      if (leaving) this.dropFromChainPile(leaving, Math.max(lunge, baseDelay))
      switch (e.type) {
        case EventType.MESSAGE: {
          // Heads/Tails is the prompt for the chooser. The other seat already
          // has the waiting line, so this toast is not shown to either one.
          if (e.code === 'coinCall') break
          const text = localizeDuelMessage(e)
          if (!text) break
          if (holdBattle && deferredBattle) {
            deferredBattleToasts.push(text)
            break
          }
          if (opts.startAt || baseDelay > 0) {
            this.time.delayedCall(baseDelay, () => this.toast(text, 'info'))
          } else {
            this.toast(text, 'info')
          }
          break
        }
        case EventType.CHAIN_LINK_ADDED: {
          const wait = Math.max(lunge, baseDelay, flipLaneEnd)
          baseDelay = Math.max(baseDelay, this.playChainLinkAdded(e, wait))
          break
        }
        case EventType.CHAIN_RESOLVING:
        case EventType.CHAIN_LINK_RESOLVING:
          break
        case EventType.COIN_TOSSED:
          baseDelay = Math.max(baseDelay, this.playCoinToss(e, baseDelay))
          break
        case EventType.CARD_DRAWN:
          if (deferDraws) {
            pendingDraws.push(e)
            break
          }
          drawChainMs = this.animateDraw(
            e.seat,
            e.cardUid,
            e.cardId,
            Math.max(baseDelay, drawChainMs),
          )
          baseDelay = Math.max(baseDelay, drawChainMs)
          break
        case EventType.ATTACK_DECLARED: {
          if (holdBattle) {
            skippedAttack = {
              attackerUid: e.attackerUid,
              targetUid: e.targetUid,
            }
            break
          }
          const start = baseDelay
          const timing = this.attackTiming(!!e.targetUid)
          const playLunge = (): void => {
            DuelSfx.play('attack')
            this.playAttackTrail(e.attackerUid, e.targetUid)
            this.followOpponentAttack(e.attackerUid)
            this.animateAttack(e.attackerUid, e.targetUid)
          }
          if (start > 0) this.time.delayedCall(start, playLunge)
          else playLunge()
          lunge = Math.max(lunge, start + timing.total)
          lungeContact = Math.max(lungeContact, start + timing.outbound)
          baseDelay = Math.max(baseDelay, lunge)
          break
        }
        case EventType.ATTACK_REPLAY:
          this.toast(t('duel.toast.replay'), 'info')
          break
        case EventType.CARD_FLIPPED: {
          const battle = battleFlipUids.has(e.cardUid)
          // A Damage Step card in the same batch as damage used to hold this
          // reveal until the hit, so ATK/DEF changes landed on a card that
          // still looked Set. Play the flip after any Battle Step trap
          // already queued, and before the damage-step card that follows.
          const startDelay = Math.max(
            battle && holdBattle
              ? baseDelay
              : battle
                ? Math.max(lungeContact, opts.startAt ?? 0)
                : Math.max(lunge, baseDelay),
            flipLaneEnd,
          )
          const flipEnd = this.scheduleFlipReveal(
            e,
            startDelay,
            flipSummonUids.has(e.cardUid),
            battle,
          )
          flipLaneEnd = Math.max(flipLaneEnd, flipEnd)
          baseDelay = Math.max(baseDelay, flipEnd)
          break
        }
        case EventType.CARD_SUMMONED: {
          const to = this.zoneSlotPos(e.seat, 'monsters', e.zoneIndex)
          const startAt = baseDelay
          const origin = this.summonOrigin(e.seat, e.cardUid)
          const defense = e.position === CardPosition.DEFENSE
          const tribute = e.tribute || (e.tributeUids?.length ?? 0) > 0
          const overlayMs = tribute ? SPELL_OVERLAY_MS : 0
          const fromHand = origin.from === 'hand'
          const dur = this.summonFlyMs(fromHand, defense)
          flown.add(e.cardUid)
          this.fieldLand.set(e.cardUid, {
            ...to,
            cardId: e.cardId,
          })
          // Park until commit (or until a same-batch GY fly releases the mesh).
          // Without this, a GY summon dies at land while the spell still flies
          // to the GY, then the Phaser sprite pops in with the summon burst.
          const gate = this.ensureMeshHold(e.cardUid)
          const playSummon = (): void => {
            if (tribute) {
              this.flashActivation(e.cardId, 'summon', this.foilOf(e.cardUid))
              this.scheduleReturnToHome(SPELL_OVERLAY_MS)
            }
            DuelSfx.play('summon')
            this.followOpponentReveal(e.seat, e.cardId)
            this.animateSummon(
              e.seat,
              e.cardUid,
              e.cardId,
              to,
              true,
              defense,
              { holdGate: gate, origin },
            )
          }
          if (startAt > 0) this.time.delayedCall(startAt, playSummon)
          else playSummon()
          const burstAt = startAt + (tribute ? overlayMs : dur)
          this.time.delayedCall(burstAt, () => {
            if (!this.playFoil('foilField', to.x, to.y, e.cardUid)) {
              this.burstRing(to.x, to.y, 0xffe066, 18, 56, this.ms(280))
              this.playVfx('summon', to.x, to.y)
            }
          })
          let after = startAt + Math.max(dur + this.ms(80), overlayMs)
          if (origin.from === 'deck') {
            this.time.delayedCall(after, () => this.animateDeckShuffle(e.seat))
            after += this.deckShuffleTiming().total
          }
          baseDelay = Math.max(baseDelay, after)
          break
        }
        case EventType.CARD_SET: {
          const dest =
            e.zone === 'fieldSpell'
              ? this.fieldSlotPos(e.seat)
              : this.zoneSlotPos(e.seat, e.zone, e.zoneIndex)
          if (e.zone !== 'monsters') this.activationLand.set(e.cardUid, dest)
          const alreadyOnField = this.cardFieldPos(e.cardUid)
          const activating = activatingUids.has(e.cardUid)
          if (alreadyOnField) break
          flown.add(e.cardUid)
          const startAt = baseDelay
          const defense = e.zone === 'monsters'
          const cardId = this.findCardByUid(e.cardUid)?.cardId ?? ''
          const origin = this.summonOrigin(e.seat, e.cardUid)
          const dur = this.summonFlyMs(origin.from === 'hand', defense)
          const playSet = (): void => {
            if (activating) {
              this.leaveHandForActivation(e.cardUid)
              const gate = this.ensureActivationHold(e.cardUid, () => undefined)
              this.animateSummon(e.seat, e.cardUid, cardId, dest, true, false, {
                holdGate: gate,
                origin,
              })
            } else {
              DuelSfx.play('set')
              const gate = this.ensureMeshHold(e.cardUid)
              this.animateSummon(
                e.seat,
                e.cardUid,
                cardId,
                dest,
                false,
                defense,
                { holdGate: gate, origin },
              )
            }
          }
          if (startAt > 0) this.time.delayedCall(startAt, playSet)
          else playSet()
          baseDelay = Math.max(baseDelay, startAt + dur)
          break
        }
        case EventType.BATTLE_RESOLVED: {
          if (holdBattle) {
            skippedAttack = skippedAttack ?? {
              attackerUid: e.attackerUid,
              targetUid: e.targetUid,
            }
            deferredBattle = e
            break
          }
          if (skippedAttack) break
          const hitAt = Math.max(lungeContact, opts.startAt ?? 0)
          const attackerUid = e.attackerUid
          const targetUid = e.targetUid
          const damageSeat = e.damageSeat
          this.time.delayedCall(hitAt, () =>
            this.playBattleImpact(attackerUid, targetUid, damageSeat),
          )
          baseDelay = Math.max(baseDelay, hitAt + this.visualMs(180))
          break
        }
        case EventType.SPELL_ACTIVATED: {
          const startAt = Math.max(baseDelay, flipLaneEnd)
          const end = this.playActivation('spell', e, events, flown, startAt)
          flipLaneEnd = Math.max(flipLaneEnd, end)
          baseDelay = Math.max(baseDelay, end)
          break
        }
        case EventType.TRAP_ACTIVATED: {
          const startAt = Math.max(baseDelay, flipLaneEnd)
          const end = this.playActivation('trap', e, events, flown, startAt)
          flipLaneEnd = Math.max(flipLaneEnd, end)
          baseDelay = Math.max(baseDelay, end)
          break
        }
        case EventType.LIFE_POINTS_CHANGED:
          if (e === battleLpEvent) {
            deferredLp.push(e)
            break
          }
          if (e.delta !== 0) {
            const wait = Math.max(lunge, baseDelay)
            const seat = e.seat
            const delta = e.delta
            const lifePoints = e.lifePoints
            const hit = delta < 0
            // Battle already shook at contact; a second LP shake would flatten ATK scale.
            const alreadyShook = events.some(
              (ev) => ev.type === EventType.BATTLE_RESOLVED,
            )
            const plan = this.lpRollPlan(Math.abs(delta))
            this.time.delayedCall(wait, () => {
              DuelSfx.play(hit ? 'damage' : 'heal')
              this.playLifeChange(seat, delta, lifePoints, !alreadyShook, plan)
            })
            baseDelay = Math.max(baseDelay, wait + plan.duration)
          }
          break
        case EventType.CARD_DESTROYED: {
          // Trap kills (Two-Pronged, …) emit before BATTLE_RESOLVED; battle
          // deaths come after, once deferredBattle is set.
          if (
            holdBattle &&
            deferredBattle &&
            !activatingUids.has(e.cardUid)
          ) {
            deferredDestroys.push(e)
            break
          }
          // Field Spell zone: souls to the GY, no monster shatter.
          if (this.fieldSpellSeatOf(e.cardUid) !== null) break
          const pos =
            this.cardFieldPos(e.cardUid) ??
            this.fieldLand.get(e.cardUid) ??
            this.activationLand.get(e.cardUid)
          const boomAt = Math.max(lunge, baseDelay)
          const uid = e.cardUid
          this.time.delayedCall(boomAt, () => {
            this.playDestroyShatter(uid, pos)
          })
          baseDelay = Math.max(baseDelay, boomAt + this.shatterHoldMs())
          break
        }
        case EventType.CARD_TO_GRAVEYARD:
          graveEvents.push({ uid: e.cardUid, seat: e.seat })
          // Field Zone: soul the old card first so the replacement can land on
          // an empty slot and play its activation afterwards.
          if (this.fieldSpellSeatOf(e.cardUid) !== null) {
            const wait = Math.max(lunge, baseDelay)
            const uid = e.cardUid
            const seat = e.seat
            this.time.delayedCall(wait, () => {
              this.animateDestroyToGrave(uid, seat)
            })
            fieldSlotSouled.add(uid)
            baseDelay = Math.max(
              baseDelay,
              wait + this.visualMs(DESTROY_SOUL_MS) + this.visualMs(80),
            )
          }
          break
        case EventType.CARD_BANISHED: {
          const wait = Math.max(lunge, baseDelay)
          const uid = e.cardUid
          const seat = e.seat
          const gySeat = this.gySeatOf(uid)
          if (gySeat !== null) {
            const from = this.graveyardPos(gySeat)
            this.time.delayedCall(wait, () => {
              this.animateBanishFromGrave(uid, seat, from)
            })
            baseDelay = Math.max(
              baseDelay,
              wait + this.visualMs(DESTROY_SOUL_MS) + this.visualMs(50),
            )
          } else {
            this.time.delayedCall(wait, () => {
              DuelSfx.play('banish')
              this.animateToPile(uid, this.banishedPos(seat))
            })
            baseDelay = Math.max(baseDelay, wait + this.flightMs(300))
          }
          break
        }
        case EventType.CARD_TO_HAND: {
          if (holdBattle && deferredBattle) {
            deferredBounces.push(e)
            break
          }
          const wait = Math.max(lunge, baseDelay)
          const uid = e.cardUid
          const seat = e.seat
          const bounceMs = this.animateReturnToHand(uid, seat, wait)
          this.time.delayedCall(wait, () => DuelSfx.play('toHand'))
          baseDelay = Math.max(baseDelay, wait + bounceMs)
          break
        }
        case EventType.POSITION_CHANGED: {
          if (flipSummonUids.has(e.cardUid)) break
          const wait = Math.max(lunge, baseDelay)
          const uid = e.cardUid
          const position = e.position
          // The effect destello is in this same payload, so the board commits
          // later. Keep the turned card up until then, or the old Attack
          // sprite shows through as soon as the short spin ends.
          const holdUntilCommit = events.some(
            (ev) => ev.type === EventType.EFFECT_RESOLVED && ev.cardUid === uid,
          )
          this.time.delayedCall(wait, () => {
            DuelSfx.play('click')
            this.animatePositionChange(uid, position, holdUntilCommit)
          })
          baseDelay = Math.max(baseDelay, wait + this.flightMs(180))
          break
        }
        case EventType.STAT_CHANGED: {
          const wait = Math.max(lunge, baseDelay, flipLaneEnd)
          const pos =
            this.cardFieldPos(e.cardUid) ?? this.fieldLand.get(e.cardUid) ?? null
          const boosted = this.findCardByUid(e.cardUid)
          const atk = e.atk
          const def = e.def
          const fromAtk = boosted ? this.fieldAtk(e.cardUid) : atk
          const fromDef = boosted
            ? Math.max(0, this.battleBases(boosted).def + this.statBonus(boosted).def)
            : def
          if (fromAtk === atk && fromDef === def) break
          const battleAt = events.findIndex((ev) => ev.type === EventType.BATTLE_RESOLVED)
          const attackAt = events.findIndex(
            (ev) => ev.type === EventType.ATTACK_DECLARED && ev.attackerUid === e.cardUid,
          )
          // An Equip that falls off a monster already destroyed emits
          // STAT_CHANGED after BATTLE_RESOLVED; that drop is not a preview.
          const previewsBattle = battleAt >= 0 && events.indexOf(e) < battleAt
          // Insect Soldiers gains ATK before the lunge. The strip still updates.
          // There is no ticking number, and the attack does not wait for one.
          const risesBeforeLunge =
            atk > fromAtk && attackAt >= 0 && events.indexOf(e) < attackAt
          const leaves = events.some((ev) => {
            if (
              ev.type !== EventType.CARD_DESTROYED &&
              ev.type !== EventType.CARD_TO_GRAVEYARD &&
              ev.type !== EventType.CARD_BANISHED
            ) {
              return false
            }
            return ev.cardUid === e.cardUid
          })
          if (leaves && !previewsBattle && !risesBeforeLunge) break
          const stillSet = !!boosted && !boosted.faceUp
          const holdMs = this.flightMs(220)
          this.time.delayedCall(wait, () => {
            if (!pos || !boosted) return
            this.burstRing(pos.x, pos.y, 0xfbbf24, 14, 40, this.ms(220))
            this.previewStatLabel(boosted, pos.x, pos.y, pos.scale, atk, def)
            if (stillSet) this.followAction(pos.x, pos.y)
          })
          if (!statSfxPlayed) {
            statSfxPlayed = true
            const sfxAt = wait + (lpInBatch || phaseInBatch ? 160 : 0)
            this.time.delayedCall(sfxAt, () => DuelSfx.play('stat'))
          }
          baseDelay = Math.max(baseDelay, wait + holdMs)
          break
        }
        case EventType.PHASE_CHANGED:
          if (battleInBatch) {
            deferPhaseChime = true
            break
          }
          if (!holdCamera) DuelSfx.play('phase')
          break
        case EventType.TURN_CHANGED:
          if (!holdCamera) DuelSfx.play('turn')
          break
        case EventType.EFFECT_RESOLVED: {
          // The overlay already played for this activation. A later
          // EFFECT_RESOLVED from the same card (Ultimate Offering's paid
          // ignition) still destellos.
          const followsActivation =
            activatingUids.has(e.cardUid) || e.cardUid === this.lastActivationUid
          if (e.cardUid === this.lastActivationUid) this.lastActivationUid = null
          if (followsActivation) break
          const inst =
            this.findCardByUid(e.cardUid) ??
            this.findCardInState(this.pendingCommit, e.cardUid)
          const cardId = inst?.cardId
          const fromGrave =
            graveEvents.some((g) => g.uid === e.cardUid) ||
            returnsToDeck.has(e.cardUid) ||
            this.cardInGraveyard(e.cardUid)
          if (fromGrave && cardId) {
            const piled = (opts.whole ?? events).some(
              (ev) =>
                ev.type === EventType.CHAIN_LINK_ADDED &&
                ev.links.some((link) => link.cardUid === e.cardUid),
            )
            if (!piled) gyFlashes.push({ cardId, foil: this.foilOf(e.cardUid) })
            break
          }
          const data = cardId ? CARD_INDEX.get(cardId) : undefined
          const kind = data ? getCardKind(data) : null
          // Flip Effects already destello'd on CARD_FLIPPED, before the prompt.
          if (cardId && this.cardHasFlipEffect(cardId)) break
          const sfx = kind === CardKind.TRAP ? 'trap' : 'spell'
          if (holdBattle && deferredBattle) {
            deferredEffectFlashes.push({ uid: e.cardUid, sfx })
            break
          }
          const wait = Math.max(lunge, baseDelay)
          baseDelay = Math.max(
            baseDelay,
            this.scheduleEffectFlash(e.cardUid, wait, sfx),
          )
          break
        }
        case EventType.CARD_MOVED: {
          if (e.from.zone === 'graveyard' && e.to.zone === 'deck') {
            gyToDeck.push({ cardUid: e.cardUid, seat: e.to.seat })
            break
          }
          if (e.from.zone === 'hand' && e.to.zone === 'deck') {
            handToDeck.push({ cardUid: e.cardUid, seat: e.to.seat })
            break
          }
          const wait = Math.max(lunge, baseDelay)
          const dur = this.scheduleFieldMove(e, wait)
          baseDelay = Math.max(baseDelay, wait + dur)
          break
        }
        case EventType.TARGETS_CHOSEN: {
          if (e.seat === this.mySeat && !this.isWatching()) break
          const wait = Math.max(lunge, baseDelay)
          const beat = this.playOpponentTargetCue(e.targetUids, wait)
          baseDelay = Math.max(baseDelay, wait + beat)
          break
        }
        case EventType.DECK_SHUFFLED: {
          const wait = Math.max(lunge, baseDelay)
          const dur = this.deckShuffleTiming().total
          this.time.delayedCall(wait, () => this.animateDeckShuffle(e.seat))
          baseDelay = Math.max(baseDelay, wait + dur)
          break
        }
        case EventType.REQUEST_TARGETS: {
          const sourceCardId = e.sourceCardId
          const sourceUid = e.sourceUid
          if (
            sourceCardId &&
            sourceUid &&
            graveEvents.some((g) => g.uid === sourceUid)
          ) {
            gyFlashes.push({ cardId: sourceCardId, foil: this.foilOf(sourceUid) })
            break
          }
          if (
            sourceCardId &&
            sourceUid !== this.lastActivationUid &&
            !this.cardFieldPos(sourceUid ?? '')
          ) {
            const wait = Math.max(lunge, baseDelay)
            this.time.delayedCall(wait, () => {
              this.flashActivation(
                sourceCardId,
                'spell',
                this.foilOf(sourceUid ?? ''),
              )
            })
            baseDelay = Math.max(baseDelay, wait + SPELL_OVERLAY_MS)
          }
          break
        }
        case EventType.REQUEST_PRIORITY:
        case EventType.GAME_OVER:
          break
        case EventType.HAND_SHUFFLED: {
          const wait = Math.max(lunge, baseDelay)
          baseDelay = Math.max(baseDelay, wait + this.animateHandShuffle(e.seat, e.order, wait))
          break
        }
        case EventType.PEEK_CARDS: {
          if (e.cards.length === 0) break
          const field = e.cards.filter((c) => this.cardFieldPos(c.uid))
          if (field.length > 0 && field.length === e.cards.length) {
            const wait = Math.max(lunge, baseDelay)
            const dur = this.scheduleFieldPeekFlips(field, wait)
            baseDelay = Math.max(baseDelay, wait + dur)
          }
          // Sangan and Witch add the fifth piece in this same payload. That
          // reveal is a full-screen tap target, and the Exodia cinematic
          // skips on any click, so the assembly never played. The cinematic
          // is the reveal.
          const scope = opts.whole ?? events
          const exodiaWin = scope.some(
            (ev) => ev.type === EventType.GAME_OVER && ev.reason === 'exodia',
          )
          if (exodiaWin) break
          if (e.reveal || e.seat === this.mySeat || this.isWatching()) {
            this.pendingPeek = { cards: e.cards, reveal: !!e.reveal, search: !!e.search }
          }
          break
        }
      }
    }

    const graveDur = this.flightMs(320)
    const soulDur = this.visualMs(DESTROY_SOUL_MS)
    const soulGap = this.visualMs(50)
    const deferredDestroyUids = new Set(deferredDestroys.map((d) => d.cardUid))
    const shattered = graveEvents.filter(
      (g) =>
        destroyedUids.has(g.uid) &&
        !activatingUids.has(g.uid) &&
        !deferredDestroyUids.has(g.uid) &&
        this.fieldSpellSeatOf(g.uid) === null,
    )
    const fieldSlot = graveEvents.filter(
      (g) =>
        !activatingUids.has(g.uid) &&
        !tributeUids.has(g.uid) &&
        !fieldSlotSouled.has(g.uid) &&
        this.fieldSpellSeatOf(g.uid) !== null,
    )
    const discarded = graveEvents.filter(
      (g) =>
        !activatingUids.has(g.uid) &&
        !destroyedUids.has(g.uid) &&
        !tributeUids.has(g.uid) &&
        !battleDiscardUids.has(g.uid) &&
        (this.handSeatOf(g.uid) !== null || handDiscardUids.has(g.uid)),
    )
    const others = graveEvents.filter(
      (g) =>
        !activatingUids.has(g.uid) &&
        !destroyedUids.has(g.uid) &&
        !tributeUids.has(g.uid) &&
        !handDiscardUids.has(g.uid) &&
        this.handSeatOf(g.uid) === null &&
        this.fieldSpellSeatOf(g.uid) === null,
    )
    const self = graveEvents.filter((g) => activatingUids.has(g.uid))
    let graveAt = baseDelay
    const flyGrave = (
      g: { uid: string; seat: Seat },
      start: number,
      followCamera = true,
    ): void => {
      this.time.delayedCall(start, () => {
        this.animateToGrave(
          g.uid,
          g.seat,
          this.activationLand.get(g.uid) ?? this.fieldLand.get(g.uid),
          followCamera,
        )
      })
    }
    const soulGrave = (g: { uid: string; seat: Seat }, start: number): void => {
      this.time.delayedCall(start, () => {
        this.animateDestroyToGrave(g.uid, g.seat)
      })
    }
    const discardGrave = (
      g: { uid: string; seat: Seat; from: SlotPose },
      start: number,
      followCamera: boolean,
    ): void => {
      this.time.delayedCall(start, () => {
        this.animateDiscardToGrave(g.uid, g.seat, g.from, followCamera)
      })
    }
    for (const g of shattered) soulGrave(g, graveAt)
    if (shattered.length > 0) graveAt += soulDur
    for (const g of fieldSlot) {
      this.time.delayedCall(graveAt, () => {
        this.animateDestroyToGrave(g.uid, g.seat)
      })
      graveAt += soulDur + soulGap
    }
    const discardFlights = discarded.map((g) => {
      const handSeat = this.handSeatOf(g.uid) ?? g.seat
      return { ...g, from: this.handOrigin(handSeat, g.uid) }
    })
    const lateBattleDiscards = [...battleDiscardUids].flatMap((uid) => {
      const grave = graveEvents.find((g) => g.uid === uid)
      if (!grave) return []
      const handSeat = this.handSeatOf(uid) ?? grave.seat
      return [{ ...grave, from: this.handOrigin(handSeat, uid) }]
    })
    for (const g of discardFlights) {
      discardGrave(g, graveAt, !holdCamera)
      graveAt += soulDur + soulGap
    }
    if (holdCamera) {
      const afterDiscard = graveAt
      this.time.delayedCall(afterDiscard, () => {
        DuelSfx.play('phase')
        DuelSfx.play('turn')
        this.publishPhaseHud(payload)
      })
    }
    if (deferDraws && pendingDraws.length > 0) {
      for (const e of pendingDraws) {
        drawChainMs = this.animateDraw(
          e.seat,
          e.cardUid,
          e.cardId,
          Math.max(graveAt, drawChainMs),
        )
        graveAt = Math.max(graveAt, drawChainMs)
      }
      // Last draw is often the opponent's. Hold that shot, then home (hand).
      this.time.delayedCall(graveAt, () => {
        this.scheduleReturnToHome(FOCUS_HOME_HOLD_MS)
      })
    }
    const followRest = !deferDraws
    const shareTributes = this.sharePainTributeGraves(events)
    const shareTributeUids = new Set(shareTributes.map((g) => g.uid))
    if (shareTributes.length > 0) {
      graveAt = this.playTributeReleases(shareTributes, graveAt, false)
    }
    let flewOthers = false
    for (const g of others) {
      if (shareTributeUids.has(g.uid)) continue
      flyGrave(g, graveAt, followRest)
      flewOthers = true
    }
    if (flewOthers) graveAt += graveDur
    let flewSelf = false
    for (const g of self) {
      if (shareTributeUids.has(g.uid)) continue
      flyGrave(g, graveAt, followRest)
      flewSelf = true
    }
    if (flewSelf) graveAt += graveDur

    const playDeferredAttack = holdBattle && !!skippedAttack
    if (playDeferredAttack && skippedAttack) {
      const start = graveAt
      const attackerUid = skippedAttack.attackerUid
      const targetUid = skippedAttack.targetUid
      const timing = this.attackTiming(!!targetUid)
      const damageSeat = deferredBattle?.damageSeat ?? null
      this.time.delayedCall(start, () => {
        DuelSfx.play('attack')
        this.playAttackTrail(attackerUid, targetUid)
        this.followOpponentAttack(attackerUid)
        this.animateAttack(attackerUid, targetUid)
        this.time.delayedCall(timing.outbound, () =>
          this.playBattleImpact(attackerUid, targetUid, damageSeat),
        )
      })
      graveAt += timing.total
      const contactAt = start + timing.outbound
      for (const lp of deferredLp) {
        const plan = this.lpRollPlan(Math.abs(lp.delta))
        const seat = lp.seat
        const delta = lp.delta
        const lifePoints = lp.lifePoints
        this.time.delayedCall(contactAt, () => {
          DuelSfx.play(delta < 0 ? 'damage' : 'heal')
          this.playLifeChange(seat, delta, lifePoints, false, plan)
        })
        graveAt = Math.max(graveAt, contactAt + plan.duration)
      }
      for (const d of deferredDestroys) {
        const pos =
          this.cardFieldPos(d.cardUid) ??
          this.fieldLand.get(d.cardUid) ??
          this.activationLand.get(d.cardUid)
        const uid = d.cardUid
        this.time.delayedCall(contactAt, () => {
          this.playDestroyShatter(uid, pos)
        })
      }
      if (deferredDestroys.length > 0) {
        graveAt = Math.max(graveAt, contactAt + this.shatterHoldMs())
        for (const g of graveEvents) {
          if (!deferredDestroyUids.has(g.uid)) continue
          soulGrave(g, graveAt)
        }
        graveAt += soulDur
      }
      for (const b of deferredBounces) {
        const uid = b.cardUid
        const seat = b.seat
        const bounceMs = this.animateReturnToHand(uid, seat, graveAt)
        this.time.delayedCall(graveAt, () => DuelSfx.play('toHand'))
        graveAt += bounceMs
      }
    }

    for (const flash of deferredEffectFlashes) {
      graveAt = Math.max(
        graveAt,
        this.scheduleEffectFlash(flash.uid, graveAt, flash.sfx),
      )
    }
    for (const text of deferredBattleToasts) {
      const line = text
      this.time.delayedCall(graveAt, () => this.toast(line, 'info'))
    }
    for (const g of lateBattleDiscards) {
      discardGrave(g, graveAt, !holdCamera)
      graveAt += soulDur + soulGap
    }

    const seenFlash = new Set<string>()
    for (const flash of gyFlashes) {
      if (seenFlash.has(flash.cardId)) continue
      seenFlash.add(flash.cardId)
      const cardId = flash.cardId
      const foil = flash.foil
      const data = CARD_INDEX.get(cardId)
      const kind = !data
        ? 'spell'
        : getCardKind(data) === CardKind.MONSTER
          ? 'summon'
          : getCardKind(data) === CardKind.TRAP
            ? 'trap'
            : 'spell'
      this.time.delayedCall(graveAt, () => {
        this.flashActivation(cardId, kind, foil)
      })
      graveAt += SPELL_OVERLAY_MS
    }
    if (deferPhaseChime) {
      this.time.delayedCall(graveAt, () => {
        if (!holdCamera) DuelSfx.play('phase')
      })
    }
    for (const move of gyToDeck) {
      graveAt += this.animateGraveToDeck(move.cardUid, move.seat, graveAt)
    }
    for (const move of handToDeck) {
      graveAt += this.animateHandToDeck(move.cardUid, move.seat, graveAt)
    }

    return graveAt
  }

  /**
   * End Phase hand-limit discard plus the turn pass, in one payload.
   * Not Card Destruction (that batch also has a spell/trap activation).
   */
  private isHandLimitPass(events: GameEvent[]): boolean {
    let sawHandDiscard = false
    let sawTurn = false
    for (const e of events) {
      if (
        e.type === EventType.SPELL_ACTIVATED ||
        e.type === EventType.TRAP_ACTIVATED
      ) {
        return false
      }
      if (
        e.type === EventType.CARD_TO_GRAVEYARD &&
        this.handSeatOf(e.cardUid) !== null
      ) {
        sawHandDiscard = true
      }
      if (
        e.type === EventType.PHASE_CHANGED ||
        e.type === EventType.TURN_CHANGED
      ) {
        sawTurn = true
      }
    }
    return sawHandDiscard && sawTurn
  }

  /**
   * Card Destruction (and similar): hand cards hit the GY before the redraw.
   * Play those discards first so draws do not start from a still-full fan.
   */
  private handDiscardsBeforeDraws(events: GameEvent[]): boolean {
    const activating = new Set<string>()
    for (const ev of events) {
      if (
        ev.type === EventType.SPELL_ACTIVATED ||
        ev.type === EventType.TRAP_ACTIVATED
      ) {
        activating.add(ev.cardUid)
      }
    }
    for (const e of events) {
      if (e.type === EventType.CARD_DRAWN) return false
      if (
        e.type === EventType.CARD_TO_GRAVEYARD &&
        !activating.has(e.cardUid) &&
        this.handSeatOf(e.cardUid) !== null
      ) {
        return true
      }
    }
    return false
  }

  /**
   * Uids that Flip Summon in this batch (reveal + stand to ATK). Battle flips
   * only emit CARD_FLIPPED and stay in Defense.
   */
  private flipSummonUids(events: GameEvent[]): Set<string> {
    const flipped = new Set<string>()
    const stood = new Set<string>()
    for (const e of events) {
      if (e.type === EventType.CARD_FLIPPED && e.faceUp) flipped.add(e.cardUid)
      if (e.type === EventType.POSITION_CHANGED && e.position === CardPosition.ATTACK) {
        stood.add(e.cardUid)
      }
    }
    const both = new Set<string>()
    for (const uid of flipped) {
      if (stood.has(uid)) both.add(uid)
    }
    return both
  }

  /**
   * The Stern Mystic: flip each Set card on the field with the same 3D
   * reveal used for Flip Summon, then leave them face-down in state.
   * Strictly sequential so the next card starts only after the previous
   * one has flipped back down.
   */
  private scheduleFieldPeekFlips(
    cards: { uid: string; cardId: string }[],
    startDelay: number,
  ): number {
    const gap = this.ms(220)
    const hold = this.ms(1000)
    const flipMs = this.ms(520)
    let offset = startDelay
    let lastEnd = startDelay
    for (const card of cards) {
      const pos = this.cardFieldPos(card.uid)
      if (!pos || !card.cardId) continue
      const end = this.showFlipAnimation(
        card.cardId,
        pos.x,
        pos.y,
        pos.defense,
        hold,
        offset,
        {
          scale: pos.scale,
          mine: this.cardIsMine(card.uid),
          flipMs,
          cardUid: card.uid,
        },
      )
      lastEnd = Math.max(lastEnd, end)
      offset = end + gap
    }
    return Math.max(0, lastEnd - startDelay)
  }

  /**
   * Schedules the reveal for a CARD_FLIPPED: SFX/VFX, the 3D flip, the camera
   * follow, and a Flip Effect destello when the card has one. Returns the time
   * the sequence finishes (absolute ms from now).
   */
  private scheduleFlipReveal(
    e: { cardUid: string; cardId: string; faceUp: boolean },
    startDelay: number,
    standUp: boolean,
    battle: boolean,
  ): number {
    const flipAt = this.cardFieldPos(e.cardUid)
    if (e.faceUp) {
      this.time.delayedCall(startDelay, () => {
        DuelSfx.play('flip')
        if (flipAt) {
          if (!this.playFoil('foilActivate', flipAt.x, flipAt.y, e.cardUid)) {
            this.playVfx('flip', flipAt.x, flipAt.y)
          }
        }
      })
    }
    const flipEnd = this.flashFlip(
      e.cardUid,
      e.cardId,
      e.faceUp,
      startDelay,
      standUp,
      battle,
    )
    if (e.faceUp) {
      const flipSeat = this.seatOfUid(e.cardUid)
      if (flipSeat !== null) {
        this.followOpponentReveal(flipSeat, e.cardId, startDelay)
      }
    }
    if (e.faceUp && this.cardHasFlipEffect(e.cardId)) {
      return Math.max(flipEnd, this.scheduleEffectFlash(e.cardUid, flipEnd))
    }
    return flipEnd
  }

  /** Plays the flip reveal for a CARD_FLIPPED event; returns its duration (ms). */
  private flashFlip(
    cardUid: string,
    cardId: string,
    faceUp: boolean,
    startDelay = 0,
    standUp = false,
    battle = false,
  ): number {
    if (!faceUp || !cardId) return 0
    // Uses the *current* (pre-resolution) snapshot to locate the card, so the
    // flip plays where the face-down monster still sits.
    const pos = this.cardFieldPos(cardUid)
    if (!pos) return 0
    // A revealed Set Spell/Trap is the player's only chance to read it (it may go
    // straight back face-down, e.g. Reaper of the Cards), so hold it much longer
    // than a monster flip, which simply stays face-up on the board afterwards.
    // A monster flipped by battle may still die right after, so it needs long
    // enough to be identified. Flip Summon stands into ATK in the same motion,
    // so the hold is only a short settle.
    const hold = standUp
      ? this.ms(160)
      : battle
        ? this.ms(420)
        : pos.zone === 'monsters'
          ? 650
          : 1400
    // Park the 3D reveal until commit (or a same-batch GY fly). A timed slot
    // mask expires while Stop Defense still flies to the GY, and the pre-commit
    // Set DEF sprite pops back under the already-stood ATK mesh.
    if (pos.zone === 'monsters') {
      this.revealedUids.add(cardUid)
      this.fieldLand.set(cardUid, {
        ...pos,
        cardId,
      })
      const gate = this.motionMinimal()
        ? undefined
        : this.ensureActivationHold(cardUid, () => undefined)
      if (!this.motionMinimal()) {
        const hide = (): void => this.leaveFieldForActivation(cardUid)
        if (startDelay > 0) this.time.delayedCall(startDelay, hide)
        else hide()
      }
      return this.showFlipAnimation(
        cardId,
        pos.x,
        pos.y,
        pos.defense,
        hold,
        startDelay,
        {
          standUp,
          holdGate: gate,
          scale: pos.scale,
          mine: this.cardIsMine(cardUid),
          cardUid,
        },
      )
    }
    return this.showFlipAnimation(
      cardId,
      pos.x,
      pos.y,
      pos.defense,
      hold,
      startDelay,
      {
        standUp,
        scale: pos.scale,
        mine: this.cardIsMine(cardUid),
        cardUid,
      },
    )
  }

  /** Shows an activated spell/trap; prefers the HTML overlay (above the HUD). */
  private flashActivation(
    cardId: string,
    kind: 'spell' | 'trap' | 'summon' = 'spell',
    foil: CardFoilId = 'common',
    turnMax = 0,
  ): void {
    if (this.activationCb) this.activationCb(cardId, kind, foil, turnMax)
    else this.showCardActivation(cardId, foil, turnMax)
  }

  /**
   * Briefly shows a spell/trap card large in the center of the screen when it is
   * activated, so the player can see it before it resolves and moves to the
   * graveyard. Drawn on the scene root (not the board layer) so re-renders don't
   * wipe it mid-animation.
   */
  private showCardActivation(
    cardId: string,
    foil: CardFoilId = 'common',
    turnMax = 0,
  ): void {
    if (!cardId) return
    const w = CARD_W * 2.1
    const h = CARD_H * 2.1
    const container = this.add.container(this.scale.width / 2, this.scale.height / 2).setDepth(1500)

    const glowColor =
      foil !== 'common' && foil in FOIL_GLOW
        ? FOIL_GLOW[foil as keyof typeof FOIL_GLOW]
        : 0x66ccff
    const glow = this.add
      .rectangle(0, 0, w + 14, h + 14, glowColor, 0.15)
      .setStrokeStyle(4, glowColor)
    container.add(glow)

    const key = textureKey(cardId)
    if (this.textures.exists(key)) {
      const face = this.add.image(0, 0, key).setDisplaySize(w, h)
      container.add(face)
      applyCardRarityFx(this, container, foil, w, h, face, cardId)
    } else {
      const data = CARD_INDEX.get(cardId)
      container.add(this.add.rectangle(0, 0, w, h, 0x1a2b4a).setStrokeStyle(2, 0x88aacc))
      container.add(
        this.add
          .text(0, 0, data?.name ?? cardId, { fontSize: '16px', color: '#ffffff', wordWrap: { width: w - 12 }, align: 'center' })
          .setOrigin(0.5),
      )
      this.loadTexture(cardId)
    }

    if (turnMax > 0) {
      const disc = this.add.circle(0, 0, 22, 0x000000, 0.78)
      disc.setStrokeStyle(3, 0xfacc15, 0.95)
      const label = this.add
        .text(0, 0, '1', {
          fontSize: '28px',
          fontStyle: 'bold',
          color: '#fde047',
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5)
      container.add(disc)
      container.add(label)
      const step = SPELL_OVERLAY_MS / turnMax
      for (let n = 2; n <= turnMax; n++) {
        this.time.delayedCall(step * (n - 1), () => {
          if (!label.active) return
          label.setText(String(n))
          label.setScale(0.45)
          this.tweens.add({ targets: label, scale: 1, duration: 180, ease: 'Back.easeOut' })
        })
      }
    }

    container.setScale(0.4).setAlpha(0)
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: container,
          alpha: 0,
          delay: 750,
          duration: 300,
          onComplete: () => container.destroy(),
        })
      },
    })
  }

  /**
   * Plays a face-down -> face-up flip on a monster at (x, y). Drawn on the
   * Three.js overlay above the board so re-renders don't wipe it; the caller
   * defers applying the resolved state until the flip finishes, so
   * damage/flip-effects are shown only after the card is revealed.
   * Returns the total duration in ms.
   */
  private showFlipAnimation(
    cardId: string,
    x: number,
    y: number,
    defense: boolean,
    hold = 380,
    startDelay = 0,
    opts: {
      standUp?: boolean
      holdGate?: { released: boolean }
      scale?: number
      mine?: boolean
      flipMs?: number
      cardUid?: string
    } = {},
  ): number {
    const standUp = opts.standUp ?? false
    const holdGate = opts.holdGate
    const scale = opts.scale ?? 1
    if (this.motionMinimal()) {
      const reveal = (): void => {
        if (opts.cardUid) {
          this.previewFace.set(opts.cardUid, {
            faceUp: true,
            defense: standUp ? false : defense,
          })
          this.renderAll()
        }
        this.followAction(x, y)
      }
      if (startDelay > 0) this.time.delayedCall(startDelay, reveal)
      else reveal()
      return startDelay + MINIMAL_BEAT_MS
    }
    const flipMs = opts.flipMs ?? (standUp ? this.ms(520) : 400)
    const start = (): void => {
      if (!holdGate) {
        const angle = defense ? 90 : 0
        this.coverSlot(x, y, angle, flipMs + hold, scale)
      }
      this.playVfx('flip', x, y)
      this.followAction(x, y)
      const flipAt = this.overlayNorm(x, y)
      const flipSize = this.overlaySize(CARD_W * scale, CARD_H * scale)
      getDuelVfx()?.flipReveal({
        x: flipAt.x,
        y: flipAt.y,
        cardW: flipSize.w,
        cardH: flipSize.h,
        cardId,
        backUrl: this.sleeveBackUrl(opts.mine !== false),
        foil: opts.cardUid ? this.foilOf(opts.cardUid) : undefined,
        defense,
        standUp,
        delay: 0,
        flip: flipMs / 1000,
        hold: hold / 1000,
        holdGate,
        worldTrack: {
          x,
          y,
          project: (wx, wy) =>
            this.sys?.isActive() ? this.overlayNorm(wx, wy) : { x: 0.5, y: 0.5 },
          cardSize: () =>
            this.sys?.isActive()
              ? this.overlaySize(CARD_W * scale, CARD_H * scale)
              : { w: flipSize.w, h: flipSize.h },
        },
      })
    }
    if (startDelay > 0) this.time.delayedCall(startDelay, start)
    else start()
    return startDelay + flipMs + hold
  }

  // Depths for the transient movement layer (above the board, below menus/priority).
  private static readonly MASK_DEPTH = 1340

  /** Covers a field slot with the board background so a moving copy has no static twin. */
  private maskSlot(
    x: number,
    y: number,
    angle = 0,
    scale = 1,
  ): Phaser.GameObjects.GameObject[] {
    const bg = this.add
      .rectangle(x, y, CARD_W, CARD_H, 0x0f172a, 1)
      .setDepth(NetworkDuelScene.MASK_DEPTH)
      .setAngle(angle)
      .setScale(scale)
    const frame = this.add
      .rectangle(x, y, CARD_W, CARD_H, 0x000000, 0.15)
      .setStrokeStyle(1, 0x335577)
      .setDepth(NetworkDuelScene.MASK_DEPTH)
      .setAngle(angle)
      .setScale(scale)
    return [bg, frame]
  }

  /**
   * Outbound / hold / round-trip times for an attack lunge. Always follows
   * the speed control so x1 / x2 / x3 change how fast the attacker moves.
   */
  private attackTiming(hasTarget: boolean): {
    outbound: number
    hold: number
    total: number
  } {
    if (this.motionMinimal()) {
      return { outbound: MINIMAL_BEAT_MS, hold: 0, total: MINIMAL_BEAT_MS }
    }
    const outbound = this.ms(hasTarget ? 320 : 340)
    const hold = this.ms(80)
    return { outbound, hold, total: outbound * 2 + hold }
  }

  /**
   * From the attacker to where the lunge should stop. `trail` ends at the
   * near edge of the defending card so the beam does not draw through it.
   */
  private attackPath(
    attackerUid: string,
    targetUid: string | null,
  ): {
    from: SlotPose & { defense: boolean }
    contact: { x: number; y: number; scale: number }
    trail: { x: number; y: number }
    hasTarget: boolean
  } | null {
    const from = this.cardFieldPos(attackerUid)
    if (!from) return null
    const target = targetUid ? this.cardFieldPos(targetUid) : null
    const attackerIsMine = !!this.state?.zones[this.mySeat].monsters.some(
      (c) => c?.uid === attackerUid,
    )
    const opp = (this.mySeat === 0 ? 1 : 0) as Seat
    const dest = target ?? this.directAttackPos(attackerIsMine ? opp : this.mySeat)
    const dx = dest.x - from.x
    const dy = dest.y - from.y
    const dist = Math.hypot(dx, dy) || 1
    const reach = target ? 0.9 : 0.92
    const contact = {
      x: from.x + dx * reach,
      y: from.y + dy * reach,
      scale: from.scale,
    }
    let trail = contact
    if (target) {
      const nx = dx / dist
      const ny = dy / dist
      const hw = ((target.defense ? CARD_H : CARD_W) / 2) * target.scale
      const hh = ((target.defense ? CARD_W : CARD_H) / 2) * target.scale
      const toEdge = Math.min(
        Math.abs(nx) < 1e-4 ? dist : hw / Math.abs(nx),
        Math.abs(ny) < 1e-4 ? dist : hh / Math.abs(ny),
      )
      const stop = Math.max(24, dist - toEdge - 6)
      trail = {
        x: from.x + nx * stop,
        y: from.y + ny * stop,
        scale: from.scale,
      }
    }
    return { from, contact, trail, hasTarget: !!target }
  }

  /**
   * Hit flash + particles at lunge contact (not after the attacker returns).
   */
  private playBattleImpact(
    attackerUid: string,
    targetUid: string | null,
    damageSeat: Seat | null,
  ): void {
    const hit = targetUid
      ? this.cardFieldPos(targetUid)
      : damageSeat != null
        ? this.directAttackPos(damageSeat)
        : this.cardFieldPos(attackerUid)
    const attr = this.attributeOf(attackerUid)
    const shake = this.attackShake(this.fieldAtk(attackerUid))
    DuelSfx.play('impact')
    if (shake.duration > 0) {
      this.cameras.main.shake(shake.duration, shake.intensity)
      this.screenShakeCb?.(shake.duration)
    }
    if (hit) this.playVfx('impact', hit.x, hit.y, { attribute: attr })
  }

  /** Bolt + beam aimed at the defender's front edge, not through the card. */
  private playAttackTrail(
    attackerUid: string,
    targetUid: string | null,
  ): void {
    const path = this.attackPath(attackerUid, targetUid)
    if (!path) return
    this.playVfx('attack', path.from.x, path.from.y, {
      x2: path.trail.x,
      y2: path.trail.y,
      attribute: this.attributeOf(attackerUid),
    })
  }

  /**
   * Coin dialog over the board. High quality is the 3D toss. Low quality is
   * the flat CSS spin and does not open a WebGL context. The Phaser clock
   * stays paused for the spin plus the result hold, armed before the first
   * frame. If that frame never reports, the pause still ends and the attack
   * continues.
   */
  private playCoinToss(
    event: { seat: Seat; cardId: string; results: ('heads' | 'tails')[] },
    startAt: number,
  ): number {
    const results = event.results
    const room = (this.client.roomCode ?? '').slice(0, 16)
    const card = event.cardId.slice(0, 16)
    const role = event.seat === this.mySeat ? 'call' : 'wait'
    const flat = useDuelPrefsStore().coinQuality === 'low'
    const open = (): void => {
      if (!this.sys?.isActive()) return
      if (results.length === 0) return
      trackNow('coin_open', { room, card, role, gl: flat ? 'css' : '3d' })
      const openedAt = performance.now()
      this.holdCoinClock()
      let released = false
      let deadline = 0
      let stuck = 0
      const release = (how: CoinDialogClose | 'deadline' | 'stuck'): void => {
        if (released) return
        released = true
        window.clearTimeout(deadline)
        window.clearTimeout(stuck)
        if (this.coinDeadlineRearm === rearm) this.coinDeadlineRearm = null
        const holdMs = Math.max(0, Math.round(performance.now() - openedAt))
        trackNow('coin_close', { room, card, how, holdMs })
        if (how === 'deadline' || how === 'stuck') {
          try {
            cancelDuelCoinToss()
          } catch {
            /* the dialog is already gone */
          }
        }
        this.releaseCoinClock()
      }
      const budget = coinPauseBudgetMs(results.length, this.motionMinimal(), (ms) =>
        this.aiPlayback ? ms : this.ms(ms),
      )
      // Armed before the first frame. The extra beat lets a toss that lands
      // on time close itself; this timer is only the one that cannot miss.
      const rearm = (): void => {
        if (released) return
        window.clearTimeout(deadline)
        window.clearTimeout(stuck)
        deadline = window.setTimeout(() => release('deadline'), Math.max(budget + 750, 1))
        stuck = window.setTimeout(() => release('stuck'), 8000)
      }
      this.coinDeadlineRearm = rearm
      rearm()
      const board = this.game.canvas?.parentElement
      const stage = board?.parentElement ?? board ?? document.body
      try {
        void playDuelCoinToss(stage, results, {
          reduced: this.motionMinimal(),
          flat,
          art: duelCoinArt(),
          scale: (ms) => (this.aiPlayback ? ms : this.ms(ms)),
          onDone: (how) => release(how),
        }).catch(() => {
          try {
            cancelDuelCoinToss()
          } catch {
            /* already closed */
          }
          release('done')
        })
      } catch {
        try {
          cancelDuelCoinToss()
        } catch {
          /* already closed */
        }
        release('done')
      }
    }
    if (startAt <= 0) open()
    else this.time.delayedCall(startAt, open)
    return results.length > 0 ? startAt + 80 : startAt
  }

  private holdCoinClock(): void {
    this.coinClockHolds += 1
    if (this.coinHeldSince === 0) this.coinHeldSince = performance.now()
    this.time.paused = true
    if (this.coinClockHolds === 1) this.noteCoinClockHeld()
  }

  private releaseCoinClock(): void {
    if (this.coinClockHolds <= 0) return
    this.coinClockHolds -= 1
    if (this.coinClockHolds === 0) this.coinHeldSince = 0
    if (this.coinClockHolds === 0 && this.sys?.isActive()) this.time.paused = false
    if (this.coinClockHolds === 0) this.noteCoinClockReleased()
  }

  /** Lunge toward a monster, or straight at the defending player's hand. */
  private animateAttack(attackerUid: string, targetUid: string | null): number {
    const path = this.attackPath(attackerUid, targetUid)
    const card = this.findCardByUid(attackerUid)
    if (!path || !card) return 0
    const { from, contact, hasTarget } = path
    const { outbound: DUR, hold: HOLD, total } = this.attackTiming(hasTarget)
    const shakeMs = this.attackShake(this.fieldAtk(attackerUid)).duration
    const angle = from.defense ? 90 : 0
    this.coverSlot(from.x, from.y, angle, total, from.scale)
    this.followAction(from.x, from.y, {
      restMs: total + FOCUS_HOME_HOLD_MS,
    })
    this.followAction(contact.x, contact.y, {
      delay: DUR + shakeMs,
      restMs: Math.max(0, total - DUR - shakeMs) + FOCUS_HOME_HOLD_MS,
    })
    this.flyOverlay(from, contact, {
      cardUid: attackerUid,
      cardId: card.cardId,
      mine: card.owner === this.mySeat,
      faceUp: card.faceUp,
      duration: DUR / 1000,
      startAngle: angle,
      endAngle: angle,
      yoyo: true,
      yoyoHold: HOLD / 1000,
      ease: 'sineInOut',
      lift: 0.55,
      startScale: 1,
      endScale: 1.06,
    })
    return total
  }

  /**
   * Red blink and a pointing hand on cards the opponent just chose.
   * Holds the rest of the batch so the mark is visible before the card moves.
   */
  private playOpponentTargetCue(uids: string[], startAt: number): number {
    const poses: { x: number; y: number; defense: boolean }[] = []
    for (const uid of uids) {
      const pose = this.chosenTargetPose(uid)
      if (pose) poses.push(pose)
    }
    if (poses.length === 0) return 0
    const beat = this.visualMs(1600)
    const seq = this.renderSeq
    const run = (): void => {
      if (seq !== this.renderSeq || !this.sys?.isActive()) return
      const first = poses[0]
      if (first) this.followAction(first.x, first.y, { restMs: beat })
      const vfx = getDuelVfx()
      for (const pose of poses) {
        const wrap = this.add.container(pose.x, pose.y)
        this.boardLayer.add(wrap)
        addEnemyTargetCue(this, wrap, pose.defense)
        const cardH = pose.defense ? CARD_W : CARD_H
        const handH = Math.max(30, Math.round(cardH * 0.4))
        const tipY = pose.y + handH * 0.16
        vfx?.showPointHand(
          () => {
            const at = this.overlayNorm(pose.x, tipY)
            return { x: at.x, y: at.y, h: this.overlaySize(1, handH).h }
          },
          beat / 1000,
        )
      }
    }
    if (startAt > 0) this.time.delayedCall(startAt, run)
    else run()
    return beat
  }

  /** Where a just-chosen target is sitting on the pre-resolution board. */
  private chosenTargetPose(
    uid: string,
  ): { x: number; y: number; defense: boolean } | null {
    const field = this.cardFieldPos(uid)
    if (field) return { x: field.x, y: field.y, defense: field.defense }
    if (!this.state) return null
    for (const seat of [0, 1] as Seat[]) {
      const hand = this.handScreenPos(seat, uid)
      if (hand) return { x: hand.x, y: hand.y, defense: false }
      const z = this.state.zones[seat]
      if (z.graveyard.some((c) => c.uid === uid)) {
        const p = this.graveyardPos(seat)
        return { x: p.x, y: p.y, defense: false }
      }
      if (z.deck.some((c) => c.uid === uid)) {
        const p = this.deckPos(seat)
        return { x: p.x, y: p.y, defense: false }
      }
      if (z.banished.some((c) => c.uid === uid)) {
        const p = this.banishedPos(seat)
        return { x: p.x, y: p.y, defense: false }
      }
    }
    return null
  }

  /**
   * Monster (or S/T) slides to another zone — Change of Heart, End Phase
   * control return. Pre-commit snapshot still has the card on `from`.
   */
  private scheduleFieldMove(
    e: Extract<GameEvent, { type: EventType.CARD_MOVED }>,
    startAt: number,
  ): number {
    const fromZone = e.from.zone
    const toZone = e.to.zone
    if (
      (fromZone !== 'monsters' && fromZone !== 'spellTraps') ||
      (toZone !== 'monsters' && toZone !== 'spellTraps')
    ) {
      return 0
    }
    const fromIndex = e.from.index ?? 0
    const toIndex = e.to.index ?? 0
    const from =
      this.cardFieldPos(e.cardUid) ??
      this.zoneSlotPos(e.from.seat, fromZone, fromIndex)
    const to = this.zoneSlotPos(e.to.seat, toZone, toIndex)
    if (Math.abs(from.x - to.x) < 4 && Math.abs(from.y - to.y) < 4) return 0
    const inst =
      this.findCardByUid(e.cardUid) ??
      this.findCardInState(this.pendingCommit, e.cardUid)
    const cardId = inst?.cardId ?? ''
    if (!cardId) return 0
    const defense =
      this.cardFieldPos(e.cardUid)?.defense ??
      inst?.position === CardPosition.DEFENSE
    const dur = this.flightMs(440)
    const run = (): void => {
      this.leaveFieldForActivation(e.cardUid)
      this.fieldLand.set(e.cardUid, { ...to, cardId })
      const gate = this.ensureMeshHold(e.cardUid)
      this.followFlight(from, to, dur)
      this.flyOverlay(from, to, {
        cardUid: e.cardUid,
        cardId,
        mine: inst ? this.sleeveMine(inst) : e.from.seat === this.mySeat,
        faceUp: inst?.faceUp ?? true,
        duration: dur / 1000,
        startAngle: defense ? 90 : 0,
        endAngle: defense ? 90 : 0,
        startScale: 1,
        endScale: 1,
        arc: 40 / this.scale.width,
        ease: 'sineInOut',
        lift: 0.5,
        holdGate: gate,
      })
      this.time.delayedCall(dur, () => {
        this.burstRing(to.x, to.y, 0xa78bfa, 14, 44, this.ms(220))
      })
    }
    if (startAt > 0) this.time.delayedCall(startAt, run)
    else run()
    return dur
  }

  /** Hand → monster / S/T flight length (same recipe as desktop). */
  private summonFlyMs(fromHand: boolean, defense: boolean): number {
    if (this.motionMinimal()) return MINIMAL_BEAT_MS
    if (this.aiPlayback) return 560
    return this.ms(defense || !fromHand ? 320 : 240)
  }

  /**
   * Slides a card onto a monster / S/T slot. Origin is the pre-commit location
   * (hand, GY, Extra, Banished), not always the summoner's hand.
   */
  private animateSummon(
    seat: Seat,
    cardUid: string,
    cardId: string,
    to: SlotPose,
    faceUp: boolean,
    defense = false,
    opts?: { holdGate?: { released: boolean }; origin?: SummonOrigin },
  ): number {
    const holdGate = opts?.holdGate
    const origin = opts?.origin ?? this.summonOrigin(seat, cardUid)
    const from: SlotPose = origin
    const fromHand = origin.from === 'hand'
    const dur = this.summonFlyMs(fromHand, defense)
    if (fromHand) {
      if (holdGate) this.leaveHandForActivation(cardUid)
      else this.coverSlot(from.x, from.y, 0, dur, from.scale)
    } else if (origin.from !== 'field') {
      this.leavePile(cardUid)
    }
    this.followFlight(from, to, dur)
    const flyer = this.findCardByUid(cardUid)
    this.flyOverlay(from, to, {
      cardUid,
      cardId,
      mine: flyer ? this.sleeveMine(flyer) : seat === this.mySeat,
      faceUp,
      duration: dur / 1000,
      startScale: 1,
      endScale: 1,
      startAngle: 0,
      peakAngle: defense ? 70 : 0,
      endAngle: defense ? 90 : 0,
      arc: 28 / this.scale.width,
      ease: 'quadOut',
      lift: 0.55,
      holdGate,
    })
    if (holdGate && this.activationHold.get(cardUid)?.gate === holdGate) {
      const seq = this.renderSeq
      this.time.delayedCall(dur, () => {
        if (seq !== this.renderSeq || !this.sys?.isActive()) return
        if (holdGate.released) return
        this.parkSlotStandIn(cardUid, to, faceUp, defense)
        holdGate.released = true
      })
    }
    return dur
  }

  /** Hand slot for `uid`, or the middle of that player's hand row. */
  private handOrigin(seat: Seat, uid: string): SlotPose {
    return (
      this.handScreenPos(seat, uid) ?? this.handAt(seat === this.mySeat ? 5 : 0, 2, 5)
    )
  }

  /** Sends a card from its field slot to the owner's graveyard pile. */
  private animateToGrave(
    cardUid: string,
    seat: Seat,
    fromOverride?: SlotLand,
    followCamera = true,
  ): number {
    const field = this.cardFieldPos(cardUid)
    const stored = this.activationLand.get(cardUid) ?? this.fieldLand.get(cardUid)
    const from = fromOverride
      ? { x: fromOverride.x, y: fromOverride.y, scale: fromOverride.scale }
      : field
        ? { x: field.x, y: field.y, scale: field.scale }
        : stored
          ? { x: stored.x, y: stored.y, scale: stored.scale }
          : undefined
    const card = this.findCardByUid(cardUid)
    const cardId =
      fromOverride?.cardId || stored?.cardId || card?.cardId || ''
    if (!from || !cardId) return 0
    this.dropSlotStandIn(cardUid)
    this.releaseActivationMesh(cardUid)
    this.leaveFieldForActivation(cardUid)
    const to = this.graveyardPos(seat)
    const DUR = this.flightMs(320)
    const activating = !!fromOverride || this.activationLand.has(cardUid)
    const angle = field?.defense ? 90 : 0
    this.followFlight(from, to, DUR, { camera: followCamera })
    this.flyOverlay(from, to, {
      cardUid,
      cardId,
      mine: seat === this.mySeat,
      faceUp: activating || !!card?.faceUp || this.revealedUids.has(cardUid),
      duration: DUR / 1000,
      startAngle: angle,
      endAngle: 0,
      startScale: 1,
      endScale: 0.7,
      startAlpha: 1,
      endAlpha: 0.25,
      arc: 36 / this.scale.width,
      ease: 'quadIn',
      lift: 0.45,
    })
    this.landOnGrave(cardUid, DUR)
    return DUR
  }

  /** Seat that still holds this uid in the Field Spell zone, or null. */
  private fieldSpellSeatOf(uid: string): Seat | null {
    if (!this.state) return null
    for (const seat of [0, 1] as Seat[]) {
      if (this.state.zones[seat].fieldSpell?.uid === uid) return seat
    }
    return null
  }

  /** Seat that still holds this uid in the pre-commit hand, or null. */
  private handSeatOf(uid: string): Seat | null {
    if (!this.state) return null
    for (const seat of [0, 1] as Seat[]) {
      if (this.state.zones[seat].hand.some((c) => c.uid === uid)) return seat
    }
    return null
  }

  /** Seat whose graveyard still holds this uid in the pre-commit snapshot. */
  private gySeatOf(uid: string): Seat | null {
    if (!this.state) return null
    for (const seat of [0, 1] as Seat[]) {
      if (this.state.zones[seat].graveyard.some((c) => c.uid === uid)) return seat
    }
    return null
  }

  /**
   * After a shatter, the card is gone — wisps carry it to the GY. The Phaser
   * pile sprite appears on commit when this flight ends.
   */
  private animateDestroyToGrave(cardUid: string, seat: Seat): number {
    const field = this.cardFieldPos(cardUid)
    const stored =
      this.activationLand.get(cardUid) ?? this.fieldLand.get(cardUid)
    let from: SlotPose | undefined
    if (field) from = { x: field.x, y: field.y, scale: field.scale }
    else if (stored) from = { x: stored.x, y: stored.y, scale: stored.scale }
    if (!from) return 0
    this.dropSlotStandIn(cardUid)
    this.releaseActivationMesh(cardUid)
    this.leaveFieldForActivation(cardUid)
    const to = this.graveyardPos(seat)
    const DUR = this.visualMs(DESTROY_SOUL_MS)
    this.followFlight(from, to, DUR)
    if (!this.overlaySoul(from, to, DUR / 1000)) {
      return this.animateToGrave(cardUid, seat, stored)
    }
    this.landOnGrave(cardUid, DUR)
    return DUR
  }

  /**
   * Hand → GY discard: hide the fan copy and send soul wisps to the pile.
   */
  private animateDiscardToGrave(
    cardUid: string,
    seat: Seat,
    from: SlotPose,
    followCamera = true,
  ): number {
    this.keptDiscardUids.delete(cardUid)
    this.leaveHandForActivation(cardUid)
    const to = this.graveyardPos(seat)
    const DUR = this.visualMs(DESTROY_SOUL_MS)
    this.followFlight(from, to, DUR, followCamera ? undefined : { camera: false })
    if (!this.overlaySoul(from, to, DUR / 1000)) {
      const card = this.findCardByUid(cardUid)
      const cardId = card?.cardId || ''
      if (!cardId) return DUR
      this.flyOverlay(from, to, {
        cardUid,
        cardId,
        mine: seat === this.mySeat,
        faceUp: seat === this.mySeat,
        duration: DUR / 1000,
        startScale: 1,
        endScale: 0.55,
        startAlpha: 1,
        endAlpha: 0.2,
        arc: 48 / this.scale.width,
        ease: 'quadIn',
        lift: 0.55,
      })
    }
    this.landOnGrave(cardUid, DUR)
    return DUR
  }

  /**
   * GY → Banished: drop the pile copy and send soul wisps to the Banished pile.
   */
  private animateBanishFromGrave(
    cardUid: string,
    toSeat: Seat,
    from: SlotPose,
  ): number {
    // Reveal on BAN as the soul leaves GY. departingPileUids only hides the GY
    // copy; banishedLive treats this uid as arrived.
    this.landedBanishedUids.add(cardUid)
    this.leavePile(cardUid)
    const to = this.banishedPos(toSeat)
    const DUR = this.visualMs(DESTROY_SOUL_MS)
    DuelSfx.play('banish')
    this.followFlight(from, to, DUR)
    if (!this.overlaySoul(from, to, DUR / 1000)) {
      const card =
        this.findCardInState(this.pendingCommit, cardUid) ??
        this.findCardByUid(cardUid)
      const cardId = card?.cardId || ''
      if (cardId) {
        this.flyOverlay(from, to, {
          cardUid,
          cardId,
          mine: this.cardIsMine(cardUid),
          faceUp: true,
          duration: DUR / 1000,
          startScale: 0.7,
          endScale: 0.45,
          startAlpha: 1,
          endAlpha: 0.2,
          arc: 40 / this.scale.width,
          ease: 'quadIn',
          lift: 0.5,
        })
      }
    }
    return DUR
  }

  /**
   * Deck pile → one card into the hand.
   *
   * Multi-draw effects (Pot of Greed, any future "draw 2+") emit several
   * CARD_DRAWN events in a single payload. Callers must pass `startAt` as the
   * end of the previous draw (see `drawChainMs` in flashEvents). Starting two
   * flights at the same `startAt` is a bug — the cards must leave the deck
   * one at a time, after the previous one has landed.
   *
   * @returns The timestamp (ms from now) when the next draw is allowed to start.
   */
  private animateDraw(
    seat: Seat,
    cardUid: string,
    cardId: string | null,
    startAt = 0,
  ): number {
    const mine = seat === this.mySeat
    const reveal = mine && !!cardId
    const minimal = this.motionMinimal()
    const fly = minimal ? MINIMAL_BEAT_MS : this.ms(DRAW_FLY_MS)
    const settle = minimal ? 0 : this.ms(DRAW_SETTLE_MS)
    const beat = minimal ? 0 : this.ms(DRAW_BEAT_MS)
    const hold = minimal ? 0 : Math.max(50, this.ms(DRAW_HOLD_MS))
    if (!this.arrivalStillDue(cardUid)) {
      this.forgetArrivingHand(cardUid)
      this.destroyHandSprite(cardUid)
      this.handDisplayX.delete(cardUid)
      return startAt
    }
    this.time.delayedCall(startAt, () => {
      if (!this.arrivalStillDue(cardUid)) {
        this.forgetArrivingHand(cardUid)
        this.destroyHandSprite(cardUid)
        this.handDisplayX.delete(cardUid)
        return
      }
      const pile = this.deckPos(seat)
      const from = isDuelCompactNow()
        ? {
            ...pile,
            x: pile.x + DRAW_START_NUDGE_X,
            y: pile.y + DRAW_START_NUDGE_Y,
          }
        : pile
      this.arrivingHandUids.add(cardUid)
      this.arrivingHandSeat.set(cardUid, seat)
      this.arrivingHandCardId.set(cardUid, cardId ?? '')
      this.relayoutHand(seat)
      const to = this.drawLandingPos(seat, cardUid)
      DuelSfx.play('draw')
      this.followDrawCamera(to, hold + fly)
      this.playVfx('draw', from.x, from.y)
      this.flyOverlay(from, to, {
        cardUid,
        cardId,
        mine,
        faceUp: false,
        reveal,
        duration: fly / 1000,
        settle: settle / 1000,
        startHold: hold / 1000,
        startScale: 1,
        endScale: 1,
        startAngle: 0,
        peakAngle: mine ? 8 : -8,
        endAngle: 0,
        arc: 40 / this.scale.width,
        ease: 'sineInOut',
        ghosts: !isDuelCompactNow(),
        lift: 0.7,
      })
      this.time.delayedCall(hold + fly, () => this.playVfx('draw', to.x, to.y))
    })
    const tail = minimal ? 0 : this.ms(60)
    this.time.delayedCall(startAt + hold + fly + settle + tail, () => {
      if (!this.arrivingHandUids.has(cardUid)) return
      if (!this.arrivalStillDue(cardUid)) {
        this.forgetArrivingHand(cardUid)
        this.destroyHandSprite(cardUid)
        this.handDisplayX.delete(cardUid)
        if (seat === 0 || seat === 1) this.relayoutHand(seat)
        return
      }
      this.landedHandUids.add(cardUid)
      this.tryRevealHandCard(cardUid)
    })
    return startAt + hold + fly + settle + beat
  }

  /** Where a flying draw should land, using the reserved fan (gap included). */
  private drawLandingPos(seat: Seat, uid: string): SlotPose {
    const raw = this.state?.zones[seat].hand ?? []
    const hand = raw.filter((c) => !this.departingHandUids.has(c.uid))
    const extras = this.extraArrivingUids(seat, hand)
    const inHand = hand.findIndex((c) => c.uid === uid)
    const length = Math.max(1, hand.length + extras.length)
    let index = inHand
    if (index < 0) {
      const extraAt = extras.indexOf(uid)
      index = extraAt === -1 ? length - 1 : hand.length + extraAt
    }
    const row = seat === this.mySeat ? 5 : 0
    return this.handAt(row, index, length)
  }

  private clearArrivingHands(): void {
    this.arrivingHandUids.clear()
    this.arrivingHandSeat.clear()
    this.arrivingHandCardId.clear()
    this.landedHandUids.clear()
  }

  /**
   * Shows a drawn card in the Phaser hand after the 3D flight has finished.
   * Before the engine snapshot commits, the card is painted as a reserved extra.
   */
  private tryRevealHandCard(uid: string): void {
    if (!this.arrivingHandUids.has(uid) || !this.landedHandUids.has(uid)) return
    if (!this.state) return
    const seat = this.arrivingHandSeat.get(uid)
    const inHand = this.handKeepsUid(this.state, uid)
    if (!inHand && !this.arrivalStillDue(uid)) {
      this.forgetArrivingHand(uid)
      this.destroyHandSprite(uid)
      this.handDisplayX.delete(uid)
      if (seat === 0 || seat === 1) this.relayoutHand(seat)
      return
    }
    if (inHand) {
      this.forgetArrivingHand(uid)
      // commitBoard already drew this card. Another full rebuild here
      // stacked on the turn-change camera and stalled the phone.
      // An arrival placeholder is not clickable; that one has to be replaced
      // or the returned card stays in the fan and cannot be summoned.
      const sprite = this.handSprites.get(uid)
      if (sprite?.input?.enabled) return
      if (sprite) this.destroyHandSprite(uid)
    }
    if (seat === 0 || seat === 1) this.relayoutHand(seat)
  }

  private animateToPile(cardUid: string, to: SlotPose): number {
    const from = this.cardFieldPos(cardUid)
    const card = this.findCardByUid(cardUid)
    if (!from || !card) return 0
    const DUR = this.flightMs(300)
    const angle = from.defense ? 90 : 0
    this.coverSlot(from.x, from.y, angle, DUR, from.scale)
    this.followFlight(from, to, DUR)
    this.flyOverlay(from, to, {
      cardUid,
      cardId: card.cardId,
      mine: card.owner === this.mySeat,
      faceUp: card.faceUp,
      duration: DUR / 1000,
      startAngle: angle,
      endAngle: 0,
      startScale: 1,
      endScale: 0.55,
      startAlpha: 1,
      endAlpha: 0.2,
      arc: 24 / this.scale.width,
      ease: 'quadIn',
      lift: 0.4,
    })
    return DUR
  }

  /**
   * Field → hand (Hane-Hane, Wall of Illusion) or Graveyard → hand
   * (Magician of Faith, Mask of Darkness, and any later card that uses
   * the same event). Leaves the slot or the pile immediately and lands
   * in a reserved fan gap, like a draw.
   *
   * @returns Duration until the next event in this batch may start.
   */
  /**
   * Sword of Deep-Seated leaves the Graveyard and lands on the Deck.
   * The face stays up during the flight so the card is readable.
   */
  /** Yado Karu: the hand card flies into the Deck. The last one is the bottom. */
  private animateHandToDeck(cardUid: string, seat: Seat, startAt = 0): number {
    const sprite = this.handSprites.get(cardUid)
    const card =
      this.findCardByUid(cardUid) ??
      this.findCardInState(this.pendingCommit, cardUid)
    const cardId = card?.cardId ?? ''
    if (!cardId) return 0
    const from = sprite
      ? { x: sprite.x, y: sprite.y }
      : {
          x: this.scale.width / 2,
          y: this.rowY(seat === this.mySeat ? 5 : 0),
        }
    const to = this.deckPos(seat)
    const minimal = this.motionMinimal()
    const fly = minimal ? MINIMAL_BEAT_MS : this.ms(420)
    const play = (): void => {
      this.leaveHandForActivation(cardUid)
      DuelSfx.play('toHand')
      this.followFlight(from, to, fly)
      this.flyOverlay(from, to, {
        cardUid,
        cardId,
        mine: seat === this.mySeat,
        faceUp: true,
        duration: fly / 1000,
        startScale: sprite?.scaleX ?? 0.7,
        endScale: 0.42,
        startAlpha: 1,
        endAlpha: 0.15,
        arc: 48 / this.scale.width,
        ease: 'sineIn',
        lift: 0.7,
        ghosts: true,
      })
    }
    if (startAt > 0) this.time.delayedCall(startAt, play)
    else play()
    return fly + (minimal ? 0 : this.ms(50))
  }

  private animateGraveToDeck(cardUid: string, seat: Seat, startAt = 0): number {
    const card =
      this.findCardByUid(cardUid) ??
      this.findCardInState(this.pendingCommit, cardUid)
    const cardId = card?.cardId ?? ''
    if (!cardId) return 0
    const from = this.graveyardPos(seat)
    const to = this.deckPos(seat)
    const minimal = this.motionMinimal()
    const fly = minimal ? MINIMAL_BEAT_MS : this.ms(560)
    const play = (): void => {
      this.leavePile(cardUid)
      DuelSfx.play('toHand')
      this.followFlight(from, to, fly)
      this.flyOverlay(from, to, {
        cardUid,
        cardId,
        mine: seat === this.mySeat,
        faceUp: true,
        duration: fly / 1000,
        startScale: 0.9,
        endScale: 0.48,
        startAlpha: 1,
        endAlpha: 0.2,
        arc: 56 / this.scale.width,
        ease: 'sineInOut',
        lift: 0.85,
        ghosts: true,
      })
    }
    if (startAt > 0) this.time.delayedCall(startAt, play)
    else play()
    return fly + (minimal ? 0 : this.ms(80))
  }

  private animateReturnToHand(
    cardUid: string,
    seat: Seat,
    startAt = 0,
  ): number {
    const fieldFrom = this.cardFieldPos(cardUid)
    const gySeat = fieldFrom ? null : this.gySeatOf(cardUid)
    const card =
      this.findCardByUid(cardUid) ?? this.findCardInState(this.pendingCommit, cardUid)
    if (!card || (!fieldFrom && gySeat === null)) return 0
    if (!this.arrivalStillDue(cardUid)) {
      this.forgetArrivingHand(cardUid)
      this.destroyHandSprite(cardUid)
      this.handDisplayX.delete(cardUid)
      return startAt
    }
    const from = fieldFrom ?? { ...this.graveyardPos(gySeat!), defense: false }
    const minimal = this.motionMinimal()
    const hold = minimal ? 0 : Math.max(50, this.ms(DRAW_HOLD_MS))
    const fly = minimal ? MINIMAL_BEAT_MS : this.ms(DRAW_FLY_MS)
    const settle = minimal ? 0 : this.ms(DRAW_SETTLE_MS)
    const beat = minimal ? 0 : this.ms(DRAW_BEAT_MS)
    const flightMs = hold + fly
    const angle = from.defense ? 90 : 0
    const mine = seat === this.mySeat
    const stored = this.activationLand.get(cardUid) ?? this.fieldLand.get(cardUid)
    const cardId = stored?.cardId || card.cardId || ''
    const revealed = gySeat !== null || !!card.faceUp || this.revealedUids.has(cardUid)
    const play = (): void => {
      if (!this.arrivalStillDue(cardUid)) {
        this.forgetArrivingHand(cardUid)
        this.destroyHandSprite(cardUid)
        this.handDisplayX.delete(cardUid)
        return
      }
      if (gySeat !== null) this.leavePile(cardUid)
      else this.leaveFieldForActivation(cardUid)
      this.arrivingHandUids.add(cardUid)
      this.arrivingHandSeat.set(cardUid, seat)
      this.arrivingHandCardId.set(cardUid, cardId)
      this.renderAll()
      const to = this.drawLandingPos(seat, cardUid)
      this.followReturnToHand(from, to, seat, flightMs)
      this.flyOverlay(from, to, {
        cardUid,
        cardId,
        mine,
        faceUp: revealed,
        reveal: !revealed && mine && !!cardId,
        duration: fly / 1000,
        settle: settle / 1000,
        startHold: hold / 1000,
        startAngle: angle,
        peakAngle: mine ? 8 : -8,
        endAngle: 0,
        startScale: gySeat !== null ? 0.72 : 1,
        endScale: 1,
        arc: (gySeat !== null ? 64 : 48) / this.scale.width,
        ease: 'sineInOut',
        ghosts: true,
        lift: gySeat !== null ? 0.85 : 0.7,
      })
    }
    if (startAt > 0) this.time.delayedCall(startAt, play)
    else play()
    const tail = minimal ? 0 : this.ms(60)
    this.time.delayedCall(startAt + flightMs + settle + tail, () => {
      if (!this.arrivingHandUids.has(cardUid)) return
      if (!this.arrivalStillDue(cardUid)) {
        this.forgetArrivingHand(cardUid)
        this.destroyHandSprite(cardUid)
        this.handDisplayX.delete(cardUid)
        this.relayoutHand(seat)
        return
      }
      this.landedHandUids.add(cardUid)
      this.tryRevealHandCard(cardUid)
    })
    return flightMs + settle + beat
  }

  private animatePositionChange(
    cardUid: string,
    position: CardPosition,
    holdUntilCommit = false,
  ): number {
    const from = this.cardFieldPos(cardUid)
    const card = this.findCardByUid(cardUid)
    if (!from || !card) return 0
    const DUR = this.flightMs(180)
    this.followAction(from.x, from.y)
    const startAngle = from.defense ? 90 : 0
    const endAngle = position === CardPosition.DEFENSE ? 90 : 0
    const hold = holdUntilCommit && !this.motionMinimal()
    const gate = hold ? this.ensureMeshHold(cardUid) : undefined
    if (hold) this.leaveFieldForActivation(cardUid)
    else this.coverSlot(from.x, from.y, startAngle, DUR, from.scale)
    this.flyOverlay(from, from, {
      cardUid,
      cardId: card.cardId,
      mine: card.owner === this.mySeat,
      faceUp: card.faceUp,
      duration: DUR / 1000,
      startAngle,
      peakAngle: (startAngle + endAngle) / 2,
      endAngle,
      ease: 'sineInOut',
      lift: 0.55,
      ...(gate ? { holdGate: gate } : {}),
    })
    return DUR
  }

  private burstRing(x: number, y: number, color: number, fromR: number, toR: number, duration: number): void {
    if (this.motionMinimal()) return
    const ring = this.add.graphics().setDepth(1380)
    const draw = (r: number, alpha: number) => {
      ring.clear()
      ring.lineStyle(3, color, alpha)
      ring.strokeCircle(x, y, r)
    }
    draw(fromR, 0.9)
    const proxy = { r: fromR, a: 0.9 }
    this.tweens.add({
      targets: proxy,
      r: toR,
      a: 0,
      duration,
      ease: 'Quad.easeOut',
      onUpdate: () => draw(proxy.r, proxy.a),
      onComplete: () => ring.destroy(),
    })
  }

  private flashWhite(x: number, y: number, scale = 1): void {
    if (this.motionMinimal()) return
    const flash = this.add
      .rectangle(x, y, CARD_W + 6, CARD_H + 6, 0xffffff, 0.75)
      .setDepth(1370)
      .setScale(scale)
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: this.ms(160),
      onComplete: () => flash.destroy(),
    })
  }

  /** Bright white star destello on a card that just activated an effect. */
  private playEffectFlash(x: number, y: number, scale = 1): void {
    this.playVfx('effectFlash', x, y, { scale })
  }

  /** True when this card has a Flip Effect (destello on reveal, not on resolve). */
  private cardHasFlipEffect(cardId: string): boolean {
    registerEngineEffects()
    return engineEffects.has(Number(cardId), EngineEffectTrigger.FLIP)
  }

  /**
   * Destello on the card at `atMs`. Returns when that flash ends so the target
   * prompt (commitBoard) does not open over it.
   */
  private scheduleEffectFlash(
    uid: string,
    atMs: number,
    sfx: 'spell' | 'trap' = 'spell',
  ): number {
    const dur = this.visualMs(EFFECT_FLASH_MS)
    this.time.delayedCall(atMs, () => {
      const pos =
        this.cardFieldPos(uid) ??
        this.fieldLand.get(uid) ??
        this.activationLand.get(uid)
      if (!pos) return
      DuelSfx.play(sfx)
      this.playEffectFlash(pos.x, pos.y, pos.scale)
    })
    return atMs + dur
  }

  /**
   * Rolls the Vue LP digits and bursts beside that plate. Direct attacks
   * lunge at the hand; this flash stays on the gutter numbers.
   */
  private playLifeChange(
    seat: Seat,
    delta: number,
    lifePoints: number,
    shake = true,
    plan?: { step: number; duration: number },
  ): void {
    const mine = seat === this.mySeat
    const pos = this.lifePos(seat)
    const hit = delta < 0
    const from = mine ? this.displayedMyLp : this.displayedOppLp
    const roll = plan ?? this.lpRollPlan(Math.abs(lifePoints - from))
    this.lpPunchCb?.(mine, roll.duration)
    this.pushLpHud(
      mine,
      from,
      hit ? 'hit' : 'heal',
      delta,
    )
    if (hit && shake && !this.animationsReduced()) {
      const burst = this.ms(200)
      this.cameras.main.shake(burst, 0.012)
      this.screenShakeCb?.(burst)
    }
    if (!isDuelCompactNow()) {
      this.playVfx(hit ? 'lpHit' : 'lpHeal', pos.x, pos.y)
    }
    this.tweenLp(mine, lifePoints, roll)
  }

  private showGameOver(over: GameOverPayload): void {
    const winner = over.winner
    if (over.winReason !== 'exodia') {
      DuelSfx.play(winner === this.mySeat ? 'win' : 'lose')
    }
    // Prefer the shared Vue GameOverOverlay when wired; fall back to a canvas overlay.
    if (this.gameOverCb) {
      this.gameOverCb(over)
      return
    }
    const won = winner === this.mySeat
    const x = this.scale.width / 2
    const y = this.scale.height / 2
    const overlay = this.add.rectangle(x, y, this.scale.width, this.scale.height, 0x000000, 0.7).setDepth(2000)
    const text = this.add
      .text(x, y - 20, won ? t('duel.youWin') : winner === null ? t('duel.gameOver') : t('duel.youLose'), {
        fontSize: '48px',
        color: won ? '#4ade80' : '#f87171',
      })
      .setOrigin(0.5)
      .setDepth(2001)
    const back = this.makeButton(x, y + 50, t('duel.backToMenu'), () => {
      window.location.href = '/'
    })
    back.setDepth(2001)
    void overlay
    void text
  }

  // ---------------------------------------------------------------------------
  // Small helpers
  // ---------------------------------------------------------------------------

  private send(action: PlayerAction): boolean {
    if (this.isWatching()) return false
    if (action.type === ActionType.ADVANCE_PHASE) this.clearTributePick(true)
    this.actionRejected = false
    this.client.send(action)
    return !this.actionRejected
  }

  /**
   * Same guards as GameEngine.flipSummon: Set this turn cannot flip yet.
   */
  private canFlipSummon(card: CardInstance): boolean {
    return !card.faceUp && !card.summonedThisTurn
  }

  /**
   * Same guards as GameEngine.changePosition: summoned / already switched /
   * already declared an attack (including a cancelled Replay) / Dragon Capture Jar.
   */
  private canChangePosition(card: CardInstance): boolean {
    if (!card.faceUp) return false
    if (
      card.summonedThisTurn ||
      card.positionChangedThisTurn ||
      card.attackedThisTurn ||
      card.declaredAttackThisTurn
    ) {
      return false
    }
    return !this.isDragonLocked(card)
  }

  /** True while a face-up Dragon Capture Jar locks this Dragon in Defense. */
  private isDragonLocked(card: CardInstance): boolean {
    if (!this.state) return false
    if (CARD_INDEX.get(card.cardId)?.race !== 'Dragon') return false
    return this.state.zones.some((z) =>
      z.spellTraps.some((c) => c && c.faceUp && Number(c.cardId) === 50045299),
    )
  }

  /**
   * Mirrors `onHandCardClick`: glow only when that tap would open a real action
   * (summon, set, activate, or the end-of-turn discard).
   *
   * Do not wait for eventsPlaying to clear. The committed board is drawn while
   * that flag is still set, and nothing renders again when it drops — so the
   * halo would stay missing until a window switch redraws the idle board.
   */
  private handHasAction(card: CardInstance): boolean {
    if (this.isWatching() || !this.state) return false
    if (this.clickMode !== 'idle') return false
    if (isWaitingOnOpponent(this.state, this.mySeat)) return false
    if (this.state.pendingDiscard?.seat === this.mySeat) return true
    if (this.state.pendingFlipEffect?.chooser === this.mySeat) return false
    if (this.state.pendingTargets?.chooser === this.mySeat) return false
    const data = CARD_INDEX.get(card.cardId)
    const kind = data ? getCardKind(data) : CardKind.MONSTER
    if (!this.isMyMain() && !(kind === CardKind.MONSTER && this.canSummonNow())) {
      return false
    }
    if (kind === CardKind.MONSTER) {
      if (Number(card.cardId) === GATE_GUARDIAN) return this.canGateSummon()
      if (mothEvolveTurn(card.cardId) !== null) return this.canEvolveMoth(Number(card.cardId))
      if (Number(card.cardId) === COCOON_OF_EVOLUTION && this.canEquipCocoon()) return true
      if (cannotNormalSummon(card.cardId)) return false
      if (this.canIgniteThunder(card)) return true
      if (!this.canSummonNow()) return false
      const level = data?.level ?? 0
      if (level >= 5) {
        const need = level >= 7 ? 2 : 1
        return this.tributeCandidateUids().length >= need
      }
      return this.firstFree('monsters', this.mySeat) !== -1
    }
    if (this.isFieldSpell(card.cardId)) return true
    return this.firstFree('spellTraps', this.mySeat) !== -1
  }

  /**
   * Mirrors `onFieldCardClick` for our own cards: flip, change position,
   * attack, a coin ignition, or an activatable spell/trap.
   */
  private fieldHasAction(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
  ): boolean {
    if (this.isWatching() || !this.state) return false
    if (seat !== this.mySeat) return false
    if (this.clickMode !== 'idle') return false
    if (this.state.pendingFlipEffect?.chooser === this.mySeat) return false
    if (this.state.pendingTargets?.chooser === this.mySeat) return false
    if (this.priorityActivatable(card.uid)) return true
    if (zone === 'monsters') {
      if (this.canShowAttackSword(card, seat, zone)) return true
      if (!this.isMyMain()) return false
      return (
        this.canFlipSummon(card) ||
        this.canChangePosition(card) ||
        this.canIgniteCoinEffect(card) ||
        this.canIgniteTribute(card)
      )
    }
    return this.isActivatable(card.uid)
  }

  /**
   * Thunder Dragon in hand, only while the engine lists it as activatable.
   * That list is empty when the Deck has no copy left.
   */
  private canIgniteThunder(card: CardInstance): boolean {
    if (!this.state || !this.isMyMain() || this.clickMode !== 'idle') return false
    if (isWaitingOnOpponent(this.state, this.mySeat)) return false
    if (handIgnitionOf(card.cardId) !== 'thunderDragon') return false
    return this.isActivatable(card.uid)
  }

  /**
   * Time Wizard and Barrel Dragon in your Main Phase, while the ignition
   * is still legal. Jirai Gumo is not an ignition: it glows via the attack
   * sword when it can declare an attack.
   */
  private canIgniteCoinEffect(card: CardInstance, duringPick = false): boolean {
    const state = this.state
    if (!state || !this.isMyMain()) return false
    if (!duringPick && this.clickMode !== 'idle') return false
    if (!card.faceUp || card.ignitionUsedThisTurn) return false
    if (state.pendingCoinCall || state.pendingChoice || state.pendingChainOrder || state.pendingTargets || state.pendingPriority) return false
    if (state.chain.length > 0) return false
    const kind = coinIgnitionOf(card.cardId)
    if (!kind) return false
    if (kind === 'barrel') {
      const opp = (this.mySeat === 0 ? 1 : 0) as Seat
      return state.zones[opp].monsters.some((monster: CardInstance | null) => monster != null)
    }
    return true
  }

  /**
   * Cannon Soldier, Catapult Turtle, and The Little Swordsman of Aile.
   * Your Main Phase, while a legal Tribute is available.
   */
  private canIgniteTribute(card: CardInstance, duringPick = false): boolean {
    const state = this.state
    if (!state || !this.isMyMain()) return false
    if (!duringPick && this.clickMode !== 'idle') return false
    if (!card.faceUp) return false
    if (state.pendingCoinCall || state.pendingChoice || state.pendingChainOrder || state.pendingTargets || state.pendingPriority) return false
    if (state.chain.length > 0) return false
    const kind = tributeIgnitionOf(card.cardId)
    if (!kind) return false
    return tributeCandidateUids(state, CARD_INDEX, this.mySeat, card.uid, kind).length > 0
  }

  /**
   * Glow for a card the player may (or already did) pick: tribute leftovers,
   * spell/flip targets, or attack targets.
   */
  private targetGlowKind(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
  ): TargetGlowKind | null {
    if (this.clickMode === 'selectCocoonHost') {
      if (zone !== 'monsters' || !card.faceUp || Number(card.cardId) !== PETIT_MOTH) return null
      return 'selectable'
    }
    if (this.clickMode === 'selectTribute') {
      if (this.pendingTribute?.gate) {
        if (zone !== 'monsters' || seat !== this.mySeat || !isGatePiece(card.cardId)) return null
        return this.tributeSelected.includes(card.uid) ? null : 'selectable'
      }
      const own = zone === 'monsters' && seat === this.mySeat
      const soul = zone === 'monsters' && this.isSoulTribute(card.uid)
      const picked = this.tributeSelected.includes(card.uid)
      return (own || soul) && !picked ? 'selectable' : null
    }
    if (this.targetPicked.includes(card.uid)) return 'picked'
    if (this.legalEngineFieldTarget(card.uid)) return 'selectable'
    if (this.clickMode === 'selectAttackTarget' && zone === 'monsters' && seat !== this.mySeat) {
      const forced = this.state ? magnetismTargets(this.state, seat) : []
      if (forced.length > 0 && !forced.some((m) => m.uid === card.uid)) return null
      return 'selectable'
    }
    const flip = this.state?.pendingFlipEffect
    if (flip?.chooser === this.mySeat && flip.targetUids.includes(card.uid)) {
      return 'selectable'
    }
    if (this.opponentMarkedTarget(card.uid)) return 'alert'
    return null
  }

  /**
   * A card the opponent is choosing, or already chose, as an effect target.
   * The outline is red so it is not the blue glow of your own selection.
   */
  private opponentMarkedTarget(uid: string): boolean {
    const state = this.state
    if (!state) return false
    const flip = state.pendingFlipEffect
    if (flip && flip.chooser !== this.mySeat && flip.targetUids.includes(uid)) return true
    const pending = state.pendingTargets
    if (
      pending &&
      pending.chooser !== this.mySeat &&
      pending.candidateUids.includes(uid)
    ) {
      return true
    }
    return state.chain.some(
      (link) => link.controller !== this.mySeat && link.targetUids.includes(uid),
    )
  }

  /** True when this monster should show the Battle-Phase attack sword. */
  private canShowAttackSword(
    card: CardInstance,
    seat: Seat,
    zone: 'monsters' | 'spellTraps',
  ): boolean {
    const state = this.state
    if (!state || zone !== 'monsters') return false
    if (state.phase !== EnginePhase.BATTLE) return false
    if (state.turnPlayer !== seat) return false
    if (isOpeningTurn(state)) return false
    if (state.zones[seat].attackLockTurns > 0) return false
    if (attackLockCode(state, seat, card)) return false
    return (
      card.faceUp &&
      card.position === CardPosition.ATTACK &&
      !card.attackedThisTurn
    )
  }

  private isMyMain(): boolean {
    return (
      !!this.state &&
      this.state.turnPlayer === this.mySeat &&
      (this.state.phase === EnginePhase.MAIN_1 || this.state.phase === EnginePhase.MAIN_2)
    )
  }

  /**
   * Paid Ultimate Offering still owes a summon, and this monster tap cannot
   * do it. That is the hand staying locked after the 500 LP.
   */
  private noteOfferingHandFault(kind: CardKind): void {
    if (kind !== CardKind.MONSTER) return
    const state = this.state
    if (!state) return
    if (state.pendingOfferingSummon !== this.mySeat) return
    if (state.zones[this.mySeat].extraNormalSummons <= 0) return
    if (this.canSummonNow()) return
    this.offeringHandFaultCb?.()
  }

  /**
   * Normal Summon/Set: own Main Phase, or the opponent's Battle Phase when
   * Ultimate Offering granted an extra summon. A declared attack stays on
   * the state while that window is open; it must not hide the hand. The
   * engine still accepts the summon, and a new monster replays the attack.
   */
  private canSummonNow(): boolean {
    if (!this.state) return false
    if (
      this.state.pendingPriority ||
      this.state.pendingChainOrder ||
      this.state.chain.length > 0 ||
      this.state.pendingTargets ||
      this.state.pendingFlipEffect
    ) {
      return false
    }
    const z = this.state.zones[this.mySeat]
    const myTurn = this.state.turnPlayer === this.mySeat
    const phase = this.state.phase
    const main =
      phase === EnginePhase.MAIN_1 || phase === EnginePhase.MAIN_2
    if (myTurn && main) {
      return !z.hasNormalSummoned || z.extraNormalSummons > 0
    }
    return (
      !myTurn &&
      phase === EnginePhase.BATTLE &&
      this.state.battleStep !== 'DAMAGE' &&
      z.extraNormalSummons > 0
    )
  }

  /** Skip the remaining Ultimate Offering summons on the opponent's Battle. */
  passOfferingSummon(): void {
    if (this.state?.pendingOfferingSummon !== this.mySeat) return
    this.send({ type: ActionType.ADVANCE_PHASE })
  }

  /** Pay 500 LP on the face-up Ultimate Offering from the prompt button. */
  payOffering(cardUid: string): void {
    const state = this.state
    if (!state || state.pendingOfferingSummon !== this.mySeat) return
    if (state.zones[this.mySeat].extraNormalSummons > 0) return
    this.send({ type: ActionType.ACTIVATE_TRAP, cardUid })
  }

  /** Paint a card on the GY pile after its flight lands. */
  private landOnGrave(cardUid: string, afterMs: number): void {
    this.time.delayedCall(afterMs, () => {
      this.landedGraveUids.add(cardUid)
      if (this.sys?.isActive()) this.renderAll()
    })
  }

  /** Legs of the table riffle, already scaled by AnimationSpeed. */
  private deckShuffleTiming(): {
    lift: number
    split: number
    riffle: number
    bridge: number
    drop: number
    total: number
  } {
    if (this.motionMinimal()) {
      return { lift: 0, split: 0, riffle: 0, bridge: 0, drop: 0, total: MINIMAL_BEAT_MS }
    }
    const lift = this.ms(DECK_SHUFFLE_LIFT_MS)
    const split = this.ms(DECK_SHUFFLE_SPLIT_MS)
    const riffle = this.ms(DECK_SHUFFLE_RIFFLE_MS)
    const bridge = this.ms(DECK_SHUFFLE_BRIDGE_MS)
    const drop = this.ms(DECK_SHUFFLE_DROP_MS)
    return {
      lift,
      split,
      riffle,
      bridge,
      drop,
      total: lift + split + riffle + bridge + drop,
    }
  }

  /**
   * Midpoint of a seat's field (monster row ↔ S/T row), away from the
   * phase HUD that sits on the seam between both playmats.
   */
  private ownerFieldCenter(seat: Seat): SlotPose {
    const monsters = this.zoneSlotPos(seat, 'monsters', 2)
    const spells = this.zoneSlotPos(seat, 'spellTraps', 2)
    return {
      x: monsters.x,
      y: (monsters.y + spells.y) / 2,
      scale: 1,
    }
  }

  /**
   * Table / anime riffle on the owner's field: lift the pile, split into two
   * packets, riffle the inner corners together, bridge-cascade, then drop home.
   */
  private animateDeckShuffle(seat: Seat): number {
    if (this.motionMinimal()) {
      DuelSfx.play('packSlide')
      return MINIMAL_BEAT_MS
    }
    const pile = this.deckPos(seat)
    const stage = this.ownerFieldCenter(seat)
    const { lift, split, riffle, bridge, drop, total } = this.deckShuffleTiming()
    const mine = seat === this.mySeat
    const s = DECK_SHUFFLE_SCALE
    const halfW = CARD_W * s * 0.92
    const left = { x: stage.x - halfW, y: stage.y + CARD_H * 0.04, scale: s }
    const right = { x: stage.x + halfW, y: stage.y + CARD_H * 0.04, scale: s }
    const mid = { x: stage.x, y: stage.y, scale: s }
    const stackGate = { released: false }
    const leftGate = { released: false }
    const rightGate = { released: false }
    const midGate = { released: false }
    const layer = (base: { x: number; y: number }, i: number) => ({
      x: base.x + (DECK_SHUFFLE_LAYERS - 1 - i) * 2,
      y: base.y - (DECK_SHUFFLE_LAYERS - 1 - i) * 2,
      scale: s,
    })

    this.setDeckLifted(seat, true)
    DuelSfx.play('packSlide')
    this.followAction(stage.x, stage.y, { restMs: total - drop })

    for (let i = 0; i < DECK_SHUFFLE_LAYERS; i++) {
      this.flyDeckBack(mine, pile, layer(mid, i), {
        duration: lift / 1000,
        ease: 'cubicOut',
        lift: 0.85,
        arc: 0.02,
        holdGate: stackGate,
      })
    }

    this.time.delayedCall(lift, () => {
      stackGate.released = true
      DuelSfx.play('packSlide')
      for (let i = 0; i < DECK_SHUFFLE_LAYERS; i++) {
        this.flyDeckBack(mine, layer(mid, i), layer(left, i), {
          duration: split / 1000,
          startAngle: 0,
          peakAngle: -10,
          endAngle: -16,
          ease: 'cubicOut',
          lift: 0.25,
          holdGate: leftGate,
        })
        this.flyDeckBack(mine, layer(mid, i), layer(right, i), {
          duration: split / 1000,
          startAngle: 0,
          peakAngle: 10,
          endAngle: 16,
          ease: 'cubicOut',
          lift: 0.25,
          holdGate: rightGate,
        })
      }
    })

    const riffleStart = lift + split
    const step = riffle / DECK_SHUFFLE_RIFFLE_CARDS
    for (let i = 0; i < DECK_SHUFFLE_RIFFLE_CARDS; i++) {
      const fromLeft = i % 2 === 0
      const from = fromLeft ? left : right
      const tilt = fromLeft ? -16 : 16
      this.time.delayedCall(riffleStart + i * step * 0.62, () => {
        if (i === 0 || i === 4) DuelSfx.play('packWhoosh')
        this.flyDeckBack(
          mine,
          from,
          { x: stage.x, y: stage.y - i * 1.4, scale: s },
          {
            duration: 0.2,
            startAngle: tilt,
            peakAngle: tilt * 0.35,
            endAngle: 0,
            ease: 'quadOut',
            lift: 0.2,
            holdGate: midGate,
          },
        )
      })
    }

    const bridgeStart = riffleStart + riffle
    this.time.delayedCall(bridgeStart, () => {
      leftGate.released = true
      rightGate.released = true
      DuelSfx.play('packWhoosh')
      const archY = stage.y - CARD_H * s * 0.62
      const span = CARD_W * s * 0.58
      for (let i = 0; i < 8; i++) {
        const side = i % 2 === 0 ? -1 : 1
        this.time.delayedCall(i * 38, () => {
          this.flyDeckBack(
            mine,
            mid,
            { x: stage.x + side * span, y: archY, scale: s },
            {
              duration: 0.2,
              yoyo: true,
              yoyoHold: 0.07,
              startAngle: 0,
              peakAngle: side * 30,
              endAngle: side * 8,
              ease: 'sineInOut',
              lift: 0.75,
            },
          )
        })
      }
    })

    this.time.delayedCall(bridgeStart + bridge, () => {
      midGate.released = true
      DuelSfx.play('packSlide')
      for (let i = 0; i < DECK_SHUFFLE_LAYERS; i++) {
        this.flyDeckBack(mine, layer(mid, i), pile, {
          duration: drop / 1000,
          startAngle: 0,
          peakAngle: 5,
          endAngle: 0,
          ease: 'quadIn',
          lift: 0.55,
          arc: 0.02,
        })
      }
    })
    this.time.delayedCall(total, () => this.setDeckLifted(seat, false))
    return total
  }

  /**
   * Gather this seat's hand to the row center, then commitBoard fans it back
   * from `handDisplayX` into the new order.
   */
  private animateHandShuffle(seat: Seat, order: string[], startAt = 0): number {
    if (this.motionMinimal()) {
      this.time.delayedCall(startAt, () => DuelSfx.play('packSlide'))
      return MINIMAL_BEAT_MS
    }
    const row = seat === this.mySeat ? 5 : 0
    const centerX = this.handAt(row, 0, 1).x
    const gather = this.ms(220)
    const stagger = this.ms(18)
    const hold = this.ms(80)
    const n = Math.max(order.length, 1)
    const total = gather + stagger * (n - 1) + hold
    const lift = CARD_H * 0.08

    const play = (): void => {
      DuelSfx.play('packSlide')
      this.time.delayedCall(gather, () => DuelSfx.play('packWhoosh'))

      order.forEach((uid, i) => {
        this.handDisplayX.set(uid, centerX)
        const sprite = this.handSprites.get(uid)
        if (!sprite) return
        this.boardLayer.bringToTop(sprite)
        const tilt = i % 2 === 0 ? -10 : 10
        this.tweens.add({
          targets: sprite,
          x: centerX,
          y: sprite.y - lift,
          angle: tilt,
          duration: gather,
          delay: i * stagger,
          ease: 'Cubic.easeInOut',
        })
      })
    }
    if (startAt > 0) this.time.delayedCall(startAt, play)
    else play()
    return total
  }

  /** Face-down sleeve flight used by the table riffle. */
  private flyDeckBack(
    mine: boolean,
    from: { x: number; y: number; scale?: number },
    to: { x: number; y: number; scale?: number },
    opts: {
      duration: number
      startAngle?: number
      peakAngle?: number
      endAngle?: number
      holdGate?: { released: boolean }
      yoyo?: boolean
      yoyoHold?: number
      ease?: 'cubicOut' | 'quadOut' | 'quadIn' | 'sineInOut'
      lift?: number
      arc?: number
    },
  ): void {
    this.flyOverlay(from, to, {
      cardId: '',
      mine,
      faceUp: false,
      duration: opts.duration,
      startScale: 1,
      endScale: 1,
      startAngle: opts.startAngle ?? 0,
      peakAngle: opts.peakAngle ?? 0,
      endAngle: opts.endAngle ?? 0,
      yoyo: opts.yoyo,
      yoyoHold: opts.yoyoHold,
      ease: opts.ease ?? 'sineInOut',
      lift: opts.lift ?? 0.4,
      arc: opts.arc ?? 0,
      holdGate: opts.holdGate,
    })
  }

  /** Hide or restore the Deck pile art while the overlay carries it. */
  private setDeckLifted(seat: Seat, lifted: boolean): void {
    if (lifted) this.deckLiftedSeats.add(seat)
    else this.deckLiftedSeats.delete(seat)
    if (this.sys?.isActive()) this.renderAll()
  }

  /** Own tributes free a zone. A Soul Exchange tribute does not. */
  private tributeOpensZone(uids: string[]): boolean {
    if (!this.state) return false
    const own = new Set(this.myMonsterUids())
    const freed = uids.filter((uid) => own.has(uid)).length
    const open = this.state.zones[this.mySeat].monsters.filter((c) => c === null).length
    return open + freed > 0
  }

  private isSoulTribute(uid: string): boolean {
    if (!this.state) return false
    return soulExchangeTributeUids(this.state, this.mySeat).includes(uid)
  }

  private tributeCandidateUids(): string[] {
    const mine = this.myMonsterUids()
    if (!this.state) return mine
    return [...mine, ...soulExchangeTributeUids(this.state, this.mySeat)]
  }

  private isMyTurn(): boolean {
    return !!this.state && this.state.turnPlayer === this.mySeat
  }

  private firstFree(zone: 'monsters' | 'spellTraps', seat: Seat): number {
    if (!this.state) return -1
    return this.state.zones[seat][zone].findIndex((c) => c === null)
  }

  /** Whether a spell/trap (by uid) can be activated right now, per the authoritative state. */
  private isActivatable(uid: string): boolean {
    return this.state?.activatableUids?.includes(uid) ?? false
  }

  /**
   * Why this Equip cannot be activated right now, or null when it is not an Equip.
   * The menu used to offer only Set, with no reason, so a type-locked Equip
   * looked broken (Mystical Moon on a Beast or a Warrior).
   */
  private equipBlockToast(cardId: string): string | null {
    const cfg = getEquipConfig(Number(cardId))
    if (!cfg) return null
    if (cfg.raceFilter) {
      return t('duel.toast.equipNeedsRace', { race: t(`card.race.${cfg.raceFilter}`) })
    }
    if (cfg.attributeFilter) {
      return t('duel.toast.equipNeedsAttr', { attr: t(`card.attr.${cfg.attributeFilter}`) })
    }
    if (cfg.excludeRace) {
      return t('duel.toast.equipNotRace', { race: t(`card.race.${cfg.excludeRace}`) })
    }
    return t('duel.toast.equipNeedsMonster')
  }

  /** Card the engine listed on the open trap / Ultimate Offering prompt. */
  private priorityActivatable(uid: string): boolean {
    const pending = this.state?.pendingPriority
    return !!pending && pending.responder === this.mySeat && pending.activatableUids.includes(uid)
  }

  /**
   * Face-down trap of ours during our response window. Auto may not have
   * listed it. The tap still names this card.
   */
  private ownSetTrapDuringPriority(card: CardInstance): boolean {
    const pending = this.state?.pendingPriority
    if (!pending || pending.responder !== this.mySeat || card.faceUp) return false
    const data = CARD_INDEX.get(card.cardId)
    return !!data && getCardKind(data) === CardKind.TRAP
  }

  private myMonsterUids(): string[] {
    return this.monsterUids(this.mySeat)
  }

  private monsterUids(seat: Seat): string[] {
    if (!this.state) return []
    return this.state.zones[seat].monsters.filter((c): c is CardInstance => c !== null).map((c) => c.uid)
  }
}
